// Translation dictionaries for the platform

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext, CurrentTenantContext } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';

/** UI locale codes mirrored in Settings → Language */
export const ALLOWED_UI_LANGUAGE_CODES = new Set(['en', 'es', 'pt-BR', 'fr']);

/** Fallback when neither profile nor ui-language storage is set (per-tenant UX default). */
export function getDefaultUiLanguageForTenant(tenantId) {
  if (!tenantId) return 'en';
  if (tenantId === 'calmedica') return 'fr';
  return 'en';
}

function resolveEffectiveTenantIdForLanguage(user, currentTenantOverride) {
  const override =
    typeof currentTenantOverride === 'string' && currentTenantOverride.trim()
      ? currentTenantOverride.trim()
      : null;
  if (override) return override;
  if (typeof window === 'undefined') return 'instructure';
  const role = user?.role || localStorage.getItem('user-role');
  const adminActive = role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
  const sessionTenant = typeof window !== 'undefined' ? window.__NEO_SESSION_TENANT_ID : null;
  const fallbackTenant = localStorage.getItem('user-tenant-id');
  return resolveEffectiveTenant(adminActive, sessionTenant, user || { tenant_id: fallbackTenant, role }, 'instructure');
}

/**
 * Active UI language: ui-language storage (explicit save) → profile → tenant default → en.
 * localStorage is checked first so a successful Settings save applies immediately on reload
 * even when PATCH /api/user/me does not persist ui_language (common on procure-only tenants).
 * `currentTenantOverride` should be Layout's resolved active tenant when available.
 *
 * Also sent to chatbot APIs as `language` (`en` | `es` | `pt-BR` | `fr`).
 */
export function resolvePlatformUiLanguage(user, currentTenantOverride) {
  if (typeof window !== 'undefined') {
    const fromStorage = localStorage.getItem('ui-language');
    if (fromStorage && ALLOWED_UI_LANGUAGE_CODES.has(fromStorage)) return fromStorage;
  }

  const fromProfile = typeof user?.ui_language === 'string' ? user.ui_language.trim() : '';
  if (fromProfile && ALLOWED_UI_LANGUAGE_CODES.has(fromProfile)) return fromProfile;

  const tenantId = resolveEffectiveTenantIdForLanguage(user, currentTenantOverride);
  return getDefaultUiLanguageForTenant(tenantId);
}

/**
 * When a translation key is missing in both the active locale and English,
 * turn camelCase / snake_case ids into readable labels (e.g. accountsWithSignals → Accounts With Signals).
 */
function humanizeTranslationKey(key) {
  if (key == null || typeof key !== 'string') return String(key ?? '');
  const trimmed = key.trim();
  if (!trimmed) return key;
  if (/\s/.test(trimmed)) return trimmed;
  const looksLikeId =
    /^[A-Za-z][A-Za-z0-9_$]*$/.test(trimmed) &&
    (trimmed.includes('_') || (/[a-z]/.test(trimmed) && /[A-Z]/.test(trimmed)));
  if (!looksLikeId) return trimmed;
  const spaced = trimmed
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      const lower = w.toLowerCase();
      if (lower === 'neo') return 'NEO';
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(' ');
}

export const translations = {
  en: {
    home: 'Home',
    market: 'Market',
    accounts: 'Accounts',
    people: 'People',
    signals: 'Signals',
    procurement: 'Procurement',
    playbooks: 'Playbooks',
    settings: 'Settings',
    help: 'Help',
    admin: 'Admin',
    segments: 'Segments',
    analytics: 'Analytics',
    outbox: 'Outbox',
    logout: 'Log out',
    
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    export: 'Export',
    retry: 'Retry',
    refresh: 'Refresh',
    backTo: 'Back to',
    viewProfile: 'View Profile',
    noDataAvailable: 'No data available.',

    // NEO Chat panel
    neoChatDefaultGreeting: "Hello! I'm NEO, your GTM assistant. How can I help you today?",
    neoChatAssistantTitle: 'NEO Assistant',
    neoChatNewChat: 'New chat',
    neoChatInputPlaceholder: 'Ask NEO anything...',
    neoChatLoadingChat: 'Loading chat',
    neoChatChipShowTenders: 'Show tenders',
    neoChatPromptShowTenders: 'Show me relevant tenders',
    neoChatChipMeetingPrep: 'Meeting Prep',
    neoChatPromptMeetingPrep: 'Prepare me for a meeting',
    neoChatStepAccount: 'Account',
    neoChatStepType: 'Type',
    neoChatStepAttendees: 'Attendees',
    neoChatSelectAccount: 'Select Account',
    neoChatLoadingAccounts: 'Loading accounts...',
    neoChatSearchAccounts: 'Search accounts...',
    neoChatNoAccountsFound: 'No accounts found.',
    neoChatSelectMeetingType: 'Select Meeting Type',
    neoChatMeetingWith: 'Meeting with {accountName}',
    neoChatAskingAbout: 'Asking about {accountName}',
    neoChatClearAccount: 'Stop asking about this account',
    neoChatBack: 'Back',
    neoChatSelectAttendeesOptional: 'Select Attendees (Optional)',
    neoChatLoadingPeople: 'Loading people...',
    neoChatSearchPeople: 'Search people...',
    neoChatNoPeopleFound: 'No people found',
    neoChatSkip: 'Skip',
    neoChatGenerate: 'Generate',
    neoChatMeetingDiscovery: 'Discovery Call',
    neoChatMeetingDemo: 'Demo / Presentation',
    neoChatMeetingProposal: 'Proposal Review',
    neoChatMeetingNegotiation: 'Negotiation',
    neoChatMeetingRenewal: 'Renewal Discussion',
    neoChatMeetingQbr: 'QBR / Review',
    neoChatPrepareMeetingUser: 'Prepare me for a {meetingType} with {accountName}',
    neoChatGiveBriefOn: 'Give me a brief on {accountName}',
    neoChatBriefOnAccountName: 'Give me a brief on {account_name}',
    neoChatErrorCallNeo: "Sorry, I couldn't call NEO: {error}",
    neoChatErrorMeetingPrep: "Sorry, I couldn't generate the meeting prep brief. {error}",
    neoChatErrorMeetingPrepTimeout:
      'The request timed out. Meeting prep can take up to a minute — please try again.',
    neoChatErrorAccountBrief: "Sorry, I couldn't generate the account brief. Error: {error}",
    neoChatErrorOutreach: "Sorry, I couldn't generate the outreach message right now. Error: {error}",
    neoChatErrorTenders: "Sorry, I couldn't fetch tender information. {hint}",
    neoChatNoDebrief:
      "I couldn't find a detailed brief for **{accountName}**. This usually means we don't have enough data yet. Please try again later or ask a different question.",
    neoChatBriefGreetingFallback: 'Hey',
    neoChatBriefThings1: 'one thing worth your time',
    neoChatBriefThings2: 'two things worth your time',
    neoChatBriefThings3: 'three things worth your time',
    neoChatBriefThings4: 'four things worth your time',
    neoChatBriefThings5: 'five things worth your time',
    neoChatTriggerIntroOne: 'Just in:',
    neoChatTriggerIntroMany: 'Just in — fresh activity on your accounts:',
    neoChatSelectAccountAndPerson: 'Select Account & Person',
    neoChatAccountLabel: 'Account',
    neoChatPersonLabel: 'Person',
    neoChatSelectAccountPlaceholder: 'Select account...',
    neoChatSelectPersonPlaceholder: 'Select person...',
    neoChatNoPeopleForAccount: 'No people found for this account',
    neoChatConfigureOutreachTo: 'Configure outreach to {name}',
    neoChatAtAccount: 'at {accountName}',
    neoChatBackToPeople: 'Back to people selection',
    neoChatChannelLabel: 'Channel',
    neoChatToneLabel: 'Tone',
    neoChatMessageTypeLabel: 'Message Type',
    neoChatContextTriggerLabel: 'Context/Trigger',
    neoChatGenerating: 'Generating...',
    neoChatGenerateMessage: 'Generate Message',
    neoChatGenerateSequence: 'Generate 3-Message Sequence',
    neoChatOutreachContextPlaceholder: "e.g., 'Following up on their recent LinkedIn post about AI'",
    
    // Headers
    dashboard: 'Dashboard',
    accountOverview: 'Account Overview',
    
    // Tabs
    overview: 'Overview',
    details: 'Details',
    activity: 'Activity',
    notes: 'Notes',
    connections: 'Connections',
    connectionsEmpty: 'No known connections yet for this person.',
    withConnections: 'With connections',
    withoutConnections: 'Without connections',
    inPursuit: 'In pursuit',
    notInPursuit: 'Not in pursuit',
    almaMater: 'Alma mater',
    connectionInferred: 'inferred',
    personas: 'Personas',
    departments: 'Departments',
    
    // Account Page
    techStack: 'Tech Stack',
    liveDeals: 'Live Deals',
    decisionWindow: 'Decision Window',
    renewalDate: 'Renewal Date',
    momentum: 'Momentum',
    strategicPriorities: 'Strategic Priorities',
    institutionalChallenges: 'Institutional Challenges',
    studentExpectations: 'Student Expectations',
    financialSituation: 'Financial Situation',
    marketConditions: 'Market Conditions',
    keyContacts: 'Key Contacts',
    procurementProfile: 'Procurement Profile',
    accountNotFound: 'Account not found',
    accountNotFoundDesc: 'The requested account could not be loaded or does not exist.',
    rateLimitReached: 'Rate limit reached. Showing cached data. Please wait 5 minutes before retrying.',
    showingCachedData: 'Showing cached data. Click refresh for latest updates.',
    
    // Charts & Metrics
    fitScore: 'Fit Score',
    excellent: 'Excellent',
    strong: 'Strong',
    moderate: 'Moderate',
    low: 'Low',
    scoreRationale: 'Score Rationale',
    noRationaleAvailable: 'No rationale available.',
    riskRationale: 'Risk Rationale',
    
    // People & Personas
    personasOrganizedBy: 'Personas are organized by buying committee role and ranked by their influence and fit score. Click any contact to view their full profile and activity.',
    addedToWatchlist: 'Added to watchlist',
    removedFromWatchlist: 'Removed from watchlist',
    
    // Procurement
    procurementFriendliness: 'Procurement Friendliness',
    friendly: 'Friendly',
    standard: 'Standard',
    bureaucratic: 'Bureaucratic',
    monitor: 'Monitor',
    
    // Decision Window
    lessThan12Months: '<12 months',
    months12to24: '12-24 months',
    moreThan24Months: '>24 months',
    notApplicable: 'N/A',
    decisionWindowRationale: 'Decision Window Rationale',
    
    // Signals
    momentumUpdate: 'Momentum Update',
    momentumScoreChanged: 'Momentum score changed by',
    points: 'points',
    
    // Status
    live: 'Live',
    closed: 'Closed',
    awarded: 'Awarded',
    cancelled: 'Cancelled',
    
    // Confidence levels
    high: 'High',
    medium: 'Medium',
    urgencyHigh: 'High urgency',
    urgencyMedium: 'Medium urgency',
    urgencyLow: 'Low urgency',
    priorityHigh: 'High priority',
    priorityMedium: 'Medium priority',
    priorityLow: 'Low priority',
    priorityTooltipLead: 'Priority blends how well this person fits with how fresh the signal is.',
    
    // Procurement Profile
    fieldMetric: 'Field / Metric',
    value: 'Value',
    source: 'Source',
    confidence: 'Confidence',
    procurementIntelligence: 'Comprehensive procurement intelligence and supplier pathway analysis for this institution.',
    procurementProfileNA: 'Procurement profile data is not available for this account.',
    
    // Account Header
    students: 'students',
    employees: 'employees',
    accountFitRationale: 'Account Fit Rationale',
    excellentFit: 'Excellent Fit',
    strongFit: 'Strong Fit',
    moderateFit: 'Moderate Fit',
    lowFit: 'Low Fit',
    client: 'client',
    buyingModelRationale: 'Buying Model Rationale',
    bvoClassification: 'BVO Classification',
    incumbentSentiment: 'Incumbent Sentiment',
    procurementFriendlyLabel: 'Friendly',
    procurementStandardLabel: 'Standard',
    procurementBureaucraticLabel: 'Bureaucratic',
    procurementMonitorLabel: 'Monitor',
    bvoBudgetDriven: 'Budget-Driven',
    bvoValueSeeker: 'Value-Seeker',
    bvoOutcomeFocused: 'Outcome-Focused',
    bvoInnovationDriven: 'Innovation-Driven',
    bvoRiskAverse: 'Risk-Averse',
    bvoComplianceFocused: 'Compliance-Focused',
    sentimentAtRisk: 'At risk',
    sentimentPositive: 'Positive',
    sentimentNeutral: 'Neutral',
    sentimentNegative: 'Negative',
    sentimentDissatisfied: 'Dissatisfied',
    unknown: 'Unknown',
    numberOfEmployees: 'Number of Employees',
    departmentFitRationale: 'Fit Rationale',
    
    // Procurement Friendliness
    friendlyDesc: 'This institution has streamlined procurement processes and is generally receptive to new vendor relationships.',
    standardDesc: 'This institution follows typical procurement procedures with standard evaluation timelines.',
    bureaucraticDesc: 'This institution has complex procurement processes that may require longer sales cycles and additional approvals.',
    monitorDesc: 'This institution has challenging procurement dynamics that require careful monitoring and strategic engagement.',
    procurementProcessClassification: 'Procurement process classification for this institution.',
    
    // Decision Window
    decisionWindowMonitor: 'Decision Window Monitor',
    decisionWindowDesc: 'Shows when a contract is likely up for review. Expiry dates are estimated using procurement records, typical contract cycles, and renewal notices. When data is missing, we project based on award dates and standard patterns, always tagging with confidence levels.',
    
    // Tech Stack
    noTechData: 'No specified tech data',
    
    // Tenant-specific fields
    pedagogicalModel: 'Pedagogical & Cultural Model',
    digitalLandscape: 'Digital Landscape',
    digitalFunding: 'Digital Funding Opportunities',
    learningInitiatives: 'Learning Development Initiatives',
    patientCareGaps: 'Patient Care Gaps',
    decisionDynamics: 'Stakeholder Decision Dynamics',
    contentCoverage: 'Content Coverage',
    clientOpportunities: 'Client Opportunities',
    decisionMakers: 'Decision Makers',
    engagementHooks: 'Engagement Hooks',
    recommendedTactics: 'Recommended Tactics',
    
    // People Page
    keyDecisionMakers: 'Key decision makers and influencers',
    searchPeople: 'Search people by name, role, or university...',
    name: 'Name',
    account: 'Account',
    country: 'Country',
    department: 'Department',
    archetype: 'Archetype',
    pursuit: 'Pursuit',
    socialScore: 'Social Score',
    noPeopleFound: 'No people found',
    adjustFilters: 'Try adjusting your search or filters.',
    formerClient: 'Former Client',
    previouslyWorked: 'Previously worked for one of our customers',
    socialPresence: 'Measures the persona\'s visibility and influence on social platforms like LinkedIn.',
    personaFitRationale: 'Persona Fit Rationale',
    strategic: 'Strategic',
    supportive: 'Supportive',
    passive: 'Passive',
    showContactInfo: 'Show Contact Info',
    satisfied: 'Satisfied',
    neutral: 'Neutral',
    frustrated: 'Frustrated',
    exploringAlternatives: 'Exploring alternatives',
    
    // Signals Page
    trackSignals: 'Track real-time account momentum and persona activity to identify opportunities and engage at the right moment.',
    momentumScoreAccount: 'Momentum Score (Account Level)',
    momentumScoreDesc: 'The Momentum Score reflects weekly organizational developments that make the environment more or less favourable for the client\'s engagement, expansion, or renewal.',
    signalsCaptured: 'Signals Captured:',
    scoreDerivation: 'Score Derivation:',
    personaActivityScore: 'Persona Activity Score (Individual Level)',
    personaActivityDesc: 'The Persona Activity Score tracks how visibly and substantively a contact contributes to digital, operational, or strategic change in areas relevant to the client\'s solution space.',
    allSignals: 'All Signals',
    filterBySignal: 'Filter by signal type',
    allTime: 'All Time',
    last1Day: 'Last 1 day',
    last7Days: 'Last 7 days',
    last14Days: 'Last 14 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    minScore: 'Min score',
    noAccountSignals: 'No account signals found',
    noPersonaSignals: 'No persona signals found',
    recently: 'recently',
    
    // Playbooks Page
    structuredEngagement: 'Structured engagement strategies for different scenarios',
    createPlaybook: 'Create Playbook',
    searchPlaybooks: 'Search playbooks...',
    filterByFactor: 'Filter by factor',
    allFactors: 'All Factors',
    eventPlaybooks: 'Event Playbooks',
    targetedSequences: 'Run targeted sequences for your upcoming events',
    noUpcomingEvents: 'No upcoming events',
    addEventsInSettings: 'Add Events in Settings',
    selectEvent: 'Select Event',
    sessionInviteBlockedTitle: 'Session details required for Session Invite',
    sessionInviteEventBanner:
      'Session Invite needs a session title and description for the selected event. Edit the event in Settings → Events and complete Session Details.',
    sessionInviteMissingDetailsTip:
      'Tip: this event has no session details yet. Messages will reference the event generically — add a session title in Settings → Events for stronger invites.',
    openEventsSettings: 'Open Events settings',
    sessionInviteRequiresSessionDetails:
      'Requires session title and description in Settings → Events',
    sessionInviteReady: 'Session title and description configured',
    quickConnectRequiresAttendees: 'Requires at least one confirmed attendee',
    chooseEvent: 'Choose an event',
    daysAway: 'days away',
    today: 'Today',
    previewAndApply: 'Preview & Apply',
    yourCustomPlaybooks: 'Your Custom Playbooks',
    custom: 'Custom',
    allSectors: 'All Sectors',
    sectors: 'Sectors',
    steps: 'Steps',
    clone: 'Clone',
    standardPlaybooks: 'Standard Playbooks',
    goal: 'Goal',
    enrolledAccounts: 'Enrolled accounts',
    untouchedAccounts: 'Untouched accounts',
    noPlaybooks: 'No playbooks found matching your criteria',
    deletePlaybook: 'Delete Playbook',
    deletePlaybookConfirm: 'Are you sure you want to delete',
    cannotUndo: 'This action cannot be undone.',
    playbookDeleted: 'Playbook deleted',
    failedToDelete: 'Failed to delete playbook',
    selectAccountFor: 'Select Account for',
    chooseAccountToApply: 'Choose which account to apply this playbook to',
    noEventSelected: 'No event selected',
    selectEventFirst: 'Please select an event first or add events in Settings',
    noEligibleAccounts: 'No eligible accounts',
    
    // Segments Page
    createManageExport: 'Create, manage, and export target lists',
    buildNewSegment: 'Build New Segment',
    rules: 'rules',
    created: 'Created',
    viewResults: 'View Results',
    noSegmentsYet: 'No segments yet',
    getStartedSegment: 'Get started by building your first segment.',
    deleteSegment: 'Delete Segment',
    deleteSegmentConfirm: 'Are you sure you want to delete',
    segmentDeleted: 'Segment deleted successfully',
    failedToDeleteSegment: 'Failed to delete segment',
    authRequired: 'Authentication Required',
    loginToAccess: 'Please log in to access segments.',
    logIn: 'Log In',
    
    // Home Page
    yourWatchlist: 'Your Watchlist',
    watchlistTooltip: 'Your Watchlist tracks key personas you\'re actively engaging with. These individuals are flagged for ongoing relationship building and are prioritized in your workflow.',
    open: 'Open',
    untrack: 'Untrack',
    moreTracked: 'more tracked personas…',
    refreshing: 'Refreshing…',
    
    // Procurement Page
    dealsTenders: 'Deals & Tenders',
    trackTenders: 'Track open tenders and closed deals across the market.',
    searchKeyword: 'Search by keyword, title, institution...',
    liveDealsCount: 'Live Deals',
    recentlyClosed: 'Recently Expired',
    closedDeals: 'Closed Deals',
    allInstitutions: 'All Institutions',
    filterByInstitution: 'Filter by Institution',
    allCategories: 'All Categories',
    filterByCategory: 'Filter by Category',
    filterByEvent: 'Filter by Event',
    allEvents: 'All Events',
    allPersonas: 'All personas',
    formerClientOnly: 'Former Client only',
    minSocialScore: 'Min social score',
    selectAll: 'Select all',
    clearAll: 'Clear all',
    clearFilters: 'Clear filters',
    typeToSearch: 'Type to search...',
    category: 'Category',
    vendor: 'Vendor',
    institutionType: 'Institution type',
    sentiment: 'Sentiment',
    listOnly: 'List only',
    mapPlusList: 'Map + List',
    allDeadlines: 'All deadlines',
    days0_7: '0–7 days',
    days8_30: '8–30 days',
    days31_90: '31–90 days',
    days90Plus: '90+ days',
    months0_6: '0–6 months',
    months6_12: '6–12 months',
    months12_24: '12–24 months',
    unknownExpired: 'Unknown / Expired',
    allVendors: 'All vendors',
    exit: 'Exit',
    filterByExitLeverage: 'Filter by exit leverage',
    some: 'Some',
    noSignal: 'No signal',
    loadingDeals: 'Loading deals...',
    loadingContractIntelligence: 'Loading Contract Intelligence...',
    loadingMoreDeals: 'Loading more deals...',
    loadedDeals: 'Loaded {count} {label} deals',
    outOfScope: 'Out of scope',
    pending: 'Pending',
    insufficientInfo: 'Insufficient Info',
    liveRadar: 'Live Radar',
    myDeals: 'My Deals',
    recentlyExpired: 'Recently Expired',
    contractIntelligence: 'Contract Intelligence',
    displacementRadar: 'Displacement Radar',
    benchmarking: 'Benchmarking',
    selectCategoryBenchmarkTitle: 'Select a category to view benchmarking',
    selectCategoryBenchmarkDesc: 'Use the Category filter above to choose one or more procurement categories. Benchmark data (enrolment vs $/student/year) will appear here.',
    noClosedDealsBenchmarkTitle: 'No closed deals available',
    noClosedDealsBenchmarkDesc: 'Benchmarking requires closed deals. Ensure the bootstrap returns closed deals for your tenant.',
    selectCategoryContractTitle: 'Select a category to explore contract intelligence',
    selectCategoryContractDesc: 'Use the Category filter above to choose one or more procurement categories. Historical contracts and competitor data will appear here.',
    loadingContractData: 'Loading contract data…',
    alertSettings: 'Alert Settings',
    title: 'Title',
    institution: 'Institution',
    deadline: 'Deadline',
    productFitPanel: 'Product Fit',
    noLiveDeals: 'No live deals found',
    noRecordsSelectedFilters: 'No records found for the selected category and filters. Try different categories or clear filters.',
    marketMap: 'Market map',
    marketMapHelpText: 'Click a country to filter the deals table below and see incumbent market share (pie chart). Use Vendor and Sentiment filters to refine.',
    noDealsCountryFilters: 'No deals in this country for the current filters.',
    noAttackableAccounts: 'No attackable accounts match the criteria.',
    previous: 'Previous',
    next: 'Next',
    product: 'Product',
    reseller: 'Reseller',
    procurementMethod: 'Procurement method',
    routeToBuy: 'Route to Buy',
    framework: 'Framework',
    productName: 'Product Name',
    supplier: 'Supplier',
    predictedExpiry: 'Predicted Expiry',
    
    // Product Fit (Settings)
    productFitProfileDesc: 'Define product profiles for fit scoring. Several profiles can be active at once: they drive Light Fit on Live Radar (category match) and you pick one profile when running Deep Fit on a tender.',
    createProfile: 'Create Profile',
    editProfile: 'Edit Profile',
    newProfile: 'New Profile',
    productFitName: 'Name',
    productFitPrimaryCategory: 'Primary Category',
    productFitCategories: 'Categories',
    productFitCategoriesDesc: 'Same category list as Procurement for your tenant (from live and closed deals).',
    productFitCategoriesLoading: 'Loading procurement categories…',
    productFitCategoriesFallback: 'Could not load procurement categories; showing full taxonomy. Save again after Procurement loads, or check the API.',
    productFitNoCategoriesSelected: 'No categories selected — profile will not match deals by category.',
    productFitCategoryTags: 'Category Tags (comma-separated)',
    productFitStrongSignals: 'Strong Fit Signals (one per line)',
    productFitNegativeSignals: 'Negative Signals (Hard: or Soft: prefix, one per line)',
    productFitDeploymentModels: 'Deployment Models',
    productFitCloud: 'Cloud',
    productFitSelfHosted: 'Self-hosted',
    productFitHybrid: 'Hybrid',
    productFitIntegrations: 'Integrations (comma-separated)',
    productFitSecurity: 'Security Certifications (comma-separated)',
    productFitKeyFeatures: 'Key Features (comma-separated)',
    productFitNameExample: 'e.g. LMS Core',
    productFitCategoryTagsExample: 'LMS, EdTech, Assessment',
    productFitStrongSignalsExample: 'Canvas integration required\nLTI 1.3 support\nSCORM compliance',
    productFitNegativeSignalsExample: 'Hard: Must use Microsoft SSO\nSoft: Prefers cloud deployment',
    productFitIntegrationsExample: 'Canvas, Moodle, LTI 1.3',
    productFitSecurityExample: 'ISO 27001, SOC 2',
    productFitKeyFeaturesExample: 'Analytics, Mobile app, Accessibility',
    productFitUpdate: 'Update',
    productFitCreate: 'Create',
    productFitProfiles: 'Profiles',
    productFitNoProfiles: 'No profiles yet. Create one above.',
    productFitSetAsActive: 'Set as Active',
    productFitDeleteProfile: 'Delete profile?',
    productFitDeleteConfirm: 'This will permanently delete "{name}". This action cannot be undone.',
    productFitSelectPrimaryCategory: 'Select primary category',
    productFitNone: 'None',
    productFitActive: 'Active',
    productFitDeactivate: 'Set as inactive',
    productFitActivateError: 'Could not activate profile',
    productFitDeactivateError: 'Could not deactivate profile',
    loadingProductProfiles: 'Loading product profiles...',
    
    // Person Profile
    backToPeople: 'Back to People',
    back: 'Back',
    personNotFound: 'Person not found',
    personNotFoundDesc: 'The requested person could not be loaded or does not exist.',
    signalsCount: 'Signals',
    interactions: 'Interactions',
    primaryInterests: 'Primary Interests',
    formerEmployers: 'Former Employers',
    education: 'Education',
    professionalMemberships: 'Professional Memberships',
    publicSpeakingEvents: 'Public Speaking & Events',
    recentEventsAttended: 'Recent Events Attended',
    awardsRecognition: 'Awards & Recognition',
    publications: 'Publications',
    publicationContext: 'Publication',
    firstAuthorBadge: 'First author',
    seniorAuthorBadge: 'Senior author',
    relevanceTierVeryStrong: 'Very strong match',
    relevanceTierStrong: 'Strong match',
    showAllPublications: 'Show all ({count})',
    showLess: 'Show less',
    removeFromPursuit: '✓ Remove from Pursuit',
    addToPursuit: 'Add to Pursuit',
    assignMarketTier: 'Assign Market Tier',
    tier: 'Tier',
    selectTier: 'Select a tier',
    saveChanges: 'Save changes',
    atAGlance: 'At a Glance',
    keyInitiatives: 'Key initiatives and focus areas.',
    noInitiatives: 'No initiatives listed.',
    
    // AI Outreach
    aiOutreach: 'AI Outreach',
    channel: 'Channel',
    email: 'Email',
    linkedin: 'LinkedIn',
    call: 'Call',
    tone: 'Tone',
    relationshipBuilder: 'Relationship Builder',
    thoughtLeadership: 'Thought Leadership',
    challenger: 'Challenger',
    context: 'Context',
    autoDetect: 'Auto-detect best context',
    personaInitiative: 'Persona initiatives & activity',
    institutionalChange: 'Institutional changes & momentum',
    strategicPriority: 'Strategic priorities',
    procurementSignal: 'Procurement and funding signals',
    customContext: 'Custom Context (optional)',
    addContext: 'Add specific context, talking points, or instructions...',
    generating: 'Generating...',
    generateMessage: 'Generate Message',
    
    // Home Cards
    recommendedAccounts: 'Recommended Accounts',
    recommendedAccountsTooltip: 'Highest scoring accounts based on fit and strategic alignment.',
    viewAll: 'View All',
    noAccounts: 'No accounts',
    recommendedPersonas: 'Recommended Personas',
    recommendedPersonasTooltip: 'Most relevant people based on influence and role alignment.',
    noPersonas: 'No personas',
    
    // Action Center
    priorityActions: 'Priority Actions',
    deals: 'Deals',
    all: 'All',
    myTasks: 'My Tasks',
    newLeads: 'New Leads',
    updated: 'Updated',
    loadingRecommendations: 'Loading Your Personalised Recommendations',
    analyzingPipeline: 'Analyzing your pipeline...',
    dueToday: 'Due Today',
    completeTouchVia: 'Complete touch',
    via: 'via',
    openDraft: 'Open Draft',
    noContactOnFileOpenPersonas: 'No contact on file at this account — open the Personas tab to add one.',
    openLinkedIn: 'Open LinkedIn',
    callNow: 'Call Now',
    done: 'Done',
    whyNow: 'Why now?',
    nextSteps: 'Next steps:',
    tracked: 'Tracked',
    fit: 'Fit',
    draftOutreach: 'Draft outreach',
    draftMessage: 'Draft message',
    noTasksDue: 'No tasks due today',
    noNewLeads: 'No new leads yet',
    noActionsYet: 'No actions yet',
    hitRefresh: 'Hit Refresh and I\'ll fetch what\'s next for you.',
    hotMovers: 'Hot Movers (Last 2 Weeks)',
    personaUpdates: 'Persona Updates (Last 2 Weeks)',
    noSignals2Weeks: 'No signals in the last 2 weeks',
    liveDealsTitle: 'Live Deals',
    noDeadline: 'No deadline',
    daysLeft: 'd left',
    expired: 'Expired',
    noLiveDealsCategories: 'No live deals in your selected categories.',
    noLiveDealsFound: 'No live deals found.',
    decisionWindowRadar: 'Decision Window Radar',
    decisionWindowTooltip: 'Shows when a contract is likely up for review based on procurement records and renewal patterns.',
    accountsApproaching: 'Accounts approaching renewal/decision horizons',
    noAccountsInBand: 'No accounts in this band.',
    noAccountsWithWindows: 'No accounts with decision windows found.',

    // Today's Plan
    todaysPlanTitle: "Today's Plan",
    todaysPlanAccountFit: 'Account Fit',
    todaysPlanCategoryAccountResearch: 'Account research',
    todaysPlanCategoryFitMomentum: 'Fit & Momentum',
    todaysPlanCategoryOutreach: 'Outreach',
    todaysPlanCategoryNewLeads: 'New leads',
    openPersonas: 'Open Personas',
    track: 'Track',
    impactSuffix: 'Impact',
    suggestedNextSteps: 'Suggested next steps',
    markRecommendationDone: 'Mark this recommendation as done',
    todaysPlanPressRefresh: "Press the Refresh button above to get your personalized next best moves.",
    todaysTasksTitle: "Today's Tasks",
    allCaughtUp: 'All caught up!',
    noTasksScheduledToday: 'No tasks scheduled for today',
    tasksCount: '{count} tasks',
    touchNumberLabel: 'Touch #{number}',
    sendEmail: 'Send Email',
    linkedinMessage: 'LinkedIn Message',
    makeCall: 'Make Call',
    taskCompletedTitle: 'Task completed!',
    taskCompletedDescription: 'Nice work, keep it up',
    taskCompleteError: 'Failed to mark task as complete',
    unknownAccount: 'Unknown Account',
    unknownContact: 'Unknown contact',
    completedFromTodaysPlan: "Completed from Today's Plan",
    researchGapBuyer: 'No confirmed budget owner on the Sales Map',
    researchGapChampion: 'No internal champion identified yet',
    researchGapBoth: 'Neither budget owner nor champion confirmed',
    planReasonLeadership: 'New or changing leadership — priorities may reset.',
    planReasonProcurement: 'Procurement has news (process, policy, or timeline).',
    planReasonLiveDeal: "There's an active deal to progress.",
    planReasonBoard: 'Board meeting coming up — decisions may accelerate.',
    planReasonMomentum: 'Recent engagement at the account looks strong.',
    planReasonPersonaActivity: "They've been active recently.",
    planReasonFit: 'Strong fit for us (account/persona).',
    planReasonDecisionWindowUnknown: 'Decision timing is unclear — confirm the timeline.',
    planReasonDecisionWindowMonths: 'Decision expected in ~{months} months.',
    planReasonDecisionWindowGeneric: 'Within the decision window.',
    planReasonPursuit: "You're tracking this person (Pursuit).",
    todaysPlanRateLimitActive: 'Rate limit active',
    todaysPlanCannotLoadRetry: "Cannot load Today's Plan. Will retry in {remaining}.",
    todaysPlanRateLimitReached: 'Rate limit reached',
    todaysPlanShowingCached: 'Showing cached plan. Will retry automatically in 20 minutes.',
    todaysPlanCannotLoadWait: "Could not load Today's Plan. Please wait 20 minutes.",
    todaysPlanCouldNotLoad: "Could not load Today's Plan.",
    todaysPlanCannotRefreshRetry: "Cannot refresh Today's Plan. Will retry automatically in {remaining}.",
    todaysPlanRefreshed: 'Plan refreshed',
    todaysPlanRefreshedDescription: "Today's plan has been updated with the latest recommendations.",
    todaysPlanRefreshRateLimit: 'Could not refresh the plan due to rate limit. Will retry automatically in 20 minutes.',
    todaysPlanRefreshFailed: 'Could not refresh the plan. Please try again.',
    todaysPlanRefreshFailedTitle: 'Refresh failed',
    todaysPlanAckRateLimit: 'Item acknowledged locally. Will sync when rate limit clears (up to 20 minutes).',
    todaysPlanCouldNotSaveAck: 'Could not save acknowledgment.',
    couldNotUpdatePursuit: "Couldn't update Pursuit",
    pleaseTryAgain: 'Please try again.',
    
    // Greetings
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    
    // Accounts Page
    manageExploreInstitutions: 'Manage and explore target institutions',
    searchAccounts: 'Search accounts...',
    accountFitScore: 'Account Fit Score',
    renewalRisk: 'Renewal Risk',
    expansionOpps: 'Expansion Opps',
    fariaProducts: 'Faria Products',
    departmentFitScore: 'Department Fit Score',
    noAccountsFound: 'No accounts found',
    
    // Market Intelligence
    marketIntelligence: 'Market Opportunities',
    macroTrends: 'Macro trends and opportunities in your target markets',
    buyingActivity: 'Buying Activity',
    players: 'Dynamics',
    marketMomentumTrend: 'Market Momentum Trend (12 weeks)',
    noMomentumData: 'No momentum data available',
    allCountries: 'All Countries',
    filterByCountry: 'Filter by country',
    filterByDepartment: 'Filter by department',
    allDepartments: 'All Departments',
    
    // Market Components
    topAccounts: 'Top Accounts',
    topPersonas: 'Top Personas',
    tieringSnapshot: 'Tiering Snapshot',
    tieringTooltip: 'Accounts are initially tiered based on their Account Fit Score. Tiering helps you prioritise where to focus—so you spend more time on the highest-potential accounts, run more targeted outreach, and drive better results. You can override any tier at any time on each individual account.',
    aiReadinessIndex: 'AI Readiness Index 2025',
    topInstitutionsAI: 'Top institutions by AI adoption and implementation',
    topHospitalsAI: 'Top hospitals by AI adoption and implementation',
    topSchoolsAI: 'Top international schools by AI adoption and implementation',
    topEnterprisesAI: 'Top enterprises by AI adoption and implementation',
    topCompaniesAI: 'Top companies by AI adoption and implementation',
    topAuthoritiesAI: 'Top education authorities by AI adoption and implementation',
    marketFitDistribution: 'Market Fit Distribution',
    accountFitComposition: 'Account fit score composition across 10-point bands',
    topBuyers: 'Top Buyers',
    universitiesMostVendors: 'Universities with most new vendor deployments',
    enterprisesMostVendors: 'Enterprises with most new vendor deployments',
    keyBuyingAreas: 'Key Buying Areas',
    functionalInvestment: 'Functional categories with most investment',
    procurementComplexity: 'Procurement Complexity',
    distributionProfiles: 'Distribution of procurement profiles',
    procurementRoutes: 'Procurement Routes',
    howInstitutionsBuy: 'How institutions buy technology',
    marketShare: 'Market Share',
    marketWinners: 'Market Winners',
    vendorsGaining: 'Vendors gaining new institutional deals',
    headlineTrends: 'Headline Trends',
    marketInsights: 'Market Insights',
    disconnect: 'Disconnect',
    connecting: 'Connecting...',
    disconnecting: 'Disconnecting...',
    saving: 'Saving...',
    uploading: 'Uploading...',
    default: 'Default',
    language: 'Language',
    history: 'History',
    engagement: 'Engagement',
    showAll: 'Show all',
    gtmAnalytics: 'GTM Analytics',
    accountsByTier: 'Accounts by Tier',
    avgAccountFitAcrossAccounts: 'Avg account fit (all accounts)',
    unassigned: 'Unassigned',
    connectCrm: 'Connect CRM',
    topOfFunnelIntelligence: 'Top-of-Funnel Intelligence',
    coverageTierPenetration: 'Coverage & Tier Penetration',
    outboundExecution: 'Outbound Execution',
    accountsTouched: 'Accounts Touched',
    buyerChampion: 'Buyer + Champion',
    multiThreaded: 'Multi-threaded',
    playbooksRun: 'Playbooks Run',
    neoRecommendations: 'NEO Recommendations',
    accountInteractions: 'Account Interactions',
    emailReplyRate: 'Email Reply Rate',
    linkedinResponse: 'LinkedIn Response',
    callResponse: 'Call Response',
    emailOpenRate: 'Email Open Rate',
    emailClickRate: 'Email Click Rate',
    accountsWithSignals: 'Accounts with Signals',
    tendersGenerated: 'Tenders Generated',
    tendersReviewed: 'Tenders Reviewed',
    timeSaved: 'Time Saved',
    totalHoursSaved: 'Total Hours Saved',
    hours: 'hours',
    usage: 'Usage',
    dailyActivity: 'Daily Activity',
    minPerUserWeek: 'min / user / week',
    replyRate: 'Reply Rate',
    mostActive: 'Most Active',
    signalArchetypeOpenRates: 'Signal × Archetype — Open rates',
    signalArchetypeClickRates: 'Signal × Archetype — Click rates',
    mostActiveAccounts: 'Most active accounts',
    mostActiveAccountsTooltip:
      'Accounts where multiple personas engaged and the total volume of human-verified opens exceeds the number of personas reached.',
    mostActiveAccountsEmpty: 'No accounts with multi-persona engagement yet.',
    adHoc: 'Ad-hoc',
    adHocMessagePerformance: 'Ad-hoc message performance',
    playbookAnalyticsMovedMostActive:
      'Playbook performance analytics has moved to the Most Active tab.',
    playbookAnalyticsUsePlaybooksPage:
      'Use the Playbooks page for sequence setup and operational status.',
    performanceByArchetype: 'Performance by archetype',
    performanceByDepartment: 'Performance by department',
    department: 'Department',
    performanceBySector: 'Performance by sector',
    sector: 'Sector',
    analyticsNoDataInPeriod: 'No data in selected period.',
    analyticsTableSent: 'Sent',
    analyticsOpenRateCol: 'Open rate',
    analyticsClickRateCol: 'Click rate',
    analyticsReplyRateCol: 'Reply rate',
    analyticsChannelCol: 'Channel',
    adHocReplyRateTooltip:
      'Reply rate includes verified human replies only. Self-tests and unverified inbound are excluded.',
    adHocBreakdownFocusSignalNote:
      'Focus and Signal breakdowns will populate as new ad-hoc messages are sent.',
    adHocByCtaFamilyTitle: 'BY CTA FAMILY',
    adHocByFocusTitle: 'BY FOCUS',
    adHocBySignalTitle: 'BY SIGNAL',
    adHocCtaFamilyColumn: 'CTA Family',
    adHocPrimaryAngleCol: 'Primary angle',
    adHocSignalCol: 'Signal',
    adHocSendsFeaturingSignalCol: 'Sends featuring this signal',
    adHocBySignalColumnTooltip:
      "Each send credits every signal it draws from; rows won't sum to the headline total.",
    ctaFamilyLabel_soft_binary: 'Soft-binary (yes/no question)',
    ctaFamilyLabel_action_oriented: 'Action-oriented (meeting ask)',
    ctaFamilyLabel_other: 'Other',
    ctaFamilyLabel_no_cta: 'No CTA',
    messagePerformanceInsightsTitle: 'Message Performance Insights',
    messagePerformanceInsightsDescription:
      'Cross-source breakdown of how outreach performs by CTA shape, signal family, and signal — for both playbook-driven and ad-hoc messages. Use these to see which signals and CTAs drive the strongest engagement.',
    messagePerfPlaybookMessages: 'Playbook messages',
    messagePerfAdhocMessages: 'Ad-hoc messages',
    messagePerfByCtaFamily: 'BY CTA FAMILY',
    messagePerfBySignalFamily: 'BY SIGNAL FAMILY',
    messagePerfBySignal: 'BY SIGNAL',
    messagePerfByLinkClicked: 'BY LINK CLICKED',
    messagePerfLabelLinkUrl: 'Link',
    messagePerfLinkTrueClickRate: 'True click rate',
    messagePerfLinkTotalClicks: 'Clicks',
    messagePerfLinkUniqueMessages: 'Messages',
    messagePerfLinkMessagesHeaderTooltip:
      'Distinct messages this link was clicked from.',
    messagePerfBestDayToSend: 'BEST DAY TO SEND',
    messagePerfBestHourToSend: 'BEST HOUR TO SEND',
    messagePerfSendTimingRatesLine: '{open}% open · {click}% click · {reply}% reply',
    messagePerfSendTimingFromSends: 'from {count} sends',
    messagePerfSendTimingNotEnoughData: 'Not enough data yet.',
    messagePerfSendTimingTzCaption: 'Send times shown in: {tz} (your scheduling timezone).',
    messagePerfSendTimingTzWarningUtc:
      'Send times shown in: UTC — could not resolve your scheduling timezone.',
    messagePerfSendTimingTzMismatch:
      'Send times shown in: {resolved} — could not match the selected timezone ({requested}).',
    messagePerfSendTimingTzCaptionCharts:
      'Send times shown in: {tz} — change above to compare across regions.',
    messagePerfSendTimingTzWarningUtcCharts:
      'Send times shown in: UTC — could not resolve the selected timezone.',
    messagePerfSendTimingChartEmpty: 'Not enough send data yet.',
    messagePerfKeywordsSectionTitle: 'Keywords',
    messagePerfKeywordsSectionHint:
      'Word size reflects frequency; colour reflects reply rate (greener = stronger replies). Hover for message counts and rates.',
    messagePerfKeywordsProductLabel: 'Product context',
    messagePerfKeywordsSubjectTitle: 'Subject lines',
    messagePerfKeywordsBodyTitle: 'Message body',
    messagePerfKeywordsEmpty: 'Not enough keyword data yet.',
    messagePerfSendTimingSectionTitle: 'Send timing',
    messagePerfSendTimingSeriesPlaybook: 'Playbook',
    messagePerfSendTimingSeriesAdhoc: 'Ad-hoc',
    messagePerfSendTimingMergedTooltipLine: '{reply}% reply · {sends} sends',
    messagePerfBySendDayChartTitle: 'BY DAY OF WEEK',
    messagePerfBySendHourChartTitle: 'BY HOUR OF DAY',
    messagePerfSendTimingChartTooltip:
      '{label} · {sends} sends · {open}% open · {click}% click · {reply}% reply',
    messagePerfSendTimeTimezoneLabel: 'Send-time timezone:',
    messagePerfSendTimeSchedulingTzHint: '(your scheduling timezone is {tz})',
    messagePerfTzSchedulingSuffix: 'scheduling default',
    messagePerfTzPlaceholder: 'Select timezone',
    messagePerfSendTimingYReply: 'Reply %',
    messagePerfSendTimingYOpen: 'Open %',
    messagePerfSendTimingLowVolumeNote: 'Low volume — {sends} sends; rate not reliable',
    messagePerfByPrimaryAngle: 'BY PRIMARY ANGLE (FOCUS)',
    messagePerfLabelCtaFamily: 'CTA Family',
    messagePerfLabelSignalFamily: 'Signal Family',
    messagePerfLabelSignal: 'Signal',
    messagePerfLabelPrimaryAngle: 'Primary Angle',
    messagePerfSignalSentTooltip:
      "Each send credits every signal it draws from, so rows won't sum to the total in the headline metrics — this is intentional and lets you compare per-signal performance.",
    messagePerfPlaybookEmptyCaption:
      'Playbook data not available yet — playbook sends have not been recorded in the selected window.',
    messagePerfAdhocEmptyCaption:
      'Ad-hoc data not available yet — no ad-hoc messages sent in the selected window.',
    messagePerfNoBreakdownData: 'No message performance data in this period.',
    messagePerfShowAll: 'Show all ({count})',
    messagePerfShowLess: 'Show less',
    signalFamilyLabel_strategic_priority: 'Stated Priority',
    signalFamilyHint_strategic_priority:
      'A strategic priority the institution has explicitly named in their plan or strategy.',
    signalFamilyLabel_account_insights: 'Account Insights',
    signalFamilyHint_account_insights:
      'Strategic-plan excerpts or references to full strategy documents — combined in one row for easier comparison.',
    signalFamilyLabel_momentum: 'Organizational Update',
    signalFamilyHint_momentum:
      'An organizational change, announcement, or public-facing update worth referencing.',
    signalFamilyLabel_activity: 'Personal Activity',
    signalFamilyHint_activity:
      'Something the recipient has personally done (talk, paper, post).',
    signalFamilyLabel_mandatory_signal: 'Top-Scoring Signal',
    signalFamilyHint_mandatory_signal:
      'The signal that ranked highest for this recipient or outreach context.',
    signalFamilyLabel_framework_based: 'Framework Reference',
    signalFamilyHint_framework_based:
      'Tied to a methodology or framework (for example, an accreditation cycle).',
    signalFamilyLabel_trend_based: 'Sector Trend',
    signalFamilyHint_trend_based: 'A broader sector or industry trend.',
    signalFamilyLabel_deal: 'Procurement',
    signalFamilyHint_deal:
      'Procurement-, sourcing-, or purchasing-related activity (for example RFP, renewal, or vendor process).',
    signalFamilyLabel_case_study: 'Customer Reference',
    signalFamilyHint_case_study: 'A reference to an existing customer story.',
    signalFamilyLabel_default: 'Other',
    signalFamilyHint_default: 'Uncategorized fallback.',
    analyticsAccount: 'Account',
    analyticsAccountFit: 'Account fit',
    analyticsPersonasSent: 'Personas sent',
    analyticsMultiPersonaOpens: 'Multi-persona opens',
    analyticsOpensPerPersona: 'Opens / persona',
    analyticsOpensPerPersonaSubtitle: 'total opens ÷ personas',
    analyticsMultiPersonaClicks: 'Multi-persona clicks',
    sent: 'sent',
    calls: 'calls',
    allTiers: 'All Tiers',
    allChannels: 'All Channels',
    signalTypePerformance: 'Signal Type Performance',
    signalTypePerformanceTooltip: 'Account signals (momentum): performance of outreach at accounts where the signal existed in the window. Persona signals: performance of outreach to the specific person the signal is about. Both include playbook and manual messages.',
    noSignalDataSelectedPeriod: 'No signal data in selected period',
    signalType: 'Signal Type',
    touched: 'Touched',
    bestChannel: 'Best Channel',
    sample: 'Sample',
    lowSampleSize: 'Low sample size (<30)',
    topResponsiveAccountsFor: 'Top Responsive Accounts for',
    noAccountsTouchedForSignal: 'No accounts touched for this signal',
    na: 'N/A',
    signalArchetypeReplyRates: 'Signal × Archetype Reply Rates',
    notEnoughDataForMatrix: 'Not enough data for matrix',
    filterByTier: 'Filter by Tier',
    accountCommunicationsDepth: 'Account Communications Depth',
    depthScoreTooltip: 'Depth Score (0-100): Personas (30pts) + Archetypes (30pts) + Replies (30pts) + Recency (10pts)',
    noAccountDataSelectedPeriod: 'No account data in selected period',
    more: 'more',
    depth: 'Depth',
    archetypeCoverage: 'Archetype Coverage',
    notTouched: 'Not Touched',
    replied: 'Replied',
    recommendations: 'Recommendations',
    goodCoverage: 'Good coverage!',
    recentActivity: 'Recent Activity',
    customEventPlaybook: 'Custom Event Playbook',
    untitledPlaybook: 'Untitled Playbook',
    selectedForComparison: 'selected for comparison',
    playbookPerformance: 'Playbook Performance',
    noPlaybookDataSelectedPeriod: 'No playbook data in selected period',
    playbook: 'Playbook',
    runs: 'Runs',
    completionPercent: 'Completion %',
    bestArchetype: 'Best Archetype',
    comparePlaybooks: 'Compare Playbooks',
    completionRate: 'Completion Rate',
    medianReplyTime: 'Median Reply Time',
    stepFunnel: 'Step Funnel',
    reply: 'reply',
    trackYourGoToMarketPerformance: 'Track your go-to-market performance',
    viewing: 'Viewing',
    allTeamActivity: 'All team activity',
    myActivity: 'My activity',
    specificTeammate: 'Specific teammate',
    chooseTeammate: 'Choose teammate',
    pipelineDeals: 'Pipeline & Deals',
    openPipeline: 'Open pipeline',
    meetingsBooked: 'Meetings Booked',
    dealsCreated: 'Deals Created',
    winRate: 'Win Rate',
    languagePreferenceSaved: 'Language preference saved!',
    failedToSaveLanguagePreference: 'Failed to save language preference',
    selectPreferredLanguageDescription: 'Select your preferred language for the platform interface. Translations will be applied to headers, buttons, and navigation elements.',
    interfaceLanguage: 'Interface Language',
    choosePreferredLanguage: 'Choose your preferred language for the platform interface',
    selectLanguage: 'Select language',
    languageScopeDisclaimer: 'Platform headers, navigation, buttons, and labels will be translated. Account names, persona names, and data content will remain in their original language.',
    saveLanguagePreference: 'Save Language Preference',
    failedToLoadProfile: 'Failed to load profile',
    profileUpdatedSuccessfully: 'Profile updated successfully',
    failedToSaveProfile: 'Failed to save profile',
    pleaseSelectImageFile: 'Please select an image file',
    imageSizeLessThan5MB: 'Image size must be less than 5MB',
    profilePhotoUpdatedSuccessfully: 'Profile photo updated successfully',
    failedToUploadPhoto: 'Failed to upload photo',
    updatePersonalInfoAndPhoto: 'Update your personal information and profile photo',
    profilePhoto: 'Profile Photo',
    profile: 'Profile',
    updateYourProfilePicture: 'Update your profile picture',
    uploadPhoto: 'Upload Photo',
    photoFormatAndSizeHint: 'JPG, PNG or GIF. Max size 5MB.',
    personalInformation: 'Personal Information',
    personalInfoGreetingUsage: 'This information will be used in greetings and communications',
    displayName: 'Display Name',
    yourName: 'Your name',
    displayNameUsageHint: 'This name will be used in greetings and communications',
    jobTitle: 'Job Title',
    yourRoleOrPosition: 'Your role or position in the organization',
    yourOrganization: 'Your organization',
    usedInEmailSignatures: 'Used in email signatures and formal communications',
    saveProfile: 'Save Profile',
    gmailIntegration: 'Gmail Integration',
    connectGmailDescription: 'Connect your Gmail account to send emails directly from NEO',
    loadingGmailStatus: 'Loading Gmail status...',
    gmailComingSoon: 'Gmail integration is coming soon. The feature is currently being set up.',
    gmailConnected: 'Gmail Connected',
    gmailNotConnected: 'Gmail Not Connected',
    connectAccountToSendEmails: 'Connect your account to send emails',
    connectGmail: 'Connect Gmail',
    sendEmailsFrom: 'Send Emails From',
    selectEmailAddress: 'Select email address',
    loadingSignature: 'Loading signature...',
    emailSignaturePreview: 'Email Signature Preview',
    whyConnectGmail: 'Why connect Gmail?',
    gmailBenefitSendPersonalized: 'Send personalized emails directly from NEO',
    gmailBenefitTrackEngagement: 'Track email opens and engagement',
    gmailBenefitManageOutreach: 'Manage your outreach campaigns efficiently',
    searchDrafts: 'Search person, subject, account, or role…',
    allProducts: 'All products',
    allPlaybooks: 'All playbooks',
    noDraftsFound: 'No drafts found',
    createPlaybooksToGenerateDrafts: 'Create playbooks to generate email drafts',
    cancelDraft: 'Cancel Draft',
    cancelDrafts: 'Cancel Drafts',
    deleting: 'Deleting...',
    deleteDraft: 'Delete Draft',
    failedToLoadSchedule: 'Failed to load schedule',
    scheduleSaved: 'Schedule saved',
    communicationScheduleUpdated: 'Your communication schedule has been updated',
    failedToSaveSchedule: 'Failed to save schedule. Please try again.',
    atLeastOneWindowRequired: 'At least one window required',
    addWindowBeforeRemoving: 'Add a new window before removing this one.',
    communicationSchedule: 'Communication Schedule',
    configureCommsSchedule: 'Configure when NEO can send communications on your behalf',
    commsScheduleHelp: 'When you run a playbook, every step (email, LinkedIn, call) is scheduled into the next available window from the day-offset you set. Outside these windows, no communications are scheduled.',
    complete: 'Complete',
    incomplete: 'Incomplete',
    availabilityWindows: 'Availability Windows',
    addWindow: 'Add Window',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    startTime: 'Start Time',
    endTime: 'End Time',
    saveSchedule: 'Save Schedule',
    userIdMissing: 'User ID is missing',
    outreachHistory: 'Outreach History',
    outreachHistorySubtitle: 'Timeline of all messaging tasks completed by your team',
    allUsers: 'All Users',
    noOutreachHistoryPeriod: 'No outreach history found for this period.',
    failedToLoadEmailAddresses: 'Failed to load email addresses',
    emailAddressUpdated: 'Email Address Updated',
    emailsWillBeSentFrom: 'Emails will be sent from',
    emailAddressesRefreshed: 'Email Addresses Refreshed',
    emailAliasesReloaded: 'Your email aliases have been reloaded',
    signatureSaved: 'Signature Saved',
    emailSignatureSavedSuccessfully: 'Your email signature has been saved successfully.',
    failedToSaveSignature: 'Failed to save signature. Please try again.',
    outlookIntegration: 'Outlook Integration',
    connectOutlookDescription: 'Connect your Microsoft Outlook account to send emails directly from NEO',
    loadingOutlookStatus: 'Loading Outlook status...',
    outlookComingSoon: 'Outlook integration is coming soon.',
    outlookConnected: 'Outlook Connected',
    outlookNotConnected: 'Outlook Not Connected',
    connectOutlook: 'Connect Outlook',
    emailSignature: 'Email Signature',
    saveSignature: 'Save Signature',
    clear: 'Clear',
    error: 'Error',
    configureWorkspacePreferences: 'Configure your workspace and preferences',
    profile: 'Profile',
    emails: 'Emails',
    schedule: 'Schedule',
    personalization: 'Personalization',
    events: 'Events',
    productFit: 'Product Fit',
    completingGmailConnection: 'Completing Gmail connection...',
    authorizationFailedOrCancelled: 'Authorization failed or was cancelled',
    gmailConnectedClosingWindow: 'Gmail connected. Closing window...',
    failedToConnectGmail: 'Failed to connect Gmail',
    pleaseWait: 'Please wait...',
    windowWillCloseAutomatically: 'This window will close automatically.',
    youCanCloseThisWindow: 'You can close this window.',
    completingOutlookConnection: 'Completing Outlook connection...',
    outlookConnectedClosingWindow: 'Outlook connected. Closing window...',
    failedToConnectOutlook: 'Failed to connect Outlook',
    connectionFailed: 'Connection Failed',
    gmailDisconnected: 'Gmail Disconnected',
    gmailAccountDisconnected: 'Your Gmail account has been disconnected.',
    disconnectNotApplied: 'Disconnect not applied',
    backendStillReportsGmailConnected: 'Backend still reports Gmail as connected. Please retry; if it persists, this likely needs a backend fix.',
    outlookDisconnected: 'Outlook Disconnected',
    outlookAccountDisconnected: 'Your Outlook account has been disconnected.',
    backendStillReportsOutlookConnected: 'Backend still reports Outlook as connected. Please retry; if it persists, this likely needs a backend fix.',
    unsavedChanges: 'Unsaved changes',
    howToCopyOutlookSignature: 'How to copy your Outlook signature:',
    pasteSignatureHere: 'Paste your signature here...',
    whyConnectOutlook: 'Why connect Outlook?',
    outlookWorksWith365: 'Works with Microsoft 365 and Outlook.com accounts',
    connectedAs: 'Connected as',
    gmailConnectedSuccessfully: 'Gmail connected successfully',
    outlookConnectedSuccessfully: 'Outlook connected successfully',
    emailAddressUpdatedLocalOnly: 'Email Address Updated (local only)',
    usingInSessionBackendRejectedAlias: 'Using in this session. Backend rejected saving preferred_outlook_alias (400):',
    copySignatureAndPasteBelow: 'Copy your signature from Outlook and paste it below',
    outlookSignatureStep1: 'Open Outlook -> File -> Options -> Mail -> Signatures',
    outlookSignatureStep2: 'Select your signature, press Ctrl+A (select all), then Ctrl+C (copy)',
    outlookSignatureStep3: 'Click in the box below and press Ctrl+V (paste)',
    signatureSavedWillBeUsed: 'Signature saved and will be used in your emails',
    editSignatureBelowOrPullFromGmail: 'Edit your signature below, or pull the current one from your Gmail account.',
    pasteOrTypeSignatureHere: 'Paste your signature here, or type it directly.',
    useGmailSignature: 'Use Gmail signature',
    noGmailSignatureFound: 'No Gmail signature found',
    selectAliasFirstOrSetInGmail: 'Select an alias first, or set a signature in your Gmail settings.',
    preEvent: 'Pre-Event',
    dayOf: 'Day-Of',
    postEvent: 'Post-Event',
    dayLabel: 'Day',
    noGoalSet: 'No goal set',
    asset: 'asset',
    dayOffset: 'Day Offset',
    negativeBeforeEvent: 'Negative = before event',
    channel: 'Channel',
    useAiSignals: 'Use AI Signals',
    includeBookingLink: 'Include booking link',
    autoInsertAvailabilityLink: "Auto-inserts the event's availability link into the message body.",
    addAvailabilityLinkToEnable: 'Add an availability link to the event settings to enable this.',
    touchGoal: 'Touch Goal',
    touchGoalPlaceholder: 'What should this message accomplish?',
    messagingNotes: 'Messaging Notes',
    messagingNotesPlaceholder: 'Specific points to include, tone guidance, things to mention or avoid...',
    instructionsPassedToAiTouch: 'These instructions will be passed directly to the AI for this specific touch',
    collateralToShare: 'Collateral to Share',
    warmUpReading: 'Warm-up reading',
    noAssetsConfiguredInPersonalization: 'No assets configured in Settings - Personalization',
    selected: 'selected',
    failedToLoadEvents: 'Failed to load events',
    missingRequiredFields: 'Missing required fields',
    eventsSaved: 'Events saved',
    eventScheduleUpdated: 'Your event schedule has been updated',
    eventScheduleUpdatedLocal: 'Your event schedule has been updated (local storage)',
    saveFailed: 'Save failed',
    failedToSaveEvents: 'Failed to save events',
    eventsAndConferences: 'Events & Conferences',
    trackEventsAndConferences: "Track events and conferences you're attending",
    usingLocalStorageApiUnavailable: 'Using local storage (API unavailable',
    myEvents: 'My Events',
    addConferencesTradeShows: 'Add conferences, trade shows, and speaking engagements',
    addEvent: 'Add Event',
    noEventsAddedYet: 'No events added yet',
    addEventsToEnableEventMessaging: 'Add events to enable event-based messaging',
    event: 'Event',
    collapse: 'Collapse',
    expand: 'Expand',
    eventName: 'Event Name',
    eventNameExample: 'e.g., EDUCAUSE 2025',
    city: 'City',
    cityExample: 'e.g., Denver',
    venue: 'Venue',
    venueExample: 'e.g., Convention Center',
    startDate: 'Start Date',
    endDate: 'End Date',
    boothNumber: 'Booth Number',
    boothNumberExample: 'e.g., Hall A, Booth 123',
    availabilityLink: 'Availability Link',
    availabilityLinkPlaceholder: 'https://calendly.com/...',
    meetingOffer: 'Meeting Offer',
    meetingOfferExample: 'e.g., 15-minute product demo',
    messagingGuidelines: 'Messaging Guidelines',
    customInstructionsForAi: 'Custom instructions for AI-generated messages at each phase',
    preEventGuidelines: 'Pre-Event Guidelines',
    preEventGuidelinesExample: "e.g., Mention our new AI-powered analytics launching at the event. Highlight the live demo we're offering.",
    infoToIncludeBeforeEvent: 'Info to include in messages before the event',
    duringEventGuidelines: 'During-Event Guidelines',
    duringEventGuidelinesExample: 'e.g., Offer on-the-spot demos. Mention we have limited appointment slots left.',
    postEventGuidelines: 'Post-Event Guidelines',
    postEventGuidelinesExample: 'e.g., Reference the Q&A from our session. Offer the recording and slides. Mention the special post-event pricing.',
    sessionDetailsOptional: 'Session Details',
    sessionDetailsSessionInviteNote:
      '(Session title and description are required to use the Session Invite playbook on Playbooks.)',
    ifSpeakingOrWorkshop: "If you're speaking or running a workshop",
    sessionTitle: 'Session Title',
    sessionTitleExample: "e.g., AI in Higher Ed: What's Next",
    sessionDescription: 'Session Description',
    sessionDescriptionPlaceholder: 'Brief description to entice attendees...',
    postEventAssets: 'Post-Event Assets',
    linksToShareFollowUp: 'Links to share in follow-up messages',
    sessionRecordingLink: 'Session Recording Link',
    slidesLink: 'Slides Link',
    postEventResourceLink: 'Post-Event Resource (Whitepaper, etc.)',
    messageSequence: 'Message Sequence',
    defineTouchesOutreachSequence: 'Define the touches in your outreach sequence',
    usingDefaultPlaybook: 'Using default playbook. Enable custom playbook to define your own sequence.',
    createCustomSequence: 'Create Custom Sequence',
    addTouch: 'Add Touch',
    targetRegion: 'Target Region',
    targetCountries: 'Target Countries',
    writeInNativeLanguage: "Write in each country's native language",
    writeInNativeLanguageHelp: "Off by default — every message goes out in English. When on, each message is written in the language of the recipient account's country (Portuguese, Spanish or French; English elsewhere).",
    deselectAll: 'Deselect All',
    selectAllNoRestrictions: 'Select All (No Restrictions)',
    allCountriesSelectedStrong: 'All countries selected',
    noGeoRestrictions: 'No geographic restrictions will be applied.',
    personasAnyCountryIncluded: 'Personas from any country will be included.',
    fixRequiredFieldsBeforeSaving: 'Fix required fields before saving',
    saveEvents: 'Save Events',
    urlPlaceholder: 'https://...',
    settingsSavedSuccessfully: 'Settings saved successfully!',
    failedToSaveSettingsTryAgain: 'Failed to save settings. Please try again.',
    usingLocalStorageApiUnavailableShort: 'Using local storage (API unavailable)',
    languageBrandVoice: 'Language & Brand Voice',
    configureNeoCommunicationTenant: 'Configure how NEO communicates for this tenant',
    english: 'English',
    spanish: 'Spanish',
    french: 'French',
    german: 'German',
    brazilianPortuguese: 'Brazilian Portuguese',
    arabic: 'Arabic',
    additionalStylePreferencesPlaceholder: 'Extra style rules that override defaults. Examples: Short punchy sentences; No buzzwords; UK spelling; Never use exclamation marks; Always lead with a question',
    stylePreferencesOverrideHelp: 'These preferences override default style/tone rules but cannot bypass QA safety rules (URL whitelist, banned phrases, length limits, placeholder validation).',
    companyProduct: 'Company & Product',
    customizeCompanyProductPositioning: 'Customize how your company and product are positioned. Leave blank to use tenant defaults.',
    companyNameExample: 'e.g., Acme Learning',
    productNameExample: 'e.g., Future Skills Enrichment Programs',
    coreValuePropositionExample: 'e.g., Personalized enrichment programs that engage and inspire students',
    leaveBlankUseTenantDefaultValueProp: 'Leave blank to use tenant default value proposition',
    keyPerformanceIndicators: 'Key Performance Indicators (KPIs)',
    defineMetricsThatMatter: "Define metrics that matter to this tenant's customers",
    kpiNamePlaceholder: 'KPI Name (e.g., Student Engagement)',
    kpiDescriptionPlaceholder: 'Description (e.g., 45% increase in active daily users)',
    productOptional: 'Product (optional)',
    addKpi: 'Add KPI',
    messagingHooksDescription: 'Key talking points and value propositions by archetype.',
    useProductSelectorForHooks: 'Use the Product selector to align hooks with specific products.',
    selectPersona: 'Select persona',
    universalAllPersonas: 'Universal (All personas)',
    champion: 'Champion',
    influencer: 'Influencer',
    evaluatorTechnical: 'Evaluator (Technical)',
    endUser: 'End User',
    hookUsedForPersonaType: 'This hook will only be used for this persona type',
    hookText: 'Hook Text',
    hookTextExample: 'e.g., Revenue-generating enrichment with zero capital investment',
    caseStudiesClientReferences: 'Case Studies & Client References',
    caseStudiesClientReferencesDescription: 'Add full case studies (with links) or client references (institution names only)',
    type: 'Type',
    selectType: 'Select type',
    caseStudyWithLink: 'Case Study (with link)',
    clientReferenceNameOnly: 'Client Reference (name only)',
    institutionName: 'Institution Name',
    institutionNameExample: 'e.g., Oundle School',
    clientReferenceUsagePrefix: 'Will be used as: "Schools like',
    clientReferenceUsageSuffix: 'have found..."',
    caseStudyTitle: 'Case Study Title',
    caseStudyTitleExample: 'e.g., University of Melbourne Success Story',
    caseStudyUrlPlaceholder: 'https://example.com/case-study',
    descriptionOptional: 'Description (optional)',
    briefOutcomeSummary: 'Brief outcome summary',
    addCaseStudyClientReference: 'Add Case Study / Client Reference',
    usefulResourcesDocumentation: 'Useful resources and documentation for this tenant',
    linkNamePlaceholder: 'Link Name (e.g., Product Demo)',
    linkUrlPlaceholder: 'URL (e.g., https://example.com/demo)',
    addProductLink: 'Add Product Link',
    educationalResources: 'Educational Resources',
    educationalResourcesLead:
      'Warm-up content used early in cadences to build credibility before any sales ask.',
    educationalResourcesExamples:
      'Examples: framework articles, third-party authority guides, "what good looks like" pieces.',
    resourceTitle: 'Title',
    educationalResourceTitlePlaceholder:
      'e.g., Strategic Considerations for a Successful Student Survey Tool',
    educationalResourceUrlPlaceholder: 'https://example.com/article',
    addEducationalResource: '+ Add Educational Resource',
    invalidHttpUrl: 'Enter a valid http(s) URL',
    educationalResourceTitleTooLong: 'Title must be 200 characters or fewer',
    educationalProductContextUnset: 'Not set (treated as ALL)',
    selectResources: 'Select resources…',
    noEducationalResourcesYet:
      'No resources saved yet — add via Settings → Educational Resources',
    educationalResourcesHelp:
      'Optional. Pick the warm-up articles/videos this touch should reference. Empty = engine picks for you.',
    saveSettings: 'Save Settings',
    eg: 'e.g.,',
    // [2026-10-29] i18n audit: keys added
    status: 'Status',
    replies: 'Replies',
    brandVoiceDescription: 'Brand voice description',
    brandVoiceDescriptionPlaceholder: 'Describe how your brand should sound, e.g. warm, expert, concise',
    additionalStylePreferencesOverride: 'Additional style preferences (override)',
    companyName: 'Company Name',
    coreValueProposition: 'Core value proposition',
    messagingHooks: 'Messaging hooks',
    targetPersona: 'Target persona',
    economicBuyer: 'Economic Buyer',
    addMessagingHook: 'Add messaging hook',
    url: 'URL',
    productLinks: 'Product links',
    timezone: 'Time zone',
    caseStudy: 'case study',
    customPlaybook: 'Custom playbook',
    // [2026-10-29] i18n: UI chrome keys (market / procurement / account / playbooks)
    mkt12w: '12w',
    mkt4w: '4w',
    mktAboutEnrichment: 'About enrichment',
    mktAboutLabel: 'About {label}',
    mktAccountBarometer: 'Account barometer',
    mktAccountPageNotFound: 'Account page not found',
    mktAccountsAffected: 'Accounts affected',
    mktAccountsAffectedTip: 'Number of your target accounts where at least one signal mentions this competitor. Counts each account once, even if there are multiple signals.',
    mktAccountsExaminedOf: '{examined} of {total} accounts examined',
    mktAccountsInMotion: 'Accounts in motion',
    mktAccountsInMotionHint: 'Institutions whose signals are moving. Labelled “in motion” unless there is explicit buying evidence.',
    mktAcrossNCountries: 'across {n} countries',
    mktAcrossNProgrammes: 'across {n} programmes',
    mktAdd: 'Add',
    mktAddPastedVendors: 'Add pasted vendors',
    mktAddToAccounts: 'Add to Accounts',
    mktAddToPilot: 'Add to pilot',
    mktAddedLoadingNow: 'Its people, signals and history are loading now.',
    mktAddedToAccounts: 'Added to accounts',
    mktAddedToYourAccounts: 'Added to your accounts',
    mktAdding: 'Adding…',
    mktAddressable: 'Addressable',
    mktAdjacent: 'Adjacent',
    mktAdjacentMarket: 'Adjacent market',
    mktAdjacentPlayer: 'Adjacent Player',
    mktAdjacentVendor: 'Adjacent vendor',
    mktAfter: 'After',
    mktAiDef1: 'Exploring. Nothing published and no sign of anyone using AI tools yet.',
    mktAiDef2: 'Piloting. Something is running, with no policy around it.',
    mktAiDef3: 'Policy in place. Published and approved, but we cannot see it being used.',
    mktAiDef4: 'Governed adoption. Both the policy and real usage are evidenced.',
    mktAiDef5: 'Embedded. AI is part of how they teach and assess.',
    mktAiEmbedded: 'Embedded',
    mktAiExploring: 'Exploring',
    mktAiGovernedAdoption: 'Governed adoption',
    mktAiPiloting: 'Piloting',
    mktAiPolicyInPlace: 'Policy in place',
    mktAiReadiness: 'AI readiness',
    mktAiReadinessBelow: 'AI readiness below',
    mktAiReadinessIndex: 'AI Readiness Index',
    mktAiReadinessWholeMarketHint: 'AI readiness across the whole market, not just your opportunities.',
    mktAiScale: 'We look at two things, how mature their policy is and how deeply they have adopted tools, and take the higher of the two. Levels 4 and 5 need both to check out; a policy on its own stops at 3. Anything we have not seen confirmed in 18 months drops a level.',
    mktAllCategories: 'All categories',
    mktAllCountries: 'All countries',
    mktAllEntitledCountries: 'All entitled countries',
    mktAllProgrammes: 'All programmes ({n})',
    mktAlreadyFunded: 'Already funded',
    mktAlreadyOnYourList: 'Already on your list',
    mktAlsoPriority: 'Also priority',
    mktAlsoPriorityHint: 'Eligible accounts that are already a priority in your market.',
    mktAmount: 'Amount',
    mktAnnualRunRate: 'Annual run-rate {annual}.',
    mktAwardCeiling: 'Award ceiling',
    mktAwardCeilingHint: 'The range a single award can reach, in the funder\'s own wording.',
    mktBackOnTeamSettings: 'Back on the team settings.',
    mktBandCapacity: '{band} capacity',
    mktBarometerScore: 'Barometer score',
    mktBarometerScoreAccountTip: 'Competitor proximity from 0 to 100. Computed from signal strength × recency decay. Higher = stronger competitor presence at this account. Direction (favourable/unfavourable) is shown separately under Impact.',
    mktBarometerScorePersonaTip: 'Competitor proximity from 0 to 100 for this persona. Computed from signal strength × recency decay. Higher = stronger competitor presence. Direction is shown separately under Impact.',
    mktBarrier: 'Barrier',
    mktBefore: 'Before',
    mktBuyingEvidence: 'Buying evidence',
    mktBuyingSignals: 'Buying signals',
    mktByCountryN: 'By country ({n})',
    mktCandidates: 'Candidates',
    mktChangeAFilterFirst: 'Change a filter first.',
    mktChooseProductCategory: 'Choose a product category',
    mktCloseCountryPanel: 'Close country panel',
    mktClosingSoon: 'Closing soon',
    mktClosingSoonHint: 'Open rounds with a deadline in the next 45 days.',
    mktCompetitor: 'Competitor',
    mktCompetitorLocked: 'Competitor locked — never displace',
    mktCompetitorLockedHint: 'Institutions running these vendors stay out of your opportunity lists. They stay on the competitor list, you are just not trying to win them.',
    mktCompetitorSet: 'Competitor set',
    mktCompetitorSetHint: 'If a vendor is on this list and they are at an institution, and something is happening there, that is a displacement opportunity. Leave the vendor off and the same account reads as coexistence. One name per supplier is enough, we resolve the variants.',
    mktCompetitorView: 'Competitor view',
    mktCompetitorsPresent: 'Competitors present',
    mktConfirmEmptyCompetitorSet: 'With nobody on this list, nothing can be a displacement. Your market will shrink to greenfield only. Save anyway?',
    mktContextOnly: 'Context only',
    mktContractScope: 'Contract scope',
    mktContractScopeUnknownHint: 'Nobody has established this yet. It does not mean the deployment is small, so we leave it out of the score rather than marking it down.',
    mktCouldNotAddAccount: 'Could not add account',
    mktCouldNotAddThisAccount: 'Could not add this account',
    mktCouldNotCompleteAction: 'Could not complete action',
    mktCouldNotLoadAccountsInMotion: 'Could not load accounts in motion',
    mktCouldNotLoadCategory: 'Could not load this category',
    mktCouldNotLoadCompetitorData: 'Couldn\'t load competitor data — try again.',
    mktCouldNotLoadFunding: 'Could not load funding',
    mktCouldNotLoadIgnored: 'Could not load ignored accounts',
    mktCouldNotLoadMarketData: 'Could not load market data. Please refresh.',
    mktCouldNotLoadOpportunities: 'Could not load opportunities',
    mktCouldNotLoadPlayers: 'Could not load players',
    mktCouldNotLoadPreferences: 'Could not load preferences',
    mktCouldNotLoadRenewalRisk: 'Could not load renewal risk',
    mktCouldNotLoadSummary: 'Could not load market summary',
    mktCouldNotMoveAccountBack: 'Could not move account back',
    mktCouldNotOpenAccount: 'Could not open account',
    mktCouldNotRemoveAccount: 'Could not remove account.',
    mktCouldNotResolveAccount: 'Could not resolve this account in your Accounts list yet.',
    mktCouldNotSave: 'Could not save',
    mktCouldNotSwitch: 'Could not switch',
    mktCountries: 'Countries',
    mktCoverage: 'Coverage',
    mktCurrentVersion: 'current',
    mktDeadlineSpendBy: 'Deadline / spend by',
    mktDisplacement: 'Displacement',
    mktDisqualifiers: 'Disqualifiers',
    mktEligibleAccounts: 'Eligible accounts',
    mktEligibleSpend: 'Eligible spend',
    mktEmptyCompetitorSetWarning: 'With nobody on this list, nothing can be a displacement. Your market will shrink to greenfield only.',
    mktEnrichRequestToast: 'We’ll process your request within 48 hours and notify you when it’s ready.',
    mktEnrichTooltip: 'Sends this account to the research team to fill in the gaps: who they use, how widely, when the contract ends, and whether they are happy with it. Your score will not change today. When the research comes back the account is rescored and you will see it in What changed.',
    mktEnrichmentRequested: 'Enrichment requested',
    mktEnrichmentRequestedOn: 'Requested on {date}. We will rescore this account when the research comes back.',
    mktEstSpendBy: 'est. spend by',
    mktEstimatedValue: 'Estimated value',
    mktEvidence: 'Evidence',
    mktExaminedOf: '{examined} of {total} examined',
    mktExaminedSuffix: ' examined',
    mktExcludeCurrentCustomers: 'Exclude current customers',
    mktExistingCustomer: 'Existing customer',
    mktExistingExpansion: 'Existing / expansion',
    mktExpansion: 'Expansion',
    mktFavourable: 'Favourable',
    mktFavourableDisplacementOpps: 'Favourable displacement opportunities',
    mktFinancialBandBelow: 'Financial band below',
    mktFinancialBandBelowHint: 'Estimated headroom to fund new initiatives, based on operating spend and reserves per student and operating margin, ranked against domestic peers in the same sector. High means more room to invest; Low can signal budget pressure. It reflects capacity to spend, not budget already earmarked for software.',
    mktFinancialCapacity: 'Financial capacity',
    mktFinancialWholeMarketHint: 'Financial capacity across the whole market. How much room institutions have to fund something new, not what they spend on EdTech.',
    mktFitAddUnscoredFallback: 'This account is in your Accounts, but Market had no score for it.',
    mktFlat: 'Flat',
    mktFullAssessment: 'Full assessment',
    mktFunder: 'Funder',
    mktFunding: 'Funding',
    mktFundingAccountsHint: 'Accounts this funding can reach. How to pitch asks NEO using the programme, amount and eligibility on that row.',
    mktFundingEligibility: 'Funding eligibility',
    mktFundingEligible: 'Eligible',
    mktFundingEligibleHint: 'Accounts that can spend this funding on your category, out of the accounts in this market.',
    mktFundingNotSwitchedOn: 'Funding is not switched on for this tenant yet.',
    mktGapClientsOnly: 'On your client list. We have not confirmed which product they run.',
    mktGapStackOnly: 'Running your product according to the vendor record, but not on your client list.',
    mktGradeDocumented: 'Documented',
    mktGradeDocumentedHint: 'The institution has published a specific failing in the vendor\'s product, usually in an accessibility statement.',
    mktGradeExplicit: 'Explicit',
    mktGradeExplicitHint: 'A governing body, senate, committee or students\' union put the complaint on the record.',
    mktGradeImplied: 'Implied',
    mktGradeImpliedHint: 'We found pain in the workflow this vendor owns, but nobody has said they want to leave.',
    mktGradeReported: 'Reported',
    mktGradeReportedHint: 'The complaint is published somewhere, but not in a meeting where decisions get made.',
    mktGradeTiming: 'Timing',
    mktGradeTimingHint: 'Their contract runs out inside your renewal window, so it goes back out to tender whatever they think of the vendor.',
    mktGreenfield: 'Greenfield',
    mktGreenfieldAdjacent: 'Greenfield / adjacent',
    mktHasOpportunities: 'Has opportunities',
    mktHeld: 'Held',
    mktHeldHint: 'Held is not excluded. We are waiting on a full student count before applying your minimum, rather than dropping an account on a partial figure.',
    mktHideCountryDetail: 'Hide country detail',
    mktHideOlderEvents: 'Hide older and off-category events',
    mktHighProximityAccounts: 'High-proximity accounts',
    mktHighestInYourMarket: 'Highest in your market: {names}',
    mktHowMuchWeHaveLookedAt: 'How much we have looked at',
    mktHowToPitch: 'How to pitch →',
    mktIgnore: 'Ignore',
    mktIgnoreAccount: 'Ignore account',
    mktIgnoreHint: 'Accounts we checked and set aside. This is not the same as Rest of market — those are accounts nobody has looked at yet.',
    mktIgnoreNotSwitchedOn: 'This list shows accounts we checked and set aside — not accounts nobody has looked at. It is not switched on for this tenant yet.',
    mktImpactAccountTip: 'Whether the competitor evidence at this account is bad for you (Unfavourable), good for you (Favourable — e.g. dissatisfaction / replacement opportunity), Mixed, or Context only.',
    mktImpactPersonaTip: 'Whether the competitor evidence for this persona is bad for you (Unfavourable), good for you (Favourable), Mixed, or Context only.',
    mktInHouse: 'In-house',
    mktInOurFavour: 'In our favour',
    mktInPilot: 'In pilot',
    mktInPlay: 'In play',
    mktInScope: 'in {label}',
    mktInThisMarket: 'in this market',
    mktInYourMarket: 'In your market',
    mktIncumbent: 'Incumbent',
    mktIncumbentSentimentLine: 'Incumbent sentiment: {sentiment}',
    mktInstitutionShare: 'Institution share',
    mktInstitutions: 'Institutions',
    mktKeySectorTrends: 'Key sector trends',
    mktLastRefreshed: 'Last refreshed',
    mktLatestSignal: 'Latest signal',
    mktLatestSignalAccountTip: 'Date of the most recent competitor signal at this account.',
    mktLatestSignalPersonaTip: 'Date of the most recent competitor signal for this persona.',
    mktLatestUpdate: 'Latest update',
    mktLatestUpdateTip: 'Most recent evidence summary across all signals for this competitor.',
    mktLeaving: 'Leaving: {names}',
    mktLens: 'Lens',
    mktLimited: 'Limited',
    mktLiveSignal: 'Live signal',
    mktLmsChipTitle: 'Not a category you sell into. It is here because it is what everything else plugs into.',
    mktLmsFamiliesNote: 'Your view only. Family names, not raw vendor strings — Open LMS and Totara are separate from Moodle.',
    mktLmsShareHint: 'The share of institutions running each LMS. It is a count of institutions, not revenue.',
    mktLoadingEllipsis: 'Loading…',
    mktLockPastedVendors: 'Lock pasted vendors',
    mktMainThemes: 'Main themes',
    mktMainThemesTip: 'Themes pulled from signal Themes: tags and market update theme tags. These describe what the competitor is talking about — not the strength of the signal.',
    mktMapTipEmpty: '{label}. Loaded, but nothing qualifies in this category.',
    mktMapTipNOpportunities: '{label}. {count} opportunities.',
    mktMapTipNotLoaded: '{label}. We have not loaded this market yet.',
    mktMapTipOneOpportunity: '{label}. {count} opportunity.',
    mktMarkRead: 'Mark read',
    mktMarketEvents: 'Market events',
    mktMarketEventsTip: 'Count of market-level competitor activity attributable to this competitor — regional events, external adoptions, and other moves not tied to your target accounts.',
    mktMarketIsRescoring: 'The market is rescoring. You can keep reading this view.',
    mktMarketOpportunity: 'Market opportunity',
    mktMarketPreferences: 'Market preferences',
    mktMarketRecomputed: 'Market recomputed.',
    mktMarketScore: 'Market score',
    mktMarketSummary: 'Market summary',
    mktMarketTracker: 'Market tracker',
    mktMatchesForms: 'Matches {forms}',
    mktMatching: 'Matching…',
    mktMaximumEnrolment: 'Maximum enrolment',
    mktMinimumEnrolment: 'Minimum enrolment',
    mktMinimumEnrolmentHint: 'Where we only have a partial student count, we hold the account rather than dropping it, so a subset figure never rules out a large institution.',
    mktMinimumPain: 'Minimum pain',
    mktMinimumPainHint: 'Scored 0 to 5. How plainly a problem must be stated before an account counts as in your market. 2 is a passing mention or an intention to fix something. 3 is a stated fault. 4 is a specific, plainly described defect. 5 is a failure with a named consequence, such as a compliance breach. Most teams sit at 3; moving to 4 cuts the qualifying evidence by about 40%.',
    mktMix: 'Mix',
    mktMixed: 'Mixed',
    mktMoveBackToMyList: 'Move back to my list',
    mktMovedBackToYourList: 'Moved back to your list',
    mktMovedToIgnore: 'Moved to Ignore',
    mktMoving: 'Moving…',
    mktNAccounts: '{n} accounts',
    mktNAccountsExcluded: '{n} accounts excluded',
    mktNCategories: '{n} categories',
    mktNDays: '{n} days',
    mktNHaveWonBefore: '{n} have won before',
    mktNHeldPendingEnrolment: '{n} held pending an enrolment figure',
    mktNInMarket: '{n} in market',
    mktNInYourMarket: '{n} in your market',
    mktNLmsCells: '{n} LMS cells',
    mktNMarketEvents: '{n} market events',
    mktNMatchingQuery: '{n} matching “{query}”',
    mktNNewSignals: '{n} new signals',
    mktNOfTotalSetAside: '{n} of {total} accounts set aside',
    mktNPersonas: '{n} personas',
    mktNSelected: '{n} selected',
    mktNSetAside: '{n} accounts set aside',
    mktNSignals: '{n} signals',
    mktNamesYourCategories: 'Names your categories ({n})',
    mktNeoDidNotSayWhy: 'NEO did not say why.',
    mktNeoView: 'NEO view',
    mktNetImpact: 'Net impact',
    mktNetImpactTip: 'Whether the competitor evidence here is bad for you (Unfavourable), good for you (Favourable e.g. dissatisfaction / replacement opportunity), Mixed, or Context only (informational, no clear direction).',
    mktNewSignals90d: 'New signals (last 90d)',
    mktNewSignals90dTip: 'Signals dated within the last 90 days.',
    mktNo: 'No',
    mktNoAccountsInGroup: 'No accounts in this group right now.',
    mktNoAccountsInMotionFor: 'No accounts in motion for {scope}.',
    mktNoAccountsInView: 'No accounts in this view.',
    mktNoAccountsMatchQuery: 'No accounts match “{query}”.',
    mktNoAccountsMatchSearch: 'No accounts match this search.',
    mktNoAccountsSetAside: 'No accounts set aside for this market yet.',
    mktNoAccountsWithReason: 'No accounts with this reason in the current filter.',
    mktNoAiCompositionFor: 'No AI readiness composition for {scope}.',
    mktNoAtRisk: 'No accounts at known risk in this lens yet.',
    mktNoAtRiskForCountry: 'No accounts at known risk in this lens for {country} yet.',
    mktNoCachedDataWait20: 'No cached data available. Please wait 20 minutes before refreshing.',
    mktNoCompetitorEvidence: 'No competitor evidence loaded yet.',
    mktNoFinancialCompositionFor: 'No financial capacity composition for {scope}.',
    mktNoLmsDataFor: 'No LMS vendor-stack data for {scope}',
    mktNoPriorVersions: 'No prior versions.',
    mktNoProgrammesInView: 'No programmes in this view.',
    mktNoRecentChanges: 'No recent market changes.',
    mktNoRenewalEvidence: 'No renewal evidence on file yet.',
    mktNoSectorValues: 'No sector values on accounts yet.',
    mktNoVendorDataForCategory: 'No vendor data for this category',
    mktNotAnOpportunity: 'Not an opportunity',
    mktNotLoadedYet: 'Not loaded yet',
    mktNothingChangedAlreadyThere: 'Nothing changed — it was already there.',
    mktNothingClassifiedYet: 'Nothing classified in this lens yet.',
    mktNothingInListYet: 'Nothing in this list yet.',
    mktNothingLocked: 'Nothing locked. Every vendor on your competitor list is in scope.',
    mktNothingToPreview: 'Nothing to preview for this account yet.',
    mktNothingToPreviewTitle: 'Nothing to preview',
    mktOfN: 'of {n}',
    mktOfSeparator: ' of ',
    mktOneAccount: '{n} account',
    mktOneMarketEvent: '{n} market event',
    mktOneNewSignal: '{n} new signal',
    mktOnePersona: '{n} persona',
    mktOneSignal: '{n} signal',
    mktOpenRound: 'Open round',
    mktOpenRounds: 'Open rounds',
    mktOpenSalesMap: 'Open SalesMap',
    mktOpenSetup: 'Open setup',
    mktOpening: 'Opening…',
    mktOpportunities: 'Opportunities',
    mktOpportunity: 'Opportunity',
    mktOtherN: 'Other ({n})',
    mktOurCategories: 'Our categories',
    mktOurCategoriesLine: 'Our categories: {categories}',
    mktOurs: 'Ours',
    mktPageNOfM: 'Page {n} of {m}',
    mktPageSubtitle: 'Your personalised market opportunity and latest GTM intelligence.',
    mktPasteVendorsPlaceholder: 'Paste vendors, one per line or comma-separated',
    mktPasteVendorsToLockPlaceholder: 'Paste vendors to lock, one per line or comma-separated',
    mktPersona: 'Persona',
    mktPersonaBarometer: 'Persona barometer',
    mktPersonasAffected: 'Personas affected',
    mktPersonasAffectedTip: 'Number of target personas with direct competitor evidence — quoted, tagged, named in a case study, or engaged by a competitor actor. Personas tied to the account but not personally named don\'t count.',
    mktPersonasWithCompetitorLinks: 'Personas with competitor links',
    mktPlayersChartTooltip: 'The share of institutions in your market running each vendor for this category. It is a count of institutions, not revenue. Institution shares can sum above 100% because an account can run more than one vendor.',
    mktPleaseTryAgain: 'Please try again',
    mktPreferences: 'Preferences',
    mktPreferencesSaved: 'Preferences saved',
    mktPrefsIntro: 'Set the rules that decide what counts as an opportunity.',
    mktPrefsOnOwnSettings: 'You’re on your own settings. The team’s differ.',
    mktPrefsOnTeamSettings: 'You’re on the team’s settings. Saving will create your own.',
    mktPreviewChange: 'Preview change',
    mktPreviewCounts: 'In your market {sam} · Priority {priority} · Excluded {excluded}',
    mktPreviewFailed: 'Preview failed',
    mktPreviewMoved: 'Moved in {in} · out {out} · promoted {promoted} · demoted {demoted}',
    mktPreviewing: 'Previewing…',
    mktPreviouslyFunded: 'Previously funded',
    mktPriority: 'Priority',
    mktPriorityScoreThreshold: 'Priority score threshold',
    mktPriorityScoreThresholdHint: 'The score an account needs before we flag it as Priority. It blends how well an institution fits what you sell with how much is actually happening there. Set it near 60 and you get institutions that look right for you even if nothing is moving yet; near 70 most will also have a stated problem; at 75 and above both are strong and the list is short. It changes where you start, not who is in your market.',
    mktProductCategory: 'Product category',
    mktProgramme: 'Programme',
    mktProgrammes: 'Programmes',
    mktProgrammesHint: 'Opens on the programmes that name one of your categories; the other two views widen it. A programme with 0 eligible accounts still appears. The gap is the point. A deadline is shown only when the round is open; an active programme shows the date its allocated money must be spent by, which is not something you apply to. Pre-announced means watch this, not that it closes soon.',
    mktProvisional: 'Provisional',
    mktQueueIntroAdjacent: 'Shows vendors already present that you don’t compete with, helping you understand the existing tech stack and where your solution could fit alongside it.',
    mktQueueIntroInMarket: 'Real openings at institutions that fit you less well. Worth working once you have been through Priority.',
    mktQueueIntroPriority: 'Your best openings right now - greenfield, displacement or expansion.',
    mktQueueIntroRest: 'The rest of the market you could sell into. Something is missing before you can judge them. Best fits are at the top.',
    mktRangeOfTotal: '{from}–{to} of {total}',
    mktRareEvent: 'Rare event',
    mktRareEventMeans: 'A rare qualifying event lifted the live activity axis on this score.',
    mktRateLimitActive: '⏸️ Rate limit active',
    mktRateLimitReached: '⚠️ Rate limit reached',
    mktRecalculateFailed: 'Recalculate failed',
    mktRecalculateMarket: 'Recalculate market',
    mktRecalculated: 'Recalculated',
    mktRecalculating: 'Recalculating…',
    mktRecalculationComplete: 'Recalculation complete',
    mktRecency: 'Recency',
    mktRecommendation: 'Recommendation',
    mktRecommendationTip: 'Opens the SalesMap for this account. When proximity reaches 50, a competitive read auto-syncs to the current_vendor row in SalesMap.',
    mktRecomputedCounts: 'In your market {sam} · Priority {priority}.',
    mktRelationship: 'Relationship',
    mktRemove: 'Remove',
    mktRemoveSkipNotFromMarket: 'This account wasn\'t added from Market. Use Disengage instead.',
    mktRemoveSkipNotInAccounts: 'That account isn\'t in your list.',
    mktRenewalDate: 'Renewal date',
    mktRenewalNotSwitchedOn: 'This tab covers the institutions already running your product, so it answers who you might lose rather than who you could win. It is not switched on for this tenant yet.',
    mktRenewalOpen: 'Renewal open',
    mktRenewalWindow: 'Renewal window',
    mktReplacingInHouse: 'Replacing in-house',
    mktRequestEnrichment: 'Request enrichment',
    mktRequesting: 'Requesting…',
    mktRescoringTheMarket: 'Rescoring the market',
    mktRestOfMarket: 'Rest of market',
    mktRestoredToAccounts: 'Restored to accounts',
    mktReturnsOn: 'Returns to your list on {date}',
    mktRisk: 'Risk',
    mktRiskConfirmed: 'Confirmed risk',
    mktRiskConfirmedMeans: 'They have said they are unhappy with us, or their contract ends inside the horizon.',
    mktRiskHint: 'Named facts about what is going wrong — a complaint, dissatisfaction, a published product failing, or a contract end — not a score. Colour still marks confirmed versus watch.',
    mktRiskLookedNothingFound: 'Looked · nothing found',
    mktRiskNotYetChecked: 'Not yet checked',
    mktRiskQuietMeans: 'We looked and found nothing. That is a real answer, not a gap.',
    mktRiskUnknownMeans: 'Nobody has looked at this account. It is not the same as it being quiet.',
    mktRiskWatch: 'Watch',
    mktRiskWatchMeans: 'There is a published failing in our product here, or a competitor has moved into a neighbouring category.',
    mktRollBack: 'Roll back',
    mktRubricFitHint: 'How well an institution suits what you sell: what they use today and how replaceable it is, their size, and how ready they look to change. Turn it up to favour the right-shaped accounts even when nothing is happening there yet.',
    mktRubricHistory: 'Rubric history',
    mktRubricSignalsHint: 'Signs they are actually moving: a stated problem with what they use now, plus procurement, budget, a renewal coming up or a new award. Turn it up to lead with what is happening rather than who looks right on paper.',
    mktRubricWeights: 'Rubric weights',
    mktRunningAgainstUs: 'Running against us',
    mktSaveForEveryone: 'Save for everyone',
    mktSaveForMe: 'Save for me',
    mktSavedRescoreFailed: 'Saved, but rescore failed',
    mktSaving: 'Saving…',
    mktScore: 'Score',
    mktScoresMayReflectPrevious: 'Scores may still reflect the previous settings.',
    mktSearchAccounts: 'Search accounts',
    mktSearchAccountsByName: 'Search accounts by name',
    mktSearchByAccountName: 'Search by account name',
    mktSectorExclude: 'Sector exclude',
    mktSelectSectors: 'Select sectors',
    mktSectors: 'Sectors',
    mktSectorsHint: 'The institution types that make up your market. Choose one or several.',
    mktNOfMSelected: '{n} of {m} selected',
    mktSelectAll: 'Select all',
    mktClearAll: 'Clear',
    mktChooseOneSector: 'Choose at least one sector.',
    mktRescoreStillRunning: 'Still rescoring',
    mktRescoreStillRunningHint: 'Your settings are saved. The market is still being rescored; refresh the page in a few minutes.',
    mktSelectedAccount: 'Selected Account',
    mktSetUpMarket: 'Set up Market',
    mktSetupBodyEditor: 'Market is switched on for this tenant, but nobody has picked a product category yet. Choose one and we will build your opportunity list.',
    mktSetupBodyViewer: 'Market is switched on, but an admin needs to pick a product category before there is anything to show here.',
    mktShare: 'Share',
    mktShareCaption: 'Institution shares can sum above 100%. Long-tail vendors under {pct}% are grouped as Other (muted).',
    mktShowNOlderEvents: 'Show {n} older or off-category events',
    mktShowOneOlderEvent: 'Show {n} older or off-category event',
    mktShowingAccountsOnPage: 'Showing the accounts on this page.',
    mktShowingCachedDataRetry20: 'Showing cached data. Will retry automatically in 20 minutes.',
    mktSpendBy: 'spend by',
    mktSpendEstimateTitle: 'Our estimate of how long the money runs, not a date the funder published',
    mktStage: 'Stage',
    mktStale: 'Stale',
    mktStanding: 'Standing',
    mktStateBuiltInHouse: 'Built in-house',
    mktStateLmsNative: 'LMS-native',
    mktStateNothingInPlace: 'Nothing in place',
    mktStateOpenSource: 'Open source',
    mktStateSuiteModule: 'Suite module',
    mktStatus: 'Status',
    mktStatusEmpty: 'empty',
    mktStatusNotLoaded: 'not loaded',
    mktStrongestAccounts: 'Strongest accounts',
    mktStrongestEvidence: 'Strongest evidence',
    mktStrongestEvidenceAccountTip: 'Highest-scoring single signal for this account — what\'s driving most of the proximity.',
    mktStrongestEvidencePersonaTip: 'Highest-scoring single signal for this persona — what\'s driving most of the proximity.',
    mktStrongestPersonas: 'Strongest personas',
    mktSubset: 'Subset: {measure}',
    mktSumValue: 'Sum: {value}',
    mktSwitching: 'Switching…',
    mktTabCompetitors: 'Competitors',
    mktTabRenewalRisk: 'Renewal risk',
    mktTargetThresholds: 'Target opportunity thresholds',
    mktThisMarket: 'this market',
    mktTipAddressable: 'The ones from that list you could realistically sell to. If this matches Total market, no exclusion rules have been set up yet, so nothing is being filtered.',
    mktTipAiReadiness: 'How far this institution has taken AI in teaching and assessment, from 1 (just exploring) to 5 (built into how they work).',
    mktTipContractScope: 'How deeply their current vendor is embedded: across the whole institution, several faculties, one department, or a trial. The wider it goes the bigger the deal, and the harder they are to unseat.',
    mktTipEvidence: 'How much of the usual background we hold on this account, judged against similar institutions rather than against a perfect record. Where it is thin we hold the score back instead of guessing upward. This is about how sure we are, not how good the account is.',
    mktTipExamined: 'How much of this market anyone has actually looked at. A low number means the research has not been done, not that the market is empty.',
    mktTipFinancial: 'How much room this institution has to fund something new, compared with others of its size and type. It is not a read on what they spend on EdTech.',
    mktTipGrade: 'How solid the case is that their current vendor could lose the account. The strongest is a complaint written into the minutes of a governing body. The weakest is pain we have inferred from their workflow, where nobody has actually complained.',
    mktTipInMarket: 'Accounts where we found something to work with: either the vendor they use looks like it could lose the account, or nobody has bought anything and there is a real need. It does not mean a deal is open.',
    mktTipIncumbent: 'Who they use for this category today. Institutions usually run different vendors in different categories, so this is the one that matters for this deal, not the only one they have.',
    mktTipKnown: 'Every institution we hold a record for in the countries you cover. Nothing has been filtered out yet.',
    mktTipMomentum: 'Purchase momentum over the last 4 and 12 weeks. Negative scores indicate unfavourable signals; positive scores indicate favourable signals.',
    mktTipPriority: 'The accounts from your market that also score high enough on fit to be worth starting with. Start here.',
    mktTipScore: 'Out of 100. How well the institution fits what you sell, combined with what is happening there right now, then held back if we are working from thin evidence.',
    mktTipType: 'What kind of deal this would be. Hover any badge for what it means.',
    mktTopCompetitor: 'Top competitor',
    mktTopCompetitorAccountTip: 'Competitor with the highest weighted signal contribution at this account. Weighted by signal strength and recency.',
    mktTopCompetitorPersonaTip: 'Competitor with the highest weighted signal contribution for this persona. Weighted by signal strength and recency.',
    mktTotalCompetitorSignals: 'Total competitor signals',
    mktTotalFundingEligibility: 'Total funding eligibility',
    mktTotalFundingEligibilityHint: 'What this institution could apply for across every open round it is eligible for. A ceiling, not a forecast — eligibility is a verified register entry, and we have no evidence any of them has applied.',
    mktTotalMarket: 'Total market',
    mktTrajectory24m: 'Trajectory (24m)',
    mktTrajectoryTooltip: 'Proximity score replayed at monthly anchors over the last 24 months. Rising bars = fresh signals landing. Falling bars = evidence ageing off.',
    mktTriggers: 'Triggers',
    mktUnfavourable: 'Unfavourable',
    mktUnfavourableBlockers: 'Unfavourable competitor blockers',
    mktUnknownVendor: 'Unknown vendor',
    mktUpcomingWindows: 'Upcoming windows',
    mktUpdate: 'Update',
    mktUpdatingCompetitorSet: 'Updating competitor set and filters…',
    mktUseTeamSettings: 'Use the team’s settings',
    mktUserSelected: 'User Selected',
    mktUsingCachedDataRetryIn: 'Using cached data. Will retry in {time}.',
    mktUsingTeamSettings: 'Using the team’s settings',
    mktValueWholeContract: 'Value shown is the whole contract, not one year. That is {annual} a year.',
    mktValueWholeContractOverYears: 'Value shown is the whole contract, not one year. That is {annual} a year over {years} years.',
    mktWhatChanged: 'What changed',
    mktWhatItIsWorth: 'What it is worth',
    mktWhatThisMeans: 'What this means',
    mktWhatThisMeansFundingHint: 'The same voice as the Overview brief. Eligibility is not pipeline.',
    mktWhoIsInPlace: 'Who is in place',
    mktWhoMightILose: 'Who might I lose',
    mktWholeMarket: 'Whole market',
    mktWhy: 'Why',
    mktWhyNow: 'Why now',
    mktWhyTheyQualify: 'Why they qualify',
    mktWhyThisScore: 'Why this score',
    mktWindowClosed: 'window closed',
    mktNoFixedDeadline: 'no fixed deadline',
    mktStandingProgramme: 'standing programme',
    mktDateNotPublished: 'date not yet published',
    mktClosedOn: 'closed {date}',
    mktNoDeadlinePublished: 'no deadline published',
    mktWonBefore: 'Won before',
    mktWorkingAgainst: 'Working against',
    mktWorkingInFavour: 'Working in favour',
    mktWorthALook: 'Worth a look',
    mktYes: 'Yes',
    mktYourPilotIsFull: 'Your pilot is full',
    procToastDataIncomplete: 'Some data may be incomplete',
    procToastTimeoutPage: 'The server timed out on a page of results. Showing partial data.',
    procToastTimeoutRecords: 'The server timed out loading additional records. Showing partial results.',
    procToastLoadingDeals: 'Loading deals',
    procToastFetchingInitialData: 'Fetching initial data',
    procToastLoadingMoreDeals: 'Loading more deals',
    procToastFetchingLiveRadarDeals: 'Fetching Live Radar deals',
    procToastFetchedLiveDeals: 'Fetched {n} live deals…',
    procToastFailedLiveDeals: 'Failed to load live deals',
    procLoadingRestOfMarket: 'Loading the rest of the market…',
    procToastFetchingLabelDeals: 'Fetching {label} deals…',
    procToastProcessingDealsLoaded: 'Processing… {n} deals loaded',
    procToastFailedLabelDeals: 'Failed to load {label} deals',
    procToastLabelReady: '{label} ready',
    procToastFetchedVisible: '{fetched} fetched, {visible} visible with active filters.',
    procToastStatusClearFailed: 'Status clear failed',
    procToastStatusUpdateFailed: 'Status update failed',
    procToastValueOverrideFailed: 'Value override failed',
    procRetired: 'Retired',
    procDealActivityUnavailable: 'Deal activity unavailable — status and viewer badges may be incomplete. Try refreshing.',
    procMapNoDeals: 'No deals',
    procMapTotalValueUsd: 'Total value (USD)',
    procMapTop: 'Top',
    procMapMetric: 'Map metric:',
    procMapTotalValue: 'Total value',
    procMapCountries: 'Countries',
    procMapClearSelection: 'Clear selection',
    procRtbRouteFramework: 'framework',
    procRtbRouteConsortium: 'consortium',
    procRtbRouteCooperative: 'cooperative',
    procRtbRouteBuyingOrg: 'buying org',
    procRtbRouteProgramme: 'programme',
    procRtbThroughFramework: 'Through a framework',
    procRtbThroughReseller: 'Through a reseller',
    procRtbDirectToSupplier: 'Direct to the supplier',
    procAllCategories: 'All categories',
    procYourCategories: 'Your categories',
    procRtbSelectCategoriesForLens: 'Select categories to use this lens',
    procFailedToLoad: 'Failed to load',
    procRtbSubtitle: 'How this market actually buys — the vehicles, and who sells through them.',
    // Route to Buy verdicts (2026-11-02)
    procRtbVerdicts: 'Verdicts',
    procRtbNotEnoughRouteDeals: 'Not enough deals with a known route in these categories yet.',
    procRtbMarketSignals: 'Market signals',
    procRtbTileLocalEntity: 'Local entity',
    procRtbTileFrameworks: 'Frameworks',
    procRtbTileFriendliness: 'Friendliness',
    procRtbTilePublicShare: 'Public share',
    procRtbTileIntegrations: 'Integrations',
    procRtbLeNotRequired: 'Not required',
    procRtbLeSomeRoutes: 'Required for some routes',
    procRtbLeRequired: 'Required',
    procRtbLeEffectively: 'Effectively required',
    procRtbLeUnknown: 'Unknown',
    procRtbConfidence: 'Confidence {c}',
    procRtbToJoinN: '{n} to join',
    procRtbWithCompetitorN: '{n} with a competitor on it',
    procRtbNoCompetitorListed: 'No competitor listed',
    procRtbTeachingInstitutionsN: '{n} teaching institutions',
    procRtbPlanFor: 'What to plan for here:',
    procRtbBasedOnPublic: 'Based on {n} public institutions.',
    procRtbContractStartMonths: 'Contract start months',
    procRtbBiddersPerTender: 'Bidders per tender',
    procRtbBiddersLine: '{m} median · {s}% single-bid',
    procRtbYou: 'You',
    procRtbYouOnIt: 'On it',
    procRtbYouViaReseller: 'Via reseller',
    procRtbYouNotListed: 'Not listed',
    procRtbYouToJoin: 'To join',
    procRtbCompetitorOnIt: 'Competitor on it',
    procRtbEnds: 'Ends {d}',
    procRtbEndsLabel: 'Ends',
    procRtbDealsThroughIt: 'Deals through it',
    procRtbNoPublishedCallOffs: 'No published call-offs',
    procRtbNoVehicleForCategories: 'No buying vehicle in {country} has a lot for your categories, a recorded deal in them, or a competitor on it.',
    procRtbTipLocalEntity: 'Whether a foreign vendor can bid, contract and get paid without a local company or local tax ID. Required for some routes: needed for some procedures or buyers (e.g. national-only tenders). Effectively required: not required by law, but in practice you cannot sell or be paid without a local entity or representative.',
    procRtbTipFrameworks: 'Frameworks and buying routes in this market with a lot in your categories (or a general software lot) that you are not on record as being on. Competitor on it: a competitor is a named supplier. Status comes from public supplier lists.',
    procRtbTipFriendliness: 'How easy public institutions in this market make it for a new supplier to sell to them. Each institution is scored 0–3 on six measures. Transparency: publishes its own spending thresholds and buying rules. Portal: runs tenders on an e-procurement portal and a national notice board. Contract terms: publishes standard terms that are balanced. Payment: publishes its payment period. Pilot route: can buy a trial without a full tender. New-supplier ease: open to new and foreign suppliers. The % is the average score as a share of the maximum. 78% or more usually means 6–9 month buying cycles, 56–77% means 9–12 months, below 56% means 12–18 months. Private institutions are excluded: they publish no procurement rules, so their score would reflect missing information, not difficulty.',
    procRtbTipPublicShare: 'Share of the teaching institutions in this market (universities, colleges, specialist HEIs) that are public. Public buyers run formal tenders, thresholds and frameworks; private institutions usually buy directly, through relationships and shorter, less visible processes.',
    procRtbTipIntegrations: 'Market share of LMS and SIS platforms among teaching institutions — commercial, open source (e.g. Moodle) and state-provided (e.g. Apogée / Pégase from AMUE in France, USOS in Poland) — excluding competitors: the systems to integrate with.',
    procRtbTipContractStart: 'Months in which contracts in your categories most often start.',
    procRtbTipBidders: 'Median number of bids per tender, where tenders publish it.',
    procRtbCoverageNote: 'Covers every public buyer in the market, across all sectors — not only higher education.',
    procRtbLoadingCountry: 'Loading {country}…',
    procRtbMoreCountries: 'Other countries',
    procRtbMoreCountriesOption: 'Other countries…',
    procRtbHowCountryBuys: 'How {country} buys',
    procLoading: 'Loading…',
    procUpdating: 'Updating…',
    procRtbVehiclesThatMatter: 'Vehicles that matter',
    procRtbNoFrameworkMine: 'No named framework carries your categories in {country}.',
    procRtbNoFrameworkAll: 'No named framework carries these deals in {country}.',
    procRtbDealsLower: 'deals',
    procRtbBuyersLower: 'buyers',
    procRtbSuppliersLower: 'suppliers',
    procRtbMakersLower: 'makers',
    procRtbValueLower: 'value',
    procRtbRenewIn12Mo: 'renew in 12 mo',
    procRtbInsideVehicle: 'Inside the vehicle',
    procRtbWhoSellsThroughIt: 'Who sells through it (deals)',
    procRtbResellersOnIt: 'Resellers on it (deals)',
    procRtbNoResellerOnVehicle: 'No reseller recorded on this vehicle',
    procRtbAreYouOnIt: 'Are you on it?',
    procRtbYesSoldThroughIt: 'Yes — your product is sold through it',
    procRtbNotOnRecord: 'Not on record',
    procRtbNotConfigured: 'Not configured for this tenant',
    procRtbResellersIn: 'Resellers in {country}',
    procRtbResellerShare: '{n}% of deals with a known route go through one',
    procRtbNoResellerMine: 'No reseller sells in your categories in {country} — every recorded deal went direct to the vendor.',
    procRtbNoResellerAll: 'No reseller sells here in {country} — every recorded deal went direct to the vendor.',
    procRtbNotStated: 'not stated',
    procRtbInsideReseller: 'Inside the reseller',
    procRtbMakersItCarries: 'Makers it carries (deals)',
    procRtbRecordNamesOnlyReseller: 'The record names only the reseller, never the maker',
    procRtbMakerNamedPct: 'Maker named on {n}% of its deals — the rest record only the reseller',
    procRtbSellsThrough: 'Sells through',
    procRtbDirectToBuyer: 'Direct to the buyer — no vehicle recorded',
    procRtbCarriesYourProduct: 'Carries your product?',
    procYes: 'Yes',
    procRtbUpTo: 'up to {amount}',
    procRtbAbove: 'above {amount}',
    procRtbHowBigBeforeTender: 'How big before it goes to tender',
    procRtbTypicalAcross: 'Typical across {n} buyers',
    procRtbMostRange: 'most {from}–{to}',
    procRtbNationalRule: 'National rule',
    procMonths24Plus: '24+ months',
    procVendorsForSelectedCategories: 'Vendors for selected categories',
    procUpdatingResults: 'Updating results',
    procClickHeadersToSort: 'Click column headers to sort',
    procFilterAppliesTableOnly: 'filter applies to this table only — the map and totals above are not narrowed.',
    procBundledContract: 'Bundled contract',
    procNoRationaleProvided: 'No rationale provided.',
    procShowingRangeDeals: 'Showing {from}–{to} of {total} deals',
    procShowingRangeAccounts: 'Showing {from}–{to} of {total} accounts',
    procMatchingDealsOnePerAccount: '{n} matching deals; table shows one top deal per account',
    procMapNoClosedDeals: 'No closed deals',
    procTopVendorsByDeals: 'Top vendors by deals',
    procTopVendorsByValue: 'Top vendors by value',
    procClear: 'Clear',
    procTop3DealsByValue: 'Top 3 deals by value',
    procMarketShare: 'Market share',
    procPieAccountsPct: '{n} accounts ({pct}%)',
    procMarketShareByVendor: 'Market share by vendor',
    procNoIncumbentDataCountry: 'No incumbent data for this country.',
    procClickCountryForMarketShare: 'Click a country on the map to see market share.',
    procWin0_6Mo: '0–6 mo',
    procWin6_12Mo: '6–12 mo',
    procWin12_24Mo: '12–24 mo',
    procWin24PlusMo: '24+ mo',
    procWinNoEndDate: 'No end date',
    procHowLabelIsMeasured: 'How {label} is measured',
    procMoveFrom: 'from',
    procMoveTo: 'to',
    procPlusNMore: '+{n} more',
    procCouldNotLoadChanges: 'Could not load the changes.',
    procWon: 'Won',
    procLost: 'Lost',
    procNoRecordedChanges: 'No recorded changes.',
    procWonLostCounts: 'won {won} · lost {lost}',
    procEstablishedRelationships: '{n} established relationships',
    procLoadingCategoryIntel: 'Loading category intelligence…',
    procNoMeasuredHistory: 'No measured history yet for the selected categories.',
    procRcViewReplacement: 'Replacement cycle',
    procRcViewExpiry: 'Contract expiry',
    procRcIntro: 'Installed kit by age, oldest first. Age runs from the last system purchase; service contracts and consumables do not reset it. Kit usually comes up for replacement after 5–7 years, long after its contract has ended.',
    procRcKpiInstalled: 'Installed kits',
    procRcKpiInstalledSub: 'from {n} closed deals',
    procRcKpiWindow: 'In replacement window',
    procRcKpiWindowSub: '{n}+ years since purchase',
    procRcKpiDue: 'Due or overdue',
    procRcKpiDueSub: '{n}+ years since purchase',
    procRcKpiOwn: 'Your kit ageing',
    procRcKpiOwnSub: 'your installs {n}+ years old',
    procRcWindowFilter: 'Replacement window:',
    procRcInstalled: 'Installed',
    procRcAge: 'Age',
    procRcWindow: 'Replacement window',
    procRcServiceEnds: 'Service contract ends',
    procRcYourKit: 'Your kit',
    procRcVia: 'via {reseller}',
    procRcPurchases: '{n} system purchases',
    procRcFloorHelp: 'No system purchase is published; dated from the earliest service or supply record, so the kit is at least this old.',
    procRcYears: '{n} yrs',
    procRcShowing: 'Showing {shown} of {total} installed kits',
    procRcBandOverdue: 'Overdue',
    procRcBandDue: 'Due',
    procRcBandPlan: 'Start planning',
    procRcBandMid: 'Mid-life',
    procRcBandNew: 'New',
    procRcBandUndated: 'Undated',
    procRcRangeOverdue: '12+ yrs',
    procRcRangeDue: '7–12 yrs',
    procRcRangePlan: '5–7 yrs',
    procRcRangeMid: '3–5 yrs',
    procRcRangeNew: 'under 3 yrs',
    procRcRangeUndated: 'no date',
    procRcRangeYears: '{a}–{b} yrs',
    procRcRangeOver: '{a}+ yrs',
    procRcRangeUnder: 'under {a} yrs',
    procRcSincePurchase: '{n}+ years since purchase',
    procRcOwnYearsOld: 'your installs {n}+ years old',
    procRcRadarSubtitle: 'How old the installed base is, who holds the ageing kit, and what institutions did when they last replaced it.',
    procRcKpiDated: '{n} with a purchase date',
    procRcUpCompetitor: 'Competitor kit up for replacement',
    procRcUpCompetitorSub: 'competitor installs {n}+ years old',
    procRcUpOwn: 'Your kit up for renewal',
    procRcUpOwnSub: 'your installs {n}+ years old',
    procRcSwitchRate: 'Switched maker at replacement',
    procRcSwitchTooFew: '{n} replacements seen; a rate needs {min}',
    procRcSwitchSub: '{switched} switched · {retained} kept the same maker',
    procRcMatrixTitle: 'Installed base by age and sentiment',
    procRcMatrixHelp: 'Each cell counts installed kits. Age is from the last system purchase.',
    procRcTotal: 'Total',
    procRcSuppliersTitle: 'Who holds the ageing kit',
    procRcSuppliersHelp: 'Installs per maker; the amber part is {n}+ years old.',
    procRcAgeingOf: '{ageing} of {n} ageing',
    procRcMovesTitle: 'Maker changes at replacement',
    procRcMovesHelp: 'A re-purchase counts as a replacement only when it comes {n}+ years after the previous one. {events} seen.',
    procRcNoMoves: 'No maker changes recorded at replacement yet.',
    procRcInstallsAcross: '{installs} installed kits at {accounts} institutions across {scope}.',
    procRcSwitchHelp: 'Each time an institution buys the same kind of system again at least {n} years after its last purchase, NEO counts it as a replacement: kept the same maker, or switched. Purchases closer together are treated as extra kit, not a replacement.',
    procRcMakersPerAccount: 'Makers per account',
    procRcAgeHelp: 'Age runs from the last purchase of the system itself. Service contracts and consumables do not reset it. Kit like this is usually replaced after {a}–{b} years.',
    procRcPipelineTitle: 'Replacement pipeline',
    procRcPipelineHint: 'Installed kit by years since purchase',
    procRcPipelineNoteBefore: 'Competitor kit in the replacement window:',
    procRcPipelineNoteAfter: 'installs are {n}+ years old.',
    procRcNetHint: 'Maker changes at replacement: won minus lost',
    procRcAgeingByMaker: 'Ageing kit by maker',
    procRcAgeingByCountry: 'Ageing kit by country',
    procRcAgeingByType: 'Ageing kit by institution type',
    procRcAgeingHint: 'Share of installs {n}+ years old',
    procRcAgeingEmpty: 'Too few installs to break down yet.',
    procRcOfInstalls: 'of {n} installs',
    procRcGridTitle: 'Incumbent position by replacement window',
    procDisplacementSubtitle: 'How often incumbents actually lose this category, and which contracts reopen next.',
    procAwardsAcrossInstitutions: '{deals} awards across {scope} and {accounts} institutions.',
    procNMarkets: '{n} markets',
    procLastUpdatedDate: 'Last updated: {date}',
    procGlobal: 'Global',
    procHighlights: 'Highlights',
    procAnnualChurn: 'Annual churn',
    procChurnSubSplit: 'of supplier relationships — {switched}% switched vendor, {stopped}% stopped buying the category',
    procChurnSubPerYear: 'of supplier relationships ending per year',
    procChurnHelpIntro: 'We count only suppliers an institution bought from at least twice.',
    procChurnHelpSwitchedLabel: 'Switched vendor:',
    procChurnHelpSwitchedBody: 'The institution continued buying in the category but switched suppliers — a potential win.',
    procChurnHelpStoppedLabel: 'Stopped buying:',
    procChurnHelpStoppedBody: 'The institution stopped buying in the category. No supplier won; treat this as an upper bound, as unpublished renewals look identical.',
    procChurnHelpAnnualLabel: 'Annual rate:',
    procChurnHelpAnnualBody: 'Averaged across all available years, not year-on-year.',
    procChurnHelpMultiLabel: 'Multi-supplier accounts:',
    procChurnHelpMultiBody: 'Losing a supplier may mean losing a panel position, not the whole customer.',
    procSuppliersPerAccount: 'Suppliers per account',
    procPctBuyersUseOne: '{n}% of buyers use just one',
    procRebuyCycle: 'Re-buy cycle',
    procUnitMo: 'mo',
    procMedianGapBetweenAwards: 'median gap between awards',
    procReopeningWithin12Mo: 'Reopening within 12 mo',
    procLiveContractsAcrossMarkets: 'live contracts across {n} markets',
    procValueInNext12Mo: 'Value in next 12 mo',
    procFromNExpiringContracts: 'from {n} expiring contracts',
    procRenewalPipeline: 'Renewal pipeline',
    procLiveContractsByTimeToExpiry: 'live contracts by time to expiry',
    procPipelineNoteBefore: '{n} contracts reopen within six months. At {churn}% annual churn, roughly',
    procPipelineNoteAfter: 'of these incumbents will actually lose the account — the rest will be re-awarded or quietly extended.',
    procNetPositionChange: 'Net position change',
    procAccountsWonMinusLost: 'accounts won minus lost',
    procTooFewRepeatAwards: 'Too few repeat awards in this market to measure net movement.',
    procNetWinners: 'Net winners',
    procNetLosers: 'Net losers',
    procNetPositionNote: 'Positions won minus positions lost across all recorded supplier changes. The losers are the openings — an incumbent shedding accounts one at a time.',
    procChurnByCountry: 'Churn by country',
    procTopMarketsByVolume: 'top markets by volume',
    procChurnByCountryEmpty: 'Not enough repeat history in any single market to rate reliably.',
    procChurnByInstitutionType: 'Churn by institution type',
    procAnnualEstablishedRelationships: 'annual, established relationships',
    procChurnByInstitutionEmpty: 'Institution type is not recorded on enough accounts in this category.',
    procIncumbentByDecisionWindow: 'How the incumbent is doing, by decision window',
    procIncumbent: 'Incumbent',
    procAccountLower: 'account',
    procAccountsLower: 'accounts',
    procSentimentIncumbent: '{sentiment} incumbent',
    procNoContractEndDate: 'no contract end date recorded',
    procContractEndsIn: 'contract ends in {window}',
    procAmountTotal: '{amount} total',
    procLoadingAccounts: 'Loading accounts…',
    procNoAccountsForCell: 'No accounts recorded for this cell.',
    procContractEnd: 'Contract end',
    procContractValue: 'Contract value',
    procWhy: 'Why',
    procDealCount: 'Deal count',
    procNegativeSharePct: 'Negative share (%)',
    procNoDisplacementData: 'No displacement data found with current filters.',
    procTryAdjustingFilters: 'Try adjusting category or country filters.',
    procCategoryRiskMosaic: 'Category Risk Mosaic',
    procCategoryRiskMosaicHelp: 'Churn risk by category and expiry bucket. Click a segment or tile to see accounts.',
    procMetric: 'Metric',
    procBucketMonths: '{b} months',
    procAtRiskValueShare: 'At-risk: {value} ({pct}%)',
    procDealLower: 'deal',
    procPctOfCell: '{pct}% of cell',
    procShowTop15: 'Show top 15',
    procShowAllCategories: 'Show all categories',
    procRationaleHighlights: 'Rationale highlights',
    procSearchRationale: 'Search rationale...',
    procNoAccountsMatch: 'No accounts match.',
    procLess: 'Less',
    procReadMore: 'Read more',
    procYearLabel: 'Year: {year}',
    procYou: 'you',
    procEnrolment: 'Enrolment',
    procUsdPerStudentYear: '$/student/year',
    procOverTermWholeContract: 'over {months} months · {amount}/student for the whole contract',
    procBenchEmptyIntro: 'No benchmark data for the current filters. Deals need',
    procBenchFieldEnrolment: 'enrolment',
    procBenchFieldDealValue: 'deal value (USD)',
    procBenchFieldSupplier: 'supplier',
    procBenchAndANumeric: 'and a numeric',
    procBenchFieldInitialTerm: 'initial term',
    procBenchEmptyOutro: 'so the price can be put on a per-year basis.',
    procBenchExcludedNoTermOne: '{n} deal matched the filters but carry no contract term, so they cannot be annualised.',
    procBenchExcludedNoTermMany: '{n} deals matched the filters but carry no contract term, so they cannot be annualised.',
    procBenchTitle: 'Enrolment vs $ per student per year',
    procBenchTitleMedian: 'Median $ per student per year',
    procBenchAnnualised: 'Annualised by contract term.',
    procTrendMedianNote: 'Each line is the yearly median, so one outlier deal cannot move it.',
    procTrendShortTermNote: 'Deals shorter than 4 months are left out: their price cannot be read as a yearly one.',
    procVendorVsCompetitors: '{vendor} vs competitors.',
    procScatter: 'Scatter',
    procTrendByCategory: 'Trend by category',
    procTrendByVendor: 'Trend by vendor',
    procTrendSuppliers: 'Suppliers',
    procTrendRuleAll: 'All suppliers',
    procTrendRuleExclude: 'Exclude selected',
    procTrendRuleOnly: 'Only selected',
    procTrendRuleSearch: 'Search suppliers',
    procTrendRuleSummaryExclude: 'Excluding {n}',
    procTrendRuleSummaryOnly: 'Only {n}',
    procTrendRuleClear: 'Clear',
    procTrendRuleDeals: '{n} deals',
    procTrendRuleHint: 'Applies to Trend by category only. Saved for this category selection.',
    procTrendLegendHint: 'Click a supplier in the legend to highlight its line.',
    procTrendLegendClear: 'Show all lines',
    procResetView: 'Reset view',
    procFxFootnote: '* A fixed FX rate to USD is assumed for all currencies.',
    procWeakFit: 'Weak Fit',
    procNoFit: 'No Fit',
    procLowEvidence: 'Low Evidence',
    procFitOutOfScopeTitle: 'Deal category does not match any active product profile',
    procFitColumnTitle: 'Out of scope = deal category doesn\'t match any active product profile',
    acctRemovedFromListTitle: 'Removed {name} from your list',
    acctAccountLower: 'account',
    acctRemovedFromListDesc: 'You can add it back any time from Market.',
    acctCouldNotRemoveAccount: 'Could not remove account',
    acctPleaseTryAgainNoDot: 'Please try again',
    acctSaved: 'Saved',
    acctTargetProductUpdated: 'Target product updated',
    acctCouldNotSaveTargetProduct: 'Could not save target product',
    acctTabContent: 'Content',
    acctTabProducts: 'Products',
    acctTabFinancialsAssets: 'Financials & Assets',
    acctTabSalesMap: 'Sales Map',
    acctTabCompetitorsCount: 'Competitors ({n})',
    acctEnrichmentUnavailable: 'Enrichment unavailable',
    acctMarketEnrichmentNotEnabled: 'Market enrichment is not enabled for this tenant.',
    acctCouldNotRequestEnrichment: 'Could not request enrichment',
    acctMarketLensNotConfigured: 'Market lens is not configured yet.',
    acctEnrichmentAlreadyRequested: 'Enrichment already requested',
    acctEnrichmentRequested: 'Enrichment requested',
    acctTryAgainLater: 'Try again later',
    acctRequesting: 'Requesting…',
    acctRequestPersonaEnrichment: 'Request persona enrichment',
    acctMeddpiccIdentifyPain: 'Identify Pain',
    acctMeddpiccMetrics: 'Metrics',
    acctMeddpiccPainTriggerTiming: 'Pain trigger · Timing',
    acctMeddpiccCompetitionIncumbent: 'Competition / Incumbent',
    acctMeddpiccEconomicBuyer: 'Economic Buyer',
    acctMeddpiccDecisionProcess: 'Decision Process',
    acctMeddpiccPaperProcess: 'Paper Process',
    acctMeddpiccChampion: 'Champion',
    acctMeddpiccTipPainNeed: 'What problem makes them want to change?',
    acctMeddpiccTipImpactScale: 'Quantified scale and impact — numbers only.',
    acctMeddpiccTipWhyNow: 'Why now — what dated trigger is driving the window?',
    acctMeddpiccTipCurrentVendor: 'Who they use today and how they feel about it.',
    acctMeddpiccTipBuyerOwner: 'Who can fund and sign off.',
    acctMeddpiccTipDecisionProcess: 'Who approves, in what sequence, through which committees.',
    acctMeddpiccTipPaperProcess: 'Procurement route, framework, renewal, contracting requirements.',
    acctMeddpiccTipChampion: 'Internal advocate — operational owner with a personal win.',
    acctSalesPointPainNeed: 'Pain / Need',
    acctSalesPointImpactScale: 'Impact / Scale',
    acctSalesPointWhyNow: 'Why Now / Timing',
    acctSalesPointCurrentVendor: 'Current Vendor / Competition',
    acctSalesPointBuyerOwner: 'Buyer / Owner',
    acctSalesPointDecisionProcess: 'Decision Process',
    acctSalesPointPaperProcess: 'Procurement / Paper Process',
    acctSalesPointChampion: 'Champion / Key Persona',
    acctSalesPointRecommendedNextMove: 'Recommended Next Move',
    acctStrengthTipMissing: 'No useful evidence on file for this point yet.',
    acctStrengthTipVeryLow: 'Generic or vague — not clearly linked to this account.',
    acctStrengthTipLow: 'Some relevant signal, but mostly inferred.',
    acctStrengthTipMedium: 'Evidence-backed, but key details are missing.',
    acctStrengthTipHigh: 'Recent, specific, and category-aligned evidence.',
    acctStrengthTipVeryHigh: 'Strongly evidenced and validated (rare in V1).',
    acctStrengthMissing: 'Missing',
    acctStrengthVeryLow: 'Very Low',
    acctStrengthVeryHigh: 'Very High',
    acctKeepDerived: 'Keep derived',
    acctEdited: 'Edited',
    acctAutoDetected: 'Auto-detected',
    acctAutoDetectedTooltip: 'Flagged automatically by the Competitor Intel module. Rep edits will overwrite this rationale.',
    acctShowLessLower: 'show less',
    acctShowMoreLower: 'show more',
    acctSourcesCount: 'Sources ({n})',
    acctRepLower: 'rep',
    acctTeamNoteFrom: 'Team note from {author}',
    acctClearOverride: 'Clear override',
    acctRationale: 'Rationale',
    acctRationalePlaceholder: 'Why is this what we know? E.g. \'Confirmed on call with X on Y — they said Z\'',
    acctOverrideScoreOptional: 'Override score (optional)',
    acctRationaleRequired: 'Please add a rationale before saving.',
    acctSalesMapUpdated: 'SalesMap updated',
    acctCouldNotSaveEdit: 'Couldn\'t save your edit',
    acctOverrideCleared: 'Override cleared',
    acctCouldNotClearOverride: 'Couldn\'t clear override',
    acctGetAdvice: 'Get Advice',
    acctCouldNotLoadSalesMap: 'Couldn\'t load Sales Map',
    acctCouldNotRefreshSalesMap: 'Couldn\'t refresh Sales Map — showing previous data.',
    acctSalesMapTitle: 'Sales Map — {account}',
    acctGeneratedAt: 'Generated {date}',
    acctPrepareForMeeting: 'Prepare for Meeting',
    acctSuggestNextMove: 'Suggest Next Move',
    acctSalesMapSparseBanner: 'This account has little evidence on file yet. Most rows are missing — the bot still has data on country-level market context if you click Get Advice.',
    acctNbmTarget: 'Target',
    acctNbmSuggestedRole: 'Suggested role to target',
    acctNbmNoNamedContactTypically: 'No named contact on file — typically: {examples}',
    acctNbmNoNamedContact: 'No named contact on file.',
    acctNbmFindOneFirst: 'Find one before reaching out. Open the Personas tab.',
    acctNextBestMove: 'Next Best Move',
    acctNbmAskNext: 'Ask in your next conversation',
    acctNeoRecommendation: 'NEO Recommendation',
    acctProfileOutreach: 'Profile Outreach',
    acctDiyPlaybook: 'DIY Playbook',
    acctPlaybookTrackingSoon: 'Playbook Tracking Coming Soon',
    acctPlaybookTrackingSoonDesc: 'Playbook engagement tracking will be available once the backend API is deployed.',
    acctNoPlaybooksOrOutreach: 'No Playbooks or Outreach',
    acctNoPlaybooksOrOutreachDesc: 'This account is not enrolled in any playbooks and has no outreach history.',
    acctEnrolledPersonasOne: 'Enrolled: {n} persona',
    acctEnrolledPersonasMany: 'Enrolled: {n} personas',
    acctCompletedOn: 'Completed: {date}',
    acctStartedOn: 'Started: {date}',
    acctEnrolledPersonasLabel: 'Enrolled Personas:',
    acctTouchOf: 'Touch {current} of {total}',
    acctNextOn: 'Next: {date}',
    acctLoadingAdHocOutreach: 'Loading ad-hoc outreach…',
    acctActivePlaybooks: 'Active Playbooks',
    acctCompletedPlaybooks: 'Completed Playbooks',
    acctScheduledPending: 'Scheduled — pending',
    acctScheduledFor: 'Scheduled for {date}',
    acctAdHocMessages: 'Ad-hoc Messages',
    acctMsgCountOne: '{n} msg',
    acctMsgCountMany: '{n} msgs',
    acctManualCompletion: 'Manual completion',
    acctNotesLoadErrorContext: 'Notes could not be loaded for this context. Try refreshing the page.',
    acctNotesLoadErrorGeneric: 'Could not load notes. Please try again.',
    acctCouldNotSaveThisNote: 'Could not save this note.',
    acctFailedToSaveNote: 'Failed to save note.',
    acctCannotDeleteNote: 'You can\'t delete this note.',
    acctFailedToDeleteNote: 'Failed to delete note.',
    acctNoteAuthorYou: 'You',
    acctNoteAuthorTeam: 'Team',
    acctNoteAuthorTeammate: 'Teammate',
    acctLoadingNotes: 'Loading notes...',
    acctAddNote: 'Add Note',
    acctWriteNotePlaceholder: 'Write your note here...',
    acctSaveNote: 'Save Note',
    acctNoNotesYet: 'No notes yet. Add your first note above.',
    acctDeleteNote: 'Delete note',
    acctCouldNotLoadCompetitorData: 'Couldn\'t load competitor data — try again.',
    acctCompetitorsAtThisAccount: 'Competitors at this account',
    acctSignalTimeline: 'Signal timeline',
    acctCompetitorsLinkedToPersona: 'Competitors linked to this persona',
    acctCompSigAccountReference: 'Account named in competitor content',
    acctCompSigAccountAdoption: 'Competitor adoption at account',
    acctCompSigPartnerEcosystem: 'Competitor partner ecosystem',
    acctCompSigProcurementSignal: 'Procurement signal',
    acctCompSigEventOverlap: 'Event overlap',
    acctCompSigDisplacementOpportunity: 'Displacement opportunity',
    acctCompSigWeaknessSignal: 'Competitor weakness',
    acctCompSigPersonaInteraction: 'Persona engaged by competitor',
    acctCompSigPersonaReference: 'Persona named in competitor content',
    acctCompSigPersonaAdoption: 'Persona linked to competitor tool',
    acctCompSigGeneric: 'Competitor signal',
    sigUpdEcosystemSignal: 'Ecosystem Signal',
    sigUpdMarketEvent: 'Market Event',
    sigUpdRegionalEvent: 'Regional Event',
    sigUpdProductRelease: 'Product Release',
    sigUpdProductLaunch: 'Product Launch',
    sigUpdPressRelease: 'Press Release',
    sigUpdPartnershipAnnouncement: 'Partnership Announcement',
    sigUpdInvestorNews: 'Investor News',
    sigUpdWebinarEvent: 'Webinar Event',
    sigUpdVendorMetricClaim: 'Vendor Metric Claim',
    sigUpdExternalAccountAdoption: 'External Account Adoption',
    sigUpdExternalReferenceAccount: 'External Reference Account',
    sigUpdMarketCommentary: 'Market Commentary',
    sigUpdConferenceRecap: 'Conference Recap',
    acctImpactUnfavourable: 'Unfavourable',
    acctImpactFavourable: 'Favourable',
    acctImpactMixed: 'Mixed',
    acctImpactContextOnly: 'Context only',
    acctProximityElevated: 'Elevated',
    acctProximityWatch: 'Watch',
    acctProximityNoEvidence: 'No evidence',
    acctJustNow: 'just now',
    acctMoreInfo: 'More info',
    acctProximityScoreTooltip: '0 to 100. Rises when fresh competitor signals land at this account/persona. Decays naturally as evidence ages.',
    acctImpactTooltip: 'Direction of the competitor evidence. Unfavourable = bad for you (competitor entrenched). Favourable = good for you (dissatisfaction / replacement opportunity). Mixed = both signals present. Context only = informational.',
    acctLabelTooltip: 'Bucket label for the score: 0 = No evidence, 1-24 = Low, 25-49 = Watch, 50-74 = Elevated, 75-100 = High.',
    acctTopCompetitorTooltip: 'Competitor with the highest weighted signal contribution at this account/persona.',
    acctLatestSignalTooltip: 'Date of the most recent competitor signal.',
    acctTrajectoryTooltip: 'Proximity score replayed at monthly anchors over the last 24 months. Rising bars = fresh signals landing. Falling = evidence ageing off.',
    acctFlatTrajectoryTooltip: 'Stable at {n} for last 24 months — evidence is older than 1 year.',
    acctSourceBadgeAccount: 'Account signal',
    acctSourceBadgeAccountTooltip: 'Tied to one of your target accounts. Contributes to the Account barometer.',
    acctSourceBadgePersona: 'Persona signal',
    acctSourceBadgePersonaTooltip: 'Tied to one of your target personas. Contributes to the Persona barometer.',
    acctSourceBadgeMarket: 'Market intel — not at a target account',
    acctSourceBadgeMarketTooltip: 'Competitor activity in your market that hasn\'t hit your target accounts. Does NOT contribute to any barometer.',
    acctCardHeaderTooltipAccount: 'How exposed this account is to competitors. Computed from signal strength × recency decay. Higher score = stronger competitor presence.',
    acctCardHeaderTooltipPersona: 'How exposed this persona is to competitors. Computed from signal strength × recency decay. Higher score = stronger competitor presence.',
    acctCompetitorFallback: 'Competitor',
    acctStrengthValue: 'Strength: {value}',
    acctShowMore: 'Show more',
    acctRecommendedAction: 'Recommended action',
    acctBreakdownSignals: 'signals: {n}',
    acctBreakdownStrongest: 'strongest: {value}',
    acctBreakdownLatest: 'latest: {value}',
    acctCompetitiveProximity: 'Competitive Proximity',
    acctCompetitorSignalsTitle: 'Competitor Signals',
    acctHelpFor: '{title} help',
    acctProximityScore: 'Proximity score',
    acctProximityScoreHelp: 'Proximity score help',
    acctLabelHelp: 'Label help',
    acctImpactHelp: 'Impact help',
    acctTrajectory24m: '24m trajectory',
    acctTrajectoryHelp: 'Trajectory help',
    acctTopCompetitorValue: 'Top: {value}',
    acctTopCompetitorHelp: 'Top competitor help',
    acctLatestValue: 'Latest: {value}',
    acctLatestSignalHelp: 'Latest signal help',
    acctLinkedPersonas: 'Linked personas:',
    sigTypeFundingNews: 'Funding News',
    sigTypeLeadershipChange: 'Leadership Change',
    sigTypeStrategicAnnouncement: 'Strategic Announcement',
    sigTypePartnership: 'Partnership',
    sigTypeTechnologyAdoption: 'Technology Adoption',
    sigTypeProcurementUpdate: 'Procurement Update',
    sigTypeAcquisition: 'Acquisition',
    sigTypeContractAward: 'Contract Award',
    sigTypeProfileUpdate: 'Profile Update',
    sigTypeRoleChange: 'Role Change',
    sigTypeAchievement: 'Achievement',
    sigTypeSocialActivity: 'Social Activity',
    sigTypeIncumbent: 'Incumbent',
    sigTypeBuying: 'Buying',
    sigTypeDisplacement: 'Displacement',
    sigTypeRenewalWindow: 'Renewal Window',
    sigTypeBudgetAllocation: 'Budget Allocation',
    sigTypeInitiative: 'Initiative',
    sigTypeRisk: 'Risk',
    sigTypeOrgChange: 'Org Change',
    sigTypeAccreditorResult: 'Accreditor Result',
    sigTypeGrantAward: 'Grant Award',
    sigTypeJobPosting: 'Job Posting',
    sigTypeEstateEvent: 'Estate Event',
    sigTypeFinancialPressure: 'Financial Pressure',
    sigTypeEnrolmentPressure: 'Enrolment Pressure',
    sigTypeSystemShortcoming: 'System Shortcoming',
    sigTypeVendorDispute: 'Vendor Dispute',
    sigTypeComplianceUpdate: 'Compliance Update',
    sigTypeBoardUpdate: 'Board Update',
    sigTypeGreenfield: 'Greenfield',
    sigMarketUpdate: 'Market update',
    sigCompetitorFilter: 'Competitor: {name}',
    sigMarketActivity: 'Market activity',
    sigAccountSignals: 'Account signals',
    sigPersonaSignals: 'Persona signals',
    sigCompetitorSignals: 'Competitor signals',
    sigNoMarketActivity: 'No market activity signals yet.',
    chatCopy: 'Copy',
    chatCopied: 'Copied',
    chatTalkingAbout: 'Talking about {account}',
    chatSourcesLabel: 'Sources:',
    pbBoothMeetingPurpose: 'Drive booth visits and in-person demos.',
    pbSessionInvitePurpose: 'Drive attendance to a speaking session.',
    pbVipOutreachPurpose: '20 minutes with executive-level targets.',
    pbQuickConnectPurpose: 'Lightweight peer outreach for confirmed attendees.',
    pbPersonaScopeAny: 'Any persona',
    pbPersonaScopeExecutiveOnly: 'Executive only',
    pbPersonaScopeConfirmedAttendees: 'Previous and confirmed attendees only',
    homeWeakFit: 'Weak Fit',
    homeNoFit: 'No Fit',
    homeNotScored: 'Not scored',
    // [2026-10-29] i18n: UI chrome keys (accounts list, account signals, archetypes)
    acctArchExecutiveBuyer: 'Executive Buyer',
    acctArchEvaluator: 'Evaluator',
    acctNoContactsYet: 'No contacts yet.',
    acctNoSignalsForAccount: 'No signals found for this account.',
    acctUntitledSignal: 'Untitled Signal',
    acctNoDescriptionAvailable: 'No description available',
    acctSignalUpdate: 'Update',
    acctImpactLabel: 'Impact:',
    acctMomentumScoreChanged: 'Momentum score changed by {n} points',
    acctsColProduct: 'Product',
    acctsColAccountTier: 'Account Tier',
    acctsColTargetProducts: 'Target Products',
    acctsColSubscribers: 'Subscribers',
    acctsTargetProductPlaceholder: 'Target product',
    procTeamStatusAria: 'Team status',
    // [2026-10-29] i18n: chat table/chart block labels (lib/chatBlockLabels.js)
    chatTblWhatTheyRunToday: 'What they run today',
    chatTblWhatChangedRecently: 'What changed recently',
    chatColCategory: 'Category',
    chatColVendor: 'Vendor',
    chatColHowTheyHoldIt: 'How they hold it',
    chatColDeployment: 'Deployment',
    chatColSentiment: 'Sentiment',
    chatColEvidence: 'Evidence',
    chatColDate: 'Date',
    chatColWhatHappened: 'What happened',
    chatColSource: 'Source',
    chatBadgeCommercialProduct: 'Commercial product',
    chatBadgeNativeToLms: 'Native to their LMS',
    chatBadgeBuiltInHouse: 'Built in-house',
    chatBadgeOpenSource: 'Open source',
    chatBadgeNothingInPlace: 'Nothing in place',
    chatBadgeInstitutionWide: 'Institution-wide',
    chatBadgeSeveralUnits: 'Several units',
    chatBadgeOneUnit: 'One unit',
    chatBadgePilot: 'Pilot',
    chatBadgePositive: 'Positive',
    chatBadgeNeutral: 'Neutral',
    chatBadgeNegative: 'Negative',
    chatChartWhichLms: 'Which LMS the market runs',
    chatChartFinancialHeadroom: 'Financial headroom across the market',
    chatChartAiReadiness: 'AI readiness across the market',
    chatChartMarketSplit: 'How the market splits',
    chatChartWhoHolds: 'Who holds {cat} across the market',
    chatUnitInstitutions: 'institutions',
    chatUnitAccounts: 'accounts',
    chatSliceLow: 'Low',
    chatSliceModerate: 'Moderate',
    chatSliceHigh: 'High',
    chatSliceOther: 'Other',
    chatSliceLevel: 'Level {n}',
    chatSlicePriorityNow: 'Priority now',
    chatSliceInYourMarket: 'In your market',
    chatSliceRestOfMarket: 'Rest of market',
    chatSliceAdjacentMarket: 'Adjacent market',
    chatDateUnknown: 'date unknown',
    chatNoteVendor: '{n} of {total} addressable institutions have a recorded {cat} vendor. Vendors below 5% are grouped as Other.',
    chatNoteLms: '{n} of {total} addressable institutions have a recorded LMS. Grouped into families, so Blackboard counts as Anthology and Canvas as Instructure. Open LMS and Totara are kept separate from Moodle.',
    chatNoteCapacity: '{n} of {total} addressable institutions carry a capacity band.',
    chatNoteReadiness: '{n} of {total} addressable institutions are scored for AI readiness.',
    chatNoteMix: '{n} accounts placed.',
  },
  
  es: {
    home: 'Inicio',
    market: 'Mercado',
    accounts: 'Cuentas',
    people: 'Personas',
    signals: 'Señales',
    procurement: 'Licitaciones',
    playbooks: 'Playbooks',
    settings: 'Configuración',
    help: 'Ayuda',
    admin: 'Administrador',
    segments: 'Segmentos',
    analytics: 'Análisis',
    outbox: 'Buzón de salida',
    logout: 'Cerrar sesión',
    
    // Common
    loading: 'Cargando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    edit: 'Editar',
    delete: 'Eliminar',
    search: 'Buscar',
    filter: 'Filtrar',
    export: 'Exportar',
    retry: 'Reintentar',
    refresh: 'Actualizar',
    backTo: 'Volver a',
    viewProfile: 'Ver perfil',
    
    // Headers
    dashboard: 'Tablero',
    accountOverview: 'Resumen de cuenta',
    
    // Tabs
    overview: 'Resumen',
    details: 'Detalles',
    activity: 'Actividad',
    notes: 'Notas',
    connections: 'Conexiones',
    connectionsEmpty: 'Aún no hay conexiones conocidas para esta persona.',
    withConnections: 'Con conexiones',
    withoutConnections: 'Sin conexiones',
    inPursuit: 'En seguimiento',
    notInPursuit: 'Sin seguimiento',
    almaMater: 'Alma mater',
    connectionInferred: 'inferida',
    personas: 'Personas',
    departments: 'Departamentos',
    
    // Account Page
    techStack: 'Pila tecnológica',
    liveDeals: 'Licitaciones abiertas',
    decisionWindow: 'Ventana de decisión',
    renewalDate: 'Fecha de renovación',
    momentum: 'Impulso',
    strategicPriorities: 'Prioridades estratégicas',
    institutionalChallenges: 'Desafíos institucionales',
    studentExpectations: 'Expectativas de los estudiantes',
    keyContacts: 'Contactos clave',
    procurementProfile: 'Perfil de adquisiciones',
    accountNotFound: 'Cuenta no encontrada',
    accountNotFoundDesc: 'La cuenta solicitada no se pudo cargar o no existe.',
    rateLimitReached: 'Límite de tasa alcanzado. Mostrando datos en caché. Espere 5 minutos antes de reintentar.',
    showingCachedData: 'Mostrando datos en caché. Haga clic en actualizar para obtener las últimas actualizaciones.',
    
    // Charts & Metrics
    fitScore: 'Puntuación de ajuste',
    excellent: 'Excelente',
    strong: 'Fuerte',
    moderate: 'Moderado',
    low: 'Bajo',
    scoreRationale: 'Justificación de puntuación',
    noRationaleAvailable: 'No hay justificación disponible.',
    riskRationale: 'Justificación de riesgo',
    
    // People & Personas
    personasOrganizedBy: 'Las personas están organizadas por rol en el comité de compras y clasificadas por su influencia y puntuación de ajuste. Haga clic en cualquier contacto para ver su perfil completo y actividad.',
    addedToWatchlist: 'Agregado a la lista de seguimiento',
    removedFromWatchlist: 'Eliminado de la lista de seguimiento',
    
    // Procurement
    procurementFriendliness: 'Amigabilidad de adquisiciones',
    friendly: 'Amigable',
    standard: 'Estándar',
    bureaucratic: 'Burocrático',
    monitor: 'Monitorear',
    
    // Decision Window
    lessThan12Months: '<12 meses',
    months12to24: '12-24 meses',
    moreThan24Months: '>24 meses',
    notApplicable: 'N/A',
    decisionWindowRationale: 'Justificación de ventana de decisión',
    
    // Signals
    momentumUpdate: 'Actualización de impulso',
    momentumScoreChanged: 'La puntuación de impulso cambió en',
    points: 'puntos',
    
    // Market Intelligence
    marketIntelligence: 'Oportunidades de mercado',
    macroTrends: 'Tendencias macro y oportunidades en sus mercados objetivo',
    buyingActivity: 'Actividad de compra',
    players: 'Dinámicas',
    
    // Filters
    allCountries: 'Todos los países',
    filterByCountry: 'Filtrar por país',
    allDepartments: 'Todos los departamentos',
    filterByDepartment: 'Filtrar por departamento',
    
    // Status
    live: 'Activo',
    closed: 'Cerrado',
    awarded: 'Adjudicado',
    cancelled: 'Cancelado',
    
    // Common - additional
    noDataAvailable: 'No hay datos disponibles.',

    // NEO Chat panel
    neoChatDefaultGreeting: '¡Hola! Soy NEO, tu asistente GTM. ¿En qué puedo ayudarte hoy?',
    neoChatAssistantTitle: 'Asistente NEO',
    neoChatNewChat: 'Nuevo chat',
    neoChatInputPlaceholder: 'Pregúntale cualquier cosa a NEO...',
    neoChatLoadingChat: 'Cargando chat',
    neoChatChipShowTenders: 'Ver licitaciones',
    neoChatPromptShowTenders: 'Muéstrame licitaciones relevantes',
    neoChatChipMeetingPrep: 'Preparación de reunión',
    neoChatPromptMeetingPrep: 'Prepárame para una reunión',
    neoChatStepAccount: 'Cuenta',
    neoChatStepType: 'Tipo',
    neoChatStepAttendees: 'Asistentes',
    neoChatSelectAccount: 'Seleccionar cuenta',
    neoChatLoadingAccounts: 'Cargando cuentas...',
    neoChatSearchAccounts: 'Buscar cuentas...',
    neoChatNoAccountsFound: 'No se encontraron cuentas.',
    neoChatSelectMeetingType: 'Seleccionar tipo de reunión',
    neoChatMeetingWith: 'Reunión con {accountName}',
    neoChatAskingAbout: 'Preguntando sobre {accountName}',
    neoChatClearAccount: 'Dejar de preguntar sobre esta cuenta',
    neoChatBack: 'Atrás',
    neoChatSelectAttendeesOptional: 'Seleccionar asistentes (opcional)',
    neoChatLoadingPeople: 'Cargando personas...',
    neoChatSearchPeople: 'Buscar personas...',
    neoChatNoPeopleFound: 'No se encontraron personas',
    neoChatSkip: 'Omitir',
    neoChatGenerate: 'Generar',
    neoChatMeetingDiscovery: 'Llamada de descubrimiento',
    neoChatMeetingDemo: 'Demo / Presentación',
    neoChatMeetingProposal: 'Revisión de propuesta',
    neoChatMeetingNegotiation: 'Negociación',
    neoChatMeetingRenewal: 'Renovación',
    neoChatMeetingQbr: 'QBR / Revisión',
    neoChatPrepareMeetingUser: 'Prepárame para una {meetingType} con {accountName}',
    neoChatGiveBriefOn: 'Dame un resumen de {accountName}',
    neoChatBriefOnAccountName: 'Dame un resumen de {account_name}',
    neoChatErrorCallNeo: 'Lo siento, no pude contactar a NEO: {error}',
    neoChatErrorMeetingPrep: 'Lo siento, no pude generar el briefing de la reunión. {error}',
    neoChatErrorMeetingPrepTimeout:
      'La solicitud expiró. La preparación puede tardar hasta un minuto; inténtalo de nuevo.',
    neoChatErrorAccountBrief: 'Lo siento, no pude generar el resumen de la cuenta. Error: {error}',
    neoChatErrorOutreach:
      'Lo siento, no pude generar el mensaje de outreach ahora. Error: {error}',
    neoChatErrorTenders: 'Lo siento, no pude obtener información de licitaciones. {hint}',
    neoChatNoDebrief:
      'No encontré un resumen detallado de **{accountName}**. Suele significar que aún no hay suficientes datos. Inténtalo más tarde o haz otra pregunta.',
    neoChatBriefGreetingFallback: 'Hola',
    neoChatBriefThings1: 'una cosa que merece tu tiempo',
    neoChatBriefThings2: 'dos cosas que merecen tu tiempo',
    neoChatBriefThings3: 'tres cosas que merecen tu tiempo',
    neoChatBriefThings4: 'cuatro cosas que merecen tu tiempo',
    neoChatBriefThings5: 'cinco cosas que merecen tu tiempo',
    neoChatTriggerIntroOne: 'Recién llegado:',
    neoChatTriggerIntroMany: 'Recién llegado — actividad nueva en tus cuentas:',
    neoChatSelectAccountAndPerson: 'Seleccionar cuenta y persona',
    neoChatAccountLabel: 'Cuenta',
    neoChatPersonLabel: 'Persona',
    neoChatSelectAccountPlaceholder: 'Seleccionar cuenta...',
    neoChatSelectPersonPlaceholder: 'Seleccionar persona...',
    neoChatNoPeopleForAccount: 'No hay personas para esta cuenta',
    neoChatConfigureOutreachTo: 'Configurar outreach para {name}',
    neoChatAtAccount: 'en {accountName}',
    neoChatBackToPeople: 'Volver a la selección de personas',
    neoChatChannelLabel: 'Canal',
    neoChatToneLabel: 'Tono',
    neoChatMessageTypeLabel: 'Tipo de mensaje',
    neoChatContextTriggerLabel: 'Contexto/Disparador',
    neoChatGenerating: 'Generando...',
    neoChatGenerateMessage: 'Generar mensaje',
    neoChatGenerateSequence: 'Generar secuencia de 3 mensajes',
    neoChatOutreachContextPlaceholder:
      'p. ej., «Haciendo seguimiento de su publicación reciente en LinkedIn sobre IA»',
    
    // Confidence levels
    high: 'Alto',
    medium: 'Medio',
    urgencyHigh: 'Alta urgencia',
    urgencyMedium: 'Urgencia media',
    urgencyLow: 'Baja urgencia',
    priorityHigh: 'Alta prioridad',
    priorityMedium: 'Prioridad media',
    priorityLow: 'Baja prioridad',
    priorityTooltipLead: 'La prioridad combina lo bien que encaja esta persona con lo reciente que es la señal.',
    
    // Procurement Profile
    fieldMetric: 'Campo / Métrica',
    value: 'Valor',
    source: 'Fuente',
    confidence: 'Confianza',
    procurementIntelligence: 'Inteligencia integral de adquisiciones y análisis de ruta de proveedores para esta institución.',
    procurementProfileNA: 'Los datos del perfil de adquisiciones no están disponibles para esta cuenta.',
    
    // Account Header
    students: 'estudiantes',
    employees: 'empleados',
    accountFitRationale: 'Justificación de ajuste de cuenta',
    excellentFit: 'Ajuste excelente',
    strongFit: 'Ajuste fuerte',
    moderateFit: 'Ajuste moderado',
    lowFit: 'Ajuste bajo',
    client: 'cliente',
    buyingModelRationale: 'Justificación del modelo de compra',
    bvoClassification: 'Clasificación BVO',
    incumbentSentiment: 'Sentimiento del titular',
    procurementFriendlyLabel: 'Amigable',
    procurementStandardLabel: 'Estándar',
    procurementBureaucraticLabel: 'Burocrático',
    procurementMonitorLabel: 'Monitorear',
    bvoBudgetDriven: 'Orientado al presupuesto',
    bvoValueSeeker: 'Buscador de valor',
    bvoOutcomeFocused: 'Orientado a resultados',
    bvoInnovationDriven: 'Orientado a la innovación',
    bvoRiskAverse: 'Adverso al riesgo',
    bvoComplianceFocused: 'Orientado al cumplimiento',
    sentimentAtRisk: 'En riesgo',
    sentimentPositive: 'Positivo',
    sentimentNeutral: 'Neutral',
    sentimentNegative: 'Negativo',
    sentimentDissatisfied: 'Insatisfecho',
    unknown: 'Desconocido',
    numberOfEmployees: 'Número de empleados',
    departmentFitRationale: 'Justificación de ajuste',
    
    // Procurement Friendliness
    friendlyDesc: 'Esta institución tiene procesos de adquisición simplificados y generalmente es receptiva a nuevas relaciones con proveedores.',
    standardDesc: 'Esta institución sigue procedimientos de adquisición típicos con plazos de evaluación estándar.',
    bureaucraticDesc: 'Esta institución tiene procesos de adquisición complejos que pueden requerir ciclos de ventas más largos y aprobaciones adicionales.',
    monitorDesc: 'Esta institución tiene dinámicas de adquisición desafiantes que requieren un monitoreo cuidadoso y un compromiso estratégico.',
    procurementProcessClassification: 'Clasificación del proceso de adquisiciones para esta institución.',
    
    // Decision Window
    decisionWindowMonitor: 'Monitor de ventana de decisión',
    decisionWindowDesc: 'Muestra cuándo es probable que un contrato esté en revisión. Las fechas de vencimiento se estiman utilizando registros de adquisiciones, ciclos de contrato típicos y avisos de renovación. Cuando faltan datos, proyectamos en base a fechas de adjudicación y patrones estándar, siempre etiquetando con niveles de confianza.',
    
    // Tech Stack
    noTechData: 'Sin datos de tecnología especificados',
    
    // Tenant-specific fields
    pedagogicalModel: 'Modelo pedagógico y cultural',
    digitalLandscape: 'Panorama digital',
    digitalFunding: 'Oportunidades de financiamiento digital',
    learningInitiatives: 'Iniciativas de desarrollo de aprendizaje',
    patientCareGaps: 'Brechas en la atención al paciente',
    decisionDynamics: 'Dinámica de decisión de las partes interesadas',
    contentCoverage: 'Cobertura de contenido',
    clientOpportunities: 'Oportunidades de clientes',
    decisionMakers: 'Tomadores de decisiones',
    engagementHooks: 'Ganchos de participación',
    recommendedTactics: 'Tácticas recomendadas',
    
    // People Page
    keyDecisionMakers: 'Tomadores de decisiones clave e influenciadores',
    searchPeople: 'Buscar personas por nombre, rol o universidad...',
    name: 'Nombre',
    account: 'Cuenta',
    country: 'País',
    department: 'Departamento',
    archetype: 'Arquetipo',
    pursuit: 'Seguimiento',
    socialScore: 'Puntuación social',
    noPeopleFound: 'No se encontraron personas',
    adjustFilters: 'Intente ajustar su búsqueda o filtros.',
    formerClient: 'Ex cliente',
    previouslyWorked: 'Trabajó anteriormente para uno de nuestros clientes',
    socialPresence: 'Mide la visibilidad y la influencia de la persona en plataformas sociales como LinkedIn.',
    personaFitRationale: 'Justificación de ajuste de persona',
    strategic: 'Estratégico',
    supportive: 'Favorable',
    passive: 'Pasivo',
    showContactInfo: 'Mostrar información de contacto',
    satisfied: 'Satisfecho',
    neutral: 'Neutral',
    frustrated: 'Frustrado',
    exploringAlternatives: 'Explorando alternativas',
    
    // Signals Page (continued in next)
    trackSignals: 'Seguimiento en tiempo real del impulso de la cuenta y la actividad de las personas para identificar oportunidades y participar en el momento adecuado.',
    momentumScoreAccount: 'Puntuación de impulso (nivel de cuenta)',
    allSignals: 'Todas las señales',
    noAccountSignals: 'No se encontraron señales de cuenta',
    noPersonaSignals: 'No se encontraron señales de persona',
    
    // Playbooks Page
    structuredEngagement: 'Estrategias de compromiso estructuradas para diferentes escenarios',
    createPlaybook: 'Crear libro de jugadas',
    searchPlaybooks: 'Buscar libros de jugadas...',
    allFactors: 'Todos los factores',
    eventPlaybooks: 'Playbooks de eventos',
    targetedSequences: 'Ejecutar secuencias dirigidas para sus próximos eventos',
    noUpcomingEvents: 'No hay eventos próximos',
    addEventsInSettings: 'Agregar eventos en Configuración',
    selectEvent: 'Seleccionar evento',
    previewAndApply: 'Vista previa y aplicar',
    yourCustomPlaybooks: 'Sus libros de jugadas personalizados',
    custom: 'Personalizado',
    goal: 'Objetivo',
    enrolledAccounts: 'Cuentas inscritas',
    untouchedAccounts: 'Cuentas sin tocar',
    noPlaybooks: 'No se encontraron libros de jugadas que coincidan con sus criterios',
    
    // Segments Page
    createManageExport: 'Crear, administrar y exportar listas objetivo',
    buildNewSegment: 'Crear nuevo segmento',
    rules: 'reglas',
    created: 'Creado',
    viewResults: 'Ver resultados',
    noSegmentsYet: 'Todavía no hay segmentos',
    getStartedSegment: 'Comience creando su primer segmento.',
    deleteSegment: 'Eliminar segmento',
    segmentDeleted: 'Segmento eliminado correctamente',
    authRequired: 'Autenticación requerida',
    loginToAccess: 'Inicie sesión para acceder a los segmentos.',
    logIn: 'Iniciar sesión',
    
    // Home Page
    yourWatchlist: 'Su lista de seguimiento',
    watchlistTooltip: 'Su lista de seguimiento rastrea las personas clave con las que está interactuando activamente. Estos individuos están marcados para la construcción continua de relaciones y son priorizados en su flujo de trabajo.',
    open: 'Abrir',
    untrack: 'Dejar de rastrear',
    moreTracked: 'más personas rastreadas…',
    refreshing: 'Actualizando…',
    
    // Procurement Page
    dealsTenders: 'Ofertas y licitaciones',
    trackTenders: 'Seguimiento de licitaciones abiertas y ofertas cerradas en el mercado.',
    searchKeyword: 'Buscar por palabra clave, título, institución...',
    liveDealsCount: 'Ofertas activas',
    recentlyClosed: 'Recientemente cerradas',
    closedDeals: 'Ofertas cerradas',
    allInstitutions: 'Todas las instituciones',
    filterByInstitution: 'Filtrar por institución',
    allCategories: 'Todas las categorías',
    filterByCategory: 'Filtrar por categoría',
    filterByEvent: 'Filtrar por evento',
    allEvents: 'Todos los eventos',
    allPersonas: 'Todas las personas',
    formerClientOnly: 'Solo ex cliente',
    minSocialScore: 'Puntuación social mín.',
    selectAll: 'Seleccionar todo',
    clearAll: 'Borrar todo',
    clearFilters: 'Limpiar filtros',
    typeToSearch: 'Escriba para buscar...',
    category: 'Categoría',
    vendor: 'Proveedor',
    institutionType: 'Tipo de institución',
    sentiment: 'Sentimiento',
    listOnly: 'Solo lista',
    mapPlusList: 'Mapa + Lista',
    allDeadlines: 'Todos los plazos',
    days0_7: '0–7 días',
    days8_30: '8–30 días',
    days31_90: '31–90 días',
    days90Plus: '90+ días',
    months0_6: '0–6 meses',
    months6_12: '6–12 meses',
    months12_24: '12–24 meses',
    unknownExpired: 'Desconocido / Expirado',
    allVendors: 'Todos los proveedores',
    exit: 'Salida',
    filterByExitLeverage: 'Filtrar por apalancamiento de salida',
    some: 'Alguno',
    noSignal: 'Sin señal',
    loadingDeals: 'Cargando ofertas...',
    loadingContractIntelligence: 'Cargando inteligencia de contratos...',
    loadingMoreDeals: 'Cargando más ofertas...',
    loadedDeals: 'Cargadas {count} ofertas de {label}',
    outOfScope: 'Fuera de alcance',
    pending: 'Pendiente',
    insufficientInfo: 'Info insuficiente',
    liveRadar: 'Radar en vivo',
    recentlyExpired: 'Recientemente expirados',
    contractIntelligence: 'Inteligencia de contratos',
    displacementRadar: 'Radar de desplazamiento',
    benchmarking: 'Comparativa',
    selectCategoryBenchmarkTitle: 'Seleccione una categoría para ver la comparativa',
    selectCategoryBenchmarkDesc: 'Use el filtro Categoría arriba para elegir una o más categorías de compras. Los datos de comparativa (matrícula vs $/estudiante/año) aparecerán aquí.',
    noClosedDealsBenchmarkTitle: 'No hay ofertas cerradas disponibles',
    noClosedDealsBenchmarkDesc: 'La comparativa requiere ofertas cerradas. Asegúrese de que el bootstrap devuelva ofertas cerradas para su tenant.',
    selectCategoryContractTitle: 'Seleccione una categoría para explorar inteligencia de contratos',
    selectCategoryContractDesc: 'Use el filtro Categoría arriba para elegir una o más categorías. Los contratos históricos y datos de competidores aparecerán aquí.',
    loadingContractData: 'Cargando datos de contratos…',
    alertSettings: 'Configuración de alertas',
    title: 'Título',
    institution: 'Institución',
    deadline: 'Fecha límite',
    productFitPanel: 'Adecuación del producto',
    noLiveDeals: 'No se encontraron ofertas activas',
    noRecordsSelectedFilters: 'No se encontraron registros para la categoría y filtros seleccionados. Pruebe otras categorías o limpie filtros.',
    marketMap: 'Mapa de mercado',
    marketMapHelpText: 'Haga clic en un país para filtrar la tabla de ofertas y ver la cuota de mercado del incumbente (gráfico circular). Use filtros de proveedor y sentimiento para refinar.',
    noDealsCountryFilters: 'No hay ofertas en este país para los filtros actuales.',
    noAttackableAccounts: 'No hay cuentas atacables que coincidan con los criterios.',
    previous: 'Anterior',
    next: 'Siguiente',
    product: 'Producto',
    reseller: 'Revendedor',
    procurementMethod: 'Método de contratación',
    framework: 'Acuerdo marco',
    productName: 'Nombre del producto',
    supplier: 'Proveedor',
    predictedExpiry: 'Vencimiento previsto',
    productFitProfileDesc: 'Defina perfiles de producto para el scoring de ajuste. Varios perfiles pueden estar activos a la vez: alimentan Light Fit en Live Radar (por categoría) y eliges un perfil al ejecutar Deep Fit en una licitación.',
    createProfile: 'Crear perfil',
    editProfile: 'Editar perfil',
    newProfile: 'Nuevo perfil',
    productFitName: 'Nombre',
    productFitPrimaryCategory: 'Categoría principal',
    productFitCategoryTags: 'Etiquetas de categoría (separadas por comas)',
    productFitStrongSignals: 'Señales de ajuste fuerte (una por línea)',
    productFitNegativeSignals: 'Señales negativas (prefijo Hard: o Soft:, una por línea)',
    productFitDeploymentModels: 'Modelos de despliegue',
    productFitCloud: 'Nube',
    productFitSelfHosted: 'Autoalojado',
    productFitHybrid: 'Híbrido',
    productFitIntegrations: 'Integraciones (separadas por comas)',
    productFitSecurity: 'Certificaciones de seguridad (separadas por comas)',
    productFitKeyFeatures: 'Características clave (separadas por comas)',
    productFitUpdate: 'Actualizar',
    productFitCreate: 'Crear',
    productFitProfiles: 'Perfiles',
    productFitNoProfiles: 'Aún no hay perfiles. Cree uno arriba.',
    productFitSetAsActive: 'Establecer como activo',
    productFitDeleteProfile: '¿Eliminar perfil?',
    productFitDeleteConfirm: 'Esto eliminará permanentemente "{name}". Esta acción no se puede deshacer.',
    productFitSelectPrimaryCategory: 'Seleccionar categoría principal',
    productFitNone: 'Ninguno',
    productFitActive: 'Activo',
    productFitDeactivate: 'Marcar como inactivo',
    productFitActivateError: 'No se pudo activar el perfil',
    productFitDeactivateError: 'No se pudo desactivar el perfil',
    loadingProductProfiles: 'Cargando perfiles de producto...',

    // Person Profile
    backToPeople: 'Volver a Personas',
    back: 'Volver',
    personNotFound: 'Persona no encontrada',
    personNotFoundDesc: 'La persona solicitada no se pudo cargar o no existe.',
    signalsCount: 'Señales',
    interactions: 'Interacciones',
    primaryInterests: 'Intereses principales',
    formerEmployers: 'Empleadores anteriores',
    education: 'Formación académica',
    professionalMemberships: 'Membresías profesionales',
    publicSpeakingEvents: 'Conferencias y eventos',
    recentEventsAttended: 'Eventos recientes asistidos',
    awardsRecognition: 'Premios y reconocimientos',
    publications: 'Publicaciones',
    publicationContext: 'Publicación',
    firstAuthorBadge: 'Primer autor',
    seniorAuthorBadge: 'Autor senior',
    relevanceTierVeryStrong: 'Coincidencia muy fuerte',
    relevanceTierStrong: 'Coincidencia fuerte',
    showAllPublications: 'Ver todas ({count})',
    showLess: 'Ver menos',
    removeFromPursuit: '✓ Eliminar del seguimiento',
    addToPursuit: 'Agregar al seguimiento',
    assignMarketTier: 'Asignar nivel de mercado',
    tier: 'Nivel',
    selectTier: 'Seleccione un nivel',
    saveChanges: 'Guardar cambios',
    atAGlance: 'De un vistazo',
    keyInitiatives: 'Iniciativas clave y áreas de enfoque.',
    noInitiatives: 'No hay iniciativas listadas.',
    
    // AI Outreach
    aiOutreach: 'Prospección con IA',
    channel: 'Canal',
    email: 'Correo electrónico',
    linkedin: 'LinkedIn',
    call: 'Llamada',
    tone: 'Tono',
    relationshipBuilder: 'Constructor de relaciones',
    thoughtLeadership: 'Liderazgo de pensamiento',
    challenger: 'Desafiante',
    context: 'Contexto',
    autoDetect: 'Detectar automáticamente el mejor contexto',
    personaInitiative: 'Iniciativas y actividad de la persona',
    institutionalChange: 'Cambios institucionales e impulso',
    strategicPriority: 'Prioridades estratégicas',
    procurementSignal: 'Señales de compras y financiación',
    customContext: 'Contexto personalizado (opcional)',
    addContext: 'Agregar contexto específico, puntos de conversación o instrucciones...',
    generating: 'Generando...',
    generateMessage: 'Generar mensaje',
    
    // Home Cards
    recommendedAccounts: 'Cuentas recomendadas',
    recommendedAccountsTooltip: 'Cuentas con mayor puntuación basadas en ajuste y alineación estratégica.',
    viewAll: 'Ver todo',
    noAccounts: 'Sin cuentas',
    recommendedPersonas: 'Personas recomendadas',
    recommendedPersonasTooltip: 'Personas más relevantes basadas en influencia y alineación de rol.',
    noPersonas: 'Sin personas',
    
    // Action Center
    priorityActions: 'Acciones prioritarias',
    deals: 'Ofertas',
    all: 'Todo',
    myTasks: 'Mis tareas',
    newLeads: 'Nuevos clientes potenciales',
    updated: 'Actualizado',
    loadingRecommendations: 'Cargando sus recomendaciones personalizadas',
    analyzingPipeline: 'Analizando su pipeline...',
    dueToday: 'Vence hoy',
    completeTouchVia: 'Completar toque',
    via: 'vía',
    openDraft: 'Abrir borrador',
    noContactOnFileOpenPersonas: 'No hay contacto registrado en esta cuenta — abra la pestaña Personas para añadir uno.',
    openLinkedIn: 'Abrir LinkedIn',
    callNow: 'Llamar ahora',
    done: 'Hecho',
    whyNow: '¿Por qué ahora?',
    nextSteps: 'Próximos pasos:',
    tracked: 'Rastreado',
    fit: 'Ajuste',
    draftOutreach: 'Borrador de divulgación',
    draftMessage: 'Redactar mensaje',
    noTasksDue: 'No hay tareas pendientes hoy',
    noNewLeads: 'Aún no hay nuevos clientes potenciales',
    noActionsYet: 'Aún no hay acciones',
    hitRefresh: 'Presione Actualizar y buscaré lo siguiente para usted.',
    hotMovers: 'Más activos (últimas 2 semanas)',
    personaUpdates: 'Actualizaciones de personas (últimas 2 semanas)',
    noSignals2Weeks: 'No hay señales en las últimas 2 semanas',
    liveDealsTitle: 'Ofertas activas',
    noDeadline: 'Sin fecha límite',
    daysLeft: 'd restantes',
    expired: 'Vencido',
    noLiveDealsCategories: 'No hay ofertas activas en sus categorías seleccionadas.',
    noLiveDealsFound: 'No se encontraron ofertas activas.',
    decisionWindowRadar: 'Radar de ventana de decisión',
    decisionWindowTooltip: 'Muestra cuándo es probable que un contrato esté en revisión según registros de adquisiciones y patrones de renovación.',
    accountsApproaching: 'Cuentas que se acercan a horizontes de renovación/decisión',
    noAccountsInBand: 'No hay cuentas en esta banda.',
    noAccountsWithWindows: 'No se encontraron cuentas con ventanas de decisión.',

    // Today's Plan
    todaysPlanTitle: 'Plan de hoy',
    todaysPlanAccountFit: 'Adecuación de la cuenta',
    todaysPlanCategoryAccountResearch: 'Investigación de cuenta',
    todaysPlanCategoryFitMomentum: 'Adecuación e impulso',
    todaysPlanCategoryOutreach: 'Prospección',
    todaysPlanCategoryNewLeads: 'Nuevos leads',
    openPersonas: 'Abrir personas',
    track: 'Seguir',
    impactSuffix: 'Impacto',
    suggestedNextSteps: 'Próximos pasos sugeridos',
    markRecommendationDone: 'Marcar esta recomendación como hecha',
    todaysPlanPressRefresh: 'Pulse el botón Actualizar arriba para obtener sus próximos movimientos personalizados.',
    todaysTasksTitle: 'Tareas de hoy',
    allCaughtUp: '¡Todo al día!',
    noTasksScheduledToday: 'No hay tareas programadas para hoy',
    tasksCount: '{count} tareas',
    touchNumberLabel: 'Contacto #{number}',
    sendEmail: 'Enviar correo',
    linkedinMessage: 'Mensaje de LinkedIn',
    makeCall: 'Llamar',
    taskCompletedTitle: '¡Tarea completada!',
    taskCompletedDescription: 'Buen trabajo, sigue así',
    taskCompleteError: 'No se pudo marcar la tarea como completada',
    unknownAccount: 'Cuenta desconocida',
    unknownContact: 'Contacto desconocido',
    completedFromTodaysPlan: 'Completado desde el plan de hoy',
    researchGapBuyer: 'Sin propietario de presupuesto confirmado en el mapa de ventas',
    researchGapChampion: 'Aún no se ha identificado un campeón interno',
    researchGapBoth: 'Ni propietario de presupuesto ni campeón confirmados',
    planReasonLeadership: 'Liderazgo nuevo o en cambio — las prioridades pueden reiniciarse.',
    planReasonProcurement: 'Adquisiciones con novedades (proceso, política o plazos).',
    planReasonLiveDeal: 'Hay un acuerdo activo que avanzar.',
    planReasonBoard: 'Próxima reunión del consejo — las decisiones pueden acelerarse.',
    planReasonMomentum: 'El compromiso reciente en la cuenta parece fuerte.',
    planReasonPersonaActivity: 'Han estado activos recientemente.',
    planReasonFit: 'Buen encaje para nosotros (cuenta/persona).',
    planReasonDecisionWindowUnknown: 'El momento de decisión no está claro — confirme el calendario.',
    planReasonDecisionWindowMonths: 'Decisión esperada en ~{months} meses.',
    planReasonDecisionWindowGeneric: 'Dentro de la ventana de decisión.',
    planReasonPursuit: 'Está siguiendo a esta persona (Pursuit).',
    todaysPlanRateLimitActive: 'Límite de tasa activo',
    todaysPlanCannotLoadRetry: 'No se puede cargar el plan de hoy. Reintento en {remaining}.',
    todaysPlanRateLimitReached: 'Límite de tasa alcanzado',
    todaysPlanShowingCached: 'Mostrando plan en caché. Reintento automático en 20 minutos.',
    todaysPlanCannotLoadWait: 'No se pudo cargar el plan de hoy. Espere 20 minutos.',
    todaysPlanCouldNotLoad: 'No se pudo cargar el plan de hoy.',
    todaysPlanCannotRefreshRetry: 'No se puede actualizar el plan de hoy. Reintento automático en {remaining}.',
    todaysPlanRefreshed: 'Plan actualizado',
    todaysPlanRefreshedDescription: 'El plan de hoy se actualizó con las últimas recomendaciones.',
    todaysPlanRefreshRateLimit: 'No se pudo actualizar por límite de tasa. Reintento en 20 minutos.',
    todaysPlanRefreshFailed: 'No se pudo actualizar el plan. Inténtelo de nuevo.',
    todaysPlanRefreshFailedTitle: 'Error al actualizar',
    todaysPlanAckRateLimit: 'Elemento reconocido localmente. Se sincronizará cuando termine el límite (hasta 20 min).',
    todaysPlanCouldNotSaveAck: 'No se pudo guardar el reconocimiento.',
    couldNotUpdatePursuit: 'No se pudo actualizar Pursuit',
    pleaseTryAgain: 'Inténtelo de nuevo.',
    
    // Market Page
    marketMomentumTrend: 'Tendencia de impulso del mercado (12 semanas)',
    noMomentumData: 'No hay datos de impulso disponibles',
    
    // Filters
    
    // Greetings
    goodMorning: 'Buenos días',
    goodAfternoon: 'Buenas tardes',
    goodEvening: 'Buenas noches',
    
    // Accounts Page
    manageExploreInstitutions: 'Administrar y explorar instituciones objetivo',
    searchAccounts: 'Buscar cuentas...',
    accountFitScore: 'Puntuación de ajuste de cuenta',
    renewalRisk: 'Riesgo de renovación',
    expansionOpps: 'Oportunidades de expansión',
    fariaProducts: 'Productos Faria',
    departmentFitScore: 'Puntuación de ajuste del departamento',
    noAccountsFound: 'No se encontraron cuentas',
    
    // Market Components
    topAccounts: 'Principales cuentas',
    topPersonas: 'Principales personas',
    tieringTooltip: 'Las cuentas se clasifican inicialmente en niveles según su Puntuación de Ajuste de Cuenta. La clasificación por niveles te ayuda a priorizar dónde enfocarte: para dedicar más tiempo a las cuentas con mayor potencial, realizar una prospección más dirigida y obtener mejores resultados. Puedes sobrescribir cualquier nivel en cualquier momento en cada cuenta individual.',
    topInstitutionsAI: 'Principales instituciones por adopción e implementación de IA',
    topHospitalsAI: 'Principales hospitales por adopción e implementación de IA',
    topSchoolsAI: 'Principales escuelas internacionales por adopción e implementación de IA',
    topEnterprisesAI: 'Principales empresas por adopción e implementación de IA',
    topCompaniesAI: 'Principales compañías por adopción e implementación de IA',
    topAuthoritiesAI: 'Principales autoridades educativas por adopción e implementación de IA',
    marketFitDistribution: 'Distribución de ajuste de mercado',
    accountFitComposition: 'Composición de puntuación de ajuste de cuenta en bandas de 10 puntos',
    topBuyers: 'Principales compradores',
    universitiesMostVendors: 'Universidades con más implementaciones de nuevos proveedores',
    enterprisesMostVendors: 'Empresas con más implementaciones de nuevos proveedores',
    keyBuyingAreas: 'Áreas clave de compra',
    functionalInvestment: 'Categorías funcionales con mayor inversión',
    procurementComplexity: 'Complejidad de adquisiciones',
    distributionProfiles: 'Distribución de perfiles de adquisiciones',
    procurementRoutes: 'Rutas de adquisición',
    howInstitutionsBuy: 'Cómo compran tecnología las instituciones',
    marketShare: 'Cuota de mercado',
    marketWinners: 'Ganadores del mercado',
    vendorsGaining: 'Proveedores que obtienen nuevos acuerdos institucionales',
    headlineTrends: 'Tendencias principales',
    marketInsights: 'Información del mercado',
  

    // Auto-filled missing keys
    momentumScoreDesc: 'La puntuación de impulso refleja la evolución semanal de la organización que hace el entorno más o menos favorable para la interacción, expansión o renovación del cliente.',
    signalsCaptured: 'Señales capturadas:',
    scoreDerivation: 'Cálculo de la puntuación:',
    personaActivityScore: 'Puntuación de actividad de la persona (nivel individual)',
    personaActivityDesc: 'La puntuación de actividad de la persona mide cuán visible y sustancialmente un contacto contribuye al cambio digital, operativo o estratégico en áreas relevantes para la solución del cliente.',
    filterBySignal: 'Filtrar por tipo de señal',
    allTime: 'Todo el periodo',
    last1Day: 'Último día',
    last7Days: 'Últimos 7 días',
    last14Days: 'Últimos 14 días',
    last30Days: 'Últimos 30 días',
    last90Days: 'Últimos 90 días',
    minScore: 'Puntuación mínima',
    recently: 'recientemente',
    filterByFactor: 'Filtrar por factor',
    chooseEvent: 'Elige un evento',
    daysAway: 'días restantes',
    today: 'Hoy',
    allSectors: 'Todos los sectores',
    sectors: 'Sectores',
    steps: 'Pasos',
    clone: 'Clonar',
    standardPlaybooks: 'Playbooks estándar',
    deletePlaybook: 'Eliminar playbook',
    deletePlaybookConfirm: '¿Seguro que quieres eliminar',
    cannotUndo: 'Esta acción no se puede deshacer.',
    playbookDeleted: 'Playbook eliminado',
    failedToDelete: 'No se pudo eliminar el playbook',
    selectAccountFor: 'Seleccionar cuenta para',
    chooseAccountToApply: 'Elige a qué cuenta aplicar este playbook',
    noEventSelected: 'Ningún evento seleccionado',
    selectEventFirst: 'Selecciona primero un evento o añade eventos en Configuración',
    noEligibleAccounts: 'No hay cuentas elegibles',
    deleteSegmentConfirm: '¿Seguro que quieres eliminar',
    failedToDeleteSegment: 'No se pudo eliminar el segmento',
    tieringSnapshot: 'Resumen por niveles',
    aiReadinessIndex: 'Índice de preparación para IA 2025',
    disconnect: 'Desconectar',
    connecting: 'Conectando...',
    disconnecting: 'Desconectando...',
    saving: 'Guardando...',
    uploading: 'Subiendo...',
    default: 'Predeterminado',
    language: 'Idioma',
    history: 'Historial',
    engagement: 'Interacción',
    showAll: 'Mostrar todo',
    gtmAnalytics: 'Analítica GTM',
    accountsByTier: 'Cuentas por nivel',
    avgAccountFitAcrossAccounts: 'Ajuste medio de cuenta (todas las cuentas)',
    unassigned: 'Sin asignar',
    connectCrm: 'Conectar CRM',
    topOfFunnelIntelligence: 'Inteligencia de parte alta del embudo',
    coverageTierPenetration: 'Cobertura y penetración por nivel',
    outboundExecution: 'Ejecución outbound',
    accountsTouched: 'Cuentas contactadas',
    buyerChampion: 'Comprador + Promotor',
    multiThreaded: 'Varios contactos',
    playbooksRun: 'Playbooks ejecutados',
    neoRecommendations: 'Recomendaciones de NEO',
    accountInteractions: 'Interacciones con cuentas',
    emailReplyRate: 'Tasa de respuesta por email',
    linkedinResponse: 'Respuesta en LinkedIn',
    callResponse: 'Respuesta a llamadas',
    emailOpenRate: 'Tasa de apertura de email',
    emailClickRate: 'Tasa de clics en email',
    accountsWithSignals: 'Cuentas con señales',
    tendersGenerated: 'Licitaciones generadas',
    tendersReviewed: 'Licitaciones revisadas',
    timeSaved: 'Tiempo ahorrado',
    totalHoursSaved: 'Horas totales ahorradas',
    hours: 'horas',
    usage: 'Uso',
    dailyActivity: 'Actividad diaria',
    minPerUserWeek: 'min / usuario / semana',
    replyRate: 'Tasa de respuesta',
    mostActive: 'Más activos',
    signalArchetypeOpenRates: 'Señal × Arquetipo — Tasas de apertura',
    signalArchetypeClickRates: 'Señal × Arquetipo — Tasas de clic',
    mostActiveAccounts: 'Cuentas más activas',
    mostActiveAccountsTooltip:
      'Cuentas con varias personas que interactuaron y donde el total de aperturas humanas verificadas supera el número de personas alcanzadas.',
    mostActiveAccountsEmpty: 'Aún no hay cuentas con participación multipersona.',
    adHoc: 'Ad hoc',
    adHocMessagePerformance: 'Rendimiento de mensajes ad hoc',
    playbookAnalyticsMovedMostActive:
      'El rendimiento de playbooks se ha trasladado a la pestaña Más activos.',
    playbookAnalyticsUsePlaybooksPage:
      'Use la página Playbooks para configurar secuencias y el estado operativo.',
    performanceByArchetype: 'Rendimiento por arquetipo',
    performanceByDepartment: 'Rendimiento por departamento',
    department: 'Departamento',
    performanceBySector: 'Rendimiento por sector',
    sector: 'Sector',
    analyticsNoDataInPeriod: 'No hay datos en el período seleccionado.',
    analyticsTableSent: 'Enviados',
    analyticsOpenRateCol: 'Tasa de apertura',
    analyticsClickRateCol: 'Tasa de clic',
    analyticsReplyRateCol: 'Tasa de respuesta',
    analyticsChannelCol: 'Canal',
    adHocReplyRateTooltip:
      'La tasa de respuesta incluye solo respuestas humanas verificadas. Las pruebas propias y los mensajes entrantes no verificados quedan excluidos.',
    adHocBreakdownFocusSignalNote:
      'Los desgloses por enfoque y por señal se rellenarán a medida que se envíen nuevos mensajes ad hoc.',
    adHocByCtaFamilyTitle: 'POR FAMILIA DE CTA',
    adHocByFocusTitle: 'POR ENFOQUE',
    adHocBySignalTitle: 'POR SEÑAL',
    adHocCtaFamilyColumn: 'Familia de CTA',
    adHocPrimaryAngleCol: 'Ángulo principal',
    adHocSignalCol: 'Señal',
    adHocSendsFeaturingSignalCol: 'Envíos que incluyen esta señal',
    adHocBySignalColumnTooltip:
      'Cada envío cuenta todas las señales de las que parte; las filas no suman el total del resumen.',
    ctaFamilyLabel_soft_binary: 'Binaria suave (pregunta sí/no)',
    ctaFamilyLabel_action_oriented: 'Orientada a la acción (solicitud de reunión)',
    ctaFamilyLabel_other: 'Otra',
    ctaFamilyLabel_no_cta: 'Sin CTA',
    messagePerformanceInsightsTitle: 'Rendimiento de mensajes',
    messagePerformanceInsightsDescription:
      'Desglose multicanal del rendimiento por forma de CTA, familia de señal y señal — mensajes de playbook y ad hoc. Úsalo para ver qué señales y CTAs generan más interacción.',
    messagePerfPlaybookMessages: 'Mensajes de playbook',
    messagePerfAdhocMessages: 'Mensajes ad hoc',
    messagePerfByCtaFamily: 'POR FAMILIA DE CTA',
    messagePerfBySignalFamily: 'POR FAMILIA DE SEÑAL',
    messagePerfBySignal: 'POR SEÑAL',
    messagePerfByLinkClicked: 'POR ENLACE PULSADO',
    messagePerfLabelLinkUrl: 'Enlace',
    messagePerfLinkTrueClickRate: 'Tasa de clic real',
    messagePerfLinkTotalClicks: 'Clics',
    messagePerfLinkUniqueMessages: 'Mensajes',
    messagePerfLinkMessagesHeaderTooltip:
      'Mensajes distintos desde los que se hizo clic en este enlace.',
    messagePerfBestDayToSend: 'MEJOR DÍA PARA ENVIAR',
    messagePerfBestHourToSend: 'MEJOR HORA PARA ENVIAR',
    messagePerfSendTimingRatesLine: '{open}% apertura · {click}% clic · {reply}% respuesta',
    messagePerfSendTimingFromSends: 'de {count} envíos',
    messagePerfSendTimingNotEnoughData: 'Aún no hay datos suficientes.',
    messagePerfSendTimingTzCaption:
      'Horarios de envío en: {tz} (zona horaria de tu programación).',
    messagePerfSendTimingTzWarningUtc:
      'Horarios de envío en: UTC — no se pudo resolver tu zona horaria de programación.',
    messagePerfSendTimingTzMismatch:
      'Horarios de envío en: {resolved} — no coincide con la zona seleccionada ({requested}).',
    messagePerfSendTimingTzCaptionCharts:
      'Horarios de envío en: {tz} — cambia arriba para comparar entre regiones.',
    messagePerfSendTimingTzWarningUtcCharts:
      'Horarios de envío en: UTC — no se pudo resolver la zona horaria seleccionada.',
    messagePerfSendTimingChartEmpty: 'Aún no hay datos de envíos suficientes.',
    messagePerfKeywordsSectionTitle: 'Palabras clave',
    messagePerfKeywordsSectionHint:
      'El tamaño refleja la frecuencia; el color la tasa de respuesta (más verde = mejores respuestas). Pasa el cursor para ver envíos y tasas.',
    messagePerfKeywordsProductLabel: 'Contexto de producto',
    messagePerfKeywordsSubjectTitle: 'Líneas de asunto',
    messagePerfKeywordsBodyTitle: 'Cuerpo del mensaje',
    messagePerfKeywordsEmpty: 'Aún no hay datos de palabras clave.',
    messagePerfSendTimingSectionTitle: 'Momento de envío',
    messagePerfSendTimingSeriesPlaybook: 'Playbook',
    messagePerfSendTimingSeriesAdhoc: 'Puntual',
    messagePerfSendTimingMergedTooltipLine: '{reply}% respuesta · {sends} envíos',
    messagePerfBySendDayChartTitle: 'POR DÍA DE LA SEMANA',
    messagePerfBySendHourChartTitle: 'POR HORA DEL DÍA',
    messagePerfSendTimingChartTooltip:
      '{label} · {sends} envíos · {open}% apertura · {click}% clic · {reply}% respuesta',
    messagePerfSendTimeTimezoneLabel: 'Zona horaria de envío:',
    messagePerfSendTimeSchedulingTzHint: '(tu zona de programación es {tz})',
    messagePerfTzSchedulingSuffix: 'programación',
    messagePerfTzPlaceholder: 'Selecciona zona horaria',
    messagePerfSendTimingYReply: 'Resp. %',
    messagePerfSendTimingYOpen: 'Apertura %',
    messagePerfSendTimingLowVolumeNote: 'Bajo volumen — {sends} envíos; la tasa no es fiable',
    messagePerfByPrimaryAngle: 'POR ÁNGULO PRINCIPAL (ENFOQUE)',
    messagePerfLabelCtaFamily: 'Familia de CTA',
    messagePerfLabelSignalFamily: 'Familia de señal',
    messagePerfLabelSignal: 'Señal',
    messagePerfLabelPrimaryAngle: 'Ángulo principal',
    messagePerfSignalSentTooltip:
      'Cada envío cuenta todas las señales que usa; las filas no suman el total del resumen — es intencional y permite comparar el rendimiento por señal.',
    messagePerfPlaybookEmptyCaption:
      'Aún no hay datos de playbook en este periodo — no se registraron envíos de playbook.',
    messagePerfAdhocEmptyCaption:
      'Aún no hay datos ad hoc en este periodo — no se enviaron mensajes ad hoc.',
    messagePerfNoBreakdownData: 'No hay datos de rendimiento de mensajes en este periodo.',
    messagePerfShowAll: 'Mostrar todo ({count})',
    messagePerfShowLess: 'Mostrar menos',
    signalFamilyLabel_strategic_priority: 'Prioridad declarada',
    signalFamilyHint_strategic_priority:
      'Una prioridad estratégica que la institución ha nombrado explícitamente en su plan o estrategia.',
    signalFamilyLabel_account_insights: 'Perspectivas de la cuenta',
    signalFamilyHint_account_insights:
      'Extractos del plan estratégico o referencias al documento completo, agrupados en una sola fila.',
    signalFamilyLabel_momentum: 'Actualización organizacional',
    signalFamilyHint_momentum:
      'Un cambio, anuncio o actualización organizativa que merece mencionarse.',
    signalFamilyLabel_activity: 'Actividad personal',
    signalFamilyHint_activity:
      'Algo que el destinatario ha hecho personalmente (charla, artículo, publicación).',
    signalFamilyLabel_mandatory_signal: 'Señal mejor puntuada',
    signalFamilyHint_mandatory_signal:
      'La señal con mayor puntuación para este destinatario o el contexto del mensaje.',
    signalFamilyLabel_framework_based: 'Referencia a marco',
    signalFamilyHint_framework_based:
      'Vinculado a una metodología o marco (por ejemplo, un ciclo de acreditación).',
    signalFamilyLabel_trend_based: 'Tendencia del sector',
    signalFamilyHint_trend_based: 'Una tendencia más amplia del sector o la industria.',
    signalFamilyLabel_deal: 'Adquisiciones',
    signalFamilyHint_deal:
      'Actividad relacionada con compras, licitaciones o procesos de adquisición.',
    signalFamilyLabel_case_study: 'Referencia de cliente',
    signalFamilyHint_case_study: 'Una referencia a un caso de cliente existente.',
    signalFamilyLabel_default: 'Otro',
    signalFamilyHint_default: 'Reserva sin categoría.',
    analyticsAccount: 'Cuenta',
    analyticsAccountFit: 'Adecuación de la cuenta',
    analyticsPersonasSent: 'Personas enviadas',
    analyticsMultiPersonaOpens: 'Aperturas multi-persona',
    analyticsOpensPerPersona: 'Aperturas / persona',
    analyticsOpensPerPersonaSubtitle: 'aperturas totales ÷ personas',
    analyticsMultiPersonaClicks: 'Clics multi-persona',
    collateralToShare: 'Material para compartir',
    warmUpReading: 'Lectura de calentamiento',
    sent: 'enviados',
    calls: 'llamadas',
    allTiers: 'Todos los niveles',
    allChannels: 'Todos los canales',
    signalTypePerformance: 'Rendimiento por tipo de señal',
    signalTypePerformanceTooltip: 'Señales de cuenta (impulso): rendimiento del outreach en cuentas donde la señal existía en el periodo. Señales de persona: rendimiento del outreach a la persona concreta de la señal. Ambas incluyen mensajes de playbook y manuales.',
    noSignalDataSelectedPeriod: 'No hay datos de señales en el periodo seleccionado',
    signalType: 'Tipo de señal',
    touched: 'Contactadas',
    bestChannel: 'Mejor canal',
    sample: 'Muestra',
    lowSampleSize: 'Muestra pequeña (<30)',
    topResponsiveAccountsFor: 'Cuentas con más respuesta para',
    noAccountsTouchedForSignal: 'No se contactó ninguna cuenta para esta señal',
    na: 'N/A',
    signalArchetypeReplyRates: 'Tasas de respuesta por señal × arquetipo',
    notEnoughDataForMatrix: 'No hay datos suficientes para la matriz',
    filterByTier: 'Filtrar por nivel',
    accountCommunicationsDepth: 'Profundidad de comunicación por cuenta',
    depthScoreTooltip: 'Puntuación de profundidad (0-100): personas (30 pts) + arquetipos (30 pts) + respuestas (30 pts) + recencia (10 pts)',
    noAccountDataSelectedPeriod: 'No hay datos de cuentas en el periodo seleccionado',
    more: 'más',
    depth: 'Profundidad',
    archetypeCoverage: 'Cobertura de arquetipos',
    notTouched: 'Sin contactar',
    replied: 'Respondió',
    recommendations: 'Recomendaciones',
    goodCoverage: '¡Buena cobertura!',
    recentActivity: 'Actividad reciente',
    customEventPlaybook: 'Playbook de evento personalizado',
    untitledPlaybook: 'Playbook sin título',
    selectedForComparison: 'seleccionados para comparar',
    playbookPerformance: 'Rendimiento de playbooks',
    noPlaybookDataSelectedPeriod: 'No hay datos de playbooks en el periodo seleccionado',
    playbook: 'Playbook',
    runs: 'Ejecuciones',
    completionPercent: '% completado',
    bestArchetype: 'Mejor arquetipo',
    comparePlaybooks: 'Comparar playbooks',
    completionRate: 'Tasa de finalización',
    medianReplyTime: 'Tiempo medio de respuesta',
    stepFunnel: 'Embudo por paso',
    reply: 'respuesta',
    trackYourGoToMarketPerformance: 'Sigue el rendimiento de tu go-to-market',
    viewing: 'Viendo',
    allTeamActivity: 'Toda la actividad del equipo',
    myActivity: 'Mi actividad',
    specificTeammate: 'Un compañero concreto',
    chooseTeammate: 'Elegir compañero',
    pipelineDeals: 'Pipeline y oportunidades',
    openPipeline: 'Pipeline abierto',
    meetingsBooked: 'Reuniones agendadas',
    dealsCreated: 'Oportunidades creadas',
    winRate: 'Tasa de éxito',
    languagePreferenceSaved: '¡Preferencia de idioma guardada!',
    failedToSaveLanguagePreference: 'No se pudo guardar la preferencia de idioma',
    selectPreferredLanguageDescription: 'Selecciona tu idioma preferido para la interfaz de la plataforma. Se traducirán los encabezados, botones y elementos de navegación.',
    interfaceLanguage: 'Idioma de la interfaz',
    choosePreferredLanguage: 'Elige tu idioma preferido para la interfaz de la plataforma',
    selectLanguage: 'Seleccionar idioma',
    languageScopeDisclaimer: 'Se traducirán los encabezados, la navegación, los botones y las etiquetas de la plataforma. Los nombres de cuentas y personas y el contenido de los datos se mantendrán en su idioma original.',
    saveLanguagePreference: 'Guardar preferencia de idioma',
    failedToLoadProfile: 'No se pudo cargar el perfil',
    profileUpdatedSuccessfully: 'Perfil actualizado correctamente',
    failedToSaveProfile: 'No se pudo guardar el perfil',
    pleaseSelectImageFile: 'Selecciona un archivo de imagen',
    imageSizeLessThan5MB: 'La imagen debe pesar menos de 5 MB',
    profilePhotoUpdatedSuccessfully: 'Foto de perfil actualizada correctamente',
    failedToUploadPhoto: 'No se pudo subir la foto',
    updatePersonalInfoAndPhoto: 'Actualiza tu información personal y tu foto de perfil',
    profilePhoto: 'Foto de perfil',
    profile: 'Perfil',
    updateYourProfilePicture: 'Actualiza tu foto de perfil',
    uploadPhoto: 'Subir foto',
    photoFormatAndSizeHint: 'JPG, PNG o GIF. Tamaño máximo 5 MB.',
    personalInformation: 'Información personal',
    personalInfoGreetingUsage: 'Esta información se usará en saludos y comunicaciones',
    displayName: 'Nombre visible',
    yourName: 'Tu nombre',
    displayNameUsageHint: 'Este nombre se usará en saludos y comunicaciones',
    jobTitle: 'Cargo',
    yourRoleOrPosition: 'Tu función o cargo en la organización',
    yourOrganization: 'Tu organización',
    usedInEmailSignatures: 'Se usa en firmas de email y comunicaciones formales',
    saveProfile: 'Guardar perfil',
    gmailIntegration: 'Integración con Gmail',
    connectGmailDescription: 'Conecta tu cuenta de Gmail para enviar emails directamente desde NEO',
    loadingGmailStatus: 'Cargando estado de Gmail...',
    gmailComingSoon: 'La integración con Gmail llegará pronto. La función se está configurando.',
    gmailConnected: 'Gmail conectado',
    gmailNotConnected: 'Gmail no conectado',
    connectAccountToSendEmails: 'Conecta tu cuenta para enviar emails',
    connectGmail: 'Conectar Gmail',
    sendEmailsFrom: 'Enviar emails desde',
    selectEmailAddress: 'Seleccionar dirección de email',
    loadingSignature: 'Cargando firma...',
    emailSignaturePreview: 'Vista previa de la firma',
    whyConnectGmail: '¿Por qué conectar Gmail?',
    gmailBenefitSendPersonalized: 'Envía emails personalizados directamente desde NEO',
    gmailBenefitTrackEngagement: 'Sigue aperturas e interacción con tus emails',
    gmailBenefitManageOutreach: 'Gestiona tus campañas de outreach de forma eficiente',
    searchDrafts: 'Buscar persona, asunto, cuenta o cargo…',
    allProducts: 'Todos los productos',
    allPlaybooks: 'Todos los playbooks',
    noDraftsFound: 'No se encontraron borradores',
    createPlaybooksToGenerateDrafts: 'Crea playbooks para generar borradores de email',
    cancelDraft: 'Cancelar borrador',
    cancelDrafts: 'Cancelar borradores',
    deleting: 'Eliminando...',
    deleteDraft: 'Eliminar borrador',
  

    // Auto-filled missing keys
    failedToLoadSchedule: 'No se pudo cargar el horario',
    scheduleSaved: 'Horario guardado',
    communicationScheduleUpdated: 'Se ha actualizado tu horario de comunicaciones',
    failedToSaveSchedule: 'No se pudo guardar el horario. Inténtalo de nuevo.',
    atLeastOneWindowRequired: 'Se necesita al menos una franja',
    addWindowBeforeRemoving: 'Añade una nueva franja antes de eliminar esta.',
    communicationSchedule: 'Horario de comunicaciones',
    configureCommsSchedule: 'Configura cuándo puede NEO enviar comunicaciones en tu nombre',
    commsScheduleHelp: 'Al ejecutar un playbook, cada paso (email, LinkedIn, llamada) se programa en la siguiente franja disponible a partir del desfase en días que definas. Fuera de estas franjas no se programa ninguna comunicación.',
    complete: 'Completo',
    incomplete: 'Incompleto',
    availabilityWindows: 'Franjas de disponibilidad',
    addWindow: 'Añadir franja',
    day: 'Día',
    week: 'Semana',
    month: 'Mes',
    startTime: 'Hora de inicio',
    endTime: 'Hora de fin',
    saveSchedule: 'Guardar horario',
    userIdMissing: 'Falta el ID de usuario',
    outreachHistory: 'Historial de outreach',
    outreachHistorySubtitle: 'Cronología de todas las tareas de mensajería completadas por tu equipo',
    allUsers: 'Todos los usuarios',
    noOutreachHistoryPeriod: 'No hay historial de outreach en este periodo.',
    failedToLoadEmailAddresses: 'No se pudieron cargar las direcciones de email',
    emailAddressUpdated: 'Dirección de email actualizada',
    emailsWillBeSentFrom: 'Los emails se enviarán desde',
    emailAddressesRefreshed: 'Direcciones de email actualizadas',
    emailAliasesReloaded: 'Se han recargado tus alias de email',
    signatureSaved: 'Firma guardada',
    emailSignatureSavedSuccessfully: 'Tu firma de email se ha guardado correctamente.',
    failedToSaveSignature: 'No se pudo guardar la firma. Inténtalo de nuevo.',
    outlookIntegration: 'Integración con Outlook',
    connectOutlookDescription: 'Conecta tu cuenta de Microsoft Outlook para enviar emails directamente desde NEO',
    loadingOutlookStatus: 'Cargando estado de Outlook...',
    outlookComingSoon: 'La integración con Outlook llegará pronto.',
    outlookConnected: 'Outlook conectado',
    outlookNotConnected: 'Outlook no conectado',
    connectOutlook: 'Conectar Outlook',
    emailSignature: 'Firma de email',
    saveSignature: 'Guardar firma',
    clear: 'Borrar',
  

    // Auto-filled missing keys
    error: 'Error',
    configureWorkspacePreferences: 'Configura tu espacio de trabajo y tus preferencias',
    emails: 'Emails',
    schedule: 'Horario',
    personalization: 'Personalización',
    events: 'Eventos',
    productFit: 'Adecuación del producto',
    completingGmailConnection: 'Completando la conexión con Gmail...',
    authorizationFailedOrCancelled: 'La autorización falló o se canceló',
    gmailConnectedClosingWindow: 'Gmail conectado. Cerrando ventana...',
    failedToConnectGmail: 'No se pudo conectar Gmail',
    pleaseWait: 'Espera un momento...',
    windowWillCloseAutomatically: 'Esta ventana se cerrará automáticamente.',
    youCanCloseThisWindow: 'Puedes cerrar esta ventana.',
    completingOutlookConnection: 'Completando la conexión con Outlook...',
    outlookConnectedClosingWindow: 'Outlook conectado. Cerrando ventana...',
    failedToConnectOutlook: 'No se pudo conectar Outlook',
    connectionFailed: 'Error de conexión',
    gmailDisconnected: 'Gmail desconectado',
    gmailAccountDisconnected: 'Tu cuenta de Gmail se ha desconectado.',
    disconnectNotApplied: 'No se aplicó la desconexión',
    backendStillReportsGmailConnected: 'El servidor sigue indicando que Gmail está conectado. Vuelve a intentarlo; si persiste, probablemente requiera una corrección en el servidor.',
    outlookDisconnected: 'Outlook desconectado',
    outlookAccountDisconnected: 'Tu cuenta de Outlook se ha desconectado.',
    backendStillReportsOutlookConnected: 'El servidor sigue indicando que Outlook está conectado. Vuelve a intentarlo; si persiste, probablemente requiera una corrección en el servidor.',
    unsavedChanges: 'Cambios sin guardar',
    howToCopyOutlookSignature: 'Cómo copiar tu firma de Outlook:',
    pasteSignatureHere: 'Pega tu firma aquí...',
    whyConnectOutlook: '¿Por qué conectar Outlook?',
    outlookWorksWith365: 'Funciona con cuentas de Microsoft 365 y Outlook.com',
  

    // Auto-filled missing keys
    connectedAs: 'Conectado como',
    gmailConnectedSuccessfully: 'Gmail conectado correctamente',
    outlookConnectedSuccessfully: 'Outlook conectado correctamente',
    emailAddressUpdatedLocalOnly: 'Dirección de email actualizada (solo local)',
    usingInSessionBackendRejectedAlias: 'Se usa en esta sesión. El servidor rechazó guardar preferred_outlook_alias (400):',
    copySignatureAndPasteBelow: 'Copia tu firma de Outlook y pégala abajo',
    outlookSignatureStep1: 'Abre Outlook -> Archivo -> Opciones -> Correo -> Firmas',
    outlookSignatureStep2: 'Selecciona tu firma, pulsa Ctrl+A (seleccionar todo) y luego Ctrl+C (copiar)',
    outlookSignatureStep3: 'Haz clic en el cuadro de abajo y pulsa Ctrl+V (pegar)',
    signatureSavedWillBeUsed: 'Firma guardada; se usará en tus emails',
  

    // Auto-filled missing keys
    preEvent: 'Antes del evento',
    dayOf: 'Día del evento',
    postEvent: 'Después del evento',
    dayLabel: 'Día',
    noGoalSet: 'Sin objetivo definido',
    asset: 'recurso',
    dayOffset: 'Desfase en días',
    negativeBeforeEvent: 'Negativo = antes del evento',
    useAiSignals: 'Usar señales de IA',
    includeBookingLink: 'Incluir enlace de reserva',
    autoInsertAvailabilityLink: 'Auto-inserts the event\'s availability link into the message body.',
    addAvailabilityLinkToEnable: 'Añade un enlace de disponibilidad en la configuración del evento para activarlo.',
    touchGoal: 'Objetivo del contacto',
    touchGoalPlaceholder: '¿Qué debe conseguir este mensaje?',
    messagingNotes: 'Notas de mensaje',
    messagingNotesPlaceholder: 'Puntos concretos a incluir, indicaciones de tono, cosas a mencionar o evitar...',
    instructionsPassedToAiTouch: 'Estas instrucciones se pasarán directamente a la IA para este contacto concreto',
    collateralToShare: 'Material para compartir',
    warmUpReading: 'Lectura de calentamiento',
    noAssetsConfiguredInPersonalization: 'No hay recursos configurados en Configuración - Personalización',
    selected: 'seleccionados',
    failedToLoadEvents: 'No se pudieron cargar los eventos',
    missingRequiredFields: 'Faltan campos obligatorios',
    eventsSaved: 'Eventos guardados',
    eventScheduleUpdated: 'Se ha actualizado tu calendario de eventos',
    eventScheduleUpdatedLocal: 'Se ha actualizado tu calendario de eventos (almacenamiento local)',
    saveFailed: 'Error al guardar',
    failedToSaveEvents: 'No se pudieron guardar los eventos',
    eventsAndConferences: 'Eventos y conferencias',
    trackEventsAndConferences: 'Track events and conferences you\'re attending',
    usingLocalStorageApiUnavailable: 'Usando almacenamiento local (API no disponible',
    myEvents: 'Mis eventos',
    addConferencesTradeShows: 'Añade conferencias, ferias y ponencias',
    addEvent: 'Añadir evento',
    noEventsAddedYet: 'Aún no hay eventos',
    addEventsToEnableEventMessaging: 'Añade eventos para activar los mensajes basados en eventos',
    event: 'Evento',
    collapse: 'Contraer',
    expand: 'Expandir',
    eventName: 'Nombre del evento',
    eventNameExample: 'p. ej., EDUCAUSE 2025',
    city: 'Ciudad',
    cityExample: 'p. ej., Denver',
    venue: 'Sede',
    venueExample: 'p. ej., Centro de convenciones',
    startDate: 'Fecha de inicio',
    endDate: 'Fecha de fin',
    boothNumber: 'Número de stand',
    boothNumberExample: 'p. ej., Pabellón A, stand 123',
    availabilityLink: 'Enlace de disponibilidad',
    availabilityLinkPlaceholder: 'https://calendly.com/...',
    meetingOffer: 'Propuesta de reunión',
    meetingOfferExample: 'p. ej., demo de producto de 15 minutos',
    messagingGuidelines: 'Pautas de mensaje',
    customInstructionsForAi: 'Instrucciones personalizadas para los mensajes generados por IA en cada fase',
    preEventGuidelines: 'Pautas antes del evento',
    preEventGuidelinesExample: 'e.g., Mention our new AI-powered analytics launching at the event. Highlight the live demo we\'re offering.',
    infoToIncludeBeforeEvent: 'Información a incluir en los mensajes antes del evento',
    duringEventGuidelines: 'Pautas durante el evento',
    duringEventGuidelinesExample: 'p. ej., Ofrece demos en el momento. Menciona que quedan pocas citas disponibles.',
    postEventGuidelines: 'Pautas después del evento',
    postEventGuidelinesExample: 'p. ej., Menciona el turno de preguntas de nuestra sesión. Ofrece la grabación y las diapositivas. Menciona el precio especial posterior al evento.',
    sessionDetailsOptional: 'Detalles de la sesión',
    sessionDetailsSessionInviteNote:
      '(Session title and description are required to use the Session Invite playbook on Playbooks.)',
    ifSpeakingOrWorkshop: 'If you\'re speaking or running a workshop',
    sessionTitle: 'Título de la sesión',
    sessionTitleExample: 'e.g., AI in Higher Ed: What\'s Next',
    sessionDescription: 'Descripción de la sesión',
    sessionDescriptionPlaceholder: 'Breve descripción para atraer asistentes...',
    postEventAssets: 'Recursos posteriores al evento',
    linksToShareFollowUp: 'Enlaces para compartir en los mensajes de seguimiento',
    sessionRecordingLink: 'Enlace a la grabación de la sesión',
    slidesLink: 'Enlace a las diapositivas',
    postEventResourceLink: 'Recurso posterior al evento (whitepaper, etc.)',
    messageSequence: 'Secuencia de mensajes',
    defineTouchesOutreachSequence: 'Define los contactos de tu secuencia de outreach',
    usingDefaultPlaybook: 'Usando el playbook predeterminado. Activa el playbook personalizado para definir tu propia secuencia.',
    createCustomSequence: 'Crear secuencia personalizada',
    addTouch: 'Añadir contacto',
    targetRegion: 'Región objetivo',
    targetCountries: 'Países objetivo',
    writeInNativeLanguage: 'Escribir en el idioma nativo de cada país',
    writeInNativeLanguageHelp: 'Desactivado por defecto: todos los mensajes se envían en inglés. Al activarlo, cada mensaje se redacta en el idioma del país de la cuenta destinataria (portugués, español o francés; inglés en los demás casos).',
    deselectAll: 'Deseleccionar todo',
    selectAllNoRestrictions: 'Seleccionar todo (sin restricciones)',
    allCountriesSelectedStrong: 'Todos los países seleccionados',
    noGeoRestrictions: 'No se aplicarán restricciones geográficas.',
    personasAnyCountryIncluded: 'Se incluirán personas de cualquier país.',
    fixRequiredFieldsBeforeSaving: 'Corrige los campos obligatorios antes de guardar',
    saveEvents: 'Guardar eventos',
    urlPlaceholder: 'https://...',
  

    // Auto-filled missing keys
    settingsSavedSuccessfully: '¡Configuración guardada correctamente!',
    failedToSaveSettingsTryAgain: 'No se pudo guardar la configuración. Inténtalo de nuevo.',
    usingLocalStorageApiUnavailableShort: 'Usando almacenamiento local (API no disponible)',
    languageBrandVoice: 'Idioma y voz de marca',
    configureNeoCommunicationTenant: 'Configura cómo se comunica NEO para este tenant',
    english: 'Inglés',
    spanish: 'Español',
    french: 'Francés',
    german: 'Alemán',
    brazilianPortuguese: 'Portugués de Brasil',
    arabic: 'Árabe',
    additionalStylePreferencesPlaceholder: 'Reglas de estilo adicionales que sustituyen a las predeterminadas. Ejemplos: frases cortas y directas; sin palabras de moda; ortografía británica; nunca uses signos de exclamación; empieza siempre con una pregunta',
    stylePreferencesOverrideHelp: 'Estas preferencias sustituyen las reglas de estilo y tono predeterminadas, pero no pueden saltarse las reglas de seguridad de QA (lista blanca de URL, frases prohibidas, límites de longitud, validación de marcadores).',
    companyProduct: 'Empresa y producto',
    customizeCompanyProductPositioning: 'Personaliza cómo se posicionan tu empresa y tu producto. Déjalo en blanco para usar los valores predeterminados del tenant.',
    companyNameExample: 'p. ej., Acme Learning',
    productNameExample: 'p. ej., Future Skills Enrichment Programs',
    coreValuePropositionExample: 'p. ej., programas de enriquecimiento personalizados que motivan e inspiran a los estudiantes',
    leaveBlankUseTenantDefaultValueProp: 'Déjalo en blanco para usar la propuesta de valor predeterminada del tenant',
    keyPerformanceIndicators: 'Indicadores clave de rendimiento (KPI)',
    defineMetricsThatMatter: 'Define metrics that matter to this tenant\'s customers',
    kpiNamePlaceholder: 'Nombre del KPI (p. ej., interacción de estudiantes)',
    kpiDescriptionPlaceholder: 'Descripción (p. ej., aumento del 45 % de usuarios activos diarios)',
    productOptional: 'Producto (opcional)',
    addKpi: 'Añadir KPI',
    messagingHooksDescription: 'Mensajes clave y propuestas de valor por arquetipo.',
    useProductSelectorForHooks: 'Usa el selector de producto para alinear los ganchos con productos concretos.',
    selectPersona: 'Seleccionar persona',
    universalAllPersonas: 'Universal (todas las personas)',
    champion: 'Promotor',
    influencer: 'Influenciador',
    evaluatorTechnical: 'Evaluador (técnico)',
    endUser: 'Usuario final',
    hookUsedForPersonaType: 'Este gancho solo se usará para este tipo de persona',
    hookText: 'Texto del gancho',
    hookTextExample: 'p. ej., enriquecimiento que genera ingresos sin inversión de capital',
    caseStudiesClientReferences: 'Casos de éxito y referencias de clientes',
    caseStudiesClientReferencesDescription: 'Añade casos de éxito completos (con enlace) o referencias de clientes (solo el nombre de la institución)',
    type: 'Tipo',
    selectType: 'Seleccionar tipo',
    caseStudyWithLink: 'Caso de éxito (con enlace)',
    clientReferenceNameOnly: 'Referencia de cliente (solo nombre)',
    institutionName: 'Nombre de la institución',
    institutionNameExample: 'p. ej., Oundle School',
    clientReferenceUsagePrefix: 'Se usará así: "Centros como',
    clientReferenceUsageSuffix: 'han comprobado..."',
    caseStudyTitle: 'Título del caso de éxito',
    caseStudyTitleExample: 'p. ej., caso de éxito de la Universidad de Melbourne',
    caseStudyUrlPlaceholder: 'https://example.com/case-study',
    descriptionOptional: 'Descripción (opcional)',
    briefOutcomeSummary: 'Breve resumen del resultado',
    addCaseStudyClientReference: 'Añadir caso de éxito / referencia de cliente',
    usefulResourcesDocumentation: 'Recursos y documentación útiles para este tenant',
    linkNamePlaceholder: 'Nombre del enlace (p. ej., demo de producto)',
    linkUrlPlaceholder: 'URL (p. ej., https://example.com/demo)',
    addProductLink: 'Añadir enlace de producto',
    educationalResources: 'Recursos educativos',
    educationalResourcesLead:
      'Contenido de calentamiento al inicio de las cadencias para generar credibilidad antes de cualquier petición comercial.',
    educationalResourcesExamples:
      'Ejemplos: artículos de marcos, guías de autoridad de terceros, piezas sobre «cómo se ve lo bueno».',
    resourceTitle: 'Título',
    educationalResourceTitlePlaceholder:
      'p. ej., Strategic Considerations for a Successful Student Survey Tool',
    educationalResourceUrlPlaceholder: 'https://example.com/article',
    addEducationalResource: '+ Añadir recurso educativo',
    invalidHttpUrl: 'Introduce una URL http(s) válida',
    educationalResourceTitleTooLong: 'El título debe tener como máximo 200 caracteres',
    educationalProductContextUnset: 'Sin definir (equivale a ALL)',
    selectResources: 'Seleccionar recursos…',
    noEducationalResourcesYet:
      'Aún no hay recursos — añade en Configuración → Recursos Educativos',
    educationalResourcesHelp:
      'Opcional. Elige los artículos/videos a referenciar en este toque. Vacío = el motor elige.',
    saveSettings: 'Guardar configuración',
    eg: 'e.g.,',
  

    // Auto-filled missing keys
    productFitNameExample: 'p. ej., LMS Core',
    productFitCategoryTagsExample: 'LMS, EdTech, Evaluación',
    productFitStrongSignalsExample: 'Se requiere integración con Canvas\nCompatibilidad con LTI 1.3\nCumplimiento de SCORM',
    productFitNegativeSignalsExample: 'Obligatorio: debe usar Microsoft SSO\nPreferencia: prefiere despliegue en la nube',
    productFitIntegrationsExample: 'Canvas, Moodle, LTI 1.3',
    productFitSecurityExample: 'ISO 27001, SOC 2',
    productFitKeyFeaturesExample: 'Analítica, app móvil, accesibilidad',
    // [2026-10-29] i18n audit: keys added
    financialSituation: 'Situación financiera',
    marketConditions: 'Condiciones del mercado',
    sessionInviteBlockedTitle: 'Se necesitan los detalles de la sesión para la invitación',
    openEventsSettings: 'Abrir configuración de eventos',
    sessionInviteReady: 'Título y descripción de la sesión configurados',
    quickConnectRequiresAttendees: 'Requiere al menos un asistente confirmado',
    myDeals: 'Mis oportunidades',
    routeToBuy: 'Ruta de compra',
    productFitCategories: 'Categorías',
    productFitCategoriesDesc: 'La misma lista de categorías que Adquisiciones para tu tenant (de oportunidades abiertas y cerradas).',
    productFitCategoriesLoading: 'Cargando categorías de adquisiciones…',
    productFitCategoriesFallback: 'No se pudieron cargar las categorías de adquisiciones; se muestra la taxonomía completa. Vuelve a guardar cuando cargue Adquisiciones o revisa la API.',
    productFitNoCategoriesSelected: 'No hay categorías seleccionadas: el perfil no se asociará a oportunidades por categoría.',
    editSignatureBelowOrPullFromGmail: 'Edita tu firma abajo o importa la actual desde tu cuenta de Gmail.',
    pasteOrTypeSignatureHere: 'Pega aquí tu firma o escríbela directamente.',
    useGmailSignature: 'Usar la firma de Gmail',
    noGmailSignatureFound: 'No se encontró ninguna firma de Gmail',
    selectAliasFirstOrSetInGmail: 'Selecciona primero un alias o define una firma en la configuración de Gmail.',
    sessionInviteEventBanner: 'La invitación a la sesión necesita un título y una descripción de la sesión para el evento seleccionado. Edita el evento en Configuración → Eventos y completa los detalles de la sesión.',
    sessionInviteMissingDetailsTip: 'Consejo: este evento aún no tiene detalles de la sesión. Los mensajes mencionarán el evento de forma genérica; añade un título de sesión en Configuración → Eventos para invitaciones más eficaces.',
    sessionInviteRequiresSessionDetails: 'Requiere título y descripción de la sesión en Configuración → Eventos',
    status: 'Estado',
    replies: 'Respuestas',
    brandVoiceDescription: 'Descripción de la voz de marca',
    brandVoiceDescriptionPlaceholder: 'Describe cómo debe sonar tu marca, p. ej., cercana, experta, concisa',
    additionalStylePreferencesOverride: 'Preferencias de estilo adicionales (sustitución)',
    companyName: 'Nombre de la empresa',
    coreValueProposition: 'Propuesta de valor principal',
    messagingHooks: 'Ganchos de mensaje',
    targetPersona: 'Persona objetivo',
    economicBuyer: 'Comprador económico',
    addMessagingHook: 'Añadir gancho de mensaje',
    url: 'URL',
    productLinks: 'Enlaces de producto',
    timezone: 'Zona horaria',
    caseStudy: 'caso de éxito',
    customPlaybook: 'Playbook personalizado',
    // [2026-10-29] i18n: UI chrome keys (market / procurement / account / playbooks)
    mkt12w: '12 sem',
    mkt4w: '4 sem',
    mktAboutEnrichment: 'Acerca del enriquecimiento',
    mktAboutLabel: 'Acerca de {label}',
    mktAccountBarometer: 'Barómetro de cuentas',
    mktAccountPageNotFound: 'No se encontró la página de la cuenta',
    mktAccountsAffected: 'Cuentas afectadas',
    mktAccountsAffectedTip: 'Número de tus cuentas objetivo en las que al menos una señal menciona a este competidor. Cada cuenta se cuenta una vez, aunque haya varias señales.',
    mktAccountsExaminedOf: '{examined} de {total} cuentas revisadas',
    mktAccountsInMotion: 'Cuentas en movimiento',
    mktAccountsInMotionHint: 'Instituciones cuyas señales se están moviendo. Se marcan como “en movimiento” salvo que haya evidencia explícita de compra.',
    mktAcrossNCountries: 'en {n} países',
    mktAcrossNProgrammes: 'en {n} programas',
    mktAdd: 'Añadir',
    mktAddPastedVendors: 'Añadir proveedores pegados',
    mktAddToAccounts: 'Añadir a Cuentas',
    mktAddToPilot: 'Añadir al piloto',
    mktAddedLoadingNow: 'Sus personas, señales e historial se están cargando.',
    mktAddedToAccounts: 'Añadida a cuentas',
    mktAddedToYourAccounts: 'Añadida a tus cuentas',
    mktAdding: 'Añadiendo…',
    mktAddressable: 'Accesible',
    mktAdjacent: 'Adyacente',
    mktAdjacentMarket: 'Mercado adyacente',
    mktAdjacentPlayer: 'Actor adyacente',
    mktAdjacentVendor: 'Proveedor adyacente',
    mktAfter: 'Después',
    mktAiDef1: 'Explorando. Nada publicado y aún sin señales de uso de herramientas de IA.',
    mktAiDef2: 'En piloto. Hay algo en marcha, sin una política que lo regule.',
    mktAiDef3: 'Política vigente. Publicada y aprobada, pero no vemos que se esté aplicando.',
    mktAiDef4: 'Adopción regulada. Hay evidencia tanto de la política como del uso real.',
    mktAiDef5: 'Integrada. La IA forma parte de cómo enseñan y evalúan.',
    mktAiEmbedded: 'Integrada',
    mktAiExploring: 'Explorando',
    mktAiGovernedAdoption: 'Adopción regulada',
    mktAiPiloting: 'En piloto',
    mktAiPolicyInPlace: 'Política vigente',
    mktAiReadiness: 'Preparación para IA',
    mktAiReadinessBelow: 'Preparación para IA inferior a',
    mktAiReadinessIndex: 'Índice de preparación para IA',
    mktAiReadinessWholeMarketHint: 'Preparación para IA en todo el mercado, no solo en tus oportunidades.',
    mktAiScale: 'Miramos dos cosas, qué tan madura es su política y qué tan a fondo han adoptado herramientas, y tomamos la más alta. Los niveles 4 y 5 requieren ambas; una política por sí sola llega como máximo a 3. Lo que no hemos visto confirmado en 18 meses baja un nivel.',
    mktAllCategories: 'Todas las categorías',
    mktAllCountries: 'Todos los países',
    mktAllEntitledCountries: 'Todos los países habilitados',
    mktAllProgrammes: 'Todos los programas ({n})',
    mktAlreadyFunded: 'Ya financiadas',
    mktAlreadyOnYourList: 'Ya está en tu lista',
    mktAlsoPriority: 'También prioritarias',
    mktAlsoPriorityHint: 'Cuentas elegibles que ya son prioritarias en tu mercado.',
    mktAmount: 'Importe',
    mktAnnualRunRate: 'Ritmo anual {annual}.',
    mktAwardCeiling: 'Importe máximo',
    mktAwardCeilingHint: 'El rango que puede alcanzar una sola adjudicación, en palabras del financiador.',
    mktBackOnTeamSettings: 'De vuelta a la configuración del equipo.',
    mktBandCapacity: 'capacidad {band}',
    mktBarometerScore: 'Puntuación del barómetro',
    mktBarometerScoreAccountTip: 'Proximidad del competidor de 0 a 100. Se calcula como fuerza de la señal × decaimiento por antigüedad. Más alto = mayor presencia del competidor en esta cuenta. La dirección (favorable/desfavorable) se muestra aparte en Impacto.',
    mktBarometerScorePersonaTip: 'Proximidad del competidor de 0 a 100 para esta persona. Se calcula como fuerza de la señal × decaimiento por antigüedad. Más alto = mayor presencia del competidor. La dirección se muestra aparte en Impacto.',
    mktBarrier: 'Barrera',
    mktBefore: 'Antes',
    mktBuyingEvidence: 'Evidencia de compra',
    mktBuyingSignals: 'Señales de compra',
    mktByCountryN: 'Por país ({n})',
    mktCandidates: 'Candidatas',
    mktChangeAFilterFirst: 'Primero cambia un filtro.',
    mktChooseProductCategory: 'Elige una categoría de producto',
    mktCloseCountryPanel: 'Cerrar panel del país',
    mktClosingSoon: 'Cierran pronto',
    mktClosingSoonHint: 'Convocatorias abiertas con fecha límite en los próximos 45 días.',
    mktCompetitor: 'Competidor',
    mktCompetitorLocked: 'Competidor bloqueado: nunca desplazar',
    mktCompetitorLockedHint: 'Las instituciones que usan estos proveedores quedan fuera de tus listas de oportunidades. Siguen en la lista de competidores; simplemente no intentas ganarlas.',
    mktCompetitorSet: 'Competidores',
    mktCompetitorSetHint: 'Si un proveedor está en esta lista, está presente en una institución y allí está pasando algo, es una oportunidad de desplazamiento. Si lo dejas fuera, la misma cuenta se considera coexistencia. Basta un nombre por proveedor; nosotros resolvemos las variantes.',
    mktCompetitorView: 'Vista por competidor',
    mktCompetitorsPresent: 'Competidores presentes',
    mktConfirmEmptyCompetitorSet: 'Si esta lista está vacía, nada puede ser un desplazamiento. Tu mercado se reducirá a cuentas sin solución actual. ¿Guardar de todos modos?',
    mktContextOnly: 'Solo contexto',
    mktContractScope: 'Alcance del contrato',
    mktContractScopeUnknownHint: 'Nadie lo ha determinado todavía. No significa que la implantación sea pequeña, así que lo dejamos fuera de la puntuación en lugar de penalizarlo.',
    mktCouldNotAddAccount: 'No se pudo añadir la cuenta',
    mktCouldNotAddThisAccount: 'No se pudo añadir esta cuenta',
    mktCouldNotCompleteAction: 'No se pudo completar la acción',
    mktCouldNotLoadAccountsInMotion: 'No se pudieron cargar las cuentas en movimiento',
    mktCouldNotLoadCategory: 'No se pudo cargar esta categoría',
    mktCouldNotLoadCompetitorData: 'No se pudieron cargar los datos de competidores. Inténtalo de nuevo.',
    mktCouldNotLoadFunding: 'No se pudo cargar la financiación',
    mktCouldNotLoadIgnored: 'No se pudieron cargar las cuentas ignoradas',
    mktCouldNotLoadMarketData: 'No se pudieron cargar los datos del mercado. Actualiza la página.',
    mktCouldNotLoadOpportunities: 'No se pudieron cargar las oportunidades',
    mktCouldNotLoadPlayers: 'No se pudieron cargar los actores',
    mktCouldNotLoadPreferences: 'No se pudieron cargar las preferencias',
    mktCouldNotLoadRenewalRisk: 'No se pudo cargar el riesgo de renovación',
    mktCouldNotLoadSummary: 'No se pudo cargar el resumen del mercado',
    mktCouldNotMoveAccountBack: 'No se pudo devolver la cuenta',
    mktCouldNotOpenAccount: 'No se pudo abrir la cuenta',
    mktCouldNotRemoveAccount: 'No se pudo quitar la cuenta.',
    mktCouldNotResolveAccount: 'Todavía no se pudo localizar esta cuenta en tu lista de Cuentas.',
    mktCouldNotSave: 'No se pudo guardar',
    mktCouldNotSwitch: 'No se pudo cambiar',
    mktCountries: 'Países',
    mktCoverage: 'Cobertura',
    mktCurrentVersion: 'actual',
    mktDeadlineSpendBy: 'Fecha límite / gastar antes de',
    mktDisplacement: 'Desplazamiento',
    mktDisqualifiers: 'Criterios de exclusión',
    mktEligibleAccounts: 'Cuentas elegibles',
    mktEligibleSpend: 'Gasto elegible',
    mktEmptyCompetitorSetWarning: 'Si esta lista está vacía, nada puede ser un desplazamiento. Tu mercado se reducirá a cuentas sin solución actual.',
    mktEnrichRequestToast: 'Procesaremos tu solicitud en 48 horas y te avisaremos cuando esté lista.',
    mktEnrichTooltip: 'Envía esta cuenta al equipo de investigación para completar lo que falta: a quién usan, con qué alcance, cuándo termina el contrato y si están satisfechos. Tu puntuación no cambiará hoy. Cuando llegue la investigación, la cuenta se vuelve a puntuar y lo verás en Qué cambió.',
    mktEnrichmentRequested: 'Enriquecimiento solicitado',
    mktEnrichmentRequestedOn: 'Solicitado el {date}. Volveremos a puntuar esta cuenta cuando llegue la investigación.',
    mktEstSpendBy: 'gastar antes de (est.)',
    mktEstimatedValue: 'Valor estimado',
    mktEvidence: 'Evidencia',
    mktExaminedOf: '{examined} de {total} revisadas',
    mktExaminedSuffix: ' revisadas',
    mktExcludeCurrentCustomers: 'Excluir clientes actuales',
    mktExistingCustomer: 'Cliente actual',
    mktExistingExpansion: 'Actuales / expansión',
    mktExpansion: 'Expansión',
    mktFavourable: 'Favorable',
    mktFavourableDisplacementOpps: 'Oportunidades de desplazamiento favorables',
    mktFinancialBandBelow: 'Nivel financiero inferior a',
    mktFinancialBandBelowHint: 'Margen estimado para financiar nuevas iniciativas, basado en el gasto operativo y las reservas por estudiante y en el margen operativo, comparado con pares nacionales del mismo sector. Alta significa más margen para invertir; Baja puede indicar presión presupuestaria. Refleja capacidad de gasto, no presupuesto ya asignado a software.',
    mktFinancialCapacity: 'Capacidad financiera',
    mktFinancialWholeMarketHint: 'Capacidad financiera en todo el mercado. Cuánto margen tienen las instituciones para financiar algo nuevo, no cuánto gastan en EdTech.',
    mktFitAddUnscoredFallback: 'Esta cuenta está en tus Cuentas, pero Mercado no tenía puntuación para ella.',
    mktFlat: 'Estable',
    mktFullAssessment: 'Evaluación completa',
    mktFunder: 'Financiador',
    mktFunding: 'Financiación',
    mktFundingAccountsHint: 'Cuentas a las que puede llegar esta financiación. Cómo plantearlo consulta a NEO con el programa, el importe y la elegibilidad de esa fila.',
    mktFundingEligibility: 'Elegibilidad para financiación',
    mktFundingEligible: 'Elegibles',
    mktFundingEligibleHint: 'Cuentas que pueden destinar esta financiación a tu categoría, del total de cuentas de este mercado.',
    mktFundingNotSwitchedOn: 'Financiación aún no está activada para este cliente.',
    mktGapClientsOnly: 'En tu lista de clientes. No hemos confirmado qué producto usan.',
    mktGapStackOnly: 'Usa tu producto según el registro de proveedores, pero no está en tu lista de clientes.',
    mktGradeDocumented: 'Documentado',
    mktGradeDocumentedHint: 'La institución ha publicado una deficiencia concreta del producto del proveedor, normalmente en una declaración de accesibilidad.',
    mktGradeExplicit: 'Explícito',
    mktGradeExplicitHint: 'Un órgano de gobierno, senado, comité o sindicato estudiantil dejó constancia oficial de la queja.',
    mktGradeImplied: 'Implícito',
    mktGradeImpliedHint: 'Encontramos problemas en el proceso que cubre este proveedor, pero nadie ha dicho que quiera cambiar.',
    mktGradeReported: 'Reportado',
    mktGradeReportedHint: 'La queja está publicada en algún lugar, pero no en una reunión donde se toman decisiones.',
    mktGradeTiming: 'Calendario',
    mktGradeTimingHint: 'Su contrato vence dentro de tu ventana de renovación, así que volverá a licitarse, opinen lo que opinen del proveedor.',
    mktGreenfield: 'Sin solución actual',
    mktGreenfieldAdjacent: 'Sin solución / adyacentes',
    mktHasOpportunities: 'Tiene oportunidades',
    mktHeld: 'En espera',
    mktHeldHint: 'En espera no es excluida. Esperamos el recuento completo de estudiantes antes de aplicar tu mínimo, en lugar de descartar una cuenta por una cifra parcial.',
    mktHideCountryDetail: 'Ocultar detalle por país',
    mktHideOlderEvents: 'Ocultar eventos antiguos y de otras categorías',
    mktHighProximityAccounts: 'Cuentas de alta proximidad',
    mktHighestInYourMarket: 'Las más altas de tu mercado: {names}',
    mktHowMuchWeHaveLookedAt: 'Cuánto hemos revisado',
    mktHowToPitch: 'Cómo plantearlo →',
    mktIgnore: 'Ignoradas',
    mktIgnoreAccount: 'Ignorar cuenta',
    mktIgnoreHint: 'Cuentas que revisamos y apartamos. No es lo mismo que Resto del mercado: esas son cuentas que nadie ha revisado todavía.',
    mktIgnoreNotSwitchedOn: 'Esta lista muestra cuentas que revisamos y apartamos, no cuentas que nadie ha revisado. Aún no está activada para este cliente.',
    mktImpactAccountTip: 'Si la evidencia sobre el competidor en esta cuenta es mala para ti (Desfavorable), buena para ti (Favorable, p. ej. insatisfacción u oportunidad de reemplazo), Mixta o Solo contexto.',
    mktImpactPersonaTip: 'Si la evidencia sobre el competidor para esta persona es mala para ti (Desfavorable), buena para ti (Favorable), Mixta o Solo contexto.',
    mktInHouse: 'Interno',
    mktInOurFavour: 'A nuestro favor',
    mktInPilot: 'En piloto',
    mktInPlay: 'En juego',
    mktInScope: 'en {label}',
    mktInThisMarket: 'en este mercado',
    mktInYourMarket: 'En tu mercado',
    mktIncumbent: 'Proveedor actual',
    mktIncumbentSentimentLine: 'Percepción del proveedor actual: {sentiment}',
    mktInstitutionShare: 'Proporción de instituciones',
    mktInstitutions: 'Instituciones',
    mktKeySectorTrends: 'Tendencias clave del sector',
    mktLastRefreshed: 'Última actualización',
    mktLatestSignal: 'Última señal',
    mktLatestSignalAccountTip: 'Fecha de la señal de competidor más reciente en esta cuenta.',
    mktLatestSignalPersonaTip: 'Fecha de la señal de competidor más reciente para esta persona.',
    mktLatestUpdate: 'Última novedad',
    mktLatestUpdateTip: 'Resumen de la evidencia más reciente entre todas las señales de este competidor.',
    mktLeaving: 'Salen: {names}',
    mktLens: 'Vista',
    mktLimited: 'Limitada',
    mktLiveSignal: 'Señal activa',
    mktLmsChipTitle: 'No es una categoría en la que vendas. Está aquí porque es a lo que se conecta todo lo demás.',
    mktLmsFamiliesNote: 'Solo para tu vista. Nombres de familia, no nombres brutos de proveedor: Open LMS y Totara son distintos de Moodle.',
    mktLmsShareHint: 'La proporción de instituciones que usan cada LMS. Es un recuento de instituciones, no de ingresos.',
    mktLoadingEllipsis: 'Cargando…',
    mktLockPastedVendors: 'Bloquear proveedores pegados',
    mktMainThemes: 'Temas principales',
    mktMainThemesTip: 'Temas extraídos de las etiquetas Themes: de las señales y de las etiquetas de tema de las novedades del mercado. Describen de qué habla el competidor, no la fuerza de la señal.',
    mktMapTipEmpty: '{label}. Cargado, pero nada califica en esta categoría.',
    mktMapTipNOpportunities: '{label}. {count} oportunidades.',
    mktMapTipNotLoaded: '{label}. Todavía no hemos cargado este mercado.',
    mktMapTipOneOpportunity: '{label}. {count} oportunidad.',
    mktMarkRead: 'Marcar como leído',
    mktMarketEvents: 'Eventos de mercado',
    mktMarketEventsTip: 'Número de actividades de mercado atribuibles a este competidor: eventos regionales, adopciones externas y otros movimientos no vinculados a tus cuentas objetivo.',
    mktMarketIsRescoring: 'Se está recalculando la puntuación del mercado. Puedes seguir consultando esta vista.',
    mktMarketOpportunity: 'Oportunidad de mercado',
    mktMarketPreferences: 'Preferencias de mercado',
    mktMarketRecomputed: 'Mercado recalculado.',
    mktMarketScore: 'Puntuación de mercado',
    mktMarketSummary: 'Resumen del mercado',
    mktMarketTracker: 'Seguimiento del mercado',
    mktMatchesForms: 'Coincide con {forms}',
    mktMatching: 'Buscando coincidencias…',
    mktMaximumEnrolment: 'Matrícula máxima',
    mktMinimumEnrolment: 'Matrícula mínima',
    mktMinimumEnrolmentHint: 'Cuando solo tenemos un recuento parcial de estudiantes, dejamos la cuenta en espera en lugar de descartarla, para que una cifra parcial nunca excluya a una institución grande.',
    mktMinimumPain: 'Problema mínimo',
    mktMinimumPainHint: 'Puntuado de 0 a 5. Con qué claridad debe expresarse un problema para que una cuenta cuente como parte de tu mercado. 2 es una mención de pasada o la intención de arreglar algo. 3 es una falla declarada. 4 es un defecto concreto descrito con claridad. 5 es una falla con una consecuencia explícita, como un incumplimiento normativo. La mayoría de los equipos usa 3; pasar a 4 reduce la evidencia válida en torno a un 40 %.',
    mktMix: 'Composición',
    mktMixed: 'Mixto',
    mktMoveBackToMyList: 'Devolver a mi lista',
    mktMovedBackToYourList: 'Devuelta a tu lista',
    mktMovedToIgnore: 'Movida a Ignoradas',
    mktMoving: 'Moviendo…',
    mktNAccounts: '{n} cuentas',
    mktNAccountsExcluded: '{n} cuentas excluidas',
    mktNCategories: '{n} categorías',
    mktNDays: '{n} días',
    mktNHaveWonBefore: '{n} ya lo han obtenido antes',
    mktNHeldPendingEnrolment: '{n} en espera de una cifra de matrícula',
    mktNInMarket: '{n} en el mercado',
    mktNInYourMarket: '{n} en tu mercado',
    mktNLmsCells: '{n} celdas de LMS',
    mktNMarketEvents: '{n} eventos de mercado',
    mktNMatchingQuery: '{n} coinciden con “{query}”',
    mktNNewSignals: '{n} señales nuevas',
    mktNOfTotalSetAside: '{n} de {total} cuentas apartadas',
    mktNPersonas: '{n} personas',
    mktNSelected: '{n} seleccionados',
    mktNSetAside: '{n} cuentas apartadas',
    mktNSignals: '{n} señales',
    mktNamesYourCategories: 'Menciona tus categorías ({n})',
    mktNeoDidNotSayWhy: 'NEO no indicó el motivo.',
    mktNeoView: 'Visión de NEO',
    mktNetImpact: 'Impacto neto',
    mktNetImpactTip: 'Si la evidencia sobre el competidor es mala para ti (Desfavorable), buena para ti (Favorable, p. ej. insatisfacción u oportunidad de reemplazo), Mixta o Solo contexto (informativa, sin dirección clara).',
    mktNewSignals90d: 'Señales nuevas (últimos 90 días)',
    mktNewSignals90dTip: 'Señales con fecha de los últimos 90 días.',
    mktNo: 'No',
    mktNoAccountsInGroup: 'No hay cuentas en este grupo en este momento.',
    mktNoAccountsInMotionFor: 'No hay cuentas en movimiento para {scope}.',
    mktNoAccountsInView: 'No hay cuentas en esta vista.',
    mktNoAccountsMatchQuery: 'Ninguna cuenta coincide con “{query}”.',
    mktNoAccountsMatchSearch: 'Ninguna cuenta coincide con esta búsqueda.',
    mktNoAccountsSetAside: 'Todavía no hay cuentas apartadas en este mercado.',
    mktNoAccountsWithReason: 'No hay cuentas con este motivo en el filtro actual.',
    mktNoAiCompositionFor: 'No hay distribución de preparación para IA para {scope}.',
    mktNoAtRisk: 'Todavía no hay cuentas con riesgo conocido en esta vista.',
    mktNoAtRiskForCountry: 'Todavía no hay cuentas con riesgo conocido en esta vista para {country}.',
    mktNoCachedDataWait20: 'No hay datos en caché. Espera 20 minutos antes de actualizar.',
    mktNoCompetitorEvidence: 'Todavía no hay evidencia de competidores cargada.',
    mktNoFinancialCompositionFor: 'No hay distribución de capacidad financiera para {scope}.',
    mktNoLmsDataFor: 'No hay datos de proveedores de LMS para {scope}',
    mktNoPriorVersions: 'No hay versiones anteriores.',
    mktNoProgrammesInView: 'No hay programas en esta vista.',
    mktNoRecentChanges: 'No hay cambios recientes en el mercado.',
    mktNoRenewalEvidence: 'Todavía no hay evidencia de renovación registrada.',
    mktNoSectorValues: 'Las cuentas aún no tienen valores de sector.',
    mktNoVendorDataForCategory: 'No hay datos de proveedores para esta categoría',
    mktNotAnOpportunity: 'No es una oportunidad',
    mktNotLoadedYet: 'Aún no cargado',
    mktNothingChangedAlreadyThere: 'No cambió nada: ya estaba ahí.',
    mktNothingClassifiedYet: 'Todavía no hay nada clasificado en esta vista.',
    mktNothingInListYet: 'Todavía no hay nada en esta lista.',
    mktNothingLocked: 'Nada bloqueado. Todos los proveedores de tu lista de competidores están incluidos.',
    mktNothingToPreview: 'Todavía no hay nada que mostrar para esta cuenta.',
    mktNothingToPreviewTitle: 'Nada que previsualizar',
    mktOfN: 'de {n}',
    mktOfSeparator: ' de ',
    mktOneAccount: '{n} cuenta',
    mktOneMarketEvent: '{n} evento de mercado',
    mktOneNewSignal: '{n} señal nueva',
    mktOnePersona: '{n} persona',
    mktOneSignal: '{n} señal',
    mktOpenRound: 'Convocatoria abierta',
    mktOpenRounds: 'Convocatorias abiertas',
    mktOpenSalesMap: 'Abrir SalesMap',
    mktOpenSetup: 'Abrir configuración',
    mktOpening: 'Abriendo…',
    mktOpportunities: 'Oportunidades',
    mktOpportunity: 'Oportunidad',
    mktOtherN: 'Otros ({n})',
    mktOurCategories: 'Nuestras categorías',
    mktOurCategoriesLine: 'Nuestras categorías: {categories}',
    mktOurs: 'Nuestro',
    mktPageNOfM: 'Página {n} de {m}',
    mktPageSubtitle: 'Tu oportunidad de mercado personalizada y la inteligencia GTM más reciente.',
    mktPasteVendorsPlaceholder: 'Pega proveedores, uno por línea o separados por comas',
    mktPasteVendorsToLockPlaceholder: 'Pega los proveedores a bloquear, uno por línea o separados por comas',
    mktPersona: 'Persona',
    mktPersonaBarometer: 'Barómetro de personas',
    mktPersonasAffected: 'Personas afectadas',
    mktPersonasAffectedTip: 'Número de personas objetivo con evidencia directa de competidores: citadas, etiquetadas, nombradas en un caso de éxito o contactadas por un actor competidor. No cuentan las personas vinculadas a la cuenta que no se nombran personalmente.',
    mktPersonasWithCompetitorLinks: 'Personas vinculadas a competidores',
    mktPlayersChartTooltip: 'La proporción de instituciones de tu mercado que usan cada proveedor en esta categoría. Es un recuento de instituciones, no de ingresos. Las proporciones pueden sumar más del 100 % porque una cuenta puede usar más de un proveedor.',
    mktPleaseTryAgain: 'Inténtalo de nuevo',
    mktPreferences: 'Preferencias',
    mktPreferencesSaved: 'Preferencias guardadas',
    mktPrefsIntro: 'Define las reglas que deciden qué cuenta como oportunidad.',
    mktPrefsOnOwnSettings: 'Estás usando tu propia configuración. La del equipo es distinta.',
    mktPrefsOnTeamSettings: 'Estás usando la configuración del equipo. Al guardar se creará la tuya.',
    mktPreviewChange: 'Previsualizar cambio',
    mktPreviewCounts: 'En tu mercado {sam} · Prioridad {priority} · Excluidas {excluded}',
    mktPreviewFailed: 'Falló la vista previa',
    mktPreviewMoved: 'Entran {in} · salen {out} · suben {promoted} · bajan {demoted}',
    mktPreviewing: 'Generando vista previa…',
    mktPreviouslyFunded: 'Financiación anterior',
    mktPriority: 'Prioridad',
    mktPriorityScoreThreshold: 'Umbral de puntuación de prioridad',
    mktPriorityScoreThresholdHint: 'La puntuación que necesita una cuenta para marcarla como Prioridad. Combina qué tan bien encaja la institución con lo que vendes y cuánto está pasando realmente allí. Cerca de 60 obtienes instituciones con buen perfil aunque aún no se muevan; cerca de 70 la mayoría también tendrá un problema declarado; a partir de 75 ambos aspectos son fuertes y la lista es corta. Cambia por dónde empiezas, no quién está en tu mercado.',
    mktProductCategory: 'Categoría de producto',
    mktProgramme: 'Programa',
    mktProgrammes: 'Programas',
    mktProgrammesHint: 'Se abre con los programas que mencionan una de tus categorías; las otras dos vistas amplían la selección. Un programa con 0 cuentas elegibles también aparece: esa brecha es lo importante. La fecha límite solo se muestra cuando la convocatoria está abierta; un programa activo muestra la fecha antes de la cual debe gastarse el dinero asignado, y a eso no se postula. Preanunciado significa que conviene vigilarlo, no que cierre pronto.',
    mktProvisional: 'Provisional',
    mktQueueIntroAdjacent: 'Muestra proveedores ya presentes con los que no compites, para ayudarte a entender el ecosistema tecnológico actual y dónde podría encajar tu solución.',
    mktQueueIntroInMarket: 'Oportunidades reales en instituciones que encajan menos contigo. Vale la pena trabajarlas después de revisar Prioridad.',
    mktQueueIntroPriority: 'Tus mejores oportunidades ahora mismo: sin solución actual, desplazamiento o expansión.',
    mktQueueIntroRest: 'El resto del mercado al que podrías vender. Falta información para poder evaluarlas. Las de mejor ajuste aparecen primero.',
    mktRangeOfTotal: '{from}–{to} de {total}',
    mktRareEvent: 'Evento poco común',
    mktRareEventMeans: 'Un evento poco común y relevante elevó el eje de actividad actual en esta puntuación.',
    mktRateLimitActive: '⏸️ Límite de solicitudes activo',
    mktRateLimitReached: '⚠️ Límite de solicitudes alcanzado',
    mktRecalculateFailed: 'Falló el recálculo',
    mktRecalculateMarket: 'Recalcular mercado',
    mktRecalculated: 'Recalculado',
    mktRecalculating: 'Recalculando…',
    mktRecalculationComplete: 'Recálculo completado',
    mktRecency: 'Recencia',
    mktRecommendation: 'Recomendación',
    mktRecommendationTip: 'Abre el SalesMap de esta cuenta. Cuando la proximidad llega a 50, un análisis competitivo se sincroniza automáticamente con la fila current_vendor del SalesMap.',
    mktRecomputedCounts: 'En tu mercado {sam} · Prioridad {priority}.',
    mktRelationship: 'Relación',
    mktRemove: 'Quitar',
    mktRemoveSkipNotFromMarket: 'Esta cuenta no se añadió desde Mercado. Usa Desvincular en su lugar.',
    mktRemoveSkipNotInAccounts: 'Esa cuenta no está en tu lista.',
    mktRenewalDate: 'Fecha de renovación',
    mktRenewalNotSwitchedOn: 'Esta pestaña cubre las instituciones que ya usan tu producto, así que responde a quién podrías perder en lugar de a quién podrías ganar. Aún no está activada para este cliente.',
    mktRenewalOpen: 'Renovación abierta',
    mktRenewalWindow: 'Ventana de renovación',
    mktReplacingInHouse: 'Reemplazo de solución interna',
    mktRequestEnrichment: 'Solicitar enriquecimiento',
    mktRequesting: 'Solicitando…',
    mktRescoringTheMarket: 'Recalculando la puntuación del mercado',
    mktRestOfMarket: 'Resto del mercado',
    mktRestoredToAccounts: 'Restaurada en cuentas',
    mktReturnsOn: 'Vuelve a tu lista el {date}',
    mktRisk: 'Riesgo',
    mktRiskConfirmed: 'Riesgo confirmado',
    mktRiskConfirmedMeans: 'Han dicho que no están contentos con nosotros, o su contrato termina dentro del horizonte.',
    mktRiskHint: 'Hechos concretos sobre lo que va mal (una queja, insatisfacción, una deficiencia publicada del producto o el fin de un contrato), no una puntuación. El color sigue distinguiendo confirmado de vigilar.',
    mktRiskLookedNothingFound: 'Revisado · sin hallazgos',
    mktRiskNotYetChecked: 'Aún sin revisar',
    mktRiskQuietMeans: 'Revisamos y no encontramos nada. Es una respuesta real, no un vacío.',
    mktRiskUnknownMeans: 'Nadie ha revisado esta cuenta. No es lo mismo que que esté tranquila.',
    mktRiskWatch: 'Vigilar',
    mktRiskWatchMeans: 'Hay una deficiencia publicada de nuestro producto aquí, o un competidor ha entrado en una categoría vecina.',
    mktRollBack: 'Revertir',
    mktRubricFitHint: 'Qué tan bien encaja una institución con lo que vendes: qué usa hoy y qué tan reemplazable es, su tamaño y qué tan preparada parece para cambiar. Súbelo para favorecer las cuentas con el perfil adecuado aunque todavía no esté pasando nada.',
    mktRubricHistory: 'Historial de la rúbrica',
    mktRubricSignalsHint: 'Señales de que realmente se están moviendo: un problema declarado con lo que usan ahora, además de compras, presupuesto, una renovación próxima o una nueva adjudicación. Súbelo para priorizar lo que está pasando por encima de quién encaja sobre el papel.',
    mktRubricWeights: 'Pesos de la rúbrica',
    mktRunningAgainstUs: 'En nuestra contra',
    mktSaveForEveryone: 'Guardar para todos',
    mktSaveForMe: 'Guardar para mí',
    mktSavedRescoreFailed: 'Guardado, pero falló el recálculo',
    mktSaving: 'Guardando…',
    mktScore: 'Puntuación',
    mktScoresMayReflectPrevious: 'Es posible que las puntuaciones aún reflejen la configuración anterior.',
    mktSearchAccounts: 'Buscar cuentas',
    mktSearchAccountsByName: 'Buscar cuentas por nombre',
    mktSearchByAccountName: 'Buscar por nombre de cuenta',
    mktSectorExclude: 'Excluir sectores',
    mktSelectSectors: 'Seleccionar sectores',
    mktSectors: 'Sectores',
    mktSectorsHint: 'Los tipos de institución que forman su mercado. Elija uno o varios.',
    mktNOfMSelected: '{n} de {m} seleccionados',
    mktSelectAll: 'Seleccionar todo',
    mktClearAll: 'Borrar',
    mktChooseOneSector: 'Elija al menos un sector.',
    mktRescoreStillRunning: 'Recalculando todavía',
    mktRescoreStillRunningHint: 'Sus ajustes están guardados. El mercado aún se está recalculando; actualice la página en unos minutos.',
    mktSelectedAccount: 'Cuenta seleccionada',
    mktSetUpMarket: 'Configurar Mercado',
    mktSetupBodyEditor: 'Mercado está activado para este cliente, pero nadie ha elegido aún una categoría de producto. Elige una y crearemos tu lista de oportunidades.',
    mktSetupBodyViewer: 'Mercado está activado, pero un administrador debe elegir una categoría de producto antes de que haya algo que mostrar aquí.',
    mktShare: 'Proporción',
    mktShareCaption: 'Las proporciones pueden sumar más del 100 %. Los proveedores minoritarios por debajo del {pct} % se agrupan como Otros (atenuado).',
    mktShowNOlderEvents: 'Mostrar {n} eventos antiguos o de otras categorías',
    mktShowOneOlderEvent: 'Mostrar {n} evento antiguo o de otra categoría',
    mktShowingAccountsOnPage: 'Se muestran las cuentas de esta página.',
    mktShowingCachedDataRetry20: 'Mostrando datos en caché. Se reintentará automáticamente en 20 minutos.',
    mktSpendBy: 'gastar antes de',
    mktSpendEstimateTitle: 'Nuestra estimación de cuánto dura el dinero, no una fecha publicada por el financiador',
    mktStage: 'Etapa',
    mktStale: 'Desactualizado',
    mktStanding: 'Situación',
    mktStateBuiltInHouse: 'Desarrollo interno',
    mktStateLmsNative: 'Nativo del LMS',
    mktStateNothingInPlace: 'Nada implantado',
    mktStateOpenSource: 'Código abierto',
    mktStateSuiteModule: 'Módulo de suite',
    mktStatus: 'Estado',
    mktStatusEmpty: 'vacío',
    mktStatusNotLoaded: 'sin cargar',
    mktStrongestAccounts: 'Cuentas más fuertes',
    mktStrongestEvidence: 'Evidencia más fuerte',
    mktStrongestEvidenceAccountTip: 'La señal individual con mayor puntuación en esta cuenta: lo que más impulsa la proximidad.',
    mktStrongestEvidencePersonaTip: 'La señal individual con mayor puntuación para esta persona: lo que más impulsa la proximidad.',
    mktStrongestPersonas: 'Personas más fuertes',
    mktSubset: 'Subconjunto: {measure}',
    mktSumValue: 'Suma: {value}',
    mktSwitching: 'Cambiando…',
    mktTabCompetitors: 'Competidores',
    mktTabRenewalRisk: 'Riesgo de renovación',
    mktTargetThresholds: 'Umbrales de oportunidad',
    mktThisMarket: 'este mercado',
    mktTipAddressable: 'Las de esa lista a las que podrías vender de forma realista. Si coincide con Mercado total, aún no se han configurado reglas de exclusión, así que no se filtra nada.',
    mktTipAiReadiness: 'Hasta dónde ha llevado esta institución la IA en la enseñanza y la evaluación, de 1 (solo explorando) a 5 (integrada en su forma de trabajar).',
    mktTipContractScope: 'Qué tan implantado está su proveedor actual: en toda la institución, en varias facultades, en un departamento o en una prueba. Cuanto más amplio, mayor es la oportunidad y más difícil desplazarlo.',
    mktTipEvidence: 'Cuánta de la información habitual tenemos sobre esta cuenta, comparada con instituciones similares y no con un registro perfecto. Si es escasa, moderamos la puntuación en lugar de suponer al alza. Indica cuán seguros estamos, no cuán buena es la cuenta.',
    mktTipExamined: 'Qué parte de este mercado se ha revisado realmente. Un número bajo significa que falta investigación, no que el mercado esté vacío.',
    mktTipFinancial: 'Cuánto margen tiene esta institución para financiar algo nuevo, comparada con otras de su tamaño y tipo. No refleja cuánto gasta en EdTech.',
    mktTipGrade: 'Qué tan sólido es el argumento de que su proveedor actual podría perder la cuenta. El más fuerte es una queja recogida en las actas de un órgano de gobierno. El más débil es un problema que inferimos de su forma de trabajar, sin que nadie se haya quejado.',
    mktTipInMarket: 'Cuentas en las que encontramos algo con qué trabajar: o su proveedor actual parece que podría perder la cuenta, o nadie ha comprado nada y hay una necesidad real. No significa que haya una oportunidad abierta.',
    mktTipIncumbent: 'A quién usan hoy para esta categoría. Las instituciones suelen tener proveedores distintos en cada categoría, así que este es el que importa para esta oportunidad, no el único que tienen.',
    mktTipKnown: 'Todas las instituciones de las que tenemos registro en los países que cubres. Todavía no se ha filtrado nada.',
    mktTipMomentum: 'Impulso de compra en las últimas 4 y 12 semanas. Las puntuaciones negativas indican señales desfavorables; las positivas, señales favorables.',
    mktTipPriority: 'Las cuentas de tu mercado que además tienen un ajuste lo bastante alto como para empezar por ellas. Empieza aquí.',
    mktTipScore: 'Sobre 100. Qué tan bien encaja la institución con lo que vendes, combinado con lo que ocurre allí ahora mismo, y moderado si la evidencia es escasa.',
    mktTipType: 'Qué tipo de oportunidad sería. Pasa el cursor sobre cualquier etiqueta para ver qué significa.',
    mktTopCompetitor: 'Competidor principal',
    mktTopCompetitorAccountTip: 'Competidor con la mayor contribución ponderada de señales en esta cuenta. Ponderado por fuerza y recencia de la señal.',
    mktTopCompetitorPersonaTip: 'Competidor con la mayor contribución ponderada de señales para esta persona. Ponderado por fuerza y recencia de la señal.',
    mktTotalCompetitorSignals: 'Total de señales de competidores',
    mktTotalFundingEligibility: 'Elegibilidad total de financiación',
    mktTotalFundingEligibilityHint: 'Lo que esta institución podría solicitar en todas las convocatorias abiertas para las que es elegible. Es un máximo, no una previsión: la elegibilidad es un registro verificado y no tenemos evidencia de que alguna haya postulado.',
    mktTotalMarket: 'Mercado total',
    mktTrajectory24m: 'Trayectoria (24 m)',
    mktTrajectoryTooltip: 'Puntuación de proximidad recalculada mes a mes durante los últimos 24 meses. Barras que suben = llegan señales nuevas. Barras que bajan = la evidencia envejece.',
    mktTriggers: 'Desencadenantes',
    mktUnfavourable: 'Desfavorable',
    mktUnfavourableBlockers: 'Bloqueos desfavorables de competidores',
    mktUnknownVendor: 'Proveedor desconocido',
    mktUpcomingWindows: 'Próximas ventanas',
    mktUpdate: 'Actualización',
    mktUpdatingCompetitorSet: 'Actualizando competidores y filtros…',
    mktUseTeamSettings: 'Usar la configuración del equipo',
    mktUserSelected: 'Seleccionado por el usuario',
    mktUsingCachedDataRetryIn: 'Usando datos en caché. Se reintentará en {time}.',
    mktUsingTeamSettings: 'Usando la configuración del equipo',
    mktValueWholeContract: 'El valor mostrado corresponde a todo el contrato, no a un año. Equivale a {annual} al año.',
    mktValueWholeContractOverYears: 'El valor mostrado corresponde a todo el contrato, no a un año. Equivale a {annual} al año durante {years} años.',
    mktWhatChanged: 'Qué cambió',
    mktWhatItIsWorth: 'Cuánto vale',
    mktWhatThisMeans: 'Qué significa',
    mktWhatThisMeansFundingHint: 'El mismo tono que el resumen de la vista general. Ser elegible no es tener pipeline.',
    mktWhoIsInPlace: 'Quién está presente',
    mktWhoMightILose: 'A quién podría perder',
    mktWholeMarket: 'Todo el mercado',
    mktWhy: 'Por qué',
    mktWhyNow: 'Por qué ahora',
    mktWhyTheyQualify: 'Por qué califican',
    mktWhyThisScore: 'Por qué esta puntuación',
    mktWindowClosed: 'plazo cerrado',
    mktNoFixedDeadline: 'sin plazo fijo',
    mktStandingProgramme: 'programa permanente',
    mktDateNotPublished: 'fecha aún no publicada',
    mktClosedOn: 'cerrado el {date}',
    mktNoDeadlinePublished: 'plazo no publicado',
    mktWonBefore: 'Ya obtenida antes',
    mktWorkingAgainst: 'En contra',
    mktWorkingInFavour: 'A favor',
    mktWorthALook: 'Vale la pena revisar',
    mktYes: 'Sí',
    mktYourPilotIsFull: 'Tu piloto está completo',
    procToastDataIncomplete: 'Es posible que algunos datos estén incompletos',
    procToastTimeoutPage: 'El servidor agotó el tiempo de espera en una página de resultados. Se muestran datos parciales.',
    procToastTimeoutRecords: 'El servidor agotó el tiempo de espera al cargar registros adicionales. Se muestran resultados parciales.',
    procToastLoadingDeals: 'Cargando oportunidades',
    procToastFetchingInitialData: 'Obteniendo datos iniciales',
    procToastLoadingMoreDeals: 'Cargando más oportunidades',
    procToastFetchingLiveRadarDeals: 'Obteniendo oportunidades del Radar en vivo',
    procToastFetchedLiveDeals: '{n} oportunidades en vivo obtenidas…',
    procToastFailedLiveDeals: 'No se pudieron cargar las oportunidades en vivo',
    procLoadingRestOfMarket: 'Cargando el resto del mercado…',
    procToastFetchingLabelDeals: 'Obteniendo oportunidades de {label}…',
    procToastProcessingDealsLoaded: 'Procesando… {n} oportunidades cargadas',
    procToastFailedLabelDeals: 'No se pudieron cargar las oportunidades de {label}',
    procToastLabelReady: '{label} listo',
    procToastFetchedVisible: '{fetched} obtenidas, {visible} visibles con los filtros activos.',
    procToastStatusClearFailed: 'No se pudo borrar el estado',
    procToastStatusUpdateFailed: 'No se pudo actualizar el estado',
    procToastValueOverrideFailed: 'No se pudo sobrescribir el valor',
    procRetired: 'Retirado',
    procDealActivityUnavailable: 'Actividad de oportunidades no disponible: las insignias de estado y de visualización pueden estar incompletas. Intenta actualizar.',
    procMapNoDeals: 'Sin oportunidades',
    procMapTotalValueUsd: 'Valor total (USD)',
    procMapTop: 'Principales',
    procMapMetric: 'Métrica del mapa:',
    procMapTotalValue: 'Valor total',
    procMapCountries: 'Países',
    procMapClearSelection: 'Borrar selección',
    procRtbRouteFramework: 'marco',
    procRtbRouteConsortium: 'consorcio',
    procRtbRouteCooperative: 'cooperativa',
    procRtbRouteBuyingOrg: 'central de compras',
    procRtbRouteProgramme: 'programa',
    procRtbThroughFramework: 'A través de un acuerdo marco',
    procRtbThroughReseller: 'A través de un revendedor',
    procRtbDirectToSupplier: 'Directo al proveedor',
    procAllCategories: 'Todas las categorías',
    procYourCategories: 'Tus categorías',
    procRtbSelectCategoriesForLens: 'Selecciona categorías para usar este filtro',
    procFailedToLoad: 'No se pudo cargar',
    procRtbSubtitle: 'Cómo compra realmente este mercado: los instrumentos de compra y quién vende a través de ellos.',
    procRtbCoverageNote: 'Incluye a todos los compradores públicos del mercado, en todos los sectores, no solo la educación superior.',
    procRtbLoadingCountry: 'Cargando {country}…',
    procRtbMoreCountries: 'Otros países',
    procRtbMoreCountriesOption: 'Otros países…',
    procRtbHowCountryBuys: 'Cómo compra {country}',
    procLoading: 'Cargando…',
    procUpdating: 'Actualizando…',
    procRtbVehiclesThatMatter: 'Instrumentos de compra clave',
    procRtbNoFrameworkMine: 'Ningún acuerdo marco identificado cubre tus categorías en {country}.',
    procRtbNoFrameworkAll: 'Ningún acuerdo marco identificado cubre estas oportunidades en {country}.',
    procRtbDealsLower: 'oportunidades',
    procRtbBuyersLower: 'compradores',
    procRtbSuppliersLower: 'proveedores',
    procRtbMakersLower: 'fabricantes',
    procRtbValueLower: 'valor',
    procRtbRenewIn12Mo: 'renuevan en 12 meses',
    procRtbInsideVehicle: 'Dentro del instrumento',
    procRtbWhoSellsThroughIt: 'Quién vende a través de él (oportunidades)',
    procRtbResellersOnIt: 'Revendedores en él (oportunidades)',
    procRtbNoResellerOnVehicle: 'No hay revendedores registrados en este instrumento',
    procRtbAreYouOnIt: '¿Estás incluido?',
    procRtbYesSoldThroughIt: 'Sí: tu producto se vende a través de él',
    procRtbNotOnRecord: 'Sin registro',
    procRtbNotConfigured: 'No configurado para este cliente',
    procRtbResellersIn: 'Revendedores en {country}',
    procRtbResellerShare: 'El {n}% de las oportunidades con ruta conocida pasan por uno',
    procRtbNoResellerMine: 'Ningún revendedor vende en tus categorías en {country}: todas las oportunidades registradas fueron directas al proveedor.',
    procRtbNoResellerAll: 'Ningún revendedor vende aquí en {country}: todas las oportunidades registradas fueron directas al proveedor.',
    procRtbNotStated: 'no indicado',
    procRtbInsideReseller: 'Dentro del revendedor',
    procRtbMakersItCarries: 'Fabricantes que distribuye (oportunidades)',
    procRtbRecordNamesOnlyReseller: 'El registro solo nombra al revendedor, nunca al fabricante',
    procRtbMakerNamedPct: 'Fabricante indicado en el {n}% de sus oportunidades; el resto solo registra al revendedor',
    procRtbSellsThrough: 'Vende a través de',
    procRtbDirectToBuyer: 'Directo al comprador: sin instrumento registrado',
    procRtbCarriesYourProduct: '¿Distribuye tu producto?',
    procYes: 'Sí',
    procRtbUpTo: 'hasta {amount}',
    procRtbAbove: 'más de {amount}',
    procRtbHowBigBeforeTender: 'A partir de qué importe se licita',
    procRtbTypicalAcross: 'Habitual entre {n} compradores',
    procRtbMostRange: 'la mayoría {from}–{to}',
    procRtbNationalRule: 'Norma nacional',
    procMonths24Plus: '24+ meses',
    procVendorsForSelectedCategories: 'Proveedores de las categorías seleccionadas',
    procUpdatingResults: 'Actualizando resultados',
    procClickHeadersToSort: 'Haz clic en los encabezados de columna para ordenar',
    procFilterAppliesTableOnly: '— este filtro se aplica solo a esta tabla; el mapa y los totales de arriba no se filtran.',
    procBundledContract: 'Contrato combinado',
    procNoRationaleProvided: 'No se proporcionó justificación.',
    procShowingRangeDeals: 'Mostrando {from}–{to} de {total} oportunidades',
    procShowingRangeAccounts: 'Mostrando {from}–{to} de {total} cuentas',
    procMatchingDealsOnePerAccount: '{n} oportunidades coincidentes; la tabla muestra la oportunidad principal de cada cuenta',
    procMapNoClosedDeals: 'Sin oportunidades cerradas',
    procTopVendorsByDeals: 'Principales proveedores por oportunidades',
    procTopVendorsByValue: 'Principales proveedores por valor',
    procClear: 'Borrar',
    procTop3DealsByValue: 'Las 3 principales oportunidades por valor',
    procMarketShare: 'Cuota de mercado',
    procPieAccountsPct: '{n} cuentas ({pct}%)',
    procMarketShareByVendor: 'Cuota de mercado por proveedor',
    procNoIncumbentDataCountry: 'No hay datos de proveedores actuales para este país.',
    procClickCountryForMarketShare: 'Haz clic en un país del mapa para ver la cuota de mercado.',
    procWin0_6Mo: '0–6 meses',
    procWin6_12Mo: '6–12 meses',
    procWin12_24Mo: '12–24 meses',
    procWin24PlusMo: '24+ meses',
    procWinNoEndDate: 'Sin fecha de fin',
    procHowLabelIsMeasured: 'Cómo se mide: {label}',
    procMoveFrom: 'de',
    procMoveTo: 'a',
    procPlusNMore: '+{n} más',
    procCouldNotLoadChanges: 'No se pudieron cargar los cambios.',
    procWon: 'Ganadas',
    procLost: 'Perdidas',
    procNoRecordedChanges: 'No hay cambios registrados.',
    procWonLostCounts: 'ganadas {won} · perdidas {lost}',
    procEstablishedRelationships: '{n} relaciones consolidadas',
    procLoadingCategoryIntel: 'Cargando inteligencia de categoría…',
    procNoMeasuredHistory: 'Aún no hay historial medido para las categorías seleccionadas.',
    procRcViewReplacement: 'Ciclo de reemplazo',
    procRcViewExpiry: 'Vencimiento del contrato',
    procRcIntro: 'Equipos instalados por antigüedad, del más antiguo al más reciente. La antigüedad cuenta desde la última compra del sistema; los contratos de mantenimiento y los consumibles no la reinician. Un equipo suele reemplazarse a los 5–7 años, mucho después de que termine su contrato.',
    procRcKpiInstalled: 'Equipos instalados',
    procRcKpiInstalledSub: 'de {n} contratos adjudicados',
    procRcKpiWindow: 'En ventana de reemplazo',
    procRcKpiWindowSub: '{n}+ años desde la compra',
    procRcKpiDue: 'Por reemplazar o vencidos',
    procRcKpiDueSub: '{n}+ años desde la compra',
    procRcKpiOwn: 'Su parque envejeciendo',
    procRcKpiOwnSub: 'sus instalaciones con {n}+ años',
    procRcWindowFilter: 'Ventana de reemplazo:',
    procRcInstalled: 'Instalado',
    procRcAge: 'Antigüedad',
    procRcWindow: 'Ventana de reemplazo',
    procRcServiceEnds: 'Fin del contrato de mantenimiento',
    procRcYourKit: 'Su equipo',
    procRcVia: 'vía {reseller}',
    procRcPurchases: '{n} compras del sistema',
    procRcFloorHelp: 'No se publica ninguna compra del sistema; fechado desde el registro de mantenimiento o suministro más antiguo, así que el equipo tiene al menos esta antigüedad.',
    procRcYears: '{n} años',
    procRcShowing: 'Mostrando {shown} de {total} equipos instalados',
    procRcBandOverdue: 'Vencido',
    procRcBandDue: 'Por reemplazar',
    procRcBandPlan: 'Planificar',
    procRcBandMid: 'Vida media',
    procRcBandNew: 'Nuevo',
    procRcBandUndated: 'Sin fecha',
    procRcRangeOverdue: '12+ años',
    procRcRangeDue: '7–12 años',
    procRcRangePlan: '5–7 años',
    procRcRangeMid: '3–5 años',
    procRcRangeNew: 'menos de 3 años',
    procRcRangeUndated: 'sin fecha',
    procRcRangeYears: '{a}–{b} años',
    procRcRangeOver: '{a}+ años',
    procRcRangeUnder: 'menos de {a} años',
    procRcSincePurchase: '{n}+ años desde la compra',
    procRcOwnYearsOld: 'sus instalaciones con {n}+ años',
    procRcRadarSubtitle: 'Qué antigüedad tiene el parque instalado, quién tiene los equipos envejecidos y qué hicieron las instituciones la última vez que los reemplazaron.',
    procRcKpiDated: '{n} con fecha de compra',
    procRcUpCompetitor: 'Parque de la competencia por reemplazar',
    procRcUpCompetitorSub: 'instalaciones de la competencia con {n}+ años',
    procRcUpOwn: 'Su parque por renovar',
    procRcUpOwnSub: 'sus instalaciones con {n}+ años',
    procRcSwitchRate: 'Cambio de fabricante al reemplazar',
    procRcSwitchTooFew: '{n} reemplazos observados; una tasa requiere {min}',
    procRcSwitchSub: '{switched} cambiaron · {retained} mantuvieron el fabricante',
    procRcMatrixTitle: 'Parque instalado por antigüedad y sentimiento',
    procRcMatrixHelp: 'Cada celda cuenta equipos instalados. La antigüedad cuenta desde la última compra del sistema.',
    procRcTotal: 'Total',
    procRcSuppliersTitle: 'Quién tiene los equipos envejecidos',
    procRcSuppliersHelp: 'Instalaciones por fabricante; la parte ámbar tiene {n}+ años.',
    procRcAgeingOf: '{ageing} de {n} envejecidos',
    procRcMovesTitle: 'Cambios de fabricante al reemplazar',
    procRcMovesHelp: 'Una recompra cuenta como reemplazo solo si llega {n}+ años después de la anterior. {events} observados.',
    procRcNoMoves: 'Aún no hay cambios de fabricante registrados al reemplazar.',
    procRcInstallsAcross: '{installs} equipos instalados en {accounts} instituciones, {scope}.',
    procRcSwitchHelp: 'Cada vez que una institución vuelve a comprar el mismo tipo de sistema al menos {n} años después de su última compra, NEO lo cuenta como un reemplazo: mantuvo el fabricante o cambió. Las compras más cercanas se tratan como equipo adicional, no como reemplazo.',
    procRcMakersPerAccount: 'Fabricantes por cuenta',
    procRcAgeHelp: 'La antigüedad cuenta desde la última compra del sistema. Los contratos de mantenimiento y los consumibles no la reinician. Este tipo de equipo suele reemplazarse a los {a}–{b} años.',
    procRcPipelineTitle: 'Pipeline de reemplazo',
    procRcPipelineHint: 'Equipos instalados por años desde la compra',
    procRcPipelineNoteBefore: 'Parque de la competencia en ventana de reemplazo:',
    procRcPipelineNoteAfter: 'instalaciones tienen {n}+ años.',
    procRcNetHint: 'Cambios de fabricante al reemplazar: ganados menos perdidos',
    procRcAgeingByMaker: 'Parque envejecido por fabricante',
    procRcAgeingByCountry: 'Parque envejecido por país',
    procRcAgeingByType: 'Parque envejecido por tipo de institución',
    procRcAgeingHint: 'Proporción de instalaciones con {n}+ años',
    procRcAgeingEmpty: 'Aún hay pocas instalaciones para desglosar.',
    procRcOfInstalls: 'de {n} instalaciones',
    procRcGridTitle: 'Posición del titular por ventana de reemplazo',
    procDisplacementSubtitle: 'Con qué frecuencia los proveedores actuales pierden realmente esta categoría y qué contratos se reabren a continuación.',
    procAwardsAcrossInstitutions: '{deals} adjudicaciones en {scope} y {accounts} instituciones.',
    procNMarkets: '{n} mercados',
    procLastUpdatedDate: 'Última actualización: {date}',
    procGlobal: 'Global',
    procHighlights: 'Aspectos destacados',
    procAnnualChurn: 'Rotación anual',
    procChurnSubSplit: 'de las relaciones con proveedores: {switched}% cambió de proveedor, {stopped}% dejó de comprar la categoría',
    procChurnSubPerYear: 'de las relaciones con proveedores que terminan cada año',
    procChurnHelpIntro: 'Solo contamos los proveedores a los que una institución compró al menos dos veces.',
    procChurnHelpSwitchedLabel: 'Cambió de proveedor:',
    procChurnHelpSwitchedBody: 'La institución siguió comprando en la categoría pero cambió de proveedor: una posible oportunidad.',
    procChurnHelpStoppedLabel: 'Dejó de comprar:',
    procChurnHelpStoppedBody: 'La institución dejó de comprar en la categoría. Ningún proveedor ganó; considéralo un límite superior, ya que las renovaciones no publicadas se ven igual.',
    procChurnHelpAnnualLabel: 'Tasa anual:',
    procChurnHelpAnnualBody: 'Promedio de todos los años disponibles, no interanual.',
    procChurnHelpMultiLabel: 'Cuentas con varios proveedores:',
    procChurnHelpMultiBody: 'Perder un proveedor puede significar perder un puesto en un panel, no el cliente completo.',
    procSuppliersPerAccount: 'Proveedores por cuenta',
    procPctBuyersUseOne: 'El {n}% de los compradores usa solo uno',
    procRebuyCycle: 'Ciclo de recompra',
    procUnitMo: 'meses',
    procMedianGapBetweenAwards: 'intervalo mediano entre adjudicaciones',
    procReopeningWithin12Mo: 'Se reabren en 12 meses',
    procLiveContractsAcrossMarkets: 'contratos vigentes en {n} mercados',
    procValueInNext12Mo: 'Valor en los próximos 12 meses',
    procFromNExpiringContracts: 'de {n} contratos por vencer',
    procRenewalPipeline: 'Cartera de renovaciones',
    procLiveContractsByTimeToExpiry: 'contratos vigentes por tiempo hasta el vencimiento',
    procPipelineNoteBefore: '{n} contratos se reabren en los próximos seis meses. Con una rotación anual del {churn}%, aproximadamente',
    procPipelineNoteAfter: 'de estos proveedores actuales perderán realmente la cuenta; el resto será readjudicado o prorrogado sin anuncio.',
    procNetPositionChange: 'Cambio neto de posición',
    procAccountsWonMinusLost: 'cuentas ganadas menos perdidas',
    procTooFewRepeatAwards: 'Hay muy pocas adjudicaciones repetidas en este mercado para medir el movimiento neto.',
    procNetWinners: 'Ganadores netos',
    procNetLosers: 'Perdedores netos',
    procNetPositionNote: 'Posiciones ganadas menos posiciones perdidas en todos los cambios de proveedor registrados. Los perdedores son las oportunidades: un proveedor actual que pierde cuentas una a una.',
    procChurnByCountry: 'Rotación por país',
    procTopMarketsByVolume: 'principales mercados por volumen',
    procChurnByCountryEmpty: 'No hay suficiente historial repetido en ningún mercado para calcular una tasa fiable.',
    procChurnByInstitutionType: 'Rotación por tipo de institución',
    procAnnualEstablishedRelationships: 'anual, relaciones consolidadas',
    procChurnByInstitutionEmpty: 'El tipo de institución no está registrado en suficientes cuentas de esta categoría.',
    procIncumbentByDecisionWindow: 'Situación del proveedor actual, por ventana de decisión',
    procIncumbent: 'Proveedor actual',
    procAccountLower: 'cuenta',
    procAccountsLower: 'cuentas',
    procSentimentIncumbent: 'Proveedor actual: {sentiment}',
    procNoContractEndDate: 'sin fecha de fin de contrato registrada',
    procContractEndsIn: 'el contrato termina en {window}',
    procAmountTotal: '{amount} en total',
    procLoadingAccounts: 'Cargando cuentas…',
    procNoAccountsForCell: 'No hay cuentas registradas para esta celda.',
    procContractEnd: 'Fin del contrato',
    procContractValue: 'Valor del contrato',
    procWhy: 'Motivo',
    procDealCount: 'Número de oportunidades',
    procNegativeSharePct: 'Proporción negativa (%)',
    procNoDisplacementData: 'No se encontraron datos de desplazamiento con los filtros actuales.',
    procTryAdjustingFilters: 'Prueba a ajustar los filtros de categoría o país.',
    procCategoryRiskMosaic: 'Mosaico de riesgo por categoría',
    procCategoryRiskMosaicHelp: 'Riesgo de rotación por categoría y tramo de vencimiento. Haz clic en un segmento o celda para ver las cuentas.',
    procMetric: 'Métrica',
    procBucketMonths: '{b} meses',
    procAtRiskValueShare: 'En riesgo: {value} ({pct}%)',
    procDealLower: 'oportunidad',
    procPctOfCell: '{pct}% de la celda',
    procShowTop15: 'Mostrar las 15 principales',
    procShowAllCategories: 'Mostrar todas las categorías',
    procRationaleHighlights: 'Justificaciones destacadas',
    procSearchRationale: 'Buscar en la justificación...',
    procNoAccountsMatch: 'Ninguna cuenta coincide.',
    procLess: 'Menos',
    procReadMore: 'Leer más',
    procYearLabel: 'Año: {year}',
    procYou: 'tú',
    procEnrolment: 'Matrícula',
    procUsdPerStudentYear: '$/estudiante/año',
    procOverTermWholeContract: 'en {months} meses · {amount}/estudiante por todo el contrato',
    procBenchEmptyIntro: 'No hay datos de referencia para los filtros actuales. Las oportunidades necesitan',
    procBenchFieldEnrolment: 'matrícula',
    procBenchFieldDealValue: 'valor de la oportunidad (USD)',
    procBenchFieldSupplier: 'proveedor',
    procBenchAndANumeric: 'y un',
    procBenchFieldInitialTerm: 'plazo inicial numérico',
    procBenchEmptyOutro: 'para poder expresar el precio por año.',
    procBenchExcludedNoTermOne: '{n} oportunidad coincide con los filtros pero no tiene plazo de contrato, por lo que no se puede anualizar.',
    procBenchExcludedNoTermMany: '{n} oportunidades coinciden con los filtros pero no tienen plazo de contrato, por lo que no se pueden anualizar.',
    procBenchTitle: 'Matrícula vs $ por estudiante al año',
    procBenchTitleMedian: 'Mediana de $ por estudiante al año',
    procBenchAnnualised: 'Anualizado según el plazo del contrato.',
    procTrendMedianNote: 'Cada línea es la mediana anual, así que una oportunidad atípica no la mueve.',
    procTrendShortTermNote: 'Se excluyen los contratos de menos de 4 meses: su precio no puede leerse como anual.',
    procVendorVsCompetitors: '{vendor} frente a la competencia.',
    procScatter: 'Dispersión',
    procTrendByCategory: 'Tendencia por categoría',
    procTrendByVendor: 'Tendencia por proveedor',
    procTrendSuppliers: 'Proveedores',
    procTrendRuleAll: 'Todos los proveedores',
    procTrendRuleExclude: 'Excluir seleccionados',
    procTrendRuleOnly: 'Solo seleccionados',
    procTrendRuleSearch: 'Buscar proveedores',
    procTrendRuleSummaryExclude: 'Excluyendo {n}',
    procTrendRuleSummaryOnly: 'Solo {n}',
    procTrendRuleClear: 'Borrar',
    procTrendRuleDeals: '{n} contratos',
    procTrendRuleHint: 'Solo se aplica a Tendencia por categoría. Se guarda para esta selección de categorías.',
    procTrendLegendHint: 'Haga clic en un proveedor de la leyenda para resaltar su línea.',
    procTrendLegendClear: 'Mostrar todas las líneas',
    procResetView: 'Restablecer vista',
    procFxFootnote: '* Se asume un tipo de cambio fijo a USD para todas las monedas.',
    procWeakFit: 'Ajuste débil',
    procNoFit: 'Sin ajuste',
    procLowEvidence: 'Poca evidencia',
    procFitOutOfScopeTitle: 'La categoría de la oportunidad no coincide con ningún perfil de producto activo',
    procFitColumnTitle: 'Fuera de alcance = la categoría de la oportunidad no coincide con ningún perfil de producto activo',
    acctRemovedFromListTitle: 'Se eliminó {name} de tu lista',
    acctAccountLower: 'cuenta',
    acctRemovedFromListDesc: 'Puedes volver a agregarlo en cualquier momento desde Mercado.',
    acctCouldNotRemoveAccount: 'No se pudo eliminar la cuenta',
    acctPleaseTryAgainNoDot: 'Inténtalo de nuevo',
    acctSaved: 'Guardado',
    acctTargetProductUpdated: 'Producto objetivo actualizado',
    acctCouldNotSaveTargetProduct: 'No se pudo guardar el producto objetivo',
    acctTabContent: 'Contenido',
    acctTabProducts: 'Productos',
    acctTabFinancialsAssets: 'Finanzas y activos',
    acctTabSalesMap: 'Mapa de ventas',
    acctTabCompetitorsCount: 'Competidores ({n})',
    acctEnrichmentUnavailable: 'Enriquecimiento no disponible',
    acctMarketEnrichmentNotEnabled: 'El enriquecimiento de mercado no está habilitado para este tenant.',
    acctCouldNotRequestEnrichment: 'No se pudo solicitar el enriquecimiento',
    acctMarketLensNotConfigured: 'La vista de mercado aún no está configurada.',
    acctEnrichmentAlreadyRequested: 'Enriquecimiento ya solicitado',
    acctEnrichmentRequested: 'Enriquecimiento solicitado',
    acctTryAgainLater: 'Inténtalo más tarde',
    acctRequesting: 'Solicitando…',
    acctRequestPersonaEnrichment: 'Solicitar enriquecimiento de personas',
    acctMeddpiccIdentifyPain: 'Identificar el dolor',
    acctMeddpiccMetrics: 'Métricas',
    acctMeddpiccPainTriggerTiming: 'Detonante · Momento',
    acctMeddpiccCompetitionIncumbent: 'Competencia / Proveedor actual',
    acctMeddpiccEconomicBuyer: 'Comprador económico',
    acctMeddpiccDecisionProcess: 'Proceso de decisión',
    acctMeddpiccPaperProcess: 'Proceso administrativo',
    acctMeddpiccChampion: 'Champion',
    acctMeddpiccTipPainNeed: '¿Qué problema los lleva a querer cambiar?',
    acctMeddpiccTipImpactScale: 'Escala e impacto cuantificados: solo cifras.',
    acctMeddpiccTipWhyNow: '¿Por qué ahora? ¿Qué detonante fechado impulsa la ventana?',
    acctMeddpiccTipCurrentVendor: 'A quién usan hoy y qué opinan al respecto.',
    acctMeddpiccTipBuyerOwner: 'Quién puede financiar y aprobar.',
    acctMeddpiccTipDecisionProcess: 'Quién aprueba, en qué orden y a través de qué comités.',
    acctMeddpiccTipPaperProcess: 'Vía de compra, acuerdo marco, renovación y requisitos de contratación.',
    acctMeddpiccTipChampion: 'Defensor interno: responsable operativo con un beneficio personal.',
    acctSalesPointPainNeed: 'Dolor / Necesidad',
    acctSalesPointImpactScale: 'Impacto / Escala',
    acctSalesPointWhyNow: 'Por qué ahora / Momento',
    acctSalesPointCurrentVendor: 'Proveedor actual / Competencia',
    acctSalesPointBuyerOwner: 'Comprador / Responsable',
    acctSalesPointDecisionProcess: 'Proceso de decisión',
    acctSalesPointPaperProcess: 'Compras / Proceso administrativo',
    acctSalesPointChampion: 'Champion / Persona clave',
    acctSalesPointRecommendedNextMove: 'Siguiente paso recomendado',
    acctStrengthTipMissing: 'Aún no hay evidencia útil registrada para este punto.',
    acctStrengthTipVeryLow: 'Genérica o vaga: sin vínculo claro con esta cuenta.',
    acctStrengthTipLow: 'Alguna señal relevante, pero mayormente inferida.',
    acctStrengthTipMedium: 'Respaldada por evidencia, pero faltan detalles clave.',
    acctStrengthTipHigh: 'Evidencia reciente, específica y alineada con la categoría.',
    acctStrengthTipVeryHigh: 'Fuertemente respaldada y validada (poco común en V1).',
    acctStrengthMissing: 'Sin datos',
    acctStrengthVeryLow: 'Muy bajo',
    acctStrengthVeryHigh: 'Muy alto',
    acctKeepDerived: 'Mantener el calculado',
    acctEdited: 'Editado',
    acctAutoDetected: 'Detectado automáticamente',
    acctAutoDetectedTooltip: 'Marcado automáticamente por el módulo Competitor Intel. Las ediciones del representante reemplazarán esta justificación.',
    acctShowLessLower: 'ver menos',
    acctShowMoreLower: 'ver más',
    acctSourcesCount: 'Fuentes ({n})',
    acctRepLower: 'representante',
    acctTeamNoteFrom: 'Nota del equipo de {author}',
    acctClearOverride: 'Quitar ajuste manual',
    acctRationale: 'Justificación',
    acctRationalePlaceholder: '¿Por qué sabemos esto? P. ej.: \'Confirmado en una llamada con X el Y; dijeron Z\'',
    acctOverrideScoreOptional: 'Ajustar puntuación (opcional)',
    acctRationaleRequired: 'Agrega una justificación antes de guardar.',
    acctSalesMapUpdated: 'Mapa de ventas actualizado',
    acctCouldNotSaveEdit: 'No se pudo guardar tu edición',
    acctOverrideCleared: 'Ajuste manual eliminado',
    acctCouldNotClearOverride: 'No se pudo quitar el ajuste manual',
    acctGetAdvice: 'Pedir consejo',
    acctCouldNotLoadSalesMap: 'No se pudo cargar el mapa de ventas',
    acctCouldNotRefreshSalesMap: 'No se pudo actualizar el mapa de ventas; se muestran los datos anteriores.',
    acctSalesMapTitle: 'Mapa de ventas — {account}',
    acctGeneratedAt: 'Generado el {date}',
    acctPrepareForMeeting: 'Preparar reunión',
    acctSuggestNextMove: 'Sugerir siguiente paso',
    acctSalesMapSparseBanner: 'Esta cuenta aún tiene poca evidencia registrada. Faltan la mayoría de las filas, pero el bot sigue teniendo datos del contexto de mercado del país si haces clic en Pedir consejo.',
    acctNbmTarget: 'Objetivo',
    acctNbmSuggestedRole: 'Rol sugerido a contactar',
    acctNbmNoNamedContactTypically: 'No hay un contacto identificado; normalmente: {examples}',
    acctNbmNoNamedContact: 'No hay un contacto identificado.',
    acctNbmFindOneFirst: 'Encuentra uno antes de contactar. Abre la pestaña Personas.',
    acctNextBestMove: 'Siguiente mejor paso',
    acctNbmAskNext: 'Pregunta en tu próxima conversación',
    acctNeoRecommendation: 'Recomendación de NEO',
    acctProfileOutreach: 'Contacto desde el perfil',
    acctDiyPlaybook: 'Playbook DIY',
    acctPlaybookTrackingSoon: 'Seguimiento de Playbooks próximamente',
    acctPlaybookTrackingSoonDesc: 'El seguimiento de la interacción con los Playbooks estará disponible cuando se despliegue la API del backend.',
    acctNoPlaybooksOrOutreach: 'Sin Playbooks ni contactos',
    acctNoPlaybooksOrOutreachDesc: 'Esta cuenta no está inscrita en ningún Playbook y no tiene historial de contactos.',
    acctEnrolledPersonasOne: 'Inscritas: {n} persona',
    acctEnrolledPersonasMany: 'Inscritas: {n} personas',
    acctCompletedOn: 'Completado: {date}',
    acctStartedOn: 'Iniciado: {date}',
    acctEnrolledPersonasLabel: 'Personas inscritas:',
    acctTouchOf: 'Contacto {current} de {total}',
    acctNextOn: 'Siguiente: {date}',
    acctLoadingAdHocOutreach: 'Cargando contactos ad hoc…',
    acctActivePlaybooks: 'Playbooks activos',
    acctCompletedPlaybooks: 'Playbooks completados',
    acctScheduledPending: 'Programados: pendientes',
    acctScheduledFor: 'Programado para el {date}',
    acctAdHocMessages: 'Mensajes ad hoc',
    acctMsgCountOne: '{n} mensaje',
    acctMsgCountMany: '{n} mensajes',
    acctManualCompletion: 'Completado manualmente',
    acctNotesLoadErrorContext: 'No se pudieron cargar las notas en este contexto. Intenta actualizar la página.',
    acctNotesLoadErrorGeneric: 'No se pudieron cargar las notas. Inténtalo de nuevo.',
    acctCouldNotSaveThisNote: 'No se pudo guardar esta nota.',
    acctFailedToSaveNote: 'Error al guardar la nota.',
    acctCannotDeleteNote: 'No puedes eliminar esta nota.',
    acctFailedToDeleteNote: 'Error al eliminar la nota.',
    acctNoteAuthorYou: 'Tú',
    acctNoteAuthorTeam: 'Equipo',
    acctNoteAuthorTeammate: 'Compañero de equipo',
    acctLoadingNotes: 'Cargando notas...',
    acctAddNote: 'Agregar nota',
    acctWriteNotePlaceholder: 'Escribe tu nota aquí...',
    acctSaveNote: 'Guardar nota',
    acctNoNotesYet: 'Aún no hay notas. Agrega tu primera nota arriba.',
    acctDeleteNote: 'Eliminar nota',
    acctCouldNotLoadCompetitorData: 'No se pudieron cargar los datos de competidores. Inténtalo de nuevo.',
    acctCompetitorsAtThisAccount: 'Competidores en esta cuenta',
    acctSignalTimeline: 'Cronología de señales',
    acctCompetitorsLinkedToPersona: 'Competidores vinculados a esta persona',
    acctCompSigAccountReference: 'Cuenta mencionada en contenido de la competencia',
    acctCompSigAccountAdoption: 'Adopción de la competencia en la cuenta',
    acctCompSigPartnerEcosystem: 'Ecosistema de socios de la competencia',
    acctCompSigProcurementSignal: 'Señal de compras',
    acctCompSigEventOverlap: 'Coincidencia en eventos',
    acctCompSigDisplacementOpportunity: 'Oportunidad de desplazamiento',
    acctCompSigWeaknessSignal: 'Debilidad de la competencia',
    acctCompSigPersonaInteraction: 'Persona contactada por la competencia',
    acctCompSigPersonaReference: 'Persona mencionada en contenido de la competencia',
    acctCompSigPersonaAdoption: 'Persona vinculada a una herramienta de la competencia',
    acctCompSigGeneric: 'Señal de la competencia',
    sigUpdEcosystemSignal: 'Señal del ecosistema',
    sigUpdMarketEvent: 'Evento de mercado',
    sigUpdRegionalEvent: 'Evento regional',
    sigUpdProductRelease: 'Lanzamiento de producto',
    sigUpdProductLaunch: 'Presentación de producto',
    sigUpdPressRelease: 'Comunicado de prensa',
    sigUpdPartnershipAnnouncement: 'Anuncio de alianza',
    sigUpdInvestorNews: 'Noticias para inversores',
    sigUpdWebinarEvent: 'Webinar',
    sigUpdVendorMetricClaim: 'Cifra declarada por el proveedor',
    sigUpdExternalAccountAdoption: 'Adopción en cuenta externa',
    sigUpdExternalReferenceAccount: 'Cuenta de referencia externa',
    sigUpdMarketCommentary: 'Comentario de mercado',
    sigUpdConferenceRecap: 'Resumen de conferencia',
    acctImpactUnfavourable: 'Desfavorable',
    acctImpactFavourable: 'Favorable',
    acctImpactMixed: 'Mixto',
    acctImpactContextOnly: 'Solo contexto',
    acctProximityElevated: 'Elevado',
    acctProximityWatch: 'Vigilar',
    acctProximityNoEvidence: 'Sin evidencia',
    acctJustNow: 'justo ahora',
    acctMoreInfo: 'Más información',
    acctProximityScoreTooltip: 'De 0 a 100. Sube cuando llegan nuevas señales de la competencia a esta cuenta/persona. Baja de forma natural a medida que la evidencia envejece.',
    acctImpactTooltip: 'Dirección de la evidencia sobre la competencia. Desfavorable = malo para ti (competidor afianzado). Favorable = bueno para ti (insatisfacción / oportunidad de reemplazo). Mixto = hay señales de ambos tipos. Solo contexto = informativo.',
    acctLabelTooltip: 'Etiqueta del rango de puntuación: 0 = Sin evidencia, 1-24 = Bajo, 25-49 = Vigilar, 50-74 = Elevado, 75-100 = Alto.',
    acctTopCompetitorTooltip: 'Competidor con la mayor contribución ponderada de señales en esta cuenta/persona.',
    acctLatestSignalTooltip: 'Fecha de la señal de la competencia más reciente.',
    acctTrajectoryTooltip: 'Puntuación de proximidad recalculada mes a mes durante los últimos 24 meses. Barras que suben = llegan señales nuevas. Barras que bajan = la evidencia envejece.',
    acctFlatTrajectoryTooltip: 'Estable en {n} durante los últimos 24 meses: la evidencia tiene más de 1 año.',
    acctSourceBadgeAccount: 'Señal de cuenta',
    acctSourceBadgeAccountTooltip: 'Vinculada a una de tus cuentas objetivo. Contribuye al barómetro de cuentas.',
    acctSourceBadgePersona: 'Señal de persona',
    acctSourceBadgePersonaTooltip: 'Vinculada a una de tus personas objetivo. Contribuye al barómetro de personas.',
    acctSourceBadgeMarket: 'Inteligencia de mercado: no está en una cuenta objetivo',
    acctSourceBadgeMarketTooltip: 'Actividad de la competencia en tu mercado que no ha llegado a tus cuentas objetivo. NO contribuye a ningún barómetro.',
    acctCardHeaderTooltipAccount: 'Qué tan expuesta está esta cuenta a la competencia. Se calcula con la fuerza de la señal × la pérdida por antigüedad. Mayor puntuación = mayor presencia de la competencia.',
    acctCardHeaderTooltipPersona: 'Qué tan expuesta está esta persona a la competencia. Se calcula con la fuerza de la señal × la pérdida por antigüedad. Mayor puntuación = mayor presencia de la competencia.',
    acctCompetitorFallback: 'Competidor',
    acctStrengthValue: 'Fuerza: {value}',
    acctShowMore: 'Ver más',
    acctRecommendedAction: 'Acción recomendada',
    acctBreakdownSignals: 'señales: {n}',
    acctBreakdownStrongest: 'más fuerte: {value}',
    acctBreakdownLatest: 'más reciente: {value}',
    acctCompetitiveProximity: 'Proximidad competitiva',
    acctCompetitorSignalsTitle: 'Señales de la competencia',
    acctHelpFor: 'Ayuda: {title}',
    acctProximityScore: 'Puntuación de proximidad',
    acctProximityScoreHelp: 'Ayuda sobre la puntuación de proximidad',
    acctLabelHelp: 'Ayuda sobre la etiqueta',
    acctImpactHelp: 'Ayuda sobre el impacto',
    acctTrajectory24m: 'Trayectoria 24 m',
    acctTrajectoryHelp: 'Ayuda sobre la trayectoria',
    acctTopCompetitorValue: 'Principal: {value}',
    acctTopCompetitorHelp: 'Ayuda sobre el competidor principal',
    acctLatestValue: 'Más reciente: {value}',
    acctLatestSignalHelp: 'Ayuda sobre la señal más reciente',
    acctLinkedPersonas: 'Personas vinculadas:',
    sigTypeFundingNews: 'Noticias de financiación',
    sigTypeLeadershipChange: 'Cambio de liderazgo',
    sigTypeStrategicAnnouncement: 'Anuncio estratégico',
    sigTypePartnership: 'Alianza',
    sigTypeTechnologyAdoption: 'Adopción tecnológica',
    sigTypeProcurementUpdate: 'Novedad de compras',
    sigTypeAcquisition: 'Adquisición',
    sigTypeContractAward: 'Adjudicación de contrato',
    sigTypeProfileUpdate: 'Actualización de perfil',
    sigTypeRoleChange: 'Cambio de cargo',
    sigTypeAchievement: 'Logro',
    sigTypeSocialActivity: 'Actividad en redes',
    sigTypeIncumbent: 'Proveedor actual',
    sigTypeBuying: 'Compra',
    sigTypeDisplacement: 'Sustitución',
    sigTypeRenewalWindow: 'Ventana de renovación',
    sigTypeBudgetAllocation: 'Asignación presupuestaria',
    sigTypeInitiative: 'Iniciativa',
    sigTypeRisk: 'Riesgo',
    sigTypeOrgChange: 'Cambio organizativo',
    sigTypeAccreditorResult: 'Resultado de acreditación',
    sigTypeGrantAward: 'Concesión de subvención',
    sigTypeJobPosting: 'Oferta de empleo',
    sigTypeEstateEvent: 'Obras e instalaciones',
    sigTypeFinancialPressure: 'Presión financiera',
    sigTypeEnrolmentPressure: 'Presión de matrícula',
    sigTypeSystemShortcoming: 'Deficiencia del sistema',
    sigTypeVendorDispute: 'Conflicto con proveedor',
    sigTypeComplianceUpdate: 'Novedad de cumplimiento',
    sigTypeBoardUpdate: 'Novedad del consejo',
    sigTypeGreenfield: 'Sin solución actual',
    sigMarketUpdate: 'Novedad de mercado',
    sigCompetitorFilter: 'Competidor: {name}',
    sigMarketActivity: 'Actividad del mercado',
    sigAccountSignals: 'Señales de cuentas',
    sigPersonaSignals: 'Señales de personas',
    sigCompetitorSignals: 'Señales de la competencia',
    sigNoMarketActivity: 'Aún no hay señales de actividad del mercado.',
    chatCopy: 'Copiar',
    chatCopied: 'Copiado',
    chatTalkingAbout: 'Hablando de {account}',
    chatSourcesLabel: 'Fuentes:',
    pbBoothMeetingPurpose: 'Atrae visitas al stand y demostraciones en persona.',
    pbSessionInvitePurpose: 'Atrae asistentes a una sesión con ponencia.',
    pbVipOutreachPurpose: '20 minutos con contactos de nivel directivo.',
    pbQuickConnectPurpose: 'Contacto ágil entre pares para asistentes confirmados.',
    pbPersonaScopeAny: 'Cualquier persona',
    pbPersonaScopeExecutiveOnly: 'Solo directivos',
    pbPersonaScopeConfirmedAttendees: 'Solo asistentes anteriores y confirmados',
    homeWeakFit: 'Ajuste débil',
    homeNoFit: 'Sin ajuste',
    homeNotScored: 'Sin puntuar',
    // [2026-10-29] i18n: UI chrome keys (accounts list, account signals, archetypes)
    acctArchExecutiveBuyer: 'Comprador ejecutivo',
    acctArchEvaluator: 'Evaluador',
    acctNoContactsYet: 'Aún no hay contactos.',
    acctNoSignalsForAccount: 'No se encontraron señales para esta cuenta.',
    acctUntitledSignal: 'Señal sin título',
    acctNoDescriptionAvailable: 'Sin descripción disponible',
    acctSignalUpdate: 'Novedad',
    acctImpactLabel: 'Impacto:',
    acctMomentumScoreChanged: 'La puntuación de impulso cambió {n} puntos',
    acctsColProduct: 'Producto',
    acctsColAccountTier: 'Nivel de cuenta',
    acctsColTargetProducts: 'Productos objetivo',
    acctsColSubscribers: 'Suscriptores',
    acctsTargetProductPlaceholder: 'Producto objetivo',
    procTeamStatusAria: 'Estado del equipo',
    // [2026-10-29] i18n: chat table/chart block labels (lib/chatBlockLabels.js)
    chatTblWhatTheyRunToday: 'Lo que usan hoy',
    chatTblWhatChangedRecently: 'Cambios recientes',
    chatColCategory: 'Categoría',
    chatColVendor: 'Proveedor',
    chatColHowTheyHoldIt: 'Cómo lo tienen',
    chatColDeployment: 'Despliegue',
    chatColSentiment: 'Sentimiento',
    chatColEvidence: 'Evidencia',
    chatColDate: 'Fecha',
    chatColWhatHappened: 'Qué ocurrió',
    chatColSource: 'Fuente',
    chatBadgeCommercialProduct: 'Producto comercial',
    chatBadgeNativeToLms: 'Integrado en su LMS',
    chatBadgeBuiltInHouse: 'Desarrollo propio',
    chatBadgeOpenSource: 'Código abierto',
    chatBadgeNothingInPlace: 'Sin solución',
    chatBadgeInstitutionWide: 'Toda la institución',
    chatBadgeSeveralUnits: 'Varias unidades',
    chatBadgeOneUnit: 'Una unidad',
    chatBadgePilot: 'Piloto',
    chatBadgePositive: 'Positivo',
    chatBadgeNeutral: 'Neutral',
    chatBadgeNegative: 'Negativo',
    chatChartWhichLms: 'Qué LMS usa el mercado',
    chatChartFinancialHeadroom: 'Margen financiero en el mercado',
    chatChartAiReadiness: 'Preparación para IA en el mercado',
    chatChartMarketSplit: 'Cómo se reparte el mercado',
    chatChartWhoHolds: 'Quién tiene {cat} en el mercado',
    chatUnitInstitutions: 'instituciones',
    chatUnitAccounts: 'cuentas',
    chatSliceLow: 'Baja',
    chatSliceModerate: 'Moderada',
    chatSliceHigh: 'Alta',
    chatSliceOther: 'Otros',
    chatSliceLevel: 'Nivel {n}',
    chatSlicePriorityNow: 'Prioridad ahora',
    chatSliceInYourMarket: 'En tu mercado',
    chatSliceRestOfMarket: 'Resto del mercado',
    chatSliceAdjacentMarket: 'Mercado adyacente',
    chatDateUnknown: 'fecha desconocida',
    chatNoteVendor: '{n} de {total} instituciones accesibles tienen un proveedor de {cat} registrado. Los proveedores por debajo del 5 % se agrupan como Otros.',
    chatNoteLms: '{n} de {total} instituciones accesibles tienen un LMS registrado. Agrupados por familias: Blackboard cuenta como Anthology y Canvas como Instructure. Open LMS y Totara se mantienen separados de Moodle.',
    chatNoteCapacity: '{n} de {total} instituciones accesibles tienen una banda de capacidad.',
    chatNoteReadiness: '{n} de {total} instituciones accesibles tienen puntuación de preparación para IA.',
    chatNoteMix: '{n} cuentas clasificadas.',
  },
  
  'pt-BR': {
    // Navigation
    home: 'Início',
    market: 'Mercado',
    accounts: 'Contas',
    people: 'Pessoas',
    signals: 'Sinais',
    procurement: 'Licitações',
    playbooks: 'Playbooks',
    settings: 'Configurações',
    help: 'Ajuda',
    admin: 'Administrador',
    segments: 'Segmentos',
    analytics: 'Análises',
    outbox: 'Caixa de saída',
    logout: 'Sair',
    
    // Common
    loading: 'Carregando...',
    save: 'Salvar',
    cancel: 'Cancelar',
    edit: 'Editar',
    delete: 'Excluir',
    search: 'Pesquisar',
    filter: 'Filtrar',
    export: 'Exportar',
    retry: 'Tentar novamente',
    refresh: 'Atualizar',
    backTo: 'Voltar para',
    viewProfile: 'Ver perfil',
    
    // Headers
    dashboard: 'Painel',
    accountOverview: 'Visão geral da conta',
    
    // Tabs
    overview: 'Visão geral',
    details: 'Detalhes',
    activity: 'Atividade',
    notes: 'Notas',
    connections: 'Conexões',
    connectionsEmpty: 'Ainda não há conexões conhecidas para esta pessoa.',
    withConnections: 'Com conexões',
    withoutConnections: 'Sem conexões',
    inPursuit: 'Em acompanhamento',
    notInPursuit: 'Sem acompanhamento',
    almaMater: 'Alma mater',
    connectionInferred: 'inferida',
    personas: 'Personas',
    departments: 'Departamentos',
    
    // Account Page
    techStack: 'Pilha de tecnologia',
    liveDeals: 'Licitações abertas',
    decisionWindow: 'Janela de decisão',
    renewalDate: 'Data de renovação',
    momentum: 'Impulso',
    strategicPriorities: 'Prioridades estratégicas',
    institutionalChallenges: 'Desafios institucionais',
    studentExpectations: 'Expectativas dos alunos',
    keyContacts: 'Contatos principais',
    procurementProfile: 'Perfil de aquisições',
    accountNotFound: 'Conta não encontrada',
    accountNotFoundDesc: 'A conta solicitada não pôde ser carregada ou não existe.',
    rateLimitReached: 'Limite de taxa atingido. Mostrando dados em cache. Aguarde 5 minutos antes de tentar novamente.',
    showingCachedData: 'Mostrando dados em cache. Clique em atualizar para obter as atualizações mais recentes.',
    
    // Charts & Metrics
    fitScore: 'Pontuação de adequação',
    excellent: 'Excelente',
    strong: 'Forte',
    moderate: 'Moderado',
    low: 'Baixo',
    scoreRationale: 'Justificativa da pontuação',
    noRationaleAvailable: 'Nenhuma justificativa disponível.',
    riskRationale: 'Justificativa de risco',
    
    // People & Personas
    personasOrganizedBy: 'As personas são organizadas por função no comitê de compras e classificadas por sua influência e pontuação de adequação. Clique em qualquer contato para ver seu perfil completo e atividade.',
    addedToWatchlist: 'Adicionado à lista de observação',
    removedFromWatchlist: 'Removido da lista de observação',
    
    // Procurement
    procurementFriendliness: 'Facilidade de aquisição',
    friendly: 'Amigável',
    standard: 'Padrão',
    bureaucratic: 'Burocrático',
    monitor: 'Monitorar',
    
    // Decision Window
    lessThan12Months: '<12 meses',
    months12to24: '12-24 meses',
    moreThan24Months: '>24 meses',
    notApplicable: 'N/A',
    decisionWindowRationale: 'Justificativa da janela de decisão',
    
    // Signals
    momentumUpdate: 'Atualização de momentum',
    momentumScoreChanged: 'A pontuação de momentum mudou em',
    points: 'pontos',
    
    // Market Intelligence
    marketIntelligence: 'Oportunidades de mercado',
    macroTrends: 'Tendências macro e oportunidades em seus mercados-alvo',
    buyingActivity: 'Atividade de compra',
    players: 'Dinâmicas',
    
    // Filters
    allCountries: 'Todos os países',
    filterByCountry: 'Filtrar por país',
    allDepartments: 'Todos os departamentos',
    filterByDepartment: 'Filtrar por departamento',
    
    // Status
    live: 'Ativo',
    closed: 'Fechado',
    awarded: 'Concedido',
    cancelled: 'Cancelado',
    
    // Common - additional
    noDataAvailable: 'Nenhum dado disponível.',

    // NEO Chat panel
    neoChatDefaultGreeting: 'Olá! Sou o NEO, seu assistente de GTM. Como posso ajudar hoje?',
    neoChatAssistantTitle: 'Assistente NEO',
    neoChatNewChat: 'Novo chat',
    neoChatInputPlaceholder: 'Pergunte qualquer coisa ao NEO...',
    neoChatLoadingChat: 'Carregando chat',
    neoChatChipShowTenders: 'Ver licitações',
    neoChatPromptShowTenders: 'Mostre licitações relevantes',
    neoChatChipMeetingPrep: 'Preparação de reunião',
    neoChatPromptMeetingPrep: 'Prepare-me para uma reunião',
    neoChatStepAccount: 'Conta',
    neoChatStepType: 'Tipo',
    neoChatStepAttendees: 'Participantes',
    neoChatSelectAccount: 'Selecionar conta',
    neoChatLoadingAccounts: 'Carregando contas...',
    neoChatSearchAccounts: 'Buscar contas...',
    neoChatNoAccountsFound: 'Nenhuma conta encontrada.',
    neoChatSelectMeetingType: 'Selecionar tipo de reunião',
    neoChatMeetingWith: 'Reunião com {accountName}',
    neoChatAskingAbout: 'Perguntando sobre {accountName}',
    neoChatClearAccount: 'Parar de perguntar sobre esta conta',
    neoChatBack: 'Voltar',
    neoChatSelectAttendeesOptional: 'Selecionar participantes (opcional)',
    neoChatLoadingPeople: 'Carregando pessoas...',
    neoChatSearchPeople: 'Buscar pessoas...',
    neoChatNoPeopleFound: 'Nenhuma pessoa encontrada',
    neoChatSkip: 'Pular',
    neoChatGenerate: 'Gerar',
    neoChatMeetingDiscovery: 'Chamada de descoberta',
    neoChatMeetingDemo: 'Demo / Apresentação',
    neoChatMeetingProposal: 'Revisão de proposta',
    neoChatMeetingNegotiation: 'Negociação',
    neoChatMeetingRenewal: 'Renovação',
    neoChatMeetingQbr: 'QBR / Revisão',
    neoChatPrepareMeetingUser: 'Prepare-me para uma {meetingType} com {accountName}',
    neoChatGiveBriefOn: 'Dê-me um resumo de {accountName}',
    neoChatBriefOnAccountName: 'Dê-me um resumo de {account_name}',
    neoChatErrorCallNeo: 'Desculpe, não consegui contactar o NEO: {error}',
    neoChatErrorMeetingPrep: 'Desculpe, não consegui gerar o briefing da reunião. {error}',
    neoChatErrorMeetingPrepTimeout:
      'A solicitação expirou. A preparação pode levar até um minuto — tente novamente.',
    neoChatErrorAccountBrief: 'Desculpe, não consegui gerar o resumo da conta. Erro: {error}',
    neoChatErrorOutreach:
      'Desculpe, não consegui gerar a mensagem de outreach agora. Erro: {error}',
    neoChatErrorTenders: 'Desculpe, não consegui obter informações de licitações. {hint}',
    neoChatNoDebrief:
      'Não encontrei um resumo detalhado de **{accountName}**. Isso geralmente significa que ainda não há dados suficientes. Tente mais tarde ou faça outra pergunta.',
    neoChatBriefGreetingFallback: 'Olá',
    neoChatBriefThings1: 'uma coisa que vale seu tempo',
    neoChatBriefThings2: 'duas coisas que valem seu tempo',
    neoChatBriefThings3: 'três coisas que valem seu tempo',
    neoChatBriefThings4: 'quatro coisas que valem seu tempo',
    neoChatBriefThings5: 'cinco coisas que valem seu tempo',
    neoChatTriggerIntroOne: 'Acabou de chegar:',
    neoChatTriggerIntroMany: 'Acabou de chegar — atividade nova nas suas contas:',
    neoChatSelectAccountAndPerson: 'Selecionar conta e pessoa',
    neoChatAccountLabel: 'Conta',
    neoChatPersonLabel: 'Pessoa',
    neoChatSelectAccountPlaceholder: 'Selecionar conta...',
    neoChatSelectPersonPlaceholder: 'Selecionar pessoa...',
    neoChatNoPeopleForAccount: 'Nenhuma pessoa encontrada para esta conta',
    neoChatConfigureOutreachTo: 'Configurar outreach para {name}',
    neoChatAtAccount: 'em {accountName}',
    neoChatBackToPeople: 'Voltar à seleção de pessoas',
    neoChatChannelLabel: 'Canal',
    neoChatToneLabel: 'Tom',
    neoChatMessageTypeLabel: 'Tipo de mensagem',
    neoChatContextTriggerLabel: 'Contexto/Gatilho',
    neoChatGenerating: 'Gerando...',
    neoChatGenerateMessage: 'Gerar mensagem',
    neoChatGenerateSequence: 'Gerar sequência de 3 mensagens',
    neoChatOutreachContextPlaceholder:
      'ex.: «Fazendo follow-up da publicação recente no LinkedIn sobre IA»',
    
    // Confidence levels
    high: 'Alto',
    medium: 'Médio',
    urgencyHigh: 'Alta urgência',
    urgencyMedium: 'Urgência média',
    urgencyLow: 'Baixa urgência',
    priorityHigh: 'Alta prioridade',
    priorityMedium: 'Prioridade média',
    priorityLow: 'Baixa prioridade',
    priorityTooltipLead: 'A prioridade combina o quão bem esta pessoa se encaixa com o quão recente é o sinal.',
    
    // Procurement Profile
    fieldMetric: 'Campo / Métrica',
    value: 'Valor',
    source: 'Fonte',
    confidence: 'Confiança',
    procurementIntelligence: 'Inteligência abrangente de aquisições e análise de caminho de fornecedores para esta instituição.',
    procurementProfileNA: 'Os dados do perfil de aquisições não estão disponíveis para esta conta.',
    
    // Account Header
    students: 'estudantes',
    employees: 'funcionários',
    accountFitRationale: 'Justificativa de adequação da conta',
    excellentFit: 'Adequação excelente',
    strongFit: 'Adequação forte',
    moderateFit: 'Adequação moderada',
    lowFit: 'Adequação baixa',
    client: 'cliente',
    buyingModelRationale: 'Justificativa do modelo de compra',
    bvoClassification: 'Classificação BVO',
    incumbentSentiment: 'Sentimento do titular',
    procurementFriendlyLabel: 'Amigável',
    procurementStandardLabel: 'Padrão',
    procurementBureaucraticLabel: 'Burocrático',
    procurementMonitorLabel: 'Monitorar',
    bvoBudgetDriven: 'Orientado ao orçamento',
    bvoValueSeeker: 'Buscador de valor',
    bvoOutcomeFocused: 'Orientado a resultados',
    bvoInnovationDriven: 'Orientado à inovação',
    bvoRiskAverse: 'Avesso ao risco',
    bvoComplianceFocused: 'Orientado à conformidade',
    sentimentAtRisk: 'Em risco',
    sentimentPositive: 'Positivo',
    sentimentNeutral: 'Neutro',
    sentimentNegative: 'Negativo',
    sentimentDissatisfied: 'Insatisfeito',
    unknown: 'Desconhecido',
    numberOfEmployees: 'Número de funcionários',
    departmentFitRationale: 'Justificativa de adequação',
    
    // Procurement Friendliness
    friendlyDesc: 'Esta instituição possui processos de aquisição simplificados e geralmente é receptiva a novos relacionamentos com fornecedores.',
    standardDesc: 'Esta instituição segue procedimentos de aquisição típicos com cronogramas de avaliação padrão.',
    bureaucraticDesc: 'Esta instituição possui processos de aquisição complexos que podem exigir ciclos de vendas mais longos e aprovações adicionais.',
    monitorDesc: 'Esta instituição possui dinâmicas de aquisição desafiadoras que exigem monitoramento cuidadoso e engajamento estratégico.',
    procurementProcessClassification: 'Classificação do processo de aquisições para esta instituição.',
    
    // Decision Window
    decisionWindowMonitor: 'Monitor de janela de decisão',
    decisionWindowDesc: 'Mostra quando um contrato provavelmente estará em revisão. As datas de vencimento são estimadas usando registros de aquisições, ciclos de contrato típicos e avisos de renovação. Quando faltam dados, projetamos com base em datas de concessão e padrões padrão, sempre rotulando com níveis de confiança.',
    
    // Tech Stack
    noTechData: 'Sem dados de tecnologia especificados',
    
    // Tenant-specific fields
    pedagogicalModel: 'Modelo pedagógico e cultural',
    digitalLandscape: 'Panorama digital',
    digitalFunding: 'Oportunidades de financiamento digital',
    learningInitiatives: 'Iniciativas de desenvolvimento de aprendizagem',
    patientCareGaps: 'Lacunas no cuidado ao paciente',
    decisionDynamics: 'Dinâmica de decisão das partes interessadas',
    contentCoverage: 'Cobertura de conteúdo',
    clientOpportunities: 'Oportunidades de clientes',
    decisionMakers: 'Tomadores de decisão',
    engagementHooks: 'Ganchos de engajamento',
    recommendedTactics: 'Táticas recomendadas',
    
    // People Page
    keyDecisionMakers: 'Principais tomadores de decisão e influenciadores',
    searchPeople: 'Pesquisar pessoas por nome, função ou universidade...',
    name: 'Nome',
    account: 'Conta',
    country: 'País',
    department: 'Departamento',
    archetype: 'Arquétipo',
    pursuit: 'Acompanhamento',
    socialScore: 'Pontuação social',
    noPeopleFound: 'Nenhuma pessoa encontrada',
    adjustFilters: 'Tente ajustar sua pesquisa ou filtros.',
    formerClient: 'Ex-cliente',
    previouslyWorked: 'Trabalhou anteriormente para um de nossos clientes',
    socialPresence: 'Mede a visibilidade e influência da persona em plataformas sociais como LinkedIn.',
    personaFitRationale: 'Justificativa de adequação da persona',
    strategic: 'Estratégico',
    supportive: 'Favorável',
    passive: 'Passivo',
    showContactInfo: 'Mostrar informações de contato',
    satisfied: 'Satisfeito',
    neutral: 'Neutro',
    frustrated: 'Frustrado',
    exploringAlternatives: 'Explorando alternativas',
    
    // Signals Page
    trackSignals: 'Acompanhe o momentum da conta e a atividade da persona em tempo real para identificar oportunidades e engajar no momento certo.',
    allSignals: 'Todos os sinais',
    noAccountSignals: 'Nenhum sinal de conta encontrado',
    noPersonaSignals: 'Nenhum sinal de persona encontrado',
    
    // Playbooks Page
    structuredEngagement: 'Estratégias de engajamento estruturadas para diferentes cenários',
    createPlaybook: 'Criar manual',
    searchPlaybooks: 'Pesquisar manuais...',
    allFactors: 'Todos os fatores',
    eventPlaybooks: 'Playbooks de eventos',
    targetedSequences: 'Execute sequências direcionadas para seus próximos eventos',
    noUpcomingEvents: 'Nenhum evento próximo',
    addEventsInSettings: 'Adicionar eventos nas Configurações',
    selectEvent: 'Selecionar evento',
    previewAndApply: 'Visualizar e aplicar',
    yourCustomPlaybooks: 'Seus manuais personalizados',
    custom: 'Personalizado',
    goal: 'Objetivo',
    enrolledAccounts: 'Contas inscritas',
    untouchedAccounts: 'Contas não tocadas',
    noPlaybooks: 'Nenhum manual encontrado correspondendo aos seus critérios',
    
    // Segments Page
    createManageExport: 'Criar, gerenciar e exportar listas de destino',
    buildNewSegment: 'Criar novo segmento',
    rules: 'regras',
    created: 'Criado',
    viewResults: 'Ver resultados',
    noSegmentsYet: 'Ainda não há segmentos',
    getStartedSegment: 'Comece criando seu primeiro segmento.',
    deleteSegment: 'Excluir segmento',
    segmentDeleted: 'Segmento excluído com sucesso',
    authRequired: 'Autenticação necessária',
    loginToAccess: 'Faça login para acessar segmentos.',
    logIn: 'Entrar',
    
    // Home Page
    yourWatchlist: 'Sua lista de observação',
    watchlistTooltip: 'Sua lista de observação rastreia personas-chave com as quais você está ativamente engajando. Esses indivíduos são sinalizados para construção contínua de relacionamento e são priorizados em seu fluxo de trabalho.',
    open: 'Abrir',
    untrack: 'Parar de rastrear',
    moreTracked: 'mais personas rastreadas…',
    refreshing: 'Atualizando…',
    
    // Procurement Page
    dealsTenders: 'Ofertas e licitações',
    trackTenders: 'Acompanhe licitações abertas e ofertas fechadas no mercado.',
    searchKeyword: 'Pesquisar por palavra-chave, título, instituição...',
    liveDealsCount: 'Ofertas ativas',
    recentlyClosed: 'Recentemente fechadas',
    closedDeals: 'Ofertas fechadas',
    allInstitutions: 'Todas as instituições',
    filterByInstitution: 'Filtrar por instituição',
    allCategories: 'Todas as categorias',
    filterByCategory: 'Filtrar por categoria',
    filterByEvent: 'Filtrar por evento',
    allEvents: 'Todos os eventos',
    allPersonas: 'Todas as personas',
    formerClientOnly: 'Apenas ex-cliente',
    minSocialScore: 'Pontuação social mín.',
    selectAll: 'Selecionar tudo',
    clearAll: 'Limpar tudo',
    clearFilters: 'Limpar filtros',
    typeToSearch: 'Digite para pesquisar...',
    category: 'Categoria',
    vendor: 'Fornecedor',
    institutionType: 'Tipo de instituição',
    sentiment: 'Sentimento',
    listOnly: 'Apenas lista',
    mapPlusList: 'Mapa + Lista',
    allDeadlines: 'Todos os prazos',
    days0_7: '0–7 dias',
    days8_30: '8–30 dias',
    days31_90: '31–90 dias',
    days90Plus: '90+ dias',
    months0_6: '0–6 meses',
    months6_12: '6–12 meses',
    months12_24: '12–24 meses',
    unknownExpired: 'Desconhecido / Expirado',
    allVendors: 'Todos os fornecedores',
    exit: 'Saída',
    filterByExitLeverage: 'Filtrar por alavancagem de saída',
    some: 'Algum',
    noSignal: 'Sem sinal',
    loadingDeals: 'Carregando ofertas...',
    loadingContractIntelligence: 'Carregando inteligência de contratos...',
    loadingMoreDeals: 'Carregando mais ofertas...',
    loadedDeals: 'Carregadas {count} ofertas de {label}',
    outOfScope: 'Fora do escopo',
    pending: 'Pendente',
    insufficientInfo: 'Info insuficiente',
    liveRadar: 'Radar ao vivo',
    recentlyExpired: 'Expirados recentemente',
    contractIntelligence: 'Inteligência de contratos',
    displacementRadar: 'Radar de deslocamento',
    benchmarking: 'Comparação',
    selectCategoryBenchmarkTitle: 'Selecione uma categoria para ver a comparação',
    selectCategoryBenchmarkDesc: 'Use o filtro Categoria acima para escolher uma ou mais categorias de compras. Os dados de comparação (matrícula vs $/aluno/ano) aparecerão aqui.',
    noClosedDealsBenchmarkTitle: 'Nenhuma oferta fechada disponível',
    noClosedDealsBenchmarkDesc: 'A comparação requer ofertas fechadas. Verifique se o bootstrap retorna ofertas fechadas para o seu tenant.',
    selectCategoryContractTitle: 'Selecione uma categoria para explorar inteligência de contratos',
    selectCategoryContractDesc: 'Use o filtro Categoria acima para escolher uma ou mais categorias. Contratos históricos e dados de concorrentes aparecerão aqui.',
    loadingContractData: 'Carregando dados de contratos…',
    alertSettings: 'Configurações de alertas',
    title: 'Título',
    institution: 'Instituição',
    deadline: 'Prazo',
    productFitPanel: 'Adequação do produto',
    noLiveDeals: 'Nenhuma oferta ativa encontrada',
    noRecordsSelectedFilters: 'Nenhum registro encontrado para a categoria e filtros selecionados. Tente outras categorias ou limpe os filtros.',
    marketMap: 'Mapa de mercado',
    marketMapHelpText: 'Clique em um país para filtrar a tabela de ofertas abaixo e ver a participação de mercado do incumbente (gráfico de pizza). Use os filtros de fornecedor e sentimento para refinar.',
    noDealsCountryFilters: 'Nenhuma oferta neste país para os filtros atuais.',
    noAttackableAccounts: 'Nenhuma conta atacável corresponde aos critérios.',
    previous: 'Anterior',
    next: 'Próximo',
    product: 'Produto',
    reseller: 'Revendedor',
    procurementMethod: 'Método de contratação',
    framework: 'Acordo-quadro',
    productName: 'Nome do produto',
    supplier: 'Fornecedor',
    predictedExpiry: 'Vencimento previsto',
    productFitProfileDesc: 'Defina perfis de produto para pontuação de ajuste. Vários perfis podem estar ativos: eles alimentam o Light Fit no Live Radar (por categoria) e você escolhe um perfil ao executar Deep Fit em uma licitação.',
    createProfile: 'Criar perfil',
    editProfile: 'Editar perfil',
    newProfile: 'Novo perfil',
    productFitName: 'Nome',
    productFitPrimaryCategory: 'Categoria principal',
    productFitCategoryTags: 'Etiquetas de categoria (separadas por vírgulas)',
    productFitStrongSignals: 'Sinais de ajuste forte (um por linha)',
    productFitNegativeSignals: 'Sinais negativos (prefixo Hard: ou Soft:, um por linha)',
    productFitDeploymentModels: 'Modelos de implantação',
    productFitCloud: 'Nuvem',
    productFitSelfHosted: 'Auto-hospedado',
    productFitHybrid: 'Híbrido',
    productFitIntegrations: 'Integrações (separadas por vírgulas)',
    productFitSecurity: 'Certificações de segurança (separadas por vírgulas)',
    productFitKeyFeatures: 'Recursos principais (separados por vírgulas)',
    productFitUpdate: 'Atualizar',
    productFitCreate: 'Criar',
    productFitProfiles: 'Perfis',
    productFitNoProfiles: 'Ainda não há perfis. Crie um acima.',
    productFitSetAsActive: 'Definir como ativo',
    productFitDeleteProfile: 'Excluir perfil?',
    productFitDeleteConfirm: 'Isso excluirá permanentemente "{name}". Esta ação não pode ser desfeita.',
    productFitSelectPrimaryCategory: 'Selecionar categoria principal',
    productFitNone: 'Nenhum',
    productFitActive: 'Ativo',
    productFitDeactivate: 'Marcar como inativo',
    productFitActivateError: 'Não foi possível ativar o perfil',
    productFitDeactivateError: 'Não foi possível desativar o perfil',
    loadingProductProfiles: 'Carregando perfis de produto...',

    // Person Profile
    backToPeople: 'Voltar para Pessoas',
    back: 'Voltar',
    personNotFound: 'Pessoa não encontrada',
    personNotFoundDesc: 'A pessoa solicitada não pôde ser carregada ou não existe.',
    signalsCount: 'Sinais',
    interactions: 'Interações',
    primaryInterests: 'Interesses principais',
    formerEmployers: 'Empregadores anteriores',
    education: 'Formação acadêmica',
    professionalMemberships: 'Membros profissionais',
    publicSpeakingEvents: 'Palestras e eventos',
    recentEventsAttended: 'Eventos recentes participados',
    awardsRecognition: 'Prêmios e reconhecimentos',
    publications: 'Publicações',
    publicationContext: 'Publicação',
    firstAuthorBadge: 'Primeiro autor',
    seniorAuthorBadge: 'Autor sênior',
    relevanceTierVeryStrong: 'Correspondência muito forte',
    relevanceTierStrong: 'Correspondência forte',
    showAllPublications: 'Ver todas ({count})',
    showLess: 'Ver menos',
    removeFromPursuit: '✓ Remover do acompanhamento',
    addToPursuit: 'Adicionar ao acompanhamento',
    assignMarketTier: 'Atribuir nível de mercado',
    tier: 'Nível',
    selectTier: 'Selecione um nível',
    saveChanges: 'Salvar alterações',
    atAGlance: 'Resumo rápido',
    keyInitiatives: 'Principais iniciativas e áreas de foco.',
    noInitiatives: 'Nenhuma iniciativa listada.',
    
    // AI Outreach
    aiOutreach: 'Prospecção com IA',
    channel: 'Canal',
    email: 'E-mail',
    linkedin: 'LinkedIn',
    call: 'Chamada',
    tone: 'Tom',
    relationshipBuilder: 'Construtor de relacionamentos',
    thoughtLeadership: 'Liderança de pensamento',
    challenger: 'Desafiador',
    context: 'Contexto',
    autoDetect: 'Detectar automaticamente o melhor contexto',
    personaInitiative: 'Iniciativas e atividade da persona',
    institutionalChange: 'Mudanças institucionais e momentum',
    strategicPriority: 'Prioridades estratégicas',
    procurementSignal: 'Sinais de compras e financiamento',
    customContext: 'Contexto personalizado (opcional)',
    addContext: 'Adicionar contexto específico, pontos de discussão ou instruções...',
    generating: 'Gerando...',
    generateMessage: 'Gerar mensagem',
    
    // Home Cards
    recommendedAccounts: 'Contas recomendadas',
    recommendedAccountsTooltip: 'Contas com maior pontuação com base na adequação e alinhamento estratégico.',
    viewAll: 'Ver tudo',
    noAccounts: 'Sem contas',
    recommendedPersonas: 'Personas recomendadas',
    recommendedPersonasTooltip: 'Pessoas mais relevantes com base na influência e alinhamento de função.',
    noPersonas: 'Sem personas',
    
    // Action Center
    priorityActions: 'Ações prioritárias',
    deals: 'Ofertas',
    all: 'Tudo',
    myTasks: 'Minhas tarefas',
    newLeads: 'Novos leads',
    updated: 'Atualizado',
    loadingRecommendations: 'Carregando suas recomendações personalizadas',
    analyzingPipeline: 'Analisando seu pipeline...',
    dueToday: 'Vence hoje',
    completeTouchVia: 'Completar toque',
    via: 'via',
    openDraft: 'Abrir rascunho',
    noContactOnFileOpenPersonas: 'Nenhum contato registrado nesta conta — abra a aba Personas para adicionar um.',
    openLinkedIn: 'Abrir LinkedIn',
    callNow: 'Ligar agora',
    done: 'Feito',
    whyNow: 'Por que agora?',
    nextSteps: 'Próximos passos:',
    tracked: 'Rastreado',
    fit: 'Adequação',
    draftOutreach: 'Rascunho de divulgação',
    draftMessage: 'Rascunhar mensagem',
    noTasksDue: 'Nenhuma tarefa vence hoje',
    noNewLeads: 'Ainda não há novos leads',
    noActionsYet: 'Ainda não há ações',
    hitRefresh: 'Clique em Atualizar e buscarei o que vem a seguir para você.',
    hotMovers: 'Mais ativos (últimas 2 semanas)',
    personaUpdates: 'Atualizações de personas (últimas 2 semanas)',
    noSignals2Weeks: 'Nenhum sinal nas últimas 2 semanas',
    liveDealsTitle: 'Ofertas ativas',
    noDeadline: 'Sem prazo',
    daysLeft: 'd restantes',
    expired: 'Expirado',
    noLiveDealsCategories: 'Nenhuma oferta ativa em suas categorias selecionadas.',
    noLiveDealsFound: 'Nenhuma oferta ativa encontrada.',
    decisionWindowRadar: 'Radar de janela de decisão',
    decisionWindowTooltip: 'Mostra quando um contrato provavelmente estará em revisão com base em registros de aquisições e padrões de renovação.',
    accountsApproaching: 'Contas se aproximando de horizontes de renovação/decisão',
    noAccountsInBand: 'Nenhuma conta nesta banda.',
    noAccountsWithWindows: 'Nenhuma conta com janelas de decisão encontrada.',

    // Today's Plan
    todaysPlanTitle: 'Plano de hoje',
    todaysPlanAccountFit: 'Adequação da conta',
    todaysPlanCategoryAccountResearch: 'Pesquisa de conta',
    todaysPlanCategoryFitMomentum: 'Adequação e momentum',
    todaysPlanCategoryOutreach: 'Prospecção',
    todaysPlanCategoryNewLeads: 'Novos leads',
    openPersonas: 'Abrir personas',
    track: 'Acompanhar',
    impactSuffix: 'Impacto',
    suggestedNextSteps: 'Próximos passos sugeridos',
    markRecommendationDone: 'Marcar esta recomendação como concluída',
    todaysPlanPressRefresh: 'Pressione Atualizar acima para obter seus próximos movimentos personalizados.',
    todaysTasksTitle: 'Tarefas de hoje',
    allCaughtUp: 'Tudo em dia!',
    noTasksScheduledToday: 'Nenhuma tarefa agendada para hoje',
    tasksCount: '{count} tarefas',
    touchNumberLabel: 'Toque #{number}',
    sendEmail: 'Enviar e-mail',
    linkedinMessage: 'Mensagem no LinkedIn',
    makeCall: 'Ligar',
    taskCompletedTitle: 'Tarefa concluída!',
    taskCompletedDescription: 'Bom trabalho, continue assim',
    taskCompleteError: 'Falha ao marcar tarefa como concluída',
    unknownAccount: 'Conta desconhecida',
    unknownContact: 'Contacto desconhecido',
    completedFromTodaysPlan: 'Concluído a partir do plano de hoje',
    researchGapBuyer: 'Sem proprietário de orçamento confirmado no mapa de vendas',
    researchGapChampion: 'Ainda não foi identificado um campeão interno',
    researchGapBoth: 'Nem proprietário de orçamento nem campeão confirmados',
    planReasonLeadership: 'Liderança nova ou em mudança — prioridades podem ser redefinidas.',
    planReasonProcurement: 'Compras com novidades (processo, política ou prazos).',
    planReasonLiveDeal: 'Há um negócio ativo a avançar.',
    planReasonBoard: 'Reunião do conselho a aproximar-se — decisões podem acelerar.',
    planReasonMomentum: 'O envolvimento recente na conta parece forte.',
    planReasonPersonaActivity: 'Estiveram ativos recentemente.',
    planReasonFit: 'Boa adequação para nós (conta/persona).',
    planReasonDecisionWindowUnknown: 'O momento da decisão não está claro — confirme o calendário.',
    planReasonDecisionWindowMonths: 'Decisão esperada em ~{months} meses.',
    planReasonDecisionWindowGeneric: 'Dentro da janela de decisão.',
    planReasonPursuit: 'Está a acompanhar esta pessoa (Pursuit).',
    todaysPlanRateLimitActive: 'Limite de taxa ativo',
    todaysPlanCannotLoadRetry: 'Não é possível carregar o plano de hoje. Nova tentativa em {remaining}.',
    todaysPlanRateLimitReached: 'Limite de taxa atingido',
    todaysPlanShowingCached: 'A mostrar plano em cache. Nova tentativa automática em 20 minutos.',
    todaysPlanCannotLoadWait: 'Não foi possível carregar o plano de hoje. Aguarde 20 minutos.',
    todaysPlanCouldNotLoad: 'Não foi possível carregar o plano de hoje.',
    todaysPlanCannotRefreshRetry: 'Não é possível atualizar o plano de hoje. Nova tentativa automática em {remaining}.',
    todaysPlanRefreshed: 'Plano atualizado',
    todaysPlanRefreshedDescription: 'O plano de hoje foi atualizado com as recomendações mais recentes.',
    todaysPlanRefreshRateLimit: 'Não foi possível atualizar devido ao limite de taxa. Nova tentativa em 20 minutos.',
    todaysPlanRefreshFailed: 'Não foi possível atualizar o plano. Tente novamente.',
    todaysPlanRefreshFailedTitle: 'Falha na atualização',
    todaysPlanAckRateLimit: 'Item reconhecido localmente. Sincroniza quando o limite terminar (até 20 min).',
    todaysPlanCouldNotSaveAck: 'Não foi possível guardar o reconhecimento.',
    couldNotUpdatePursuit: 'Não foi possível atualizar Pursuit',
    pleaseTryAgain: 'Tente novamente.',
    
    // Market Page
    marketMomentumTrend: 'Tendência de momentum do mercado (12 semanas)',
    noMomentumData: 'Nenhum dado de momentum disponível',
    
    // Greetings
    goodMorning: 'Bom dia',
    goodAfternoon: 'Boa tarde',
    goodEvening: 'Boa noite',
    
    // Accounts Page
    manageExploreInstitutions: 'Gerenciar e explorar instituições-alvo',
    searchAccounts: 'Pesquisar contas...',
    accountFitScore: 'Pontuação de adequação da conta',
    renewalRisk: 'Risco de renovação',
    expansionOpps: 'Oportunidades de expansão',
    fariaProducts: 'Produtos Faria',
    departmentFitScore: 'Pontuação de adequação do departamento',
    noAccountsFound: 'Nenhuma conta encontrada',
    
    // Market Components
    topAccounts: 'Principais contas',
    topPersonas: 'Principais personas',
    tieringSnapshot: 'Instantâneo de níveis',
    tieringTooltip: 'As contas são inicialmente classificadas por níveis com base na sua Pontuação de Adequação da Conta. A classificação por níveis ajuda a priorizar onde focar — para dedicar mais tempo às contas com maior potencial, realizar uma prospeção mais direcionada e obter melhores resultados. Pode substituir qualquer nível a qualquer momento em cada conta individual.',
    aiReadinessIndex: 'Índice de prontidão para IA 2025',
    topInstitutionsAI: 'Principais instituições por adoção e implementação de IA',
    topHospitalsAI: 'Principais hospitais por adoção e implementação de IA',
    topSchoolsAI: 'Principais escolas internacionais por adoção e implementação de IA',
    topEnterprisesAI: 'Principais empresas por adoção e implementação de IA',
    topCompaniesAI: 'Principais companhias por adoção e implementação de IA',
    topAuthoritiesAI: 'Principais autoridades educacionais por adoção e implementação de IA',
    marketFitDistribution: 'Distribuição de adequação de mercado',
    accountFitComposition: 'Composição de pontuação de adequação de conta em faixas de 10 pontos',
    topBuyers: 'Principais compradores',
    universitiesMostVendors: 'Universidades com mais implementações de novos fornecedores',
    enterprisesMostVendors: 'Empresas com mais implementações de novos fornecedores',
    keyBuyingAreas: 'Áreas-chave de compra',
    functionalInvestment: 'Categorias funcionais com mais investimento',
    procurementComplexity: 'Complexidade de aquisições',
    distributionProfiles: 'Distribuição de perfis de aquisições',
    procurementRoutes: 'Rotas de aquisição',
    howInstitutionsBuy: 'Como as instituições compram tecnologia',
    marketShare: 'Participação de mercado',
    marketWinners: 'Vencedores do mercado',
    vendorsGaining: 'Fornecedores ganhando novos acordos institucionais',
    headlineTrends: 'Tendências principais',
    marketInsights: 'Insights de mercado',
  

    // Auto-filled missing keys
    momentumScoreAccount: 'Pontuação de impulso (nível da conta)',
    momentumScoreDesc: 'A pontuação de impulso reflete a evolução semanal da organização que torna o ambiente mais ou menos favorável ao engajamento, expansão ou renovação do cliente.',
    signalsCaptured: 'Sinais capturados:',
    scoreDerivation: 'Cálculo da pontuação:',
    personaActivityScore: 'Pontuação de atividade da persona (nível individual)',
    personaActivityDesc: 'A pontuação de atividade da persona mede quão visível e substancialmente um contato contribui para mudanças digitais, operacionais ou estratégicas em áreas relevantes para a solução do cliente.',
    filterBySignal: 'Filtrar por tipo de sinal',
    allTime: 'Todo o período',
    last1Day: 'Último dia',
    last7Days: 'Últimos 7 dias',
    last14Days: 'Últimos 14 dias',
    last30Days: 'Últimos 30 dias',
    last90Days: 'Últimos 90 dias',
    minScore: 'Pontuação mínima',
    recently: 'recentemente',
    filterByFactor: 'Filtrar por fator',
    chooseEvent: 'Escolha um evento',
    daysAway: 'dias restantes',
    today: 'Hoje',
    allSectors: 'Todos os setores',
    sectors: 'Setores',
    steps: 'Etapas',
    clone: 'Clonar',
    standardPlaybooks: 'Playbooks padrão',
    deletePlaybook: 'Excluir playbook',
    deletePlaybookConfirm: 'Tem certeza de que deseja excluir',
    cannotUndo: 'Esta ação não pode ser desfeita.',
    playbookDeleted: 'Playbook excluído',
    failedToDelete: 'Não foi possível excluir o playbook',
    selectAccountFor: 'Selecionar conta para',
    chooseAccountToApply: 'Escolha a qual conta aplicar este playbook',
    noEventSelected: 'Nenhum evento selecionado',
    selectEventFirst: 'Selecione primeiro um evento ou adicione eventos em Configurações',
    noEligibleAccounts: 'Nenhuma conta elegível',
    deleteSegmentConfirm: 'Tem certeza de que deseja excluir',
    failedToDeleteSegment: 'Não foi possível excluir o segmento',
    disconnect: 'Desconectar',
    connecting: 'Conectando...',
    disconnecting: 'Desconectando...',
    saving: 'Salvando...',
    uploading: 'Enviando...',
    default: 'Padrão',
    language: 'Idioma',
    history: 'Histórico',
    engagement: 'Engajamento',
    showAll: 'Mostrar tudo',
    gtmAnalytics: 'Análises GTM',
    accountsByTier: 'Contas por nível',
    avgAccountFitAcrossAccounts: 'Adequação média da conta (todas as contas)',
    unassigned: 'Não atribuído',
    connectCrm: 'Conectar CRM',
    topOfFunnelIntelligence: 'Inteligência de topo de funil',
    coverageTierPenetration: 'Cobertura e penetração por nível',
    outboundExecution: 'Execução outbound',
    accountsTouched: 'Contas contatadas',
    buyerChampion: 'Comprador + Defensor',
    multiThreaded: 'Vários contatos',
    playbooksRun: 'Playbooks executados',
    neoRecommendations: 'Recomendações do NEO',
    accountInteractions: 'Interações com contas',
    emailReplyRate: 'Taxa de resposta por e-mail',
    linkedinResponse: 'Resposta no LinkedIn',
    callResponse: 'Resposta a ligações',
    emailOpenRate: 'Taxa de abertura de e-mail',
    emailClickRate: 'Taxa de cliques em e-mail',
    accountsWithSignals: 'Contas com sinais',
    tendersGenerated: 'Licitações geradas',
    tendersReviewed: 'Licitações revisadas',
    timeSaved: 'Tempo economizado',
    totalHoursSaved: 'Total de horas economizadas',
    hours: 'horas',
    usage: 'Uso',
    dailyActivity: 'Atividade diária',
    minPerUserWeek: 'min / usuário / semana',
    replyRate: 'Taxa de resposta',
    mostActive: 'Mais ativos',
    signalArchetypeOpenRates: 'Sinal × Arquétipo — Taxas de abertura',
    signalArchetypeClickRates: 'Sinal × Arquétipo — Taxas de clique',
    mostActiveAccounts: 'Contas mais ativas',
    mostActiveAccountsTooltip:
      'Contas com várias personas engajadas em que o total de aberturas humanas verificadas excede o número de personas alcançadas.',
    mostActiveAccountsEmpty: 'Ainda não há contas com engajamento multipersona.',
    adHoc: 'Ad hoc',
    adHocMessagePerformance: 'Desempenho de mensagens ad hoc',
    playbookAnalyticsMovedMostActive:
      'A análise de desempenho de playbooks foi para a aba Mais ativos.',
    playbookAnalyticsUsePlaybooksPage:
      'Use a página Playbooks para configurar sequências e o status operacional.',
    performanceByArchetype: 'Desempenho por arquétipo',
    performanceByDepartment: 'Desempenho por departamento',
    department: 'Departamento',
    performanceBySector: 'Desempenho por setor',
    sector: 'Setor',
    analyticsNoDataInPeriod: 'Sem dados no período selecionado.',
    analyticsTableSent: 'Enviados',
    analyticsOpenRateCol: 'Taxa de abertura',
    analyticsClickRateCol: 'Taxa de clique',
    analyticsReplyRateCol: 'Taxa de resposta',
    analyticsChannelCol: 'Canal',
    adHocReplyRateTooltip:
      'A taxa de resposta inclui apenas respostas humanas verificadas. Testes próprios e entradas não verificadas são excluídos.',
    adHocBreakdownFocusSignalNote:
      'Os detalhes por foco e por sinal serão preenchidos conforme novas mensagens ad hoc forem enviadas.',
    adHocByCtaFamilyTitle: 'POR FAMÍLIA DE CTA',
    adHocByFocusTitle: 'POR FOCO',
    adHocBySignalTitle: 'POR SINAL',
    adHocCtaFamilyColumn: 'Família de CTA',
    adHocPrimaryAngleCol: 'Ângulo principal',
    adHocSignalCol: 'Sinal',
    adHocSendsFeaturingSignalCol: 'Envios que incluem este sinal',
    adHocBySignalColumnTooltip:
      'Cada envio credita todos os sinais usados; as linhas não somam ao total do resumo.',
    ctaFamilyLabel_soft_binary: 'Soft-binary (pergunta sim/não)',
    ctaFamilyLabel_action_oriented: 'Orientada a ação (pedido de reunião)',
    ctaFamilyLabel_other: 'Outra',
    ctaFamilyLabel_no_cta: 'Sem CTA',
    messagePerformanceInsightsTitle: 'Desempenho de mensagens',
    messagePerformanceInsightsDescription:
      'Visão cruzada por formato de CTA, família de sinal e sinal — playbooks e mensagens ad hoc. Use para ver quais sinais e CTAs geram mais engajamento.',
    messagePerfPlaybookMessages: 'Mensagens de playbook',
    messagePerfAdhocMessages: 'Mensagens ad hoc',
    messagePerfByCtaFamily: 'POR FAMÍLIA DE CTA',
    messagePerfBySignalFamily: 'POR FAMÍLIA DE SINAL',
    messagePerfBySignal: 'POR SINAL',
    messagePerfByLinkClicked: 'POR LINK CLICADO',
    messagePerfLabelLinkUrl: 'Link',
    messagePerfLinkTrueClickRate: 'Taxa de clique real',
    messagePerfLinkTotalClicks: 'Cliques',
    messagePerfLinkUniqueMessages: 'Mensagens',
    messagePerfLinkMessagesHeaderTooltip:
      'Mensagens distintas das quais este link recebeu clique.',
    messagePerfBestDayToSend: 'MELHOR DIA PARA ENVIAR',
    messagePerfBestHourToSend: 'MELHOR HORA PARA ENVIAR',
    messagePerfSendTimingRatesLine: '{open}% abertura · {click}% clique · {reply}% resposta',
    messagePerfSendTimingFromSends: 'de {count} envios',
    messagePerfSendTimingNotEnoughData: 'Ainda não há dados suficientes.',
    messagePerfSendTimingTzCaption:
      'Horários de envio em: {tz} (fuso da sua programação).',
    messagePerfSendTimingTzWarningUtc:
      'Horários de envio em: UTC — não foi possível resolver o fuso da sua programação.',
    messagePerfSendTimingTzMismatch:
      'Horários de envio em: {resolved} — não corresponde ao fuso selecionado ({requested}).',
    messagePerfSendTimingTzCaptionCharts:
      'Horários de envio em: {tz} — altere acima para comparar entre regiões.',
    messagePerfSendTimingTzWarningUtcCharts:
      'Horários de envio em: UTC — não foi possível resolver o fuso horário selecionado.',
    messagePerfSendTimingChartEmpty: 'Ainda não há dados de envio suficientes.',
    messagePerfKeywordsSectionTitle: 'Palavras-chave',
    messagePerfKeywordsSectionHint:
      'O tamanho reflete a frequência; a cor reflete a taxa de resposta (mais verde = melhores respostas). Passe o rato para ver envios e taxas.',
    messagePerfKeywordsProductLabel: 'Contexto de produto',
    messagePerfKeywordsSubjectTitle: 'Linhas de assunto',
    messagePerfKeywordsBodyTitle: 'Corpo da mensagem',
    messagePerfKeywordsEmpty: 'Ainda não há dados de palavras-chave.',
    messagePerfSendTimingSectionTitle: 'Momento de envio',
    messagePerfSendTimingSeriesPlaybook: 'Playbook',
    messagePerfSendTimingSeriesAdhoc: 'Avulso',
    messagePerfSendTimingMergedTooltipLine: '{reply}% resposta · {sends} envios',
    messagePerfBySendDayChartTitle: 'POR DIA DA SEMANA',
    messagePerfBySendHourChartTitle: 'POR HORA DO DIA',
    messagePerfSendTimingChartTooltip:
      '{label} · {sends} envios · {open}% abertura · {click}% clique · {reply}% resposta',
    messagePerfSendTimeTimezoneLabel: 'Fuso horário de envio:',
    messagePerfSendTimeSchedulingTzHint: '(seu fuso de programação é {tz})',
    messagePerfTzSchedulingSuffix: 'programação',
    messagePerfTzPlaceholder: 'Selecione o fuso',
    messagePerfSendTimingYReply: 'Resp. %',
    messagePerfSendTimingYOpen: 'Abertura %',
    messagePerfSendTimingLowVolumeNote: 'Baixo volume — {sends} envios; taxa não confiável',
    messagePerfByPrimaryAngle: 'POR ÂNGULO PRINCIPAL (FOCO)',
    messagePerfLabelCtaFamily: 'Família de CTA',
    messagePerfLabelSignalFamily: 'Família de sinal',
    messagePerfLabelSignal: 'Sinal',
    messagePerfLabelPrimaryAngle: 'Ângulo principal',
    messagePerfSignalSentTooltip:
      'Cada envio conta todos os sinais usados; as linhas não somam ao total do resumo — é intencional e permite comparar o desempenho por sinal.',
    messagePerfPlaybookEmptyCaption:
      'Dados de playbook ainda não disponíveis — nenhum envio de playbook neste período.',
    messagePerfAdhocEmptyCaption:
      'Dados ad hoc ainda não disponíveis — nenhuma mensagem ad hoc enviada neste período.',
    messagePerfNoBreakdownData: 'Sem dados de desempenho de mensagens neste período.',
    messagePerfShowAll: 'Mostrar tudo ({count})',
    messagePerfShowLess: 'Mostrar menos',
    signalFamilyLabel_strategic_priority: 'Prioridade declarada',
    signalFamilyHint_strategic_priority:
      'Uma prioridade estratégica que a instituição nomeou explicitamente no plano ou na estratégia.',
    signalFamilyLabel_account_insights: 'Perspectivas da conta',
    signalFamilyHint_account_insights:
      'Trechos do plano estratégico ou referências ao documento completo, combinados numa única linha.',
    signalFamilyLabel_momentum: 'Atualização organizacional',
    signalFamilyHint_momentum:
      'Uma mudança, anúncio ou atualização organizacional relevante.',
    signalFamilyLabel_activity: 'Atividade pessoal',
    signalFamilyHint_activity:
      'Algo que o destinatário fez pessoalmente (palestra, artigo, post).',
    signalFamilyLabel_mandatory_signal: 'Sinal com maior pontuação',
    signalFamilyHint_mandatory_signal:
      'O sinal com a melhor pontuação para este destinatário ou o contexto do alcance.',
    signalFamilyLabel_framework_based: 'Referência a framework',
    signalFamilyHint_framework_based:
      'Ligado a uma metodologia ou framework (por exemplo, ciclo de acreditação).',
    signalFamilyLabel_trend_based: 'Tendência do setor',
    signalFamilyHint_trend_based: 'Uma tendência mais ampla do setor ou da indústria.',
    signalFamilyLabel_deal: 'Aquisições',
    signalFamilyHint_deal:
      'Atividade ligada a compras, licitações ou processos de aquisição.',
    signalFamilyLabel_case_study: 'Referência de cliente',
    signalFamilyHint_case_study: 'Referência a um caso de cliente existente.',
    signalFamilyLabel_default: 'Outro',
    signalFamilyHint_default: 'Reserva sem categoria.',
    analyticsAccount: 'Conta',
    analyticsAccountFit: 'Adequação da conta',
    analyticsPersonasSent: 'Personas enviadas',
    analyticsMultiPersonaOpens: 'Aberturas multi-persona',
    analyticsOpensPerPersona: 'Aberturas / persona',
    analyticsOpensPerPersonaSubtitle: 'aberturas totais ÷ personas',
    analyticsMultiPersonaClicks: 'Cliques multi-persona',
    collateralToShare: 'Materiais para compartilhar',
    warmUpReading: 'Leitura de aquecimento',
    sent: 'enviados',
    calls: 'ligações',
    allTiers: 'Todos os níveis',
    allChannels: 'Todos os canais',
    signalTypePerformance: 'Desempenho por tipo de sinal',
    signalTypePerformanceTooltip: 'Sinais de conta (impulso): desempenho do outreach em contas onde o sinal existia no período. Sinais de persona: desempenho do outreach para a pessoa específica do sinal. Ambos incluem mensagens de playbook e manuais.',
    noSignalDataSelectedPeriod: 'Não há dados de sinais no período selecionado',
    signalType: 'Tipo de sinal',
    touched: 'Contatadas',
    bestChannel: 'Melhor canal',
    sample: 'Amostra',
    lowSampleSize: 'Amostra pequena (<30)',
    topResponsiveAccountsFor: 'Contas com mais resposta para',
    noAccountsTouchedForSignal: 'Nenhuma conta contatada para este sinal',
    na: 'N/A',
    signalArchetypeReplyRates: 'Taxas de resposta por sinal × arquétipo',
    notEnoughDataForMatrix: 'Dados insuficientes para a matriz',
    filterByTier: 'Filtrar por nível',
    accountCommunicationsDepth: 'Profundidade de comunicação por conta',
    depthScoreTooltip: 'Pontuação de profundidade (0-100): personas (30 pts) + arquétipos (30 pts) + respostas (30 pts) + recência (10 pts)',
    noAccountDataSelectedPeriod: 'Não há dados de contas no período selecionado',
    more: 'mais',
    depth: 'Profundidade',
    archetypeCoverage: 'Cobertura de arquétipos',
    notTouched: 'Não contatado',
    replied: 'Respondeu',
    recommendations: 'Recomendações',
    goodCoverage: 'Boa cobertura!',
    recentActivity: 'Atividade recente',
    customEventPlaybook: 'Playbook de evento personalizado',
    untitledPlaybook: 'Playbook sem título',
    selectedForComparison: 'selecionados para comparação',
    playbookPerformance: 'Desempenho dos playbooks',
    noPlaybookDataSelectedPeriod: 'Não há dados de playbooks no período selecionado',
    playbook: 'Playbook',
    runs: 'Execuções',
    completionPercent: '% concluído',
    bestArchetype: 'Melhor arquétipo',
    comparePlaybooks: 'Comparar playbooks',
    completionRate: 'Taxa de conclusão',
    medianReplyTime: 'Tempo mediano de resposta',
    stepFunnel: 'Funil por etapa',
    reply: 'resposta',
    trackYourGoToMarketPerformance: 'Acompanhe o desempenho do seu go-to-market',
    viewing: 'Visualizando',
    allTeamActivity: 'Toda a atividade da equipe',
    myActivity: 'Minha atividade',
    specificTeammate: 'Um colega específico',
    chooseTeammate: 'Escolher colega',
    pipelineDeals: 'Pipeline e oportunidades',
    openPipeline: 'Pipeline aberto',
    meetingsBooked: 'Reuniões agendadas',
    dealsCreated: 'Oportunidades criadas',
    winRate: 'Taxa de sucesso',
    languagePreferenceSaved: 'Preferência de idioma salva!',
    failedToSaveLanguagePreference: 'Não foi possível salvar a preferência de idioma',
    selectPreferredLanguageDescription: 'Selecione seu idioma preferido para a interface da plataforma. Cabeçalhos, botões e elementos de navegação serão traduzidos.',
    interfaceLanguage: 'Idioma da interface',
    choosePreferredLanguage: 'Escolha seu idioma preferido para a interface da plataforma',
    selectLanguage: 'Selecionar idioma',
    languageScopeDisclaimer: 'Cabeçalhos, navegação, botões e rótulos da plataforma serão traduzidos. Nomes de contas e personas e o conteúdo dos dados permanecerão no idioma original.',
    saveLanguagePreference: 'Salvar preferência de idioma',
    failedToLoadProfile: 'Não foi possível carregar o perfil',
    profileUpdatedSuccessfully: 'Perfil atualizado com sucesso',
    failedToSaveProfile: 'Não foi possível salvar o perfil',
    pleaseSelectImageFile: 'Selecione um arquivo de imagem',
    imageSizeLessThan5MB: 'A imagem deve ter menos de 5 MB',
    profilePhotoUpdatedSuccessfully: 'Foto de perfil atualizada com sucesso',
    failedToUploadPhoto: 'Não foi possível enviar a foto',
    updatePersonalInfoAndPhoto: 'Atualize suas informações pessoais e sua foto de perfil',
    profilePhoto: 'Foto de perfil',
    profile: 'Perfil',
    updateYourProfilePicture: 'Atualize sua foto de perfil',
    uploadPhoto: 'Enviar foto',
    photoFormatAndSizeHint: 'JPG, PNG ou GIF. Tamanho máximo 5 MB.',
    personalInformation: 'Informações pessoais',
    personalInfoGreetingUsage: 'Estas informações serão usadas em saudações e comunicações',
    displayName: 'Nome de exibição',
    yourName: 'Seu nome',
    displayNameUsageHint: 'Este nome será usado em saudações e comunicações',
    jobTitle: 'Cargo',
    yourRoleOrPosition: 'Sua função ou cargo na organização',
    yourOrganization: 'Sua organização',
    usedInEmailSignatures: 'Usado em assinaturas de e-mail e comunicações formais',
    saveProfile: 'Salvar perfil',
    gmailIntegration: 'Integração com o Gmail',
    connectGmailDescription: 'Conecte sua conta do Gmail para enviar e-mails diretamente do NEO',
    loadingGmailStatus: 'Carregando status do Gmail...',
    gmailComingSoon: 'A integração com o Gmail chegará em breve. O recurso está sendo configurado.',
    gmailConnected: 'Gmail conectado',
    gmailNotConnected: 'Gmail não conectado',
    connectAccountToSendEmails: 'Conecte sua conta para enviar e-mails',
    connectGmail: 'Conectar Gmail',
    sendEmailsFrom: 'Enviar e-mails de',
    selectEmailAddress: 'Selecionar endereço de e-mail',
    loadingSignature: 'Carregando assinatura...',
    emailSignaturePreview: 'Pré-visualização da assinatura',
    whyConnectGmail: 'Por que conectar o Gmail?',
    gmailBenefitSendPersonalized: 'Envie e-mails personalizados diretamente do NEO',
    gmailBenefitTrackEngagement: 'Acompanhe aberturas e engajamento dos e-mails',
    gmailBenefitManageOutreach: 'Gerencie suas campanhas de outreach com eficiência',
    searchDrafts: 'Buscar pessoa, assunto, conta ou cargo…',
    allProducts: 'Todos os produtos',
    allPlaybooks: 'Todos os playbooks',
    noDraftsFound: 'Nenhum rascunho encontrado',
    createPlaybooksToGenerateDrafts: 'Crie playbooks para gerar rascunhos de e-mail',
    cancelDraft: 'Cancelar rascunho',
    cancelDrafts: 'Cancelar rascunhos',
    deleting: 'Excluindo...',
    deleteDraft: 'Excluir rascunho',
  

    // Auto-filled missing keys
    failedToLoadSchedule: 'Não foi possível carregar a agenda',
    scheduleSaved: 'Agenda salva',
    communicationScheduleUpdated: 'Sua agenda de comunicações foi atualizada',
    failedToSaveSchedule: 'Não foi possível salvar a agenda. Tente novamente.',
    atLeastOneWindowRequired: 'É necessária pelo menos uma janela',
    addWindowBeforeRemoving: 'Adicione uma nova janela antes de remover esta.',
    communicationSchedule: 'Agenda de comunicações',
    configureCommsSchedule: 'Configure quando o NEO pode enviar comunicações em seu nome',
    commsScheduleHelp: 'Ao executar um playbook, cada etapa (e-mail, LinkedIn, ligação) é agendada na próxima janela disponível a partir do deslocamento em dias que você definir. Fora dessas janelas, nenhuma comunicação é agendada.',
    complete: 'Completo',
    incomplete: 'Incompleto',
    availabilityWindows: 'Janelas de disponibilidade',
    addWindow: 'Adicionar janela',
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    startTime: 'Hora de início',
    endTime: 'Hora de término',
    saveSchedule: 'Salvar agenda',
    userIdMissing: 'ID do usuário ausente',
    outreachHistory: 'Histórico de outreach',
    outreachHistorySubtitle: 'Linha do tempo de todas as tarefas de mensagens concluídas pela sua equipe',
    allUsers: 'Todos os usuários',
    noOutreachHistoryPeriod: 'Nenhum histórico de outreach neste período.',
    failedToLoadEmailAddresses: 'Não foi possível carregar os endereços de e-mail',
    emailAddressUpdated: 'Endereço de e-mail atualizado',
    emailsWillBeSentFrom: 'Os e-mails serão enviados de',
    emailAddressesRefreshed: 'Endereços de e-mail atualizados',
    emailAliasesReloaded: 'Seus aliases de e-mail foram recarregados',
    signatureSaved: 'Assinatura salva',
    emailSignatureSavedSuccessfully: 'Sua assinatura de e-mail foi salva com sucesso.',
    failedToSaveSignature: 'Não foi possível salvar a assinatura. Tente novamente.',
    outlookIntegration: 'Integração com o Outlook',
    connectOutlookDescription: 'Conecte sua conta do Microsoft Outlook para enviar e-mails diretamente do NEO',
    loadingOutlookStatus: 'Carregando status do Outlook...',
    outlookComingSoon: 'A integração com o Outlook chegará em breve.',
    outlookConnected: 'Outlook conectado',
    outlookNotConnected: 'Outlook não conectado',
    connectOutlook: 'Conectar Outlook',
    emailSignature: 'Assinatura de e-mail',
    saveSignature: 'Salvar assinatura',
    clear: 'Limpar',
  

    // Auto-filled missing keys
    error: 'Erro',
    configureWorkspacePreferences: 'Configure seu espaço de trabalho e suas preferências',
    emails: 'E-mails',
    schedule: 'Agenda',
    personalization: 'Personalização',
    events: 'Eventos',
    productFit: 'Adequação do produto',
    completingGmailConnection: 'Concluindo a conexão com o Gmail...',
    authorizationFailedOrCancelled: 'A autorização falhou ou foi cancelada',
    gmailConnectedClosingWindow: 'Gmail conectado. Fechando a janela...',
    failedToConnectGmail: 'Não foi possível conectar o Gmail',
    pleaseWait: 'Aguarde...',
    windowWillCloseAutomatically: 'Esta janela será fechada automaticamente.',
    youCanCloseThisWindow: 'Você pode fechar esta janela.',
    completingOutlookConnection: 'Concluindo a conexão com o Outlook...',
    outlookConnectedClosingWindow: 'Outlook conectado. Fechando a janela...',
    failedToConnectOutlook: 'Não foi possível conectar o Outlook',
    connectionFailed: 'Falha na conexão',
    gmailDisconnected: 'Gmail desconectado',
    gmailAccountDisconnected: 'Sua conta do Gmail foi desconectada.',
    disconnectNotApplied: 'A desconexão não foi aplicada',
    backendStillReportsGmailConnected: 'O servidor ainda indica que o Gmail está conectado. Tente novamente; se persistir, provavelmente é necessária uma correção no servidor.',
    outlookDisconnected: 'Outlook desconectado',
    outlookAccountDisconnected: 'Sua conta do Outlook foi desconectada.',
    backendStillReportsOutlookConnected: 'O servidor ainda indica que o Outlook está conectado. Tente novamente; se persistir, provavelmente é necessária uma correção no servidor.',
    unsavedChanges: 'Alterações não salvas',
    howToCopyOutlookSignature: 'Como copiar sua assinatura do Outlook:',
    pasteSignatureHere: 'Cole sua assinatura aqui...',
    whyConnectOutlook: 'Por que conectar o Outlook?',
    outlookWorksWith365: 'Funciona com contas do Microsoft 365 e Outlook.com',
  

    // Auto-filled missing keys
    connectedAs: 'Conectado como',
    gmailConnectedSuccessfully: 'Gmail conectado com sucesso',
    outlookConnectedSuccessfully: 'Outlook conectado com sucesso',
    emailAddressUpdatedLocalOnly: 'Endereço de e-mail atualizado (somente local)',
    usingInSessionBackendRejectedAlias: 'Usado nesta sessão. O servidor recusou salvar preferred_outlook_alias (400):',
    copySignatureAndPasteBelow: 'Copie sua assinatura do Outlook e cole abaixo',
    outlookSignatureStep1: 'Abra o Outlook -> Arquivo -> Opções -> Email -> Assinaturas',
    outlookSignatureStep2: 'Selecione sua assinatura, pressione Ctrl+A (selecionar tudo) e depois Ctrl+C (copiar)',
    outlookSignatureStep3: 'Clique na caixa abaixo e pressione Ctrl+V (colar)',
    signatureSavedWillBeUsed: 'Assinatura salva; será usada nos seus e-mails',
  

    // Auto-filled missing keys
    preEvent: 'Antes do evento',
    dayOf: 'Dia do evento',
    postEvent: 'Depois do evento',
    dayLabel: 'Dia',
    noGoalSet: 'Nenhum objetivo definido',
    asset: 'recurso',
    dayOffset: 'Deslocamento em dias',
    negativeBeforeEvent: 'Negativo = antes do evento',
    useAiSignals: 'Usar sinais de IA',
    includeBookingLink: 'Incluir link de agendamento',
    autoInsertAvailabilityLink: 'Auto-inserts the event\'s availability link into the message body.',
    addAvailabilityLinkToEnable: 'Adicione um link de disponibilidade nas configurações do evento para ativar.',
    touchGoal: 'Objetivo do contato',
    touchGoalPlaceholder: 'O que esta mensagem deve alcançar?',
    messagingNotes: 'Notas da mensagem',
    messagingNotesPlaceholder: 'Pontos específicos a incluir, orientação de tom, o que mencionar ou evitar...',
    instructionsPassedToAiTouch: 'Estas instruções serão enviadas diretamente à IA para este contato específico',
    collateralToShare: 'Materiais para compartilhar',
    warmUpReading: 'Leitura de aquecimento',
    noAssetsConfiguredInPersonalization: 'Nenhum recurso configurado em Configurações - Personalização',
    selected: 'selecionados',
    failedToLoadEvents: 'Não foi possível carregar os eventos',
    missingRequiredFields: 'Campos obrigatórios ausentes',
    eventsSaved: 'Eventos salvos',
    eventScheduleUpdated: 'Sua agenda de eventos foi atualizada',
    eventScheduleUpdatedLocal: 'Sua agenda de eventos foi atualizada (armazenamento local)',
    saveFailed: 'Falha ao salvar',
    failedToSaveEvents: 'Não foi possível salvar os eventos',
    eventsAndConferences: 'Eventos e conferências',
    trackEventsAndConferences: 'Track events and conferences you\'re attending',
    usingLocalStorageApiUnavailable: 'Usando armazenamento local (API indisponível',
    myEvents: 'Meus eventos',
    addConferencesTradeShows: 'Adicione conferências, feiras e palestras',
    addEvent: 'Adicionar evento',
    noEventsAddedYet: 'Nenhum evento adicionado ainda',
    addEventsToEnableEventMessaging: 'Adicione eventos para ativar mensagens baseadas em eventos',
    event: 'Evento',
    collapse: 'Recolher',
    expand: 'Expandir',
    eventName: 'Nome do evento',
    eventNameExample: 'ex.: EDUCAUSE 2025',
    city: 'Cidade',
    cityExample: 'ex.: Denver',
    venue: 'Local',
    venueExample: 'ex.: Centro de convenções',
    startDate: 'Data de início',
    endDate: 'Data de término',
    boothNumber: 'Número do estande',
    boothNumberExample: 'ex.: Pavilhão A, estande 123',
    availabilityLink: 'Link de disponibilidade',
    availabilityLinkPlaceholder: 'https://calendly.com/...',
    meetingOffer: 'Proposta de reunião',
    meetingOfferExample: 'ex.: demonstração de produto de 15 minutos',
    messagingGuidelines: 'Diretrizes de mensagem',
    customInstructionsForAi: 'Instruções personalizadas para as mensagens geradas por IA em cada fase',
    preEventGuidelines: 'Diretrizes antes do evento',
    preEventGuidelinesExample: 'e.g., Mention our new AI-powered analytics launching at the event. Highlight the live demo we\'re offering.',
    infoToIncludeBeforeEvent: 'Informações a incluir nas mensagens antes do evento',
    duringEventGuidelines: 'Diretrizes durante o evento',
    duringEventGuidelinesExample: 'ex.: Ofereça demonstrações na hora. Mencione que restam poucos horários disponíveis.',
    postEventGuidelines: 'Diretrizes depois do evento',
    postEventGuidelinesExample: 'ex.: Mencione as perguntas e respostas da nossa sessão. Ofereça a gravação e os slides. Mencione o preço especial pós-evento.',
    sessionDetailsOptional: 'Detalhes da sessão',
    sessionDetailsSessionInviteNote:
      '(Session title and description are required to use the Session Invite playbook on Playbooks.)',
    ifSpeakingOrWorkshop: 'If you\'re speaking or running a workshop',
    sessionTitle: 'Título da sessão',
    sessionTitleExample: 'e.g., AI in Higher Ed: What\'s Next',
    sessionDescription: 'Descrição da sessão',
    sessionDescriptionPlaceholder: 'Breve descrição para atrair participantes...',
    postEventAssets: 'Recursos pós-evento',
    linksToShareFollowUp: 'Links para compartilhar nas mensagens de acompanhamento',
    sessionRecordingLink: 'Link da gravação da sessão',
    slidesLink: 'Link dos slides',
    postEventResourceLink: 'Recurso pós-evento (whitepaper etc.)',
    messageSequence: 'Sequência de mensagens',
    defineTouchesOutreachSequence: 'Defina os contatos da sua sequência de outreach',
    usingDefaultPlaybook: 'Usando o playbook padrão. Ative o playbook personalizado para definir sua própria sequência.',
    createCustomSequence: 'Criar sequência personalizada',
    addTouch: 'Adicionar contato',
    targetRegion: 'Região-alvo',
    targetCountries: 'Países-alvo',
    deselectAll: 'Desmarcar tudo',
    selectAllNoRestrictions: 'Selecionar tudo (sem restrições)',
    allCountriesSelectedStrong: 'Todos os países selecionados',
    noGeoRestrictions: 'Nenhuma restrição geográfica será aplicada.',
    personasAnyCountryIncluded: 'Personas de qualquer país serão incluídas.',
    fixRequiredFieldsBeforeSaving: 'Corrija os campos obrigatórios antes de salvar',
    saveEvents: 'Salvar eventos',
    urlPlaceholder: 'https://...',
  

    // Auto-filled missing keys
    settingsSavedSuccessfully: 'Configurações salvas com sucesso!',
    failedToSaveSettingsTryAgain: 'Não foi possível salvar as configurações. Tente novamente.',
    usingLocalStorageApiUnavailableShort: 'Usando armazenamento local (API indisponível)',
    languageBrandVoice: 'Idioma e voz da marca',
    configureNeoCommunicationTenant: 'Configure como o NEO se comunica para este tenant',
    english: 'Inglês',
    spanish: 'Espanhol',
    french: 'Francês',
    german: 'Alemão',
    brazilianPortuguese: 'Português do Brasil',
    arabic: 'Árabe',
    additionalStylePreferencesPlaceholder: 'Regras de estilo adicionais que substituem as padrão. Exemplos: frases curtas e diretas; sem jargões; ortografia britânica; nunca use pontos de exclamação; comece sempre com uma pergunta',
    stylePreferencesOverrideHelp: 'Estas preferências substituem as regras padrão de estilo e tom, mas não podem contornar as regras de segurança de QA (lista de URLs permitidas, frases proibidas, limites de tamanho, validação de marcadores).',
    companyProduct: 'Empresa e produto',
    customizeCompanyProductPositioning: 'Personalize como sua empresa e seu produto são posicionados. Deixe em branco para usar os padrões do tenant.',
    companyNameExample: 'ex.: Acme Learning',
    productNameExample: 'ex.: Future Skills Enrichment Programs',
    coreValuePropositionExample: 'ex.: programas de enriquecimento personalizados que engajam e inspiram os alunos',
    leaveBlankUseTenantDefaultValueProp: 'Deixe em branco para usar a proposta de valor padrão do tenant',
    keyPerformanceIndicators: 'Indicadores-chave de desempenho (KPIs)',
    defineMetricsThatMatter: 'Define metrics that matter to this tenant\'s customers',
    kpiNamePlaceholder: 'Nome do KPI (ex.: engajamento dos alunos)',
    kpiDescriptionPlaceholder: 'Descrição (ex.: aumento de 45% nos usuários ativos diários)',
    productOptional: 'Produto (opcional)',
    addKpi: 'Adicionar KPI',
    messagingHooksDescription: 'Mensagens-chave e propostas de valor por arquétipo.',
    useProductSelectorForHooks: 'Use o seletor de produto para alinhar os ganchos a produtos específicos.',
    selectPersona: 'Selecionar persona',
    universalAllPersonas: 'Universal (todas as personas)',
    champion: 'Defensor',
    influencer: 'Influenciador',
    evaluatorTechnical: 'Avaliador (técnico)',
    endUser: 'Usuário final',
    hookUsedForPersonaType: 'Este gancho só será usado para este tipo de persona',
    hookText: 'Texto do gancho',
    hookTextExample: 'ex.: enriquecimento que gera receita sem investimento de capital',
    caseStudiesClientReferences: 'Estudos de caso e referências de clientes',
    caseStudiesClientReferencesDescription: 'Adicione estudos de caso completos (com link) ou referências de clientes (apenas o nome da instituição)',
    type: 'Tipo',
    selectType: 'Selecionar tipo',
    caseStudyWithLink: 'Estudo de caso (com link)',
    clientReferenceNameOnly: 'Referência de cliente (apenas nome)',
    institutionName: 'Nome da instituição',
    institutionNameExample: 'ex.: Oundle School',
    clientReferenceUsagePrefix: 'Será usado assim: "Instituições como',
    clientReferenceUsageSuffix: 'perceberam..."',
    caseStudyTitle: 'Título do estudo de caso',
    caseStudyTitleExample: 'ex.: caso de sucesso da Universidade de Melbourne',
    caseStudyUrlPlaceholder: 'https://example.com/case-study',
    descriptionOptional: 'Descrição (opcional)',
    briefOutcomeSummary: 'Breve resumo do resultado',
    addCaseStudyClientReference: 'Adicionar estudo de caso / referência de cliente',
    usefulResourcesDocumentation: 'Recursos e documentação úteis para este tenant',
    linkNamePlaceholder: 'Nome do link (ex.: demonstração do produto)',
    linkUrlPlaceholder: 'URL (ex.: https://example.com/demo)',
    addProductLink: 'Adicionar link do produto',
    educationalResources: 'Recursos educacionais',
    educationalResourcesLead:
      'Conteúdo de aquecimento no início das cadências para construir credibilidade antes de qualquer pedido comercial.',
    educationalResourcesExamples:
      'Exemplos: artigos de frameworks, guias de autoridade de terceiros, conteúdos sobre «como é o bom».',
    resourceTitle: 'Título',
    educationalResourceTitlePlaceholder:
      'ex.: Strategic Considerations for a Successful Student Survey Tool',
    educationalResourceUrlPlaceholder: 'https://example.com/article',
    addEducationalResource: '+ Adicionar recurso educacional',
    invalidHttpUrl: 'Insira uma URL http(s) válida',
    educationalResourceTitleTooLong: 'O título deve ter no máximo 200 caracteres',
    educationalProductContextUnset: 'Não definido (equivale a ALL)',
    selectResources: 'Selecionar recursos…',
    noEducationalResourcesYet:
      'Ainda sem recursos — adicione em Configurações → Recursos Educativos',
    educationalResourcesHelp:
      'Opcional. Escolha os artigos/vídeos a referenciar neste toque. Vazio = o motor escolhe.',
    saveSettings: 'Salvar configurações',
    eg: 'e.g.,',
  

    // Auto-filled missing keys
    productFitNameExample: 'ex.: LMS Core',
    productFitCategoryTagsExample: 'LMS, EdTech, Avaliação',
    productFitStrongSignalsExample: 'Integração com Canvas obrigatória\nSuporte a LTI 1.3\nConformidade com SCORM',
    productFitNegativeSignalsExample: 'Obrigatório: deve usar Microsoft SSO\nPreferência: prefere implantação em nuvem',
    productFitIntegrationsExample: 'Canvas, Moodle, LTI 1.3',
    productFitSecurityExample: 'ISO 27001, SOC 2',
    productFitKeyFeaturesExample: 'Análises, app móvel, acessibilidade',
    // [2026-10-29] i18n audit: keys added
    financialSituation: 'Situação financeira',
    marketConditions: 'Condições de mercado',
    sessionInviteBlockedTitle: 'Detalhes da sessão necessários para o convite',
    openEventsSettings: 'Abrir configurações de eventos',
    sessionInviteReady: 'Título e descrição da sessão configurados',
    quickConnectRequiresAttendees: 'Requer pelo menos um participante confirmado',
    myDeals: 'Minhas oportunidades',
    routeToBuy: 'Rota de compra',
    productFitCategories: 'Categorias',
    productFitCategoriesDesc: 'A mesma lista de categorias de Aquisições para o seu tenant (de oportunidades abertas e encerradas).',
    productFitCategoriesLoading: 'Carregando categorias de aquisições…',
    productFitCategoriesFallback: 'Não foi possível carregar as categorias de aquisições; exibindo a taxonomia completa. Salve novamente após Aquisições carregar ou verifique a API.',
    productFitNoCategoriesSelected: 'Nenhuma categoria selecionada: o perfil não corresponderá a oportunidades por categoria.',
    editSignatureBelowOrPullFromGmail: 'Edite sua assinatura abaixo ou importe a atual da sua conta do Gmail.',
    pasteOrTypeSignatureHere: 'Cole sua assinatura aqui ou digite-a diretamente.',
    useGmailSignature: 'Usar a assinatura do Gmail',
    noGmailSignatureFound: 'Nenhuma assinatura do Gmail encontrada',
    selectAliasFirstOrSetInGmail: 'Selecione primeiro um alias ou defina uma assinatura nas configurações do Gmail.',
    writeInNativeLanguage: 'Escrever no idioma de cada país',
    writeInNativeLanguageHelp: 'Desativado por padrão: todas as mensagens saem em inglês. Quando ativado, cada mensagem é escrita no idioma do país da conta destinatária (português, espanhol ou francês; inglês nos demais).',
    sessionInviteEventBanner: 'O convite para a sessão precisa de um título e uma descrição da sessão para o evento selecionado. Edite o evento em Configurações → Eventos e preencha os detalhes da sessão.',
    sessionInviteMissingDetailsTip: 'Dica: este evento ainda não tem detalhes da sessão. As mensagens mencionarão o evento de forma genérica; adicione um título de sessão em Configurações → Eventos para convites mais eficazes.',
    sessionInviteRequiresSessionDetails: 'Requer título e descrição da sessão em Configurações → Eventos',
    status: 'Status',
    replies: 'Respostas',
    brandVoiceDescription: 'Descrição da voz da marca',
    brandVoiceDescriptionPlaceholder: 'Descreva como sua marca deve soar, ex.: acolhedora, especialista, concisa',
    additionalStylePreferencesOverride: 'Preferências de estilo adicionais (substituição)',
    companyName: 'Nome da empresa',
    coreValueProposition: 'Proposta de valor principal',
    messagingHooks: 'Ganchos de mensagem',
    targetPersona: 'Persona-alvo',
    economicBuyer: 'Comprador econômico',
    addMessagingHook: 'Adicionar gancho de mensagem',
    url: 'URL',
    productLinks: 'Links do produto',
    timezone: 'Fuso horário',
    caseStudy: 'estudo de caso',
    customPlaybook: 'Playbook personalizado',
    // [2026-10-29] i18n: UI chrome keys (market / procurement / account / playbooks)
    mkt12w: '12 sem',
    mkt4w: '4 sem',
    mktAboutEnrichment: 'Sobre o enriquecimento',
    mktAboutLabel: 'Sobre {label}',
    mktAccountBarometer: 'Barômetro de contas',
    mktAccountPageNotFound: 'Página da conta não encontrada',
    mktAccountsAffected: 'Contas afetadas',
    mktAccountsAffectedTip: 'Número de contas-alvo em que pelo menos um sinal menciona este concorrente. Cada conta é contada uma vez, mesmo com vários sinais.',
    mktAccountsExaminedOf: '{examined} de {total} contas analisadas',
    mktAccountsInMotion: 'Contas em movimento',
    mktAccountsInMotionHint: 'Instituições cujos sinais estão se movendo. Marcadas como “em movimento”, a menos que haja evidência explícita de compra.',
    mktAcrossNCountries: 'em {n} países',
    mktAcrossNProgrammes: 'em {n} programas',
    mktAdd: 'Adicionar',
    mktAddPastedVendors: 'Adicionar fornecedores colados',
    mktAddToAccounts: 'Adicionar às Contas',
    mktAddToPilot: 'Adicionar ao piloto',
    mktAddedLoadingNow: 'As pessoas, os sinais e o histórico estão carregando agora.',
    mktAddedToAccounts: 'Adicionada às contas',
    mktAddedToYourAccounts: 'Adicionada às suas contas',
    mktAdding: 'Adicionando…',
    mktAddressable: 'Endereçável',
    mktAdjacent: 'Adjacente',
    mktAdjacentMarket: 'Mercado adjacente',
    mktAdjacentPlayer: 'Player adjacente',
    mktAdjacentVendor: 'Fornecedor adjacente',
    mktAfter: 'Depois',
    mktAiDef1: 'Explorando. Nada publicado e ainda sem sinais de uso de ferramentas de IA.',
    mktAiDef2: 'Em piloto. Há algo em andamento, sem uma política que o regule.',
    mktAiDef3: 'Política em vigor. Publicada e aprovada, mas não vemos uso efetivo.',
    mktAiDef4: 'Adoção regulada. Há evidências tanto da política quanto do uso real.',
    mktAiDef5: 'Integrada. A IA faz parte de como ensinam e avaliam.',
    mktAiEmbedded: 'Integrada',
    mktAiExploring: 'Explorando',
    mktAiGovernedAdoption: 'Adoção regulada',
    mktAiPiloting: 'Em piloto',
    mktAiPolicyInPlace: 'Política em vigor',
    mktAiReadiness: 'Prontidão para IA',
    mktAiReadinessBelow: 'Prontidão para IA abaixo de',
    mktAiReadinessIndex: 'Índice de prontidão para IA',
    mktAiReadinessWholeMarketHint: 'Prontidão para IA em todo o mercado, não apenas nas suas oportunidades.',
    mktAiScale: 'Avaliamos duas coisas, a maturidade da política e a profundidade da adoção de ferramentas, e consideramos a mais alta. Os níveis 4 e 5 exigem as duas; uma política sozinha para no 3. O que não vimos confirmado em 18 meses cai um nível.',
    mktAllCategories: 'Todas as categorias',
    mktAllCountries: 'Todos os países',
    mktAllEntitledCountries: 'Todos os países habilitados',
    mktAllProgrammes: 'Todos os programas ({n})',
    mktAlreadyFunded: 'Já financiadas',
    mktAlreadyOnYourList: 'Já está na sua lista',
    mktAlsoPriority: 'Também prioritárias',
    mktAlsoPriorityHint: 'Contas elegíveis que já são prioridade no seu mercado.',
    mktAmount: 'Valor',
    mktAnnualRunRate: 'Valor anual {annual}.',
    mktAwardCeiling: 'Valor máximo',
    mktAwardCeilingHint: 'A faixa que um único financiamento pode alcançar, nas palavras do financiador.',
    mktBackOnTeamSettings: 'De volta às configurações da equipe.',
    mktBandCapacity: 'capacidade {band}',
    mktBarometerScore: 'Pontuação do barômetro',
    mktBarometerScoreAccountTip: 'Proximidade do concorrente de 0 a 100. Calculada como força do sinal × decaimento por recência. Mais alto = presença mais forte do concorrente nesta conta. A direção (favorável/desfavorável) aparece separadamente em Impacto.',
    mktBarometerScorePersonaTip: 'Proximidade do concorrente de 0 a 100 para esta persona. Calculada como força do sinal × decaimento por recência. Mais alto = presença mais forte do concorrente. A direção aparece separadamente em Impacto.',
    mktBarrier: 'Barreira',
    mktBefore: 'Antes',
    mktBuyingEvidence: 'Evidência de compra',
    mktBuyingSignals: 'Sinais de compra',
    mktByCountryN: 'Por país ({n})',
    mktCandidates: 'Candidatas',
    mktChangeAFilterFirst: 'Altere um filtro primeiro.',
    mktChooseProductCategory: 'Escolha uma categoria de produto',
    mktCloseCountryPanel: 'Fechar painel do país',
    mktClosingSoon: 'Encerram em breve',
    mktClosingSoonHint: 'Chamadas abertas com prazo nos próximos 45 dias.',
    mktCompetitor: 'Concorrente',
    mktCompetitorLocked: 'Concorrente bloqueado: nunca substituir',
    mktCompetitorLockedHint: 'Instituições que usam esses fornecedores ficam fora das suas listas de oportunidades. Continuam na lista de concorrentes; você apenas não tenta conquistá-las.',
    mktCompetitorSet: 'Concorrentes',
    mktCompetitorSetHint: 'Se um fornecedor está nesta lista, está presente em uma instituição e algo está acontecendo lá, é uma oportunidade de substituição. Deixe-o de fora e a mesma conta será tratada como coexistência. Um nome por fornecedor basta; nós resolvemos as variantes.',
    mktCompetitorView: 'Visão por concorrente',
    mktCompetitorsPresent: 'Concorrentes presentes',
    mktConfirmEmptyCompetitorSet: 'Sem ninguém nesta lista, nada pode ser uma substituição. Seu mercado ficará restrito a contas sem solução atual. Salvar mesmo assim?',
    mktContextOnly: 'Apenas contexto',
    mktContractScope: 'Abrangência do contrato',
    mktContractScopeUnknownHint: 'Ninguém determinou isso ainda. Não significa que a implantação seja pequena, então deixamos fora da pontuação em vez de penalizar.',
    mktCouldNotAddAccount: 'Não foi possível adicionar a conta',
    mktCouldNotAddThisAccount: 'Não foi possível adicionar esta conta',
    mktCouldNotCompleteAction: 'Não foi possível concluir a ação',
    mktCouldNotLoadAccountsInMotion: 'Não foi possível carregar as contas em movimento',
    mktCouldNotLoadCategory: 'Não foi possível carregar esta categoria',
    mktCouldNotLoadCompetitorData: 'Não foi possível carregar os dados de concorrentes. Tente novamente.',
    mktCouldNotLoadFunding: 'Não foi possível carregar o financiamento',
    mktCouldNotLoadIgnored: 'Não foi possível carregar as contas ignoradas',
    mktCouldNotLoadMarketData: 'Não foi possível carregar os dados do mercado. Atualize a página.',
    mktCouldNotLoadOpportunities: 'Não foi possível carregar as oportunidades',
    mktCouldNotLoadPlayers: 'Não foi possível carregar os players',
    mktCouldNotLoadPreferences: 'Não foi possível carregar as preferências',
    mktCouldNotLoadRenewalRisk: 'Não foi possível carregar o risco de renovação',
    mktCouldNotLoadSummary: 'Não foi possível carregar o resumo do mercado',
    mktCouldNotMoveAccountBack: 'Não foi possível devolver a conta',
    mktCouldNotOpenAccount: 'Não foi possível abrir a conta',
    mktCouldNotRemoveAccount: 'Não foi possível remover a conta.',
    mktCouldNotResolveAccount: 'Ainda não foi possível localizar esta conta na sua lista de Contas.',
    mktCouldNotSave: 'Não foi possível salvar',
    mktCouldNotSwitch: 'Não foi possível alternar',
    mktCountries: 'Países',
    mktCoverage: 'Cobertura',
    mktCurrentVersion: 'atual',
    mktDeadlineSpendBy: 'Prazo / gastar até',
    mktDisplacement: 'Substituição',
    mktDisqualifiers: 'Critérios de exclusão',
    mktEligibleAccounts: 'Contas elegíveis',
    mktEligibleSpend: 'Gasto elegível',
    mktEmptyCompetitorSetWarning: 'Sem ninguém nesta lista, nada pode ser uma substituição. Seu mercado ficará restrito a contas sem solução atual.',
    mktEnrichRequestToast: 'Vamos processar sua solicitação em até 48 horas e avisar quando estiver pronta.',
    mktEnrichTooltip: 'Envia esta conta para a equipe de pesquisa preencher as lacunas: quem usam, com que abrangência, quando o contrato termina e se estão satisfeitos. Sua pontuação não muda hoje. Quando a pesquisa voltar, a conta será repontuada e você verá em O que mudou.',
    mktEnrichmentRequested: 'Enriquecimento solicitado',
    mktEnrichmentRequestedOn: 'Solicitado em {date}. Vamos repontuar esta conta quando a pesquisa voltar.',
    mktEstSpendBy: 'gastar até (est.)',
    mktEstimatedValue: 'Valor estimado',
    mktEvidence: 'Evidência',
    mktExaminedOf: '{examined} de {total} analisadas',
    mktExaminedSuffix: ' analisadas',
    mktExcludeCurrentCustomers: 'Excluir clientes atuais',
    mktExistingCustomer: 'Cliente atual',
    mktExistingExpansion: 'Atuais / expansão',
    mktExpansion: 'Expansão',
    mktFavourable: 'Favorável',
    mktFavourableDisplacementOpps: 'Oportunidades de substituição favoráveis',
    mktFinancialBandBelow: 'Faixa financeira abaixo de',
    mktFinancialBandBelowHint: 'Margem estimada para financiar novas iniciativas, com base no gasto operacional e nas reservas por estudante e na margem operacional, comparada com pares nacionais do mesmo setor. Alta significa mais espaço para investir; Baixa pode indicar pressão orçamentária. Reflete capacidade de gasto, não orçamento já reservado para software.',
    mktFinancialCapacity: 'Capacidade financeira',
    mktFinancialWholeMarketHint: 'Capacidade financeira em todo o mercado. Quanta margem as instituições têm para financiar algo novo, não quanto gastam com EdTech.',
    mktFitAddUnscoredFallback: 'Esta conta está nas suas Contas, mas o Mercado não tinha pontuação para ela.',
    mktFlat: 'Estável',
    mktFullAssessment: 'Avaliação completa',
    mktFunder: 'Financiador',
    mktFunding: 'Financiamento',
    mktFundingAccountsHint: 'Contas que este financiamento pode alcançar. Como abordar consulta o NEO com o programa, o valor e a elegibilidade daquela linha.',
    mktFundingEligibility: 'Elegibilidade para financiamento',
    mktFundingEligible: 'Elegíveis',
    mktFundingEligibleHint: 'Contas que podem usar este financiamento na sua categoria, do total de contas deste mercado.',
    mktFundingNotSwitchedOn: 'Financiamento ainda não está ativado para este cliente.',
    mktGapClientsOnly: 'Na sua lista de clientes. Não confirmamos qual produto usam.',
    mktGapStackOnly: 'Usa seu produto segundo o registro de fornecedores, mas não está na sua lista de clientes.',
    mktGradeDocumented: 'Documentado',
    mktGradeDocumentedHint: 'A instituição publicou uma falha específica no produto do fornecedor, geralmente em uma declaração de acessibilidade.',
    mktGradeExplicit: 'Explícito',
    mktGradeExplicitHint: 'Um órgão de governança, senado, comitê ou entidade estudantil registrou a reclamação oficialmente.',
    mktGradeImplied: 'Implícito',
    mktGradeImpliedHint: 'Encontramos problemas no fluxo de trabalho que este fornecedor atende, mas ninguém disse que quer trocar.',
    mktGradeReported: 'Relatado',
    mktGradeReportedHint: 'A reclamação está publicada em algum lugar, mas não em uma reunião onde se tomam decisões.',
    mktGradeTiming: 'Prazo',
    mktGradeTimingHint: 'O contrato vence dentro da sua janela de renovação, então volta a licitação independentemente do que pensam do fornecedor.',
    mktGreenfield: 'Sem solução atual',
    mktGreenfieldAdjacent: 'Sem solução / adjacentes',
    mktHasOpportunities: 'Tem oportunidades',
    mktHeld: 'Em espera',
    mktHeldHint: 'Em espera não é excluída. Aguardamos a contagem completa de estudantes antes de aplicar seu mínimo, em vez de descartar uma conta com um número parcial.',
    mktHideCountryDetail: 'Ocultar detalhe por país',
    mktHideOlderEvents: 'Ocultar eventos antigos e de outras categorias',
    mktHighProximityAccounts: 'Contas de alta proximidade',
    mktHighestInYourMarket: 'Mais altas do seu mercado: {names}',
    mktHowMuchWeHaveLookedAt: 'Quanto analisamos',
    mktHowToPitch: 'Como abordar →',
    mktIgnore: 'Ignoradas',
    mktIgnoreAccount: 'Ignorar conta',
    mktIgnoreHint: 'Contas que verificamos e deixamos de lado. Não é o mesmo que Restante do mercado: essas são contas que ninguém analisou ainda.',
    mktIgnoreNotSwitchedOn: 'Esta lista mostra contas que verificamos e deixamos de lado, não contas que ninguém analisou. Ainda não está ativada para este cliente.',
    mktImpactAccountTip: 'Se a evidência sobre o concorrente nesta conta é ruim para você (Desfavorável), boa para você (Favorável, ex.: insatisfação ou oportunidade de substituição), Mista ou Apenas contexto.',
    mktImpactPersonaTip: 'Se a evidência sobre o concorrente para esta persona é ruim para você (Desfavorável), boa para você (Favorável), Mista ou Apenas contexto.',
    mktInHouse: 'Interno',
    mktInOurFavour: 'A nosso favor',
    mktInPilot: 'No piloto',
    mktInPlay: 'Em jogo',
    mktInScope: '({label})',
    mktInThisMarket: 'neste mercado',
    mktInYourMarket: 'No seu mercado',
    mktIncumbent: 'Fornecedor atual',
    mktIncumbentSentimentLine: 'Percepção do fornecedor atual: {sentiment}',
    mktInstitutionShare: 'Proporção de instituições',
    mktInstitutions: 'Instituições',
    mktKeySectorTrends: 'Principais tendências do setor',
    mktLastRefreshed: 'Última atualização',
    mktLatestSignal: 'Último sinal',
    mktLatestSignalAccountTip: 'Data do sinal de concorrente mais recente nesta conta.',
    mktLatestSignalPersonaTip: 'Data do sinal de concorrente mais recente para esta persona.',
    mktLatestUpdate: 'Última atualização',
    mktLatestUpdateTip: 'Resumo da evidência mais recente entre todos os sinais deste concorrente.',
    mktLeaving: 'Saem: {names}',
    mktLens: 'Visão',
    mktLimited: 'Limitada',
    mktLiveSignal: 'Sinal ativo',
    mktLmsChipTitle: 'Não é uma categoria em que você vende. Está aqui porque é onde todo o resto se conecta.',
    mktLmsFamiliesNote: 'Apenas para sua visão. Nomes de família, não nomes brutos de fornecedor: Open LMS e Totara são separados do Moodle.',
    mktLmsShareHint: 'A proporção de instituições que usam cada LMS. É uma contagem de instituições, não de receita.',
    mktLoadingEllipsis: 'Carregando…',
    mktLockPastedVendors: 'Bloquear fornecedores colados',
    mktMainThemes: 'Temas principais',
    mktMainThemesTip: 'Temas extraídos das tags Themes: dos sinais e das tags de tema das atualizações de mercado. Descrevem sobre o que o concorrente fala, não a força do sinal.',
    mktMapTipEmpty: '{label}. Carregado, mas nada se qualifica nesta categoria.',
    mktMapTipNOpportunities: '{label}. {count} oportunidades.',
    mktMapTipNotLoaded: '{label}. Ainda não carregamos este mercado.',
    mktMapTipOneOpportunity: '{label}. {count} oportunidade.',
    mktMarkRead: 'Marcar como lido',
    mktMarketEvents: 'Eventos de mercado',
    mktMarketEventsTip: 'Número de atividades de mercado atribuíveis a este concorrente: eventos regionais, adoções externas e outros movimentos não ligados às suas contas-alvo.',
    mktMarketIsRescoring: 'A pontuação do mercado está sendo recalculada. Você pode continuar nesta visão.',
    mktMarketOpportunity: 'Oportunidade de mercado',
    mktMarketPreferences: 'Preferências de mercado',
    mktMarketRecomputed: 'Mercado recalculado.',
    mktMarketScore: 'Pontuação de mercado',
    mktMarketSummary: 'Resumo do mercado',
    mktMarketTracker: 'Acompanhamento do mercado',
    mktMatchesForms: 'Corresponde a {forms}',
    mktMatching: 'Buscando correspondências…',
    mktMaximumEnrolment: 'Matrículas máximas',
    mktMinimumEnrolment: 'Matrículas mínimas',
    mktMinimumEnrolmentHint: 'Quando temos apenas uma contagem parcial de estudantes, mantemos a conta em espera em vez de descartá-la, para que um número parcial nunca exclua uma instituição grande.',
    mktMinimumPain: 'Problema mínimo',
    mktMinimumPainHint: 'Pontuado de 0 a 5. Com que clareza um problema precisa ser declarado para que a conta conte como parte do seu mercado. 2 é uma menção de passagem ou a intenção de corrigir algo. 3 é uma falha declarada. 4 é um defeito específico, claramente descrito. 5 é uma falha com uma consequência explícita, como uma violação de conformidade. A maioria das equipes usa 3; passar para 4 reduz a evidência válida em cerca de 40%.',
    mktMix: 'Composição',
    mktMixed: 'Misto',
    mktMoveBackToMyList: 'Voltar para minha lista',
    mktMovedBackToYourList: 'Voltou para sua lista',
    mktMovedToIgnore: 'Movida para Ignoradas',
    mktMoving: 'Movendo…',
    mktNAccounts: '{n} contas',
    mktNAccountsExcluded: '{n} contas excluídas',
    mktNCategories: '{n} categorias',
    mktNDays: '{n} dias',
    mktNHaveWonBefore: '{n} já foram contempladas',
    mktNHeldPendingEnrolment: '{n} aguardando um número de matrículas',
    mktNInMarket: '{n} no mercado',
    mktNInYourMarket: '{n} no seu mercado',
    mktNLmsCells: '{n} células de LMS',
    mktNMarketEvents: '{n} eventos de mercado',
    mktNMatchingQuery: '{n} correspondem a “{query}”',
    mktNNewSignals: '{n} sinais novos',
    mktNOfTotalSetAside: '{n} de {total} contas deixadas de lado',
    mktNPersonas: '{n} personas',
    mktNSelected: '{n} selecionados',
    mktNSetAside: '{n} contas deixadas de lado',
    mktNSignals: '{n} sinais',
    mktNamesYourCategories: 'Cita suas categorias ({n})',
    mktNeoDidNotSayWhy: 'O NEO não informou o motivo.',
    mktNeoView: 'Visão do NEO',
    mktNetImpact: 'Impacto líquido',
    mktNetImpactTip: 'Se a evidência sobre o concorrente é ruim para você (Desfavorável), boa para você (Favorável, ex.: insatisfação ou oportunidade de substituição), Mista ou Apenas contexto (informativa, sem direção clara).',
    mktNewSignals90d: 'Novos sinais (últimos 90 dias)',
    mktNewSignals90dTip: 'Sinais datados dos últimos 90 dias.',
    mktNo: 'Não',
    mktNoAccountsInGroup: 'Nenhuma conta neste grupo no momento.',
    mktNoAccountsInMotionFor: 'Nenhuma conta em movimento para {scope}.',
    mktNoAccountsInView: 'Nenhuma conta nesta visão.',
    mktNoAccountsMatchQuery: 'Nenhuma conta corresponde a “{query}”.',
    mktNoAccountsMatchSearch: 'Nenhuma conta corresponde a esta pesquisa.',
    mktNoAccountsSetAside: 'Ainda não há contas deixadas de lado neste mercado.',
    mktNoAccountsWithReason: 'Nenhuma conta com este motivo no filtro atual.',
    mktNoAiCompositionFor: 'Sem distribuição de prontidão para IA para {scope}.',
    mktNoAtRisk: 'Ainda não há contas com risco conhecido nesta visão.',
    mktNoAtRiskForCountry: 'Ainda não há contas com risco conhecido nesta visão para {country}.',
    mktNoCachedDataWait20: 'Nenhum dado em cache disponível. Aguarde 20 minutos antes de atualizar.',
    mktNoCompetitorEvidence: 'Ainda não há evidências de concorrentes carregadas.',
    mktNoFinancialCompositionFor: 'Sem distribuição de capacidade financeira para {scope}.',
    mktNoLmsDataFor: 'Sem dados de fornecedores de LMS para {scope}',
    mktNoPriorVersions: 'Nenhuma versão anterior.',
    mktNoProgrammesInView: 'Nenhum programa nesta visão.',
    mktNoRecentChanges: 'Nenhuma mudança recente no mercado.',
    mktNoRenewalEvidence: 'Ainda não há evidência de renovação registrada.',
    mktNoSectorValues: 'As contas ainda não têm valores de setor.',
    mktNoVendorDataForCategory: 'Sem dados de fornecedores para esta categoria',
    mktNotAnOpportunity: 'Não é uma oportunidade',
    mktNotLoadedYet: 'Ainda não carregado',
    mktNothingChangedAlreadyThere: 'Nada mudou: ela já estava lá.',
    mktNothingClassifiedYet: 'Ainda não há nada classificado nesta visão.',
    mktNothingInListYet: 'Ainda não há nada nesta lista.',
    mktNothingLocked: 'Nada bloqueado. Todos os fornecedores da sua lista de concorrentes estão incluídos.',
    mktNothingToPreview: 'Ainda não há nada para exibir desta conta.',
    mktNothingToPreviewTitle: 'Nada para pré-visualizar',
    mktOfN: 'de {n}',
    mktOfSeparator: ' de ',
    mktOneAccount: '{n} conta',
    mktOneMarketEvent: '{n} evento de mercado',
    mktOneNewSignal: '{n} sinal novo',
    mktOnePersona: '{n} persona',
    mktOneSignal: '{n} sinal',
    mktOpenRound: 'Chamada aberta',
    mktOpenRounds: 'Chamadas abertas',
    mktOpenSalesMap: 'Abrir SalesMap',
    mktOpenSetup: 'Abrir configuração',
    mktOpening: 'Abrindo…',
    mktOpportunities: 'Oportunidades',
    mktOpportunity: 'Oportunidade',
    mktOtherN: 'Outros ({n})',
    mktOurCategories: 'Nossas categorias',
    mktOurCategoriesLine: 'Nossas categorias: {categories}',
    mktOurs: 'Nosso',
    mktPageNOfM: 'Página {n} de {m}',
    mktPageSubtitle: 'Sua oportunidade de mercado personalizada e a inteligência GTM mais recente.',
    mktPasteVendorsPlaceholder: 'Cole fornecedores, um por linha ou separados por vírgula',
    mktPasteVendorsToLockPlaceholder: 'Cole os fornecedores a bloquear, um por linha ou separados por vírgula',
    mktPersona: 'Persona',
    mktPersonaBarometer: 'Barômetro de personas',
    mktPersonasAffected: 'Personas afetadas',
    mktPersonasAffectedTip: 'Número de personas-alvo com evidência direta de concorrentes: citadas, marcadas, nomeadas em um estudo de caso ou abordadas por um concorrente. Personas ligadas à conta, mas não citadas pessoalmente, não contam.',
    mktPersonasWithCompetitorLinks: 'Personas com vínculos a concorrentes',
    mktPlayersChartTooltip: 'A proporção de instituições do seu mercado que usam cada fornecedor nesta categoria. É uma contagem de instituições, não de receita. As proporções podem somar mais de 100% porque uma conta pode usar mais de um fornecedor.',
    mktPleaseTryAgain: 'Tente novamente',
    mktPreferences: 'Preferências',
    mktPreferencesSaved: 'Preferências salvas',
    mktPrefsIntro: 'Defina as regras que decidem o que conta como oportunidade.',
    mktPrefsOnOwnSettings: 'Você está usando suas próprias configurações. As da equipe são diferentes.',
    mktPrefsOnTeamSettings: 'Você está usando as configurações da equipe. Ao salvar, serão criadas as suas.',
    mktPreviewChange: 'Pré-visualizar alteração',
    mktPreviewCounts: 'No seu mercado {sam} · Prioridade {priority} · Excluídas {excluded}',
    mktPreviewFailed: 'Falha na pré-visualização',
    mktPreviewMoved: 'Entram {in} · saem {out} · sobem {promoted} · descem {demoted}',
    mktPreviewing: 'Gerando pré-visualização…',
    mktPreviouslyFunded: 'Financiamento anterior',
    mktPriority: 'Prioridade',
    mktPriorityScoreThreshold: 'Limite de pontuação de prioridade',
    mktPriorityScoreThresholdHint: 'A pontuação que uma conta precisa para ser marcada como Prioridade. Combina quão bem a instituição se encaixa no que você vende com quanto está de fato acontecendo lá. Perto de 60 você obtém instituições com o perfil certo mesmo que nada esteja se movendo; perto de 70 a maioria também terá um problema declarado; a partir de 75 ambos são fortes e a lista é curta. Muda por onde você começa, não quem está no seu mercado.',
    mktProductCategory: 'Categoria de produto',
    mktProgramme: 'Programa',
    mktProgrammes: 'Programas',
    mktProgrammesHint: 'Abre com os programas que citam uma das suas categorias; as outras duas visões ampliam a seleção. Um programa com 0 contas elegíveis também aparece: essa lacuna é o ponto. O prazo só aparece quando a chamada está aberta; um programa ativo mostra a data até a qual o dinheiro alocado deve ser gasto, e não é algo a que se candidata. Pré-anunciado significa acompanhar, não que encerra em breve.',
    mktProvisional: 'Provisório',
    mktQueueIntroAdjacent: 'Mostra fornecedores já presentes com os quais você não concorre, ajudando a entender o ecossistema tecnológico atual e onde sua solução pode se encaixar.',
    mktQueueIntroInMarket: 'Oportunidades reais em instituições com menor adequação. Vale trabalhar depois de passar pela Prioridade.',
    mktQueueIntroPriority: 'Suas melhores oportunidades agora: sem solução atual, substituição ou expansão.',
    mktQueueIntroRest: 'O restante do mercado para o qual você poderia vender. Falta alguma informação para avaliá-las. As de melhor adequação aparecem primeiro.',
    mktRangeOfTotal: '{from}–{to} de {total}',
    mktRareEvent: 'Evento raro',
    mktRareEventMeans: 'Um evento raro e relevante elevou o eixo de atividade atual nesta pontuação.',
    mktRateLimitActive: '⏸️ Limite de requisições ativo',
    mktRateLimitReached: '⚠️ Limite de requisições atingido',
    mktRecalculateFailed: 'Falha no recálculo',
    mktRecalculateMarket: 'Recalcular mercado',
    mktRecalculated: 'Recalculado',
    mktRecalculating: 'Recalculando…',
    mktRecalculationComplete: 'Recálculo concluído',
    mktRecency: 'Recência',
    mktRecommendation: 'Recomendação',
    mktRecommendationTip: 'Abre o SalesMap desta conta. Quando a proximidade chega a 50, uma análise competitiva é sincronizada automaticamente com a linha current_vendor do SalesMap.',
    mktRecomputedCounts: 'No seu mercado {sam} · Prioridade {priority}.',
    mktRelationship: 'Relacionamento',
    mktRemove: 'Remover',
    mktRemoveSkipNotFromMarket: 'Esta conta não foi adicionada pelo Mercado. Use Desvincular.',
    mktRemoveSkipNotInAccounts: 'Essa conta não está na sua lista.',
    mktRenewalDate: 'Data de renovação',
    mktRenewalNotSwitchedOn: 'Esta aba cobre as instituições que já usam seu produto, então responde quem você pode perder em vez de quem pode conquistar. Ainda não está ativada para este cliente.',
    mktRenewalOpen: 'Renovação aberta',
    mktRenewalWindow: 'Janela de renovação',
    mktReplacingInHouse: 'Substituindo solução interna',
    mktRequestEnrichment: 'Solicitar enriquecimento',
    mktRequesting: 'Solicitando…',
    mktRescoringTheMarket: 'Recalculando a pontuação do mercado',
    mktRestOfMarket: 'Restante do mercado',
    mktRestoredToAccounts: 'Restaurada em contas',
    mktReturnsOn: 'Volta para sua lista em {date}',
    mktRisk: 'Risco',
    mktRiskConfirmed: 'Risco confirmado',
    mktRiskConfirmedMeans: 'Disseram que estão insatisfeitos conosco, ou o contrato termina dentro do horizonte.',
    mktRiskHint: 'Fatos concretos sobre o que está dando errado (uma reclamação, insatisfação, uma falha publicada do produto ou o fim de um contrato), não uma pontuação. A cor ainda distingue confirmado de observar.',
    mktRiskLookedNothingFound: 'Verificado · nada encontrado',
    mktRiskNotYetChecked: 'Ainda não verificado',
    mktRiskQuietMeans: 'Verificamos e não encontramos nada. É uma resposta real, não uma lacuna.',
    mktRiskUnknownMeans: 'Ninguém analisou esta conta. Não é o mesmo que ela estar tranquila.',
    mktRiskWatch: 'Observar',
    mktRiskWatchMeans: 'Há uma falha publicada do nosso produto aqui, ou um concorrente entrou em uma categoria vizinha.',
    mktRollBack: 'Reverter',
    mktRubricFitHint: 'Quão bem uma instituição combina com o que você vende: o que usa hoje e quão substituível é, seu porte e quão pronta parece para mudar. Aumente para favorecer contas com o perfil certo mesmo quando nada está acontecendo ainda.',
    mktRubricHistory: 'Histórico da rubrica',
    mktRubricSignalsHint: 'Sinais de que estão de fato se movendo: um problema declarado com o que usam hoje, além de compras, orçamento, uma renovação próxima ou uma nova adjudicação. Aumente para priorizar o que está acontecendo em vez de quem parece certo no papel.',
    mktRubricWeights: 'Pesos da rubrica',
    mktRunningAgainstUs: 'Contra nós',
    mktSaveForEveryone: 'Salvar para todos',
    mktSaveForMe: 'Salvar para mim',
    mktSavedRescoreFailed: 'Salvo, mas o recálculo falhou',
    mktSaving: 'Salvando…',
    mktScore: 'Pontuação',
    mktScoresMayReflectPrevious: 'As pontuações ainda podem refletir as configurações anteriores.',
    mktSearchAccounts: 'Pesquisar contas',
    mktSearchAccountsByName: 'Pesquisar contas por nome',
    mktSearchByAccountName: 'Pesquisar por nome da conta',
    mktSectorExclude: 'Excluir setores',
    mktSelectSectors: 'Selecionar setores',
    mktSectors: 'Setores',
    mktSectorsHint: 'Os tipos de instituição que formam o seu mercado. Escolha um ou vários.',
    mktNOfMSelected: '{n} de {m} selecionados',
    mktSelectAll: 'Selecionar tudo',
    mktClearAll: 'Limpar',
    mktChooseOneSector: 'Escolha pelo menos um setor.',
    mktRescoreStillRunning: 'Ainda a recalcular',
    mktRescoreStillRunningHint: 'As suas definições estão guardadas. O mercado ainda está a ser recalculado; atualize a página dentro de alguns minutos.',
    mktSelectedAccount: 'Conta selecionada',
    mktSetUpMarket: 'Configurar Mercado',
    mktSetupBodyEditor: 'O Mercado está ativado para este cliente, mas ninguém escolheu uma categoria de produto ainda. Escolha uma e vamos montar sua lista de oportunidades.',
    mktSetupBodyViewer: 'O Mercado está ativado, mas um administrador precisa escolher uma categoria de produto antes que haja algo para mostrar aqui.',
    mktShare: 'Proporção',
    mktShareCaption: 'As proporções podem somar mais de 100%. Fornecedores minoritários abaixo de {pct}% são agrupados como Outros (esmaecido).',
    mktShowNOlderEvents: 'Mostrar {n} eventos antigos ou de outras categorias',
    mktShowOneOlderEvent: 'Mostrar {n} evento antigo ou de outra categoria',
    mktShowingAccountsOnPage: 'Mostrando as contas desta página.',
    mktShowingCachedDataRetry20: 'Exibindo dados em cache. Nova tentativa automática em 20 minutos.',
    mktSpendBy: 'gastar até',
    mktSpendEstimateTitle: 'Nossa estimativa de quanto tempo o dinheiro dura, não uma data publicada pelo financiador',
    mktStage: 'Etapa',
    mktStale: 'Desatualizado',
    mktStanding: 'Situação',
    mktStateBuiltInHouse: 'Desenvolvimento interno',
    mktStateLmsNative: 'Nativo do LMS',
    mktStateNothingInPlace: 'Nada implantado',
    mktStateOpenSource: 'Código aberto',
    mktStateSuiteModule: 'Módulo de suíte',
    mktStatus: 'Status',
    mktStatusEmpty: 'vazio',
    mktStatusNotLoaded: 'não carregado',
    mktStrongestAccounts: 'Contas mais fortes',
    mktStrongestEvidence: 'Evidência mais forte',
    mktStrongestEvidenceAccountTip: 'O sinal individual com maior pontuação nesta conta: o que mais impulsiona a proximidade.',
    mktStrongestEvidencePersonaTip: 'O sinal individual com maior pontuação para esta persona: o que mais impulsiona a proximidade.',
    mktStrongestPersonas: 'Personas mais fortes',
    mktSubset: 'Subconjunto: {measure}',
    mktSumValue: 'Soma: {value}',
    mktSwitching: 'Alternando…',
    mktTabCompetitors: 'Concorrentes',
    mktTabRenewalRisk: 'Risco de renovação',
    mktTargetThresholds: 'Limites de oportunidade',
    mktThisMarket: 'este mercado',
    mktTipAddressable: 'As dessa lista para as quais você poderia vender de forma realista. Se for igual ao Mercado total, ainda não há regras de exclusão configuradas, então nada está sendo filtrado.',
    mktTipAiReadiness: 'Até onde esta instituição levou a IA no ensino e na avaliação, de 1 (apenas explorando) a 5 (integrada à forma de trabalhar).',
    mktTipContractScope: 'Quão enraizado está o fornecedor atual: em toda a instituição, em várias faculdades, em um departamento ou em um piloto. Quanto mais amplo, maior o negócio e mais difícil substituí-lo.',
    mktTipEvidence: 'Quanto do contexto habitual temos sobre esta conta, comparado a instituições semelhantes e não a um registro perfeito. Quando é escasso, contemos a pontuação em vez de supor para cima. Indica o quanto temos certeza, não o quanto a conta é boa.',
    mktTipExamined: 'Quanto deste mercado foi de fato analisado. Um número baixo significa que a pesquisa não foi feita, não que o mercado está vazio.',
    mktTipFinancial: 'Quanta margem esta instituição tem para financiar algo novo, comparada a outras do mesmo porte e tipo. Não indica quanto ela gasta com EdTech.',
    mktTipGrade: 'Quão sólido é o argumento de que o fornecedor atual pode perder a conta. O mais forte é uma reclamação registrada em ata de um órgão de governança. O mais fraco é um problema que inferimos do fluxo de trabalho, sem que ninguém tenha reclamado.',
    mktTipInMarket: 'Contas em que encontramos algo para trabalhar: ou o fornecedor atual parece que pode perder a conta, ou ninguém comprou nada e há uma necessidade real. Não significa que há um negócio aberto.',
    mktTipIncumbent: 'Quem eles usam hoje nesta categoria. As instituições costumam ter fornecedores diferentes em cada categoria, então este é o que importa para este negócio, não o único que têm.',
    mktTipKnown: 'Todas as instituições das quais temos registro nos países que você cobre. Nada foi filtrado ainda.',
    mktTipMomentum: 'Impulso de compra nas últimas 4 e 12 semanas. Pontuações negativas indicam sinais desfavoráveis; positivas indicam sinais favoráveis.',
    mktTipPriority: 'As contas do seu mercado que também têm adequação alta o suficiente para valer a pena começar por elas. Comece aqui.',
    mktTipScore: 'De 0 a 100. Quão bem a instituição se encaixa no que você vende, combinado com o que está acontecendo lá agora, e contido quando a evidência é escassa.',
    mktTipType: 'Que tipo de negócio seria. Passe o cursor sobre qualquer selo para ver o que significa.',
    mktTopCompetitor: 'Principal concorrente',
    mktTopCompetitorAccountTip: 'Concorrente com a maior contribuição ponderada de sinais nesta conta. Ponderado pela força e recência do sinal.',
    mktTopCompetitorPersonaTip: 'Concorrente com a maior contribuição ponderada de sinais para esta persona. Ponderado pela força e recência do sinal.',
    mktTotalCompetitorSignals: 'Total de sinais de concorrentes',
    mktTotalFundingEligibility: 'Elegibilidade total de financiamento',
    mktTotalFundingEligibilityHint: 'O que esta instituição poderia solicitar em todas as chamadas abertas para as quais é elegível. É um teto, não uma previsão: a elegibilidade é um registro verificado e não temos evidência de que alguma tenha se candidatado.',
    mktTotalMarket: 'Mercado total',
    mktTrajectory24m: 'Trajetória (24 m)',
    mktTrajectoryTooltip: 'Pontuação de proximidade recalculada mês a mês nos últimos 24 meses. Barras subindo = novos sinais chegando. Barras caindo = evidências envelhecendo.',
    mktTriggers: 'Gatilhos',
    mktUnfavourable: 'Desfavorável',
    mktUnfavourableBlockers: 'Bloqueios desfavoráveis de concorrentes',
    mktUnknownVendor: 'Fornecedor desconhecido',
    mktUpcomingWindows: 'Próximas janelas',
    mktUpdate: 'Atualização',
    mktUpdatingCompetitorSet: 'Atualizando concorrentes e filtros…',
    mktUseTeamSettings: 'Usar as configurações da equipe',
    mktUserSelected: 'Selecionado pelo usuário',
    mktUsingCachedDataRetryIn: 'Usando dados em cache. Nova tentativa em {time}.',
    mktUsingTeamSettings: 'Usando as configurações da equipe',
    mktValueWholeContract: 'O valor exibido é o contrato inteiro, não um ano. Isso equivale a {annual} por ano.',
    mktValueWholeContractOverYears: 'O valor exibido é o contrato inteiro, não um ano. Isso equivale a {annual} por ano ao longo de {years} anos.',
    mktWhatChanged: 'O que mudou',
    mktWhatItIsWorth: 'Quanto vale',
    mktWhatThisMeans: 'O que isso significa',
    mktWhatThisMeansFundingHint: 'O mesmo tom do resumo da visão geral. Elegibilidade não é pipeline.',
    mktWhoIsInPlace: 'Quem está presente',
    mktWhoMightILose: 'Quem eu posso perder',
    mktWholeMarket: 'Mercado inteiro',
    mktWhy: 'Por quê',
    mktWhyNow: 'Por que agora',
    mktWhyTheyQualify: 'Por que se qualificam',
    mktWhyThisScore: 'Por que esta pontuação',
    mktWindowClosed: 'prazo encerrado',
    mktNoFixedDeadline: 'sem prazo fixo',
    mktStandingProgramme: 'programa permanente',
    mktDateNotPublished: 'data ainda não publicada',
    mktClosedOn: 'encerrado em {date}',
    mktNoDeadlinePublished: 'prazo não publicado',
    mktWonBefore: 'Já contemplada',
    mktWorkingAgainst: 'Contra',
    mktWorkingInFavour: 'A favor',
    mktWorthALook: 'Vale a pena olhar',
    mktYes: 'Sim',
    mktYourPilotIsFull: 'Seu piloto está completo',
    procToastDataIncomplete: 'Alguns dados podem estar incompletos',
    procToastTimeoutPage: 'O servidor excedeu o tempo limite em uma página de resultados. Exibindo dados parciais.',
    procToastTimeoutRecords: 'O servidor excedeu o tempo limite ao carregar registros adicionais. Exibindo resultados parciais.',
    procToastLoadingDeals: 'Carregando oportunidades',
    procToastFetchingInitialData: 'Buscando dados iniciais',
    procToastLoadingMoreDeals: 'Carregando mais oportunidades',
    procToastFetchingLiveRadarDeals: 'Buscando oportunidades do Radar ao vivo',
    procToastFetchedLiveDeals: '{n} oportunidades ao vivo obtidas…',
    procToastFailedLiveDeals: 'Falha ao carregar as oportunidades ao vivo',
    procLoadingRestOfMarket: 'Carregando o restante do mercado…',
    procToastFetchingLabelDeals: 'Buscando oportunidades de {label}…',
    procToastProcessingDealsLoaded: 'Processando… {n} oportunidades carregadas',
    procToastFailedLabelDeals: 'Falha ao carregar as oportunidades de {label}',
    procToastLabelReady: '{label} pronto',
    procToastFetchedVisible: '{fetched} obtidas, {visible} visíveis com os filtros ativos.',
    procToastStatusClearFailed: 'Falha ao limpar o status',
    procToastStatusUpdateFailed: 'Falha ao atualizar o status',
    procToastValueOverrideFailed: 'Falha ao substituir o valor',
    procRetired: 'Descontinuado',
    procDealActivityUnavailable: 'Atividade de oportunidades indisponível — os selos de status e de visualização podem estar incompletos. Tente atualizar.',
    procMapNoDeals: 'Nenhuma oportunidade',
    procMapTotalValueUsd: 'Valor total (USD)',
    procMapTop: 'Principais',
    procMapMetric: 'Métrica do mapa:',
    procMapTotalValue: 'Valor total',
    procMapCountries: 'Países',
    procMapClearSelection: 'Limpar seleção',
    procRtbRouteFramework: 'framework',
    procRtbRouteConsortium: 'consórcio',
    procRtbRouteCooperative: 'cooperativa',
    procRtbRouteBuyingOrg: 'central de compras',
    procRtbRouteProgramme: 'programa',
    procRtbThroughFramework: 'Por meio de um acordo-quadro',
    procRtbThroughReseller: 'Por meio de um revendedor',
    procRtbDirectToSupplier: 'Direto com o fornecedor',
    procAllCategories: 'Todas as categorias',
    procYourCategories: 'Suas categorias',
    procRtbSelectCategoriesForLens: 'Selecione categorias para usar este filtro',
    procFailedToLoad: 'Falha ao carregar',
    procRtbSubtitle: 'Como este mercado realmente compra — os instrumentos de compra e quem vende por meio deles.',
    procRtbCoverageNote: 'Abrange todos os compradores públicos do mercado, em todos os setores — não apenas o ensino superior.',
    procRtbLoadingCountry: 'Carregando {country}…',
    procRtbMoreCountries: 'Outros países',
    procRtbMoreCountriesOption: 'Outros países…',
    procRtbHowCountryBuys: 'Como {country} compra',
    procLoading: 'Carregando…',
    procUpdating: 'Atualizando…',
    procRtbVehiclesThatMatter: 'Instrumentos de compra relevantes',
    procRtbNoFrameworkMine: 'Nenhum acordo-quadro identificado abrange suas categorias em {country}.',
    procRtbNoFrameworkAll: 'Nenhum acordo-quadro identificado abrange estas oportunidades em {country}.',
    procRtbDealsLower: 'oportunidades',
    procRtbBuyersLower: 'compradores',
    procRtbSuppliersLower: 'fornecedores',
    procRtbMakersLower: 'fabricantes',
    procRtbValueLower: 'valor',
    procRtbRenewIn12Mo: 'renovam em 12 meses',
    procRtbInsideVehicle: 'Dentro do instrumento',
    procRtbWhoSellsThroughIt: 'Quem vende por meio dele (oportunidades)',
    procRtbResellersOnIt: 'Revendedores nele (oportunidades)',
    procRtbNoResellerOnVehicle: 'Nenhum revendedor registrado neste instrumento',
    procRtbAreYouOnIt: 'Você está nele?',
    procRtbYesSoldThroughIt: 'Sim — seu produto é vendido por meio dele',
    procRtbNotOnRecord: 'Sem registro',
    procRtbNotConfigured: 'Não configurado para este cliente',
    procRtbResellersIn: 'Revendedores em {country}',
    procRtbResellerShare: '{n}% das oportunidades com rota conhecida passam por um',
    procRtbNoResellerMine: 'Nenhum revendedor vende em suas categorias em {country} — todas as oportunidades registradas foram diretas com o fornecedor.',
    procRtbNoResellerAll: 'Nenhum revendedor vende aqui em {country} — todas as oportunidades registradas foram diretas com o fornecedor.',
    procRtbNotStated: 'não informado',
    procRtbInsideReseller: 'Dentro do revendedor',
    procRtbMakersItCarries: 'Fabricantes que distribui (oportunidades)',
    procRtbRecordNamesOnlyReseller: 'O registro cita apenas o revendedor, nunca o fabricante',
    procRtbMakerNamedPct: 'Fabricante indicado em {n}% das suas oportunidades — as demais registram apenas o revendedor',
    procRtbSellsThrough: 'Vende por meio de',
    procRtbDirectToBuyer: 'Direto ao comprador — nenhum instrumento registrado',
    procRtbCarriesYourProduct: 'Distribui seu produto?',
    procYes: 'Sim',
    procRtbUpTo: 'até {amount}',
    procRtbAbove: 'acima de {amount}',
    procRtbHowBigBeforeTender: 'A partir de que valor vai a licitação',
    procRtbTypicalAcross: 'Típico entre {n} compradores',
    procRtbMostRange: 'a maioria {from}–{to}',
    procRtbNationalRule: 'Regra nacional',
    procMonths24Plus: '24+ meses',
    procVendorsForSelectedCategories: 'Fornecedores das categorias selecionadas',
    procUpdatingResults: 'Atualizando resultados',
    procClickHeadersToSort: 'Clique nos cabeçalhos das colunas para ordenar',
    procFilterAppliesTableOnly: '— este filtro se aplica apenas a esta tabela; o mapa e os totais acima não são filtrados.',
    procBundledContract: 'Contrato agrupado',
    procNoRationaleProvided: 'Nenhuma justificativa fornecida.',
    procShowingRangeDeals: 'Exibindo {from}–{to} de {total} oportunidades',
    procShowingRangeAccounts: 'Exibindo {from}–{to} de {total} contas',
    procMatchingDealsOnePerAccount: '{n} oportunidades correspondentes; a tabela mostra a principal oportunidade de cada conta',
    procMapNoClosedDeals: 'Nenhuma oportunidade fechada',
    procTopVendorsByDeals: 'Principais fornecedores por oportunidades',
    procTopVendorsByValue: 'Principais fornecedores por valor',
    procClear: 'Limpar',
    procTop3DealsByValue: 'As 3 principais oportunidades por valor',
    procMarketShare: 'Participação de mercado',
    procPieAccountsPct: '{n} contas ({pct}%)',
    procMarketShareByVendor: 'Participação de mercado por fornecedor',
    procNoIncumbentDataCountry: 'Não há dados de fornecedores atuais para este país.',
    procClickCountryForMarketShare: 'Clique em um país no mapa para ver a participação de mercado.',
    procWin0_6Mo: '0–6 meses',
    procWin6_12Mo: '6–12 meses',
    procWin12_24Mo: '12–24 meses',
    procWin24PlusMo: '24+ meses',
    procWinNoEndDate: 'Sem data de término',
    procHowLabelIsMeasured: 'Como é medido: {label}',
    procMoveFrom: 'de',
    procMoveTo: 'para',
    procPlusNMore: '+{n} a mais',
    procCouldNotLoadChanges: 'Não foi possível carregar as mudanças.',
    procWon: 'Ganhas',
    procLost: 'Perdidas',
    procNoRecordedChanges: 'Nenhuma mudança registrada.',
    procWonLostCounts: 'ganhas {won} · perdidas {lost}',
    procEstablishedRelationships: '{n} relações consolidadas',
    procLoadingCategoryIntel: 'Carregando inteligência da categoria…',
    procNoMeasuredHistory: 'Ainda não há histórico medido para as categorias selecionadas.',
    procRcViewReplacement: 'Ciclo de substituição',
    procRcViewExpiry: 'Vencimento do contrato',
    procRcIntro: 'Equipamentos instalados por idade, do mais antigo ao mais recente. A idade conta desde a última compra do sistema; contratos de manutenção e consumíveis não a reiniciam. Um equipamento costuma ser substituído após 5–7 anos, muito depois do fim do contrato.',
    procRcKpiInstalled: 'Equipamentos instalados',
    procRcKpiInstalledSub: 'de {n} contratos adjudicados',
    procRcKpiWindow: 'Na janela de substituição',
    procRcKpiWindowSub: '{n}+ anos desde a compra',
    procRcKpiDue: 'A substituir ou atrasados',
    procRcKpiDueSub: '{n}+ anos desde a compra',
    procRcKpiOwn: 'Sua base envelhecendo',
    procRcKpiOwnSub: 'suas instalações com {n}+ anos',
    procRcWindowFilter: 'Janela de substituição:',
    procRcInstalled: 'Instalado',
    procRcAge: 'Idade',
    procRcWindow: 'Janela de substituição',
    procRcServiceEnds: 'Fim do contrato de manutenção',
    procRcYourKit: 'Seu equipamento',
    procRcVia: 'via {reseller}',
    procRcPurchases: '{n} compras do sistema',
    procRcFloorHelp: 'Nenhuma compra do sistema publicada; datado pelo registro de manutenção ou fornecimento mais antigo, então o equipamento tem pelo menos esta idade.',
    procRcYears: '{n} anos',
    procRcShowing: 'Mostrando {shown} de {total} equipamentos instalados',
    procRcBandOverdue: 'Atrasado',
    procRcBandDue: 'A substituir',
    procRcBandPlan: 'Planejar',
    procRcBandMid: 'Meia-vida',
    procRcBandNew: 'Novo',
    procRcBandUndated: 'Sem data',
    procRcRangeOverdue: '12+ anos',
    procRcRangeDue: '7–12 anos',
    procRcRangePlan: '5–7 anos',
    procRcRangeMid: '3–5 anos',
    procRcRangeNew: 'menos de 3 anos',
    procRcRangeUndated: 'sem data',
    procRcRangeYears: '{a}–{b} anos',
    procRcRangeOver: '{a}+ anos',
    procRcRangeUnder: 'menos de {a} anos',
    procRcSincePurchase: '{n}+ anos desde a compra',
    procRcOwnYearsOld: 'suas instalações com {n}+ anos',
    procRcRadarSubtitle: 'Qual a idade da base instalada, quem detém os equipamentos envelhecidos e o que as instituições fizeram na última substituição.',
    procRcKpiDated: '{n} com data de compra',
    procRcUpCompetitor: 'Base concorrente a substituir',
    procRcUpCompetitorSub: 'instalações concorrentes com {n}+ anos',
    procRcUpOwn: 'Sua base a renovar',
    procRcUpOwnSub: 'suas instalações com {n}+ anos',
    procRcSwitchRate: 'Troca de fabricante na substituição',
    procRcSwitchTooFew: '{n} substituições observadas; uma taxa exige {min}',
    procRcSwitchSub: '{switched} trocaram · {retained} mantiveram o fabricante',
    procRcMatrixTitle: 'Base instalada por idade e sentimento',
    procRcMatrixHelp: 'Cada célula conta equipamentos instalados. A idade conta desde a última compra do sistema.',
    procRcTotal: 'Total',
    procRcSuppliersTitle: 'Quem detém os equipamentos envelhecidos',
    procRcSuppliersHelp: 'Instalações por fabricante; a parte âmbar tem {n}+ anos.',
    procRcAgeingOf: '{ageing} de {n} envelhecendo',
    procRcMovesTitle: 'Trocas de fabricante na substituição',
    procRcMovesHelp: 'Uma recompra só conta como substituição quando vem {n}+ anos após a anterior. {events} observadas.',
    procRcNoMoves: 'Ainda não há trocas de fabricante registradas na substituição.',
    procRcInstallsAcross: '{installs} equipamentos instalados em {accounts} instituições, {scope}.',
    procRcSwitchHelp: 'Sempre que uma instituição volta a comprar o mesmo tipo de sistema pelo menos {n} anos após a última compra, o NEO conta como substituição: manteve o fabricante ou trocou. Compras mais próximas são tratadas como equipamento adicional, não como substituição.',
    procRcMakersPerAccount: 'Fabricantes por conta',
    procRcAgeHelp: 'A idade conta desde a última compra do sistema. Contratos de manutenção e consumíveis não a reiniciam. Este tipo de equipamento costuma ser substituído após {a}–{b} anos.',
    procRcPipelineTitle: 'Pipeline de substituição',
    procRcPipelineHint: 'Equipamentos instalados por anos desde a compra',
    procRcPipelineNoteBefore: 'Base concorrente na janela de substituição:',
    procRcPipelineNoteAfter: 'instalações têm {n}+ anos.',
    procRcNetHint: 'Trocas de fabricante na substituição: ganhas menos perdidas',
    procRcAgeingByMaker: 'Base envelhecida por fabricante',
    procRcAgeingByCountry: 'Base envelhecida por país',
    procRcAgeingByType: 'Base envelhecida por tipo de instituição',
    procRcAgeingHint: 'Parcela de instalações com {n}+ anos',
    procRcAgeingEmpty: 'Ainda há poucas instalações para detalhar.',
    procRcOfInstalls: 'de {n} instalações',
    procRcGridTitle: 'Posição do titular por janela de substituição',
    procDisplacementSubtitle: 'Com que frequência os fornecedores atuais realmente perdem esta categoria e quais contratos reabrem a seguir.',
    procAwardsAcrossInstitutions: '{deals} adjudicações em {scope} e {accounts} instituições.',
    procNMarkets: '{n} mercados',
    procLastUpdatedDate: 'Última atualização: {date}',
    procGlobal: 'Global',
    procHighlights: 'Destaques',
    procAnnualChurn: 'Rotatividade anual',
    procChurnSubSplit: 'das relações com fornecedores — {switched}% trocaram de fornecedor, {stopped}% deixaram de comprar a categoria',
    procChurnSubPerYear: 'das relações com fornecedores encerradas por ano',
    procChurnHelpIntro: 'Contamos apenas os fornecedores dos quais uma instituição comprou pelo menos duas vezes.',
    procChurnHelpSwitchedLabel: 'Trocou de fornecedor:',
    procChurnHelpSwitchedBody: 'A instituição continuou comprando na categoria, mas trocou de fornecedor — uma possível conquista.',
    procChurnHelpStoppedLabel: 'Deixou de comprar:',
    procChurnHelpStoppedBody: 'A instituição deixou de comprar na categoria. Nenhum fornecedor venceu; trate isso como um limite superior, pois renovações não publicadas parecem idênticas.',
    procChurnHelpAnnualLabel: 'Taxa anual:',
    procChurnHelpAnnualBody: 'Média de todos os anos disponíveis, não ano a ano.',
    procChurnHelpMultiLabel: 'Contas com vários fornecedores:',
    procChurnHelpMultiBody: 'Perder um fornecedor pode significar perder uma posição em um painel, não o cliente inteiro.',
    procSuppliersPerAccount: 'Fornecedores por conta',
    procPctBuyersUseOne: '{n}% dos compradores usam apenas um',
    procRebuyCycle: 'Ciclo de recompra',
    procUnitMo: 'meses',
    procMedianGapBetweenAwards: 'intervalo mediano entre adjudicações',
    procReopeningWithin12Mo: 'Reabrem em 12 meses',
    procLiveContractsAcrossMarkets: 'contratos vigentes em {n} mercados',
    procValueInNext12Mo: 'Valor nos próximos 12 meses',
    procFromNExpiringContracts: 'de {n} contratos a vencer',
    procRenewalPipeline: 'Pipeline de renovações',
    procLiveContractsByTimeToExpiry: 'contratos vigentes por tempo até o vencimento',
    procPipelineNoteBefore: '{n} contratos reabrem em até seis meses. Com rotatividade anual de {churn}%, cerca de',
    procPipelineNoteAfter: 'desses fornecedores atuais vão de fato perder a conta — os demais serão readjudicados ou prorrogados discretamente.',
    procNetPositionChange: 'Variação líquida de posição',
    procAccountsWonMinusLost: 'contas ganhas menos perdidas',
    procTooFewRepeatAwards: 'Há poucas adjudicações repetidas neste mercado para medir o movimento líquido.',
    procNetWinners: 'Ganhadores líquidos',
    procNetLosers: 'Perdedores líquidos',
    procNetPositionNote: 'Posições ganhas menos posições perdidas em todas as mudanças de fornecedor registradas. Os perdedores são as oportunidades — um fornecedor atual perdendo contas uma a uma.',
    procChurnByCountry: 'Rotatividade por país',
    procTopMarketsByVolume: 'principais mercados por volume',
    procChurnByCountryEmpty: 'Não há histórico repetido suficiente em nenhum mercado para calcular uma taxa confiável.',
    procChurnByInstitutionType: 'Rotatividade por tipo de instituição',
    procAnnualEstablishedRelationships: 'anual, relações consolidadas',
    procChurnByInstitutionEmpty: 'O tipo de instituição não está registrado em contas suficientes nesta categoria.',
    procIncumbentByDecisionWindow: 'Situação do fornecedor atual, por janela de decisão',
    procIncumbent: 'Fornecedor atual',
    procAccountLower: 'conta',
    procAccountsLower: 'contas',
    procSentimentIncumbent: 'Fornecedor atual: {sentiment}',
    procNoContractEndDate: 'sem data de término de contrato registrada',
    procContractEndsIn: 'o contrato termina em {window}',
    procAmountTotal: '{amount} no total',
    procLoadingAccounts: 'Carregando contas…',
    procNoAccountsForCell: 'Nenhuma conta registrada para esta célula.',
    procContractEnd: 'Término do contrato',
    procContractValue: 'Valor do contrato',
    procWhy: 'Motivo',
    procDealCount: 'Número de oportunidades',
    procNegativeSharePct: 'Proporção negativa (%)',
    procNoDisplacementData: 'Nenhum dado de substituição encontrado com os filtros atuais.',
    procTryAdjustingFilters: 'Tente ajustar os filtros de categoria ou país.',
    procCategoryRiskMosaic: 'Mosaico de risco por categoria',
    procCategoryRiskMosaicHelp: 'Risco de rotatividade por categoria e faixa de vencimento. Clique em um segmento ou bloco para ver as contas.',
    procMetric: 'Métrica',
    procBucketMonths: '{b} meses',
    procAtRiskValueShare: 'Em risco: {value} ({pct}%)',
    procDealLower: 'oportunidade',
    procPctOfCell: '{pct}% da célula',
    procShowTop15: 'Mostrar as 15 principais',
    procShowAllCategories: 'Mostrar todas as categorias',
    procRationaleHighlights: 'Justificativas em destaque',
    procSearchRationale: 'Pesquisar na justificativa...',
    procNoAccountsMatch: 'Nenhuma conta corresponde.',
    procLess: 'Menos',
    procReadMore: 'Ler mais',
    procYearLabel: 'Ano: {year}',
    procYou: 'você',
    procEnrolment: 'Matrículas',
    procUsdPerStudentYear: '$/aluno/ano',
    procOverTermWholeContract: 'em {months} meses · {amount}/aluno pelo contrato inteiro',
    procBenchEmptyIntro: 'Não há dados de referência para os filtros atuais. As oportunidades precisam de',
    procBenchFieldEnrolment: 'matrículas',
    procBenchFieldDealValue: 'valor da oportunidade (USD)',
    procBenchFieldSupplier: 'fornecedor',
    procBenchAndANumeric: 'e um',
    procBenchFieldInitialTerm: 'prazo inicial numérico',
    procBenchEmptyOutro: 'para que o preço possa ser calculado por ano.',
    procBenchExcludedNoTermOne: '{n} oportunidade corresponde aos filtros, mas não tem prazo de contrato, portanto não pode ser anualizada.',
    procBenchExcludedNoTermMany: '{n} oportunidades correspondem aos filtros, mas não têm prazo de contrato, portanto não podem ser anualizadas.',
    procBenchTitle: 'Matrículas vs $ por aluno por ano',
    procBenchTitleMedian: 'Mediana de $ por aluno por ano',
    procBenchAnnualised: 'Anualizado pelo prazo do contrato.',
    procTrendMedianNote: 'Cada linha é a mediana anual, por isso um negócio atípico não a desloca.',
    procTrendShortTermNote: 'Os contratos com menos de 4 meses ficam de fora: o seu preço não pode ser lido como anual.',
    procVendorVsCompetitors: '{vendor} vs. concorrentes.',
    procScatter: 'Dispersão',
    procTrendByCategory: 'Tendência por categoria',
    procTrendByVendor: 'Tendência por fornecedor',
    procTrendSuppliers: 'Fornecedores',
    procTrendRuleAll: 'Todos os fornecedores',
    procTrendRuleExclude: 'Excluir selecionados',
    procTrendRuleOnly: 'Apenas selecionados',
    procTrendRuleSearch: 'Pesquisar fornecedores',
    procTrendRuleSummaryExclude: 'Excluindo {n}',
    procTrendRuleSummaryOnly: 'Apenas {n}',
    procTrendRuleClear: 'Limpar',
    procTrendRuleDeals: '{n} contratos',
    procTrendRuleHint: 'Aplica-se apenas à Tendência por categoria. Guardado para esta seleção de categorias.',
    procTrendLegendHint: 'Clique num fornecedor na legenda para destacar a sua linha.',
    procTrendLegendClear: 'Mostrar todas as linhas',
    procResetView: 'Redefinir visualização',
    procFxFootnote: '* Assume-se uma taxa de câmbio fixa para USD em todas as moedas.',
    procWeakFit: 'Adequação fraca',
    procNoFit: 'Sem adequação',
    procLowEvidence: 'Poucas evidências',
    procFitOutOfScopeTitle: 'A categoria da oportunidade não corresponde a nenhum perfil de produto ativo',
    procFitColumnTitle: 'Fora do escopo = a categoria da oportunidade não corresponde a nenhum perfil de produto ativo',
    acctRemovedFromListTitle: 'Removemos {name} da sua lista',
    acctAccountLower: 'conta',
    acctRemovedFromListDesc: 'Você pode adicioná-lo novamente a qualquer momento em Mercado.',
    acctCouldNotRemoveAccount: 'Não foi possível remover a conta',
    acctPleaseTryAgainNoDot: 'Tente novamente',
    acctSaved: 'Salvo',
    acctTargetProductUpdated: 'Produto-alvo atualizado',
    acctCouldNotSaveTargetProduct: 'Não foi possível salvar o produto-alvo',
    acctTabContent: 'Conteúdo',
    acctTabProducts: 'Produtos',
    acctTabFinancialsAssets: 'Finanças e ativos',
    acctTabSalesMap: 'Mapa de vendas',
    acctTabCompetitorsCount: 'Concorrentes ({n})',
    acctEnrichmentUnavailable: 'Enriquecimento indisponível',
    acctMarketEnrichmentNotEnabled: 'O enriquecimento de mercado não está habilitado para este tenant.',
    acctCouldNotRequestEnrichment: 'Não foi possível solicitar o enriquecimento',
    acctMarketLensNotConfigured: 'A visão de mercado ainda não está configurada.',
    acctEnrichmentAlreadyRequested: 'Enriquecimento já solicitado',
    acctEnrichmentRequested: 'Enriquecimento solicitado',
    acctTryAgainLater: 'Tente novamente mais tarde',
    acctRequesting: 'Solicitando…',
    acctRequestPersonaEnrichment: 'Solicitar enriquecimento de personas',
    acctMeddpiccIdentifyPain: 'Identificar a dor',
    acctMeddpiccMetrics: 'Métricas',
    acctMeddpiccPainTriggerTiming: 'Gatilho · Momento',
    acctMeddpiccCompetitionIncumbent: 'Concorrência / Fornecedor atual',
    acctMeddpiccEconomicBuyer: 'Comprador econômico',
    acctMeddpiccDecisionProcess: 'Processo de decisão',
    acctMeddpiccPaperProcess: 'Processo administrativo',
    acctMeddpiccChampion: 'Champion',
    acctMeddpiccTipPainNeed: 'Que problema os faz querer mudar?',
    acctMeddpiccTipImpactScale: 'Escala e impacto quantificados — apenas números.',
    acctMeddpiccTipWhyNow: 'Por que agora — que gatilho datado está impulsionando a janela?',
    acctMeddpiccTipCurrentVendor: 'Quem usam hoje e o que acham disso.',
    acctMeddpiccTipBuyerOwner: 'Quem pode financiar e aprovar.',
    acctMeddpiccTipDecisionProcess: 'Quem aprova, em que sequência e por quais comitês.',
    acctMeddpiccTipPaperProcess: 'Via de compra, acordo-quadro, renovação e requisitos de contratação.',
    acctMeddpiccTipChampion: 'Defensor interno — responsável operacional com um ganho pessoal.',
    acctSalesPointPainNeed: 'Dor / Necessidade',
    acctSalesPointImpactScale: 'Impacto / Escala',
    acctSalesPointWhyNow: 'Por que agora / Momento',
    acctSalesPointCurrentVendor: 'Fornecedor atual / Concorrência',
    acctSalesPointBuyerOwner: 'Comprador / Responsável',
    acctSalesPointDecisionProcess: 'Processo de decisão',
    acctSalesPointPaperProcess: 'Compras / Processo administrativo',
    acctSalesPointChampion: 'Champion / Persona-chave',
    acctSalesPointRecommendedNextMove: 'Próximo passo recomendado',
    acctStrengthTipMissing: 'Ainda não há evidências úteis registradas para este ponto.',
    acctStrengthTipVeryLow: 'Genérica ou vaga — sem vínculo claro com esta conta.',
    acctStrengthTipLow: 'Algum sinal relevante, mas em grande parte inferido.',
    acctStrengthTipMedium: 'Apoiada em evidências, mas faltam detalhes importantes.',
    acctStrengthTipHigh: 'Evidência recente, específica e alinhada à categoria.',
    acctStrengthTipVeryHigh: 'Fortemente comprovada e validada (rara na V1).',
    acctStrengthMissing: 'Ausente',
    acctStrengthVeryLow: 'Muito baixo',
    acctStrengthVeryHigh: 'Muito alto',
    acctKeepDerived: 'Manter o calculado',
    acctEdited: 'Editado',
    acctAutoDetected: 'Detectado automaticamente',
    acctAutoDetectedTooltip: 'Sinalizado automaticamente pelo módulo Competitor Intel. As edições do representante substituirão esta justificativa.',
    acctShowLessLower: 'ver menos',
    acctShowMoreLower: 'ver mais',
    acctSourcesCount: 'Fontes ({n})',
    acctRepLower: 'representante',
    acctTeamNoteFrom: 'Nota da equipe de {author}',
    acctClearOverride: 'Remover ajuste manual',
    acctRationale: 'Justificativa',
    acctRationalePlaceholder: 'Por que sabemos isso? Ex.: \'Confirmado em ligação com X em Y — disseram Z\'',
    acctOverrideScoreOptional: 'Ajustar pontuação (opcional)',
    acctRationaleRequired: 'Adicione uma justificativa antes de salvar.',
    acctSalesMapUpdated: 'Mapa de vendas atualizado',
    acctCouldNotSaveEdit: 'Não foi possível salvar sua edição',
    acctOverrideCleared: 'Ajuste manual removido',
    acctCouldNotClearOverride: 'Não foi possível remover o ajuste manual',
    acctGetAdvice: 'Pedir conselho',
    acctCouldNotLoadSalesMap: 'Não foi possível carregar o mapa de vendas',
    acctCouldNotRefreshSalesMap: 'Não foi possível atualizar o mapa de vendas — exibindo os dados anteriores.',
    acctSalesMapTitle: 'Mapa de vendas — {account}',
    acctGeneratedAt: 'Gerado em {date}',
    acctPrepareForMeeting: 'Preparar reunião',
    acctSuggestNextMove: 'Sugerir próximo passo',
    acctSalesMapSparseBanner: 'Esta conta ainda tem poucas evidências registradas. A maioria das linhas está ausente — o bot ainda tem dados de contexto de mercado do país se você clicar em Pedir conselho.',
    acctNbmTarget: 'Alvo',
    acctNbmSuggestedRole: 'Cargo sugerido para abordar',
    acctNbmNoNamedContactTypically: 'Nenhum contato identificado — normalmente: {examples}',
    acctNbmNoNamedContact: 'Nenhum contato identificado.',
    acctNbmFindOneFirst: 'Encontre um antes de fazer contato. Abra a aba Personas.',
    acctNextBestMove: 'Próximo melhor passo',
    acctNbmAskNext: 'Pergunte na sua próxima conversa',
    acctNeoRecommendation: 'Recomendação do NEO',
    acctProfileOutreach: 'Contato pelo perfil',
    acctDiyPlaybook: 'Playbook DIY',
    acctPlaybookTrackingSoon: 'Acompanhamento de Playbooks em breve',
    acctPlaybookTrackingSoonDesc: 'O acompanhamento do engajamento dos Playbooks estará disponível quando a API do backend for implantada.',
    acctNoPlaybooksOrOutreach: 'Nenhum Playbook ou contato',
    acctNoPlaybooksOrOutreachDesc: 'Esta conta não está inscrita em nenhum Playbook e não tem histórico de contatos.',
    acctEnrolledPersonasOne: 'Inscritas: {n} persona',
    acctEnrolledPersonasMany: 'Inscritas: {n} personas',
    acctCompletedOn: 'Concluído: {date}',
    acctStartedOn: 'Iniciado: {date}',
    acctEnrolledPersonasLabel: 'Personas inscritas:',
    acctTouchOf: 'Contato {current} de {total}',
    acctNextOn: 'Próximo: {date}',
    acctLoadingAdHocOutreach: 'Carregando contatos ad hoc…',
    acctActivePlaybooks: 'Playbooks ativos',
    acctCompletedPlaybooks: 'Playbooks concluídos',
    acctScheduledPending: 'Agendados — pendentes',
    acctScheduledFor: 'Agendado para {date}',
    acctAdHocMessages: 'Mensagens ad hoc',
    acctMsgCountOne: '{n} mensagem',
    acctMsgCountMany: '{n} mensagens',
    acctManualCompletion: 'Concluído manualmente',
    acctNotesLoadErrorContext: 'Não foi possível carregar as notas neste contexto. Tente atualizar a página.',
    acctNotesLoadErrorGeneric: 'Não foi possível carregar as notas. Tente novamente.',
    acctCouldNotSaveThisNote: 'Não foi possível salvar esta nota.',
    acctFailedToSaveNote: 'Falha ao salvar a nota.',
    acctCannotDeleteNote: 'Você não pode excluir esta nota.',
    acctFailedToDeleteNote: 'Falha ao excluir a nota.',
    acctNoteAuthorYou: 'Você',
    acctNoteAuthorTeam: 'Equipe',
    acctNoteAuthorTeammate: 'Colega de equipe',
    acctLoadingNotes: 'Carregando notas...',
    acctAddNote: 'Adicionar nota',
    acctWriteNotePlaceholder: 'Escreva sua nota aqui...',
    acctSaveNote: 'Salvar nota',
    acctNoNotesYet: 'Ainda não há notas. Adicione sua primeira nota acima.',
    acctDeleteNote: 'Excluir nota',
    acctCouldNotLoadCompetitorData: 'Não foi possível carregar os dados de concorrentes — tente novamente.',
    acctCompetitorsAtThisAccount: 'Concorrentes nesta conta',
    acctSignalTimeline: 'Linha do tempo de sinais',
    acctCompetitorsLinkedToPersona: 'Concorrentes vinculados a esta persona',
    acctCompSigAccountReference: 'Conta mencionada em conteúdo de concorrente',
    acctCompSigAccountAdoption: 'Adoção de concorrente na conta',
    acctCompSigPartnerEcosystem: 'Ecossistema de parceiros do concorrente',
    acctCompSigProcurementSignal: 'Sinal de compras',
    acctCompSigEventOverlap: 'Sobreposição de eventos',
    acctCompSigDisplacementOpportunity: 'Oportunidade de substituição',
    acctCompSigWeaknessSignal: 'Fraqueza do concorrente',
    acctCompSigPersonaInteraction: 'Persona abordada pelo concorrente',
    acctCompSigPersonaReference: 'Persona mencionada em conteúdo de concorrente',
    acctCompSigPersonaAdoption: 'Persona vinculada a ferramenta de concorrente',
    acctCompSigGeneric: 'Sinal de concorrente',
    sigUpdEcosystemSignal: 'Sinal do ecossistema',
    sigUpdMarketEvent: 'Evento de mercado',
    sigUpdRegionalEvent: 'Evento regional',
    sigUpdProductRelease: 'Lançamento de produto',
    sigUpdProductLaunch: 'Apresentação de produto',
    sigUpdPressRelease: 'Comunicado à imprensa',
    sigUpdPartnershipAnnouncement: 'Anúncio de parceria',
    sigUpdInvestorNews: 'Notícias para investidores',
    sigUpdWebinarEvent: 'Webinar',
    sigUpdVendorMetricClaim: 'Métrica declarada pelo fornecedor',
    sigUpdExternalAccountAdoption: 'Adoção em conta externa',
    sigUpdExternalReferenceAccount: 'Conta de referência externa',
    sigUpdMarketCommentary: 'Comentário de mercado',
    sigUpdConferenceRecap: 'Resumo de conferência',
    acctImpactUnfavourable: 'Desfavorável',
    acctImpactFavourable: 'Favorável',
    acctImpactMixed: 'Misto',
    acctImpactContextOnly: 'Apenas contexto',
    acctProximityElevated: 'Elevado',
    acctProximityWatch: 'Observar',
    acctProximityNoEvidence: 'Sem evidências',
    acctJustNow: 'agora mesmo',
    acctMoreInfo: 'Mais informações',
    acctProximityScoreTooltip: 'De 0 a 100. Sobe quando novos sinais de concorrentes chegam a esta conta/persona. Cai naturalmente à medida que as evidências envelhecem.',
    acctImpactTooltip: 'Direção das evidências sobre o concorrente. Desfavorável = ruim para você (concorrente consolidado). Favorável = bom para você (insatisfação / oportunidade de substituição). Misto = há sinais dos dois tipos. Apenas contexto = informativo.',
    acctLabelTooltip: 'Rótulo da faixa de pontuação: 0 = Sem evidências, 1-24 = Baixo, 25-49 = Observar, 50-74 = Elevado, 75-100 = Alto.',
    acctTopCompetitorTooltip: 'Concorrente com a maior contribuição ponderada de sinais nesta conta/persona.',
    acctLatestSignalTooltip: 'Data do sinal de concorrente mais recente.',
    acctTrajectoryTooltip: 'Pontuação de proximidade recalculada mês a mês nos últimos 24 meses. Barras subindo = novos sinais chegando. Caindo = evidências envelhecendo.',
    acctFlatTrajectoryTooltip: 'Estável em {n} nos últimos 24 meses — as evidências têm mais de 1 ano.',
    acctSourceBadgeAccount: 'Sinal de conta',
    acctSourceBadgeAccountTooltip: 'Vinculado a uma das suas contas-alvo. Contribui para o barômetro de contas.',
    acctSourceBadgePersona: 'Sinal de persona',
    acctSourceBadgePersonaTooltip: 'Vinculado a uma das suas personas-alvo. Contribui para o barômetro de personas.',
    acctSourceBadgeMarket: 'Inteligência de mercado — fora das contas-alvo',
    acctSourceBadgeMarketTooltip: 'Atividade de concorrentes no seu mercado que não atingiu suas contas-alvo. NÃO contribui para nenhum barômetro.',
    acctCardHeaderTooltipAccount: 'O quanto esta conta está exposta a concorrentes. Calculado pela força do sinal × decaimento por antiguidade. Pontuação maior = presença mais forte de concorrentes.',
    acctCardHeaderTooltipPersona: 'O quanto esta persona está exposta a concorrentes. Calculado pela força do sinal × decaimento por antiguidade. Pontuação maior = presença mais forte de concorrentes.',
    acctCompetitorFallback: 'Concorrente',
    acctStrengthValue: 'Força: {value}',
    acctShowMore: 'Ver mais',
    acctRecommendedAction: 'Ação recomendada',
    acctBreakdownSignals: 'sinais: {n}',
    acctBreakdownStrongest: 'mais forte: {value}',
    acctBreakdownLatest: 'mais recente: {value}',
    acctCompetitiveProximity: 'Proximidade competitiva',
    acctCompetitorSignalsTitle: 'Sinais de concorrentes',
    acctHelpFor: 'Ajuda: {title}',
    acctProximityScore: 'Pontuação de proximidade',
    acctProximityScoreHelp: 'Ajuda sobre a pontuação de proximidade',
    acctLabelHelp: 'Ajuda sobre o rótulo',
    acctImpactHelp: 'Ajuda sobre o impacto',
    acctTrajectory24m: 'Trajetória 24 m',
    acctTrajectoryHelp: 'Ajuda sobre a trajetória',
    acctTopCompetitorValue: 'Principal: {value}',
    acctTopCompetitorHelp: 'Ajuda sobre o principal concorrente',
    acctLatestValue: 'Mais recente: {value}',
    acctLatestSignalHelp: 'Ajuda sobre o sinal mais recente',
    acctLinkedPersonas: 'Personas vinculadas:',
    sigTypeFundingNews: 'Notícias de financiamento',
    sigTypeLeadershipChange: 'Mudança de liderança',
    sigTypeStrategicAnnouncement: 'Anúncio estratégico',
    sigTypePartnership: 'Parceria',
    sigTypeTechnologyAdoption: 'Adoção de tecnologia',
    sigTypeProcurementUpdate: 'Atualização de compras',
    sigTypeAcquisition: 'Aquisição',
    sigTypeContractAward: 'Adjudicação de contrato',
    sigTypeProfileUpdate: 'Atualização de perfil',
    sigTypeRoleChange: 'Mudança de cargo',
    sigTypeAchievement: 'Conquista',
    sigTypeSocialActivity: 'Atividade nas redes',
    sigTypeIncumbent: 'Fornecedor atual',
    sigTypeBuying: 'Compra',
    sigTypeDisplacement: 'Substituição',
    sigTypeRenewalWindow: 'Janela de renovação',
    sigTypeBudgetAllocation: 'Alocação orçamentária',
    sigTypeInitiative: 'Iniciativa',
    sigTypeRisk: 'Risco',
    sigTypeOrgChange: 'Mudança organizacional',
    sigTypeAccreditorResult: 'Resultado de acreditação',
    sigTypeGrantAward: 'Concessão de subsídio',
    sigTypeJobPosting: 'Vaga de emprego',
    sigTypeEstateEvent: 'Obras e instalações',
    sigTypeFinancialPressure: 'Pressão financeira',
    sigTypeEnrolmentPressure: 'Pressão de matrículas',
    sigTypeSystemShortcoming: 'Deficiência do sistema',
    sigTypeVendorDispute: 'Conflito com fornecedor',
    sigTypeComplianceUpdate: 'Atualização de conformidade',
    sigTypeBoardUpdate: 'Atualização do conselho',
    sigTypeGreenfield: 'Sem solução atual',
    sigMarketUpdate: 'Atualização de mercado',
    sigCompetitorFilter: 'Concorrente: {name}',
    sigMarketActivity: 'Atividade de mercado',
    sigAccountSignals: 'Sinais de contas',
    sigPersonaSignals: 'Sinais de personas',
    sigCompetitorSignals: 'Sinais de concorrentes',
    sigNoMarketActivity: 'Ainda não há sinais de atividade de mercado.',
    chatCopy: 'Copiar',
    chatCopied: 'Copiado',
    chatTalkingAbout: 'Falando sobre {account}',
    chatSourcesLabel: 'Fontes:',
    pbBoothMeetingPurpose: 'Gere visitas ao estande e demonstrações presenciais.',
    pbSessionInvitePurpose: 'Gere público para uma sessão de palestra.',
    pbVipOutreachPurpose: '20 minutos com contatos de nível executivo.',
    pbQuickConnectPurpose: 'Abordagem leve entre pares para participantes confirmados.',
    pbPersonaScopeAny: 'Qualquer persona',
    pbPersonaScopeExecutiveOnly: 'Somente executivos',
    pbPersonaScopeConfirmedAttendees: 'Somente participantes anteriores e confirmados',
    homeWeakFit: 'Adequação fraca',
    homeNoFit: 'Sem adequação',
    homeNotScored: 'Sem pontuação',
    // [2026-10-29] i18n: UI chrome keys (accounts list, account signals, archetypes)
    acctArchExecutiveBuyer: 'Comprador executivo',
    acctArchEvaluator: 'Avaliador',
    acctNoContactsYet: 'Nenhum contato ainda.',
    acctNoSignalsForAccount: 'Nenhum sinal encontrado para esta conta.',
    acctUntitledSignal: 'Sinal sem título',
    acctNoDescriptionAvailable: 'Nenhuma descrição disponível',
    acctSignalUpdate: 'Novidade',
    acctImpactLabel: 'Impacto:',
    acctMomentumScoreChanged: 'A pontuação de impulso mudou {n} pontos',
    acctsColProduct: 'Produto',
    acctsColAccountTier: 'Nível da conta',
    acctsColTargetProducts: 'Produtos-alvo',
    acctsColSubscribers: 'Assinantes',
    acctsTargetProductPlaceholder: 'Produto-alvo',
    procTeamStatusAria: 'Status da equipe',
    // [2026-10-29] i18n: chat table/chart block labels (lib/chatBlockLabels.js)
    chatTblWhatTheyRunToday: 'O que usam hoje',
    chatTblWhatChangedRecently: 'Mudanças recentes',
    chatColCategory: 'Categoria',
    chatColVendor: 'Fornecedor',
    chatColHowTheyHoldIt: 'Como o utilizam',
    chatColDeployment: 'Implantação',
    chatColSentiment: 'Sentimento',
    chatColEvidence: 'Evidência',
    chatColDate: 'Data',
    chatColWhatHappened: 'O que aconteceu',
    chatColSource: 'Fonte',
    chatBadgeCommercialProduct: 'Produto comercial',
    chatBadgeNativeToLms: 'Nativo do LMS',
    chatBadgeBuiltInHouse: 'Desenvolvimento próprio',
    chatBadgeOpenSource: 'Código aberto',
    chatBadgeNothingInPlace: 'Sem solução',
    chatBadgeInstitutionWide: 'Toda a instituição',
    chatBadgeSeveralUnits: 'Várias unidades',
    chatBadgeOneUnit: 'Uma unidade',
    chatBadgePilot: 'Piloto',
    chatBadgePositive: 'Positivo',
    chatBadgeNeutral: 'Neutro',
    chatBadgeNegative: 'Negativo',
    chatChartWhichLms: 'Qual LMS o mercado usa',
    chatChartFinancialHeadroom: 'Margem financeira no mercado',
    chatChartAiReadiness: 'Prontidão para IA no mercado',
    chatChartMarketSplit: 'Como o mercado se divide',
    chatChartWhoHolds: 'Quem detém {cat} no mercado',
    chatUnitInstitutions: 'instituições',
    chatUnitAccounts: 'contas',
    chatSliceLow: 'Baixa',
    chatSliceModerate: 'Moderada',
    chatSliceHigh: 'Alta',
    chatSliceOther: 'Outros',
    chatSliceLevel: 'Nível {n}',
    chatSlicePriorityNow: 'Prioridade agora',
    chatSliceInYourMarket: 'No seu mercado',
    chatSliceRestOfMarket: 'Restante do mercado',
    chatSliceAdjacentMarket: 'Mercado adjacente',
    chatDateUnknown: 'data desconhecida',
    chatNoteVendor: '{n} de {total} instituições endereçáveis têm um fornecedor de {cat} registrado. Fornecedores abaixo de 5% são agrupados como Outros.',
    chatNoteLms: '{n} de {total} instituições endereçáveis têm um LMS registrado. Agrupados por famílias: Blackboard conta como Anthology e Canvas como Instructure. Open LMS e Totara ficam separados do Moodle.',
    chatNoteCapacity: '{n} de {total} instituições endereçáveis têm uma faixa de capacidade.',
    chatNoteReadiness: '{n} de {total} instituições endereçáveis têm pontuação de prontidão para IA.',
    chatNoteMix: '{n} contas classificadas.',
  },
  
  fr: {
    home: 'Accueil',
    market: 'Marché',
    accounts: 'Comptes',
    people: 'Personnes',
    signals: 'Signaux',
    procurement: 'Achats',
    playbooks: 'Playbooks',
    settings: 'Paramètres',
    help: 'Aide',
    admin: 'Administrateur',
    segments: 'Segments',
    analytics: 'Analytique',
    outbox: 'Boîte d\'envoi',
    logout: 'Déconnexion',
    
    // Common
    loading: 'Chargement...',
    save: 'Enregistrer',
    cancel: 'Annuler',
    edit: 'Modifier',
    delete: 'Supprimer',
    search: 'Rechercher',
    filter: 'Filtrer',
    export: 'Exporter',
    retry: 'Réessayer',
    refresh: 'Actualiser',
    backTo: 'Retour à',
    viewProfile: 'Voir le profil',
    
    // Headers
    dashboard: 'Tableau de bord',
    accountOverview: 'Aperçu du compte',
    
    // Tabs
    overview: 'Aperçu',
    details: 'Détails',
    activity: 'Activité',
    notes: 'Notes',
    connections: 'Relations',
    connectionsEmpty: 'Aucune relation connue pour cette personne pour le moment.',
    withConnections: 'Avec relations',
    withoutConnections: 'Sans relations',
    inPursuit: 'En suivi',
    notInPursuit: 'Hors suivi',
    almaMater: 'Alma mater',
    connectionInferred: 'inféré',
    personas: 'Personas',
    departments: 'Départements',
    
    // Account Page
    techStack: 'Pile technologique',
    liveDeals: 'Appels d\'offres en cours',
    decisionWindow: 'Fenêtre de décision',
    renewalDate: 'Date de renouvellement',
    momentum: 'Dynamique',
    strategicPriorities: 'Priorités stratégiques',
    institutionalChallenges: 'Défis institutionnels',
    studentExpectations: 'Attentes des étudiants',
    keyContacts: 'Contacts clés',
    procurementProfile: 'Profil d\'achat',
    accountNotFound: 'Compte introuvable',
    accountNotFoundDesc: 'Le compte demandé n\'a pas pu être chargé ou n\'existe pas.',
    rateLimitReached: 'Limite de taux atteinte. Affichage des données en cache. Veuillez attendre 5 minutes avant de réessayer.',
    showingCachedData: 'Affichage des données en cache. Cliquez sur actualiser pour les dernières mises à jour.',
    
    // Charts & Metrics
    fitScore: 'Score d\'adéquation',
    excellent: 'Excellent',
    strong: 'Fort',
    moderate: 'Modéré',
    low: 'Faible',
    scoreRationale: 'Justification du score',
    noRationaleAvailable: 'Aucune justification disponible.',
    riskRationale: 'Justification du risque',
    
    // People & Personas
    personasOrganizedBy: 'Les personas sont organisées par rôle dans le comité d\'achat et classées par leur influence et score d\'adéquation. Cliquez sur un contact pour voir son profil complet et son activité.',
    addedToWatchlist: 'Ajouté à la liste de surveillance',
    removedFromWatchlist: 'Retiré de la liste de surveillance',
    
    // Procurement
    procurementFriendliness: 'Facilité d\'achat',
    friendly: 'Favorable',
    standard: 'Standard',
    bureaucratic: 'Bureaucratique',
    monitor: 'Surveiller',
    
    // Decision Window
    lessThan12Months: '<12 mois',
    months12to24: '12-24 mois',
    moreThan24Months: '>24 mois',
    notApplicable: 'N/A',
    decisionWindowRationale: 'Justification de la fenêtre de décision',
    
    // Signals
    momentumUpdate: 'Mise à jour de la dynamique',
    momentumScoreChanged: 'Le score de dynamique a changé de',
    points: 'points',
    
    // Market Intelligence
    marketIntelligence: 'Opportunités de marché',
    macroTrends: 'Tendances macro et opportunités dans vos marchés cibles',
    buyingActivity: 'Activité d\'achat',
    players: 'Dynamiques',
    
    // Filters
    allCountries: 'Tous les pays',
    filterByCountry: 'Filtrer par pays',
    allDepartments: 'Tous les départements',
    filterByDepartment: 'Filtrer par département',
    
    // Status
    live: 'En cours',
    closed: 'Fermé',
    awarded: 'Attribué',
    cancelled: 'Annulé',
    
    // Common - additional
    noDataAvailable: 'Aucune donnée disponible.',

    // NEO Chat panel
    neoChatDefaultGreeting: 'Bonjour ! Je suis NEO, votre assistant GTM. Comment puis-je vous aider aujourd’hui ?',
    neoChatAssistantTitle: 'Assistant NEO',
    neoChatNewChat: 'Nouvelle conversation',
    neoChatInputPlaceholder: 'Demandez n’importe quoi à NEO...',
    neoChatLoadingChat: 'Chargement du chat',
    neoChatChipShowTenders: 'Voir les appels d’offres',
    neoChatPromptShowTenders: 'Montrez-moi les appels d’offres pertinents',
    neoChatChipMeetingPrep: 'Préparation de réunion',
    neoChatPromptMeetingPrep: 'Préparez-moi pour une réunion',
    neoChatStepAccount: 'Compte',
    neoChatStepType: 'Type',
    neoChatStepAttendees: 'Participants',
    neoChatSelectAccount: 'Sélectionner un compte',
    neoChatLoadingAccounts: 'Chargement des comptes...',
    neoChatSearchAccounts: 'Rechercher des comptes...',
    neoChatNoAccountsFound: 'Aucun compte trouvé.',
    neoChatSelectMeetingType: 'Sélectionner le type de réunion',
    neoChatMeetingWith: 'Réunion avec {accountName}',
    neoChatAskingAbout: 'Question sur {accountName}',
    neoChatClearAccount: 'Arrêter de poser des questions sur ce compte',
    neoChatBack: 'Retour',
    neoChatSelectAttendeesOptional: 'Sélectionner les participants (facultatif)',
    neoChatLoadingPeople: 'Chargement des contacts...',
    neoChatSearchPeople: 'Rechercher des contacts...',
    neoChatNoPeopleFound: 'Aucun contact trouvé',
    neoChatSkip: 'Passer',
    neoChatGenerate: 'Générer',
    neoChatMeetingDiscovery: 'Appel de découverte',
    neoChatMeetingDemo: 'Démo / Présentation',
    neoChatMeetingProposal: 'Revue de proposition',
    neoChatMeetingNegotiation: 'Négociation',
    neoChatMeetingRenewal: 'Renouvellement',
    neoChatMeetingQbr: 'QBR / Revue',
    neoChatPrepareMeetingUser: 'Préparez-moi pour une {meetingType} avec {accountName}',
    neoChatGiveBriefOn: 'Donnez-moi un brief sur {accountName}',
    neoChatBriefOnAccountName: 'Donnez-moi un brief sur {account_name}',
    neoChatErrorCallNeo: 'Désolé, je n’ai pas pu joindre NEO : {error}',
    neoChatErrorMeetingPrep: 'Désolé, je n’ai pas pu générer le brief de réunion. {error}',
    neoChatErrorMeetingPrepTimeout:
      'La requête a expiré. La préparation peut prendre jusqu’à une minute — réessayez.',
    neoChatErrorAccountBrief: 'Désolé, je n’ai pas pu générer le brief du compte. Erreur : {error}',
    neoChatErrorOutreach:
      'Désolé, je n’ai pas pu générer le message d’outreach pour le moment. Erreur : {error}',
    neoChatErrorTenders: 'Désolé, je n’ai pas pu récupérer les appels d’offres. {hint}',
    neoChatNoDebrief:
      'Je n’ai pas trouvé de brief détaillé pour **{accountName}**. Cela signifie généralement que nous n’avons pas encore assez de données. Réessayez plus tard ou posez une autre question.',
    neoChatBriefGreetingFallback: 'Bonjour',
    neoChatBriefThings1: 'une chose qui vaut votre temps',
    neoChatBriefThings2: 'deux choses qui valent votre temps',
    neoChatBriefThings3: 'trois choses qui valent votre temps',
    neoChatBriefThings4: 'quatre choses qui valent votre temps',
    neoChatBriefThings5: 'cinq choses qui valent votre temps',
    neoChatTriggerIntroOne: 'À l’instant :',
    neoChatTriggerIntroMany: 'À l’instant — nouvelle activité sur vos comptes :',
    neoChatSelectAccountAndPerson: 'Sélectionner compte et contact',
    neoChatAccountLabel: 'Compte',
    neoChatPersonLabel: 'Contact',
    neoChatSelectAccountPlaceholder: 'Sélectionner un compte...',
    neoChatSelectPersonPlaceholder: 'Sélectionner un contact...',
    neoChatNoPeopleForAccount: 'Aucun contact trouvé pour ce compte',
    neoChatConfigureOutreachTo: 'Configurer l’outreach pour {name}',
    neoChatAtAccount: 'chez {accountName}',
    neoChatBackToPeople: 'Retour à la sélection des contacts',
    neoChatChannelLabel: 'Canal',
    neoChatToneLabel: 'Ton',
    neoChatMessageTypeLabel: 'Type de message',
    neoChatContextTriggerLabel: 'Contexte/Déclencheur',
    neoChatGenerating: 'Génération...',
    neoChatGenerateMessage: 'Générer le message',
    neoChatGenerateSequence: 'Générer une séquence de 3 messages',
    neoChatOutreachContextPlaceholder:
      'ex. « Suite à leur publication LinkedIn récente sur l’IA »',
    
    // Confidence levels
    high: 'Élevé',
    medium: 'Moyen',
    urgencyHigh: 'Urgence élevée',
    urgencyMedium: 'Urgence moyenne',
    urgencyLow: 'Faible urgence',
    priorityHigh: 'Priorité élevée',
    priorityMedium: 'Priorité moyenne',
    priorityLow: 'Priorité faible',
    priorityTooltipLead: 'La priorité combine l’adéquation de cette personne et la fraîcheur du signal.',
    
    // Procurement Profile
    fieldMetric: 'Champ / Métrique',
    value: 'Valeur',
    source: 'Source',
    confidence: 'Confiance',
    procurementIntelligence: 'Intelligence complète des achats et analyse du parcours des fournisseurs pour cette institution.',
    procurementProfileNA: 'Les données du profil d\'achat ne sont pas disponibles pour ce compte.',
    
    // Account Header
    students: 'étudiants',
    employees: 'employés',
    accountFitRationale: 'Justification de l\'adéquation du compte',
    excellentFit: 'Adéquation excellente',
    strongFit: 'Adéquation forte',
    moderateFit: 'Adéquation modérée',
    lowFit: 'Adéquation faible',
    client: 'client',
    buyingModelRationale: 'Justification du modèle d\'achat',
    bvoClassification: 'Classification BVO',
    incumbentSentiment: 'Sentiment de l\'opérateur en place',
    procurementFriendlyLabel: 'Ouvert',
    procurementStandardLabel: 'Standard',
    procurementBureaucraticLabel: 'Bureaucratique',
    procurementMonitorLabel: 'À surveiller',
    bvoBudgetDriven: 'Orienté budget',
    bvoValueSeeker: 'Recherche de valeur',
    bvoOutcomeFocused: 'Orienté résultats',
    bvoInnovationDriven: 'Orienté innovation',
    bvoRiskAverse: 'Aversion au risque',
    bvoComplianceFocused: 'Orienté conformité',
    sentimentAtRisk: 'À risque',
    sentimentPositive: 'Positif',
    sentimentNeutral: 'Neutre',
    sentimentNegative: 'Négatif',
    sentimentDissatisfied: 'Insatisfait',
    unknown: 'Inconnu',
    numberOfEmployees: 'Nombre d\'employés',
    departmentFitRationale: 'Justification de l\'adéquation',
    
    // Procurement Friendliness
    friendlyDesc: 'Cette institution dispose de processus d\'achat rationalisés et est généralement réceptive aux nouvelles relations avec les fournisseurs.',
    standardDesc: 'Cette institution suit des procédures d\'achat typiques avec des délais d\'évaluation standard.',
    bureaucraticDesc: 'Cette institution a des processus d\'achat complexes qui peuvent nécessiter des cycles de vente plus longs et des approbations supplémentaires.',
    monitorDesc: 'Cette institution a des dynamiques d\'achat difficiles qui nécessitent une surveillance attentive et un engagement stratégique.',
    procurementProcessClassification: 'Classification du processus d\'achat pour cette institution.',
    
    // Decision Window
    decisionWindowMonitor: 'Moniteur de fenêtre de décision',
    decisionWindowDesc: 'Indique quand un contrat est susceptible d\'être révisé. Les dates d\'expiration sont estimées à l\'aide des registres d\'achats, des cycles de contrat typiques et des avis de renouvellement. Lorsque les données manquent, nous projetons en fonction des dates d\'attribution et des modèles standard, en étiquetant toujours avec des niveaux de confiance.',
    
    // Tech Stack
    noTechData: 'Aucune donnée technologique spécifiée',
    
    // Tenant-specific fields
    pedagogicalModel: 'Modèle pédagogique et culturel',
    digitalLandscape: 'Paysage numérique',
    digitalFunding: 'Opportunités de financement numérique',
    learningInitiatives: 'Initiatives de développement de l\'apprentissage',
    patientCareGaps: 'Lacunes dans les soins aux patients',
    decisionDynamics: 'Dynamique de décision des parties prenantes',
    contentCoverage: 'Couverture du contenu',
    clientOpportunities: 'Opportunités clients',
    decisionMakers: 'Décideurs',
    engagementHooks: 'Points d\'engagement',
    recommendedTactics: 'Tactiques recommandées',
    
    // People Page
    keyDecisionMakers: 'Principaux décideurs et influenceurs',
    searchPeople: 'Rechercher des personnes par nom, rôle ou université...',
    name: 'Nom',
    account: 'Compte',
    country: 'Pays',
    department: 'Département',
    archetype: 'Archétype',
    pursuit: 'Suivi',
    socialScore: 'Score social',
    noPeopleFound: 'Aucune personne trouvée',
    adjustFilters: 'Essayez d\'ajuster votre recherche ou vos filtres.',
    formerClient: 'Ancien client',
    formerClientOnly: 'Ancien client uniquement',
    allPersonas: 'Toutes les personas',
    minSocialScore: 'Score social min.',
    previouslyWorked: 'A précédemment travaillé pour l\'un de nos clients',
    socialPresence: 'Mesure la visibilité et l\'influence de la personne sur les plateformes sociales comme LinkedIn.',
    personaFitRationale: 'Justification de l\'adéquation de la persona',
    strategic: 'Stratégique',
    supportive: 'Favorable',
    passive: 'Passif',
    showContactInfo: 'Afficher les coordonnées',
    satisfied: 'Satisfait',
    neutral: 'Neutre',
    frustrated: 'Frustré',
    exploringAlternatives: 'Exploration d\'alternatives',
    
    // Signals Page
    trackSignals: 'Suivez l\'élan du compte et l\'activité de la persona en temps réel pour identifier les opportunités et vous engager au bon moment.',
    allSignals: 'Tous les signaux',
    noAccountSignals: 'Aucun signal de compte trouvé',
    noPersonaSignals: 'Aucun signal de persona trouvé',
    
    // Playbooks Page
    structuredEngagement: 'Stratégies d\'engagement structurées pour différents scénarios',
    createPlaybook: 'Créer un guide',
    searchPlaybooks: 'Rechercher des guides...',
    allFactors: 'Tous les facteurs',
    eventPlaybooks: 'Playbooks d\'événements',
    targetedSequences: 'Exécutez des séquences ciblées pour vos événements à venir',
    noUpcomingEvents: 'Aucun événement à venir',
    addEventsInSettings: 'Ajouter des événements dans les Paramètres',
    selectEvent: 'Sélectionner un événement',
    previewAndApply: 'Aperçu et appliquer',
    yourCustomPlaybooks: 'Vos guides personnalisés',
    custom: 'Personnalisé',
    goal: 'Objectif',
    enrolledAccounts: 'Comptes inscrits',
    untouchedAccounts: 'Comptes non touchés',
    noPlaybooks: 'Aucun guide trouvé correspondant à vos critères',
    
    // Segments Page
    createManageExport: 'Créer, gérer et exporter des listes cibles',
    buildNewSegment: 'Créer un nouveau segment',
    rules: 'règles',
    created: 'Créé',
    viewResults: 'Voir les résultats',
    noSegmentsYet: 'Pas encore de segments',
    getStartedSegment: 'Commencez par créer votre premier segment.',
    deleteSegment: 'Supprimer le segment',
    segmentDeleted: 'Segment supprimé avec succès',
    authRequired: 'Authentification requise',
    loginToAccess: 'Veuillez vous connecter pour accéder aux segments.',
    logIn: 'Se connecter',
    
    // Home Page
    yourWatchlist: 'Votre liste de surveillance',
    watchlistTooltip: 'Votre liste de surveillance suit les personas clés avec lesquelles vous vous engagez activement. Ces individus sont marqués pour la construction continue de relations et sont priorisés dans votre flux de travail.',
    open: 'Ouvrir',
    untrack: 'Arrêter de suivre',
    moreTracked: 'plus de personas suivies…',
    refreshing: 'Actualisation…',
    
    // Procurement Page
    dealsTenders: 'Offres et appels d\'offres',
    trackTenders: 'Suivez les appels d\'offres ouverts et les offres closes sur le marché.',
    searchKeyword: 'Rechercher par mot-clé, titre, institution...',
    liveDealsCount: 'Offres en cours',
    recentlyClosed: 'Récemment fermées',
    closedDeals: 'Offres fermées',
    allInstitutions: 'Toutes les institutions',
    filterByInstitution: 'Filtrer par institution',
    allCategories: 'Toutes les catégories',
    filterByCategory: 'Filtrer par catégorie',
    filterByEvent: 'Filtrer par événement',
    allEvents: 'Tous les événements',
    selectAll: 'Tout sélectionner',
    clearAll: 'Tout effacer',
    clearFilters: 'Effacer les filtres',
    typeToSearch: 'Tapez pour rechercher...',
    category: 'Catégorie',
    vendor: 'Fournisseur',
    institutionType: "Type d'établissement",
    sentiment: 'Sentiment',
    listOnly: 'Liste uniquement',
    mapPlusList: 'Carte + Liste',
    allDeadlines: 'Tous les délais',
    days0_7: '0–7 jours',
    days8_30: '8–30 jours',
    days31_90: '31–90 jours',
    days90Plus: '90+ jours',
    months0_6: '0–6 mois',
    months6_12: '6–12 mois',
    months12_24: '12–24 mois',
    unknownExpired: 'Inconnu / Expiré',
    allVendors: 'Tous les fournisseurs',
    exit: 'Sortie',
    filterByExitLeverage: 'Filtrer par levier de sortie',
    some: 'Certain',
    noSignal: 'Aucun signal',
    loadingDeals: 'Chargement des offres...',
    loadingContractIntelligence: 'Chargement de l\'intelligence contractuelle...',
    loadingMoreDeals: 'Chargement de plus d\'offres...',
    loadedDeals: 'Chargé {count} offres {label}',
    outOfScope: 'Hors périmètre',
    pending: 'En attente',
    insufficientInfo: 'Info insuffisante',
    liveRadar: 'Radar en direct',
    recentlyExpired: 'Récemment expirés',
    contractIntelligence: 'Intelligence contractuelle',
    displacementRadar: 'Radar de déplacement',
    benchmarking: 'Comparaison',
    selectCategoryBenchmarkTitle: 'Sélectionnez une catégorie pour voir la comparaison',
    selectCategoryBenchmarkDesc: 'Utilisez le filtre Catégorie ci-dessus pour choisir une ou plusieurs catégories. Les données de comparaison (effectifs vs $/étudiant/an) apparaîtront ici.',
    noClosedDealsBenchmarkTitle: 'Aucune offre close disponible',
    noClosedDealsBenchmarkDesc: 'La comparaison nécessite des offres closes. Vérifiez que le bootstrap renvoie des offres closes pour votre tenant.',
    selectCategoryContractTitle: 'Sélectionnez une catégorie pour explorer l\'intelligence contractuelle',
    selectCategoryContractDesc: 'Utilisez le filtre Catégorie ci-dessus pour choisir une ou plusieurs catégories. Les contrats historiques et données concurrentielles apparaîtront ici.',
    loadingContractData: 'Chargement des données de contrats…',
    alertSettings: 'Paramètres d\'alertes',
    title: 'Titre',
    institution: 'Établissement',
    deadline: 'Date limite',
    productFitPanel: 'Adéquation produit',
    noLiveDeals: 'Aucune offre active trouvée',
    noRecordsSelectedFilters: 'Aucun enregistrement trouvé pour la catégorie et les filtres sélectionnés. Essayez d\'autres catégories ou effacez les filtres.',
    marketMap: 'Carte du marché',
    marketMapHelpText: 'Cliquez sur un pays pour filtrer le tableau des offres ci-dessous et voir la part de marché de l\'incumbent (camembert). Utilisez les filtres Fournisseur et Sentiment pour affiner.',
    noDealsCountryFilters: 'Aucune offre dans ce pays pour les filtres actuels.',
    noAttackableAccounts: 'Aucun compte attaquable ne correspond aux critères.',
    previous: 'Précédent',
    next: 'Suivant',
    product: 'Produit',
    reseller: 'Revendeur',
    procurementMethod: 'Mode de passation',
    framework: 'Accord-cadre',
    productName: 'Nom du produit',
    supplier: 'Fournisseur',
    predictedExpiry: 'Expiration prévue',
    productFitProfileDesc: 'Définissez les profils produit pour le scoring d\'adéquation. Plusieurs profils peuvent être actifs : ils alimentent le Light Fit sur Live Radar (par catégorie) et vous choisissez un profil pour le Deep Fit sur un marché.',
    createProfile: 'Créer un profil',
    editProfile: 'Modifier le profil',
    newProfile: 'Nouveau profil',
    productFitName: 'Nom',
    productFitPrimaryCategory: 'Catégorie principale',
    productFitCategoryTags: 'Tags de catégorie (séparés par des virgules)',
    productFitStrongSignals: 'Signaux de forte adéquation (un par ligne)',
    productFitNegativeSignals: 'Signaux négatifs (préfixe Hard: ou Soft:, un par ligne)',
    productFitDeploymentModels: 'Modèles de déploiement',
    productFitCloud: 'Cloud',
    productFitSelfHosted: 'Auto-hébergé',
    productFitHybrid: 'Hybride',
    productFitIntegrations: 'Intégrations (séparées par des virgules)',
    productFitSecurity: 'Certifications de sécurité (séparées par des virgules)',
    productFitKeyFeatures: 'Fonctionnalités clés (séparées par des virgules)',
    productFitNameExample: 'ex. Noyau LMS',
    productFitCategoryTagsExample: 'LMS, EdTech, Évaluation',
    productFitStrongSignalsExample: 'Intégration Canvas requise\nSupport LTI 1.3\nConformité SCORM',
    productFitNegativeSignalsExample: 'Hard: Doit utiliser Microsoft SSO\nSoft: Préfère le déploiement cloud',
    productFitIntegrationsExample: 'Canvas, Moodle, LTI 1.3',
    productFitSecurityExample: 'ISO 27001, SOC 2',
    productFitKeyFeaturesExample: 'Analytique, application mobile, accessibilité',
    productFitUpdate: 'Mettre à jour',
    productFitCreate: 'Créer',
    productFitProfiles: 'Profils',
    productFitNoProfiles: 'Aucun profil pour le moment. Créez-en un ci-dessus.',
    productFitSetAsActive: 'Définir comme actif',
    productFitDeleteProfile: 'Supprimer le profil ?',
    productFitDeleteConfirm: 'Cela supprimera définitivement "{name}". Cette action est irréversible.',
    productFitSelectPrimaryCategory: 'Sélectionner la catégorie principale',
    productFitNone: 'Aucun',
    productFitActive: 'Actif',
    productFitDeactivate: 'Définir comme inactif',
    productFitActivateError: 'Impossible d\'activer le profil',
    productFitDeactivateError: 'Impossible de désactiver le profil',
    loadingProductProfiles: 'Chargement des profils produit...',

    // Person Profile
    backToPeople: 'Retour aux Personnes',
    back: 'Retour',
    personNotFound: 'Personne introuvable',
    personNotFoundDesc: 'La personne demandée n\'a pas pu être chargée ou n\'existe pas.',
    signalsCount: 'Signaux',
    interactions: 'Interactions',
    primaryInterests: 'Intérêts principaux',
    formerEmployers: 'Anciens employeurs',
    education: 'Formation',
    professionalMemberships: 'Adhésions professionnelles',
    publicSpeakingEvents: 'Conférences et événements',
    recentEventsAttended: 'Événements récents assistés',
    awardsRecognition: 'Prix et reconnaissances',
    publications: 'Publications',
    publicationContext: 'Publication',
    firstAuthorBadge: 'Premier auteur',
    seniorAuthorBadge: 'Auteur senior',
    relevanceTierVeryStrong: 'Pertinence très forte',
    relevanceTierStrong: 'Pertinence forte',
    showAllPublications: 'Tout afficher ({count})',
    showLess: 'Afficher moins',
    removeFromPursuit: '✓ Retirer du suivi',
    addToPursuit: 'Ajouter au suivi',
    assignMarketTier: 'Attribuer un niveau de marché',
    tier: 'Niveau',
    selectTier: 'Sélectionnez un niveau',
    saveChanges: 'Enregistrer les modifications',
    atAGlance: 'En un coup d\'œil',
    keyInitiatives: 'Initiatives clés et domaines d\'intervention.',
    noInitiatives: 'Aucune initiative listée.',
    
    // AI Outreach
    aiOutreach: 'Prospection IA',
    channel: 'Canal',
    email: 'E-mail',
    linkedin: 'LinkedIn',
    call: 'Appel',
    tone: 'Ton',
    relationshipBuilder: 'Bâtisseur de relations',
    thoughtLeadership: 'Leadership éclairé',
    challenger: 'Challenger',
    context: 'Contexte',
    autoDetect: 'Détecter automatiquement le meilleur contexte',
    personaInitiative: 'Initiatives et activité de la persona',
    institutionalChange: 'Changements institutionnels et dynamique',
    strategicPriority: 'Priorités stratégiques',
    procurementSignal: 'Signaux d\'achat et de financement',
    customContext: 'Contexte personnalisé (facultatif)',
    addContext: 'Ajouter un contexte spécifique, des points de discussion ou des instructions...',
    generating: 'Génération...',
    generateMessage: 'Générer un message',
    
    // Home Cards
    recommendedAccounts: 'Comptes recommandés',
    recommendedAccountsTooltip: 'Comptes les mieux notés en fonction de l\'adéquation et de l\'alignement stratégique.',
    viewAll: 'Voir tout',
    noAccounts: 'Aucun compte',
    recommendedPersonas: 'Personas recommandées',
    recommendedPersonasTooltip: 'Personnes les plus pertinentes en fonction de l\'influence et de l\'alignement des rôles.',
    noPersonas: 'Aucune persona',
    
    // Action Center
    priorityActions: 'Actions prioritaires',
    deals: 'Offres',
    all: 'Tout',
    myTasks: 'Mes tâches',
    newLeads: 'Nouveaux leads',
    updated: 'Mis à jour',
    loadingRecommendations: 'Chargement de vos recommandations personnalisées',
    analyzingPipeline: 'Analyse de votre pipeline...',
    dueToday: 'Échéance aujourd\'hui',
    completeTouchVia: 'Compléter le contact',
    via: 'via',
    openDraft: 'Ouvrir le brouillon',
    noContactOnFileOpenPersonas: 'Aucun contact enregistré pour ce compte — ouvrez l’onglet Personas pour en ajouter un.',
    openLinkedIn: 'Ouvrir LinkedIn',
    callNow: 'Appeler maintenant',
    done: 'Terminé',
    whyNow: 'Pourquoi maintenant?',
    nextSteps: 'Prochaines étapes:',
    tracked: 'Suivi',
    fit: 'Adéquation',
    draftOutreach: 'Brouillon de sensibilisation',
    draftMessage: 'Rédiger un message',
    noTasksDue: 'Aucune tâche à échéance aujourd\'hui',
    noNewLeads: 'Pas encore de nouveaux leads',
    noActionsYet: 'Pas encore d\'actions',
    hitRefresh: 'Cliquez sur Actualiser et je chercherai ce qui est à venir pour vous.',
    hotMovers: 'Plus actifs (2 dernières semaines)',
    personaUpdates: 'Mises à jour des personas (2 dernières semaines)',
    noSignals2Weeks: 'Aucun signal au cours des 2 dernières semaines',
    liveDealsTitle: 'Offres en cours',
    noDeadline: 'Pas de date limite',
    daysLeft: 'j restants',
    expired: 'Expiré',
    noLiveDealsCategories: 'Aucune offre en cours dans vos catégories sélectionnées.',
    noLiveDealsFound: 'Aucune offre en cours trouvée.',
    decisionWindowRadar: 'Radar de fenêtre de décision',
    decisionWindowTooltip: 'Indique quand un contrat est susceptible d\'être révisé en fonction des registres d\'achats et des modèles de renouvellement.',
    accountsApproaching: 'Comptes approchant des horizons de renouvellement/décision',
    noAccountsInBand: 'Aucun compte dans cette bande.',
    noAccountsWithWindows: 'Aucun compte avec fenêtres de décision trouvé.',

    // Today's Plan
    todaysPlanTitle: "Plan du jour",
    todaysPlanAccountFit: 'Adéquation du compte',
    todaysPlanCategoryAccountResearch: 'Recherche compte',
    todaysPlanCategoryFitMomentum: 'Adéquation et momentum',
    todaysPlanCategoryOutreach: 'Prospection',
    todaysPlanCategoryNewLeads: 'Nouveaux leads',
    openPersonas: 'Ouvrir les personas',
    track: 'Suivre',
    impactSuffix: 'Impact',
    suggestedNextSteps: 'Prochaines étapes suggérées',
    markRecommendationDone: 'Marquer cette recommandation comme terminée',
    todaysPlanPressRefresh: 'Appuyez sur Actualiser ci-dessus pour obtenir vos prochains mouvements personnalisés.',
    todaysTasksTitle: 'Tâches du jour',
    allCaughtUp: 'Tout est à jour !',
    noTasksScheduledToday: 'Aucune tâche prévue pour aujourd’hui',
    tasksCount: '{count} tâches',
    touchNumberLabel: 'Touchpoint n°{number}',
    sendEmail: 'Envoyer un e-mail',
    linkedinMessage: 'Message LinkedIn',
    makeCall: 'Appeler',
    taskCompletedTitle: 'Tâche terminée !',
    taskCompletedDescription: 'Bon travail, continuez ainsi',
    taskCompleteError: 'Impossible de marquer la tâche comme terminée',
    unknownAccount: 'Compte inconnu',
    unknownContact: 'Contact inconnu',
    completedFromTodaysPlan: 'Terminé depuis le plan du jour',
    researchGapBuyer: 'Aucun décideur budget confirmé sur la carte commerciale',
    researchGapChampion: 'Aucun champion interne identifié pour l’instant',
    researchGapBoth: 'Ni décideur budget ni champion confirmés',
    planReasonLeadership: 'Direction nouvelle ou en changement — les priorités peuvent être réinitialisées.',
    planReasonProcurement: 'Achats avec des nouveautés (processus, politique ou calendrier).',
    planReasonLiveDeal: 'Il y a une affaire active à faire avancer.',
    planReasonBoard: 'Conseil d’administration à venir — les décisions peuvent s’accélérer.',
    planReasonMomentum: 'L’engagement récent sur le compte semble fort.',
    planReasonPersonaActivity: 'Ils ont été actifs récemment.',
    planReasonFit: 'Bonne adéquation pour nous (compte/persona).',
    planReasonDecisionWindowUnknown: 'Le calendrier de décision n’est pas clair — à confirmer.',
    planReasonDecisionWindowMonths: 'Décision attendue dans ~{months} mois.',
    planReasonDecisionWindowGeneric: 'Dans la fenêtre de décision.',
    planReasonPursuit: 'Vous suivez cette personne (Pursuit).',
    todaysPlanRateLimitActive: 'Limite de débit active',
    todaysPlanCannotLoadRetry: 'Impossible de charger le plan du jour. Nouvelle tentative dans {remaining}.',
    todaysPlanRateLimitReached: 'Limite de débit atteinte',
    todaysPlanShowingCached: 'Affichage du plan en cache. Nouvelle tentative automatique dans 20 minutes.',
    todaysPlanCannotLoadWait: 'Impossible de charger le plan du jour. Veuillez attendre 20 minutes.',
    todaysPlanCouldNotLoad: 'Impossible de charger le plan du jour.',
    todaysPlanCannotRefreshRetry: 'Impossible d’actualiser le plan du jour. Nouvelle tentative automatique dans {remaining}.',
    todaysPlanRefreshed: 'Plan actualisé',
    todaysPlanRefreshedDescription: 'Le plan du jour a été mis à jour avec les dernières recommandations.',
    todaysPlanRefreshRateLimit: 'Actualisation impossible en raison de la limite de débit. Nouvelle tentative dans 20 minutes.',
    todaysPlanRefreshFailed: 'Impossible d’actualiser le plan. Réessayez.',
    todaysPlanRefreshFailedTitle: 'Échec de l’actualisation',
    todaysPlanAckRateLimit: 'Élément reconnu localement. Synchronisation à la fin de la limite (jusqu’à 20 min).',
    todaysPlanCouldNotSaveAck: 'Impossible d’enregistrer la confirmation.',
    couldNotUpdatePursuit: 'Impossible de mettre à jour Pursuit',
    pleaseTryAgain: 'Veuillez réessayer.',
    
    // Market Page
    marketMomentumTrend: 'Tendance de dynamique du marché (12 semaines)',
    noMomentumData: 'Aucune donnée de dynamique disponible',
    
    // Filters
    
    // Greetings
    goodMorning: 'Bonjour',
    goodAfternoon: 'Bon après-midi',
    goodEvening: 'Bonsoir',
    
    // Accounts Page
    manageExploreInstitutions: 'Gérer et explorer les institutions cibles',
    searchAccounts: 'Rechercher des comptes...',
    accountFitScore: 'Score d\'adéquation du compte',
    renewalRisk: 'Risque de renouvellement',
    expansionOpps: 'Opportunités d\'expansion',
    fariaProducts: 'Produits Faria',
    departmentFitScore: 'Score d\'adéquation du département',
    noAccountsFound: 'Aucun compte trouvé',
    
    // Market Components
    topAccounts: 'Meilleurs comptes',
    topPersonas: 'Meilleures personas',
    tieringTooltip: 'Les comptes sont initialement classés par niveau en fonction de leur Score d’Adéquation du Compte. La classification par niveaux vous aide à prioriser où concentrer vos efforts — afin de passer plus de temps sur les comptes à plus fort potentiel, mener une prospection plus ciblée et obtenir de meilleurs résultats. Vous pouvez modifier n’importe quel niveau à tout moment pour chaque compte.',
    topInstitutionsAI: 'Meilleures institutions par adoption et implémentation de l\'IA',
    topHospitalsAI: 'Meilleurs hôpitaux par adoption et implémentation de l\'IA',
    topSchoolsAI: 'Meilleures écoles internationales par adoption et implémentation de l\'IA',
    topEnterprisesAI: 'Meilleures entreprises par adoption et implémentation de l\'IA',
    topCompaniesAI: 'Meilleures sociétés par adoption et implémentation de l\'IA',
    topAuthoritiesAI: 'Meilleures autorités éducatives par adoption et implémentation de l\'IA',
    marketFitDistribution: 'Distribution d\'adéquation de marché',
    accountFitComposition: 'Composition du score d\'adéquation du compte sur des bandes de 10 points',
    topBuyers: 'Meilleurs acheteurs',
    universitiesMostVendors: 'Universités avec le plus de déploiements de nouveaux fournisseurs',
    enterprisesMostVendors: 'Entreprises avec le plus de déploiements de nouveaux fournisseurs',
    keyBuyingAreas: 'Principales zones d\'achat',
    functionalInvestment: 'Catégories fonctionnelles avec le plus d\'investissement',
    procurementComplexity: 'Complexité des achats',
    distributionProfiles: 'Distribution des profils d\'achat',
    procurementRoutes: 'Routes d\'achat',
    howInstitutionsBuy: 'Comment les institutions achètent la technologie',
    marketShare: 'Part de marché',
    marketWinners: 'Gagnants du marché',
    vendorsGaining: 'Fournisseurs gagnant de nouveaux contrats institutionnels',
    headlineTrends: 'Tendances principales',
    marketInsights: 'Aperçus du marché',
    disconnect: 'Déconnecter',
    connecting: 'Connexion...',
    disconnecting: 'Déconnexion...',
    saving: 'Enregistrement...',
    uploading: 'Téléversement...',
    default: 'Par défaut',
    language: 'Langue',
    history: 'Historique',
    engagement: 'Engagement',
    showAll: 'Tout afficher',
    gtmAnalytics: 'Analytique GTM',
    accountsByTier: 'Comptes par niveau',
    avgAccountFitAcrossAccounts: 'Fit moyen des comptes (tous les comptes)',
    unassigned: 'Non attribué',
    connectCrm: 'Connecter le CRM',
    topOfFunnelIntelligence: 'Intelligence haut de funnel',
    coverageTierPenetration: 'Couverture et pénétration par niveau',
    outboundExecution: 'Exécution outbound',
    accountsTouched: 'Comptes touchés',
    buyerChampion: 'Acheteur + Champion',
    multiThreaded: 'Multi-thread',
    playbooksRun: 'Playbooks exécutés',
    neoRecommendations: 'Recommandations NEO',
    accountInteractions: 'Interactions comptes',
    emailReplyRate: 'Taux de réponse e-mail',
    linkedinResponse: 'Réponse LinkedIn',
    callResponse: 'Réponse appel',
    emailOpenRate: 'Taux d’ouverture e-mail',
    emailClickRate: 'Taux de clic e-mail',
    accountsWithSignals: 'Comptes avec signaux',
    tendersGenerated: 'Appels d’offres générés',
    tendersReviewed: 'Appels d’offres consultés',
    timeSaved: 'Temps gagné',
    totalHoursSaved: 'Heures totales gagnées',
    hours: 'heures',
    usage: 'Utilisation',
    dailyActivity: 'Activité quotidienne',
    minPerUserWeek: 'min / utilisateur / semaine',
    replyRate: 'Taux de réponse',
    mostActive: 'Plus actifs',
    signalArchetypeOpenRates: 'Signal × Archétype — Taux d’ouverture',
    signalArchetypeClickRates: 'Signal × Archétype — Taux de clic',
    mostActiveAccounts: 'Comptes les plus actifs',
    mostActiveAccountsTooltip:
      'Comptes où plusieurs personas ont interagi et le volume total d’ouvertures humaines vérifiées dépasse le nombre de personas touchées.',
    mostActiveAccountsEmpty: 'Aucun compte avec engagement multi-personas pour l’instant.',
    adHoc: 'Ad hoc',
    adHocMessagePerformance: 'Performance des messages ad hoc',
    playbookAnalyticsMovedMostActive:
      'Les analyses de performance des playbooks sont dans l’onglet Plus actifs.',
    playbookAnalyticsUsePlaybooksPage:
      'Utilisez la page Playbooks pour les séquences et le statut opérationnel.',
    performanceByArchetype: 'Performance par archétype',
    performanceByDepartment: 'Performance par département',
    department: 'Département',
    performanceBySector: 'Performance par secteur',
    sector: 'Secteur',
    analyticsNoDataInPeriod: 'Aucune donnée sur la période sélectionnée.',
    analyticsTableSent: 'Envoyés',
    analyticsOpenRateCol: 'Taux d’ouverture',
    analyticsClickRateCol: 'Taux de clic',
    analyticsReplyRateCol: 'Taux de réponse',
    analyticsChannelCol: 'Canal',
    adHocReplyRateTooltip:
      'Le taux de réponse inclut uniquement les réponses humaines vérifiées. Les auto-tests et les messages entrants non vérifiés sont exclus.',
    adHocBreakdownFocusSignalNote:
      'Les ventilations par angle et par signal se rempliront au fil des nouveaux envois ad hoc.',
    adHocByCtaFamilyTitle: 'PAR FAMILLE DE CTA',
    adHocByFocusTitle: 'PAR ANGLE',
    adHocBySignalTitle: 'PAR SIGNAL',
    adHocCtaFamilyColumn: 'Famille de CTA',
    adHocPrimaryAngleCol: 'Angle principal',
    adHocSignalCol: 'Signal',
    adHocSendsFeaturingSignalCol: 'Envois incluant ce signal',
    adHocBySignalColumnTooltip:
      'Chaque envoi compte tous les signaux utilisés ; les lignes ne totalisent pas le chiffre du résumé.',
    ctaFamilyLabel_soft_binary: 'Binaire douce (question oui/non)',
    ctaFamilyLabel_action_oriented: 'Orientée action (demande de rendez-vous)',
    ctaFamilyLabel_other: 'Autre',
    ctaFamilyLabel_no_cta: 'Sans CTA',
    messagePerformanceInsightsTitle: 'Performance des messages',
    messagePerformanceInsightsDescription:
      'Vue croisée par type de CTA, famille de signal et signal — playbooks et messages ad hoc. Identifiez quels signaux et CTAs génèrent le plus d’engagement.',
    messagePerfPlaybookMessages: 'Messages playbook',
    messagePerfAdhocMessages: 'Messages ad hoc',
    messagePerfByCtaFamily: 'PAR FAMILLE DE CTA',
    messagePerfBySignalFamily: 'PAR FAMILLE DE SIGNAL',
    messagePerfBySignal: 'PAR SIGNAL',
    messagePerfByLinkClicked: 'PAR LIEN CLIQUÉ',
    messagePerfLabelLinkUrl: 'Lien',
    messagePerfLinkTrueClickRate: 'Taux de clic réel',
    messagePerfLinkTotalClicks: 'Clics',
    messagePerfLinkUniqueMessages: 'Messages',
    messagePerfLinkMessagesHeaderTooltip:
      'Messages distincts depuis lesquels ce lien a été cliqué.',
    messagePerfBestDayToSend: 'MEILLEUR JOUR POUR ENVOYER',
    messagePerfBestHourToSend: 'MEILLEURE HEURE POUR ENVOYER',
    messagePerfSendTimingRatesLine: '{open}% ouverture · {click}% clic · {reply}% réponse',
    messagePerfSendTimingFromSends: 'sur {count} envois',
    messagePerfSendTimingNotEnoughData: 'Pas encore assez de données.',
    messagePerfSendTimingTzCaption:
      'Heures d’envoi en : {tz} (fuseau de votre planification).',
    messagePerfSendTimingTzWarningUtc:
      'Heures d’envoi en : UTC — impossible de résoudre le fuseau de votre planification.',
    messagePerfSendTimingTzMismatch:
      'Heures d’envoi en : {resolved} — ne correspond pas au fuseau sélectionné ({requested}).',
    messagePerfSendTimingTzCaptionCharts:
      'Heures d’envoi en : {tz} — modifiez ci-dessus pour comparer les régions.',
    messagePerfSendTimingTzWarningUtcCharts:
      'Heures d’envoi en : UTC — impossible de résoudre le fuseau horaire sélectionné.',
    messagePerfSendTimingChartEmpty: 'Pas encore assez de données d’envoi.',
    messagePerfKeywordsSectionTitle: 'Mots-clés',
    messagePerfKeywordsSectionHint:
      'La taille reflète la fréquence ; la couleur le taux de réponse (plus vert = meilleures réponses). Survolez pour les envois et taux.',
    messagePerfKeywordsProductLabel: 'Contexte produit',
    messagePerfKeywordsSubjectTitle: 'Objets',
    messagePerfKeywordsBodyTitle: 'Corps du message',
    messagePerfKeywordsEmpty: 'Pas encore assez de données de mots-clés.',
    messagePerfSendTimingSectionTitle: 'Moment d’envoi',
    messagePerfSendTimingSeriesPlaybook: 'Playbook',
    messagePerfSendTimingSeriesAdhoc: 'Ponctuel',
    messagePerfSendTimingMergedTooltipLine: '{reply}% réponse · {sends} envois',
    messagePerfBySendDayChartTitle: 'PAR JOUR DE LA SEMAINE',
    messagePerfBySendHourChartTitle: 'PAR HEURE DE LA JOURNÉE',
    messagePerfSendTimingChartTooltip:
      '{label} · {sends} envois · {open}% ouverture · {click}% clic · {reply}% réponse',
    messagePerfSendTimeTimezoneLabel: 'Fuseau horaire d’envoi :',
    messagePerfSendTimeSchedulingTzHint: '(votre fuseau de planification est {tz})',
    messagePerfTzSchedulingSuffix: 'planification',
    messagePerfTzPlaceholder: 'Choisir un fuseau',
    messagePerfSendTimingYReply: 'Réponse %',
    messagePerfSendTimingYOpen: 'Ouverture %',
    messagePerfSendTimingLowVolumeNote: 'Faible volume — {sends} envois ; taux peu fiable',
    messagePerfByPrimaryAngle: 'PAR ANGLE PRINCIPAL (FOCUS)',
    messagePerfLabelCtaFamily: 'Famille de CTA',
    messagePerfLabelSignalFamily: 'Famille de signal',
    messagePerfLabelSignal: 'Signal',
    messagePerfLabelPrimaryAngle: 'Angle principal',
    messagePerfSignalSentTooltip:
      'Chaque envoi compte tous les signaux utilisés ; les lignes ne totalisent pas le chiffre du résumé — c’est voulu pour comparer la performance par signal.',
    messagePerfPlaybookEmptyCaption:
      'Pas encore de données playbook — aucun envoi playbook sur la période sélectionnée.',
    messagePerfAdhocEmptyCaption:
      'Pas encore de données ad hoc — aucun message ad hoc envoyé sur la période sélectionnée.',
    messagePerfNoBreakdownData: 'Aucune donnée de performance des messages sur cette période.',
    messagePerfShowAll: 'Tout afficher ({count})',
    messagePerfShowLess: 'Afficher moins',
    signalFamilyLabel_strategic_priority: 'Priorité déclarée',
    signalFamilyHint_strategic_priority:
      'Une priorité stratégique que l’établissement a explicitement nommée dans son plan ou sa stratégie.',
    signalFamilyLabel_account_insights: 'Aperçus du compte',
    signalFamilyHint_account_insights:
      'Extraits du plan stratégique ou références au document complet, regroupés sur une seule ligne.',
    signalFamilyLabel_momentum: 'Mise à jour organisationnelle',
    signalFamilyHint_momentum:
      'Un changement, une annonce ou une actualité organisationnelle pertinente.',
    signalFamilyLabel_activity: 'Activité personnelle',
    signalFamilyHint_activity:
      'Quelque chose que le destinataire a fait personnellement (conférence, article, publication).',
    signalFamilyLabel_mandatory_signal: 'Signal le mieux noté',
    signalFamilyHint_mandatory_signal:
      'Le signal ayant obtenu la meilleure note pour ce destinataire ou ce contexte d’envoi.',
    signalFamilyLabel_framework_based: 'Référence de cadre',
    signalFamilyHint_framework_based:
      'Lié à une méthodologie ou un cadre (par exemple, un cycle d’accréditation).',
    signalFamilyLabel_trend_based: 'Tendance sectorielle',
    signalFamilyHint_trend_based: 'Une tendance plus large du secteur ou de l’industrie.',
    signalFamilyLabel_deal: 'Achats',
    signalFamilyHint_deal:
      'Activité liée aux achats, marchés publics ou processus d’approvisionnement.',
    signalFamilyLabel_case_study: 'Référence client',
    signalFamilyHint_case_study: 'Une référence à un cas client existant.',
    signalFamilyLabel_default: 'Autre',
    signalFamilyHint_default: 'Catégorie fourre-tout non classée.',
    analyticsAccount: 'Compte',
    analyticsAccountFit: 'Adéquation du compte',
    analyticsPersonasSent: 'Personas contactés',
    analyticsMultiPersonaOpens: 'Ouvertures multi-personas',
    analyticsOpensPerPersona: 'Ouvertures / persona',
    analyticsOpensPerPersonaSubtitle: 'ouvertures totales ÷ personas',
    analyticsMultiPersonaClicks: 'Clics multi-personas',
    sent: 'envoyés',
    calls: 'appels',
    allTiers: 'Tous les niveaux',
    allChannels: 'Tous les canaux',
    signalTypePerformance: 'Performance par type de signal',
    signalTypePerformanceTooltip: 'Signaux compte (momentum) : performance des actions sur les comptes où le signal existait dans la période. Signaux persona : performance des actions vers la personne concernée par le signal. Les deux incluent les messages playbook et manuels.',
    noSignalDataSelectedPeriod: 'Aucune donnée de signal sur la période sélectionnée',
    signalType: 'Type de signal',
    touched: 'Touché',
    bestChannel: 'Meilleur canal',
    sample: 'Échantillon',
    lowSampleSize: 'Taille d’échantillon faible (<30)',
    topResponsiveAccountsFor: 'Comptes les plus réactifs pour',
    noAccountsTouchedForSignal: 'Aucun compte touché pour ce signal',
    na: 'N/A',
    signalArchetypeReplyRates: 'Taux de réponse Signal × Archétype',
    notEnoughDataForMatrix: 'Pas assez de données pour la matrice',
    filterByTier: 'Filtrer par niveau',
    accountCommunicationsDepth: 'Profondeur des communications compte',
    depthScoreTooltip: 'Score de profondeur (0-100) : Personas (30pts) + Archétypes (30pts) + Réponses (30pts) + Récence (10pts)',
    noAccountDataSelectedPeriod: 'Aucune donnée compte sur la période sélectionnée',
    more: 'de plus',
    depth: 'Profondeur',
    archetypeCoverage: 'Couverture des archétypes',
    notTouched: 'Non touché',
    replied: 'Répondu',
    recommendations: 'Recommandations',
    goodCoverage: 'Bonne couverture !',
    recentActivity: 'Activité récente',
    customEventPlaybook: 'Playbook événement personnalisé',
    untitledPlaybook: 'Playbook sans titre',
    selectedForComparison: 'sélectionnés pour comparaison',
    playbookPerformance: 'Performance des playbooks',
    noPlaybookDataSelectedPeriod: 'Aucune donnée playbook sur la période sélectionnée',
    playbook: 'Playbook',
    runs: 'Exécutions',
    completionPercent: '% de complétion',
    bestArchetype: 'Meilleur archétype',
    comparePlaybooks: 'Comparer les playbooks',
    completionRate: 'Taux de complétion',
    medianReplyTime: 'Délai médian de réponse',
    stepFunnel: 'Funnel par étape',
    reply: 'réponse',
    trackYourGoToMarketPerformance: 'Suivez vos performances go-to-market',
    viewing: 'Affichage',
    allTeamActivity: 'Activité de toute l\'équipe',
    myActivity: 'Mon activité',
    specificTeammate: 'Collègue spécifique',
    chooseTeammate: 'Choisir un collègue',
    pipelineDeals: 'Pipeline et Offres',
    openPipeline: 'Pipeline ouvert',
    meetingsBooked: 'Réunions planifiées',
    dealsCreated: 'Offres créées',
    winRate: 'Taux de conversion',
    languagePreferenceSaved: 'Préférence de langue enregistrée !',
    failedToSaveLanguagePreference: 'Échec de l\'enregistrement de la préférence de langue',
    selectPreferredLanguageDescription: 'Sélectionnez votre langue préférée pour l\'interface de la plateforme. Les traductions seront appliquées aux en-têtes, boutons et éléments de navigation.',
    interfaceLanguage: 'Langue de l\'interface',
    choosePreferredLanguage: 'Choisissez votre langue préférée pour l\'interface de la plateforme',
    selectLanguage: 'Sélectionner une langue',
    languageScopeDisclaimer: 'Les en-têtes, la navigation, les boutons et les libellés de la plateforme seront traduits. Les noms de compte, noms de persona et contenus de données resteront dans leur langue d\'origine.',
    saveLanguagePreference: 'Enregistrer la préférence de langue',
    failedToLoadProfile: 'Échec du chargement du profil',
    profileUpdatedSuccessfully: 'Profil mis à jour avec succès',
    failedToSaveProfile: 'Échec de l\'enregistrement du profil',
    pleaseSelectImageFile: 'Veuillez sélectionner un fichier image',
    imageSizeLessThan5MB: 'La taille de l\'image doit être inférieure à 5 Mo',
    profilePhotoUpdatedSuccessfully: 'Photo de profil mise à jour avec succès',
    failedToUploadPhoto: 'Échec du téléversement de la photo',
    updatePersonalInfoAndPhoto: 'Mettez à jour vos informations personnelles et votre photo de profil',
    profilePhoto: 'Photo de profil',
    profile: 'Profil',
    updateYourProfilePicture: 'Mettez à jour votre photo de profil',
    uploadPhoto: 'Téléverser la photo',
    photoFormatAndSizeHint: 'JPG, PNG ou GIF. Taille max 5 Mo.',
    personalInformation: 'Informations personnelles',
    personalInfoGreetingUsage: 'Ces informations seront utilisées dans les salutations et communications',
    displayName: 'Nom affiché',
    yourName: 'Votre nom',
    displayNameUsageHint: 'Ce nom sera utilisé dans les salutations et communications',
    jobTitle: 'Poste',
    yourRoleOrPosition: 'Votre rôle ou poste dans l\'organisation',
    yourOrganization: 'Votre organisation',
    usedInEmailSignatures: 'Utilisé dans les signatures e-mail et communications formelles',
    saveProfile: 'Enregistrer le profil',
    gmailIntegration: 'Intégration Gmail',
    connectGmailDescription: 'Connectez votre compte Gmail pour envoyer des e-mails directement depuis NEO',
    loadingGmailStatus: 'Chargement du statut Gmail...',
    gmailComingSoon: 'L\'intégration Gmail arrive bientôt. La fonctionnalité est en cours de configuration.',
    gmailConnected: 'Gmail connecté',
    gmailNotConnected: 'Gmail non connecté',
    connectAccountToSendEmails: 'Connectez votre compte pour envoyer des e-mails',
    connectGmail: 'Connecter Gmail',
    sendEmailsFrom: 'Envoyer les e-mails depuis',
    selectEmailAddress: 'Sélectionner une adresse e-mail',
    loadingSignature: 'Chargement de la signature...',
    emailSignaturePreview: 'Aperçu de la signature e-mail',
    whyConnectGmail: 'Pourquoi connecter Gmail ?',
    gmailBenefitSendPersonalized: 'Envoyez des e-mails personnalisés directement depuis NEO',
    gmailBenefitTrackEngagement: 'Suivez les ouvertures d\'e-mails et l\'engagement',
    gmailBenefitManageOutreach: 'Gérez vos campagnes d\'approche efficacement',
    searchDrafts: 'Rechercher personne, objet, compte ou rôle…',
    allProducts: 'Tous les produits',
    allPlaybooks: 'Tous les guides',
    noDraftsFound: 'Aucun brouillon trouvé',
    createPlaybooksToGenerateDrafts: 'Créez des guides pour générer des brouillons d\'e-mail',
    cancelDraft: 'Annuler le brouillon',
    cancelDrafts: 'Annuler les brouillons',
    deleting: 'Suppression...',
    deleteDraft: 'Supprimer le brouillon',
    failedToLoadSchedule: 'Échec du chargement du planning',
    scheduleSaved: 'Planning enregistré',
    communicationScheduleUpdated: 'Votre planning de communication a été mis à jour',
    failedToSaveSchedule: 'Échec de l\'enregistrement du planning. Veuillez réessayer.',
    atLeastOneWindowRequired: 'Au moins une plage horaire est requise',
    addWindowBeforeRemoving: 'Ajoutez une nouvelle plage avant de supprimer celle-ci.',
    communicationSchedule: 'Planning de communication',
    configureCommsSchedule: 'Configurez quand NEO peut envoyer des communications en votre nom',
    commsScheduleHelp: 'Lorsque vous lancez un playbook, chaque étape (email, LinkedIn, appel) est planifiée dans la prochaine plage disponible à partir du décalage de jour défini. En dehors de ces plages, aucune communication n\'est planifiée.',
    complete: 'Complet',
    incomplete: 'Incomplet',
    availabilityWindows: 'Plages de disponibilité',
    addWindow: 'Ajouter une plage',
    day: 'Jour',
    week: 'Semaine',
    month: 'Mois',
    startTime: 'Heure de début',
    endTime: 'Heure de fin',
    saveSchedule: 'Enregistrer le planning',
    userIdMissing: 'L\'ID utilisateur est manquant',
    outreachHistory: 'Historique de prospection',
    outreachHistorySubtitle: 'Chronologie de toutes les tâches de messagerie effectuées par votre équipe',
    allUsers: 'Tous les utilisateurs',
    noOutreachHistoryPeriod: 'Aucun historique de prospection trouvé pour cette période.',
    failedToLoadEmailAddresses: 'Échec du chargement des adresses e-mail',
    emailAddressUpdated: 'Adresse e-mail mise à jour',
    emailsWillBeSentFrom: 'Les e-mails seront envoyés depuis',
    emailAddressesRefreshed: 'Adresses e-mail actualisées',
    emailAliasesReloaded: 'Vos alias e-mail ont été rechargés',
    signatureSaved: 'Signature enregistrée',
    emailSignatureSavedSuccessfully: 'Votre signature e-mail a été enregistrée avec succès.',
    failedToSaveSignature: 'Échec de l\'enregistrement de la signature. Veuillez réessayer.',
    outlookIntegration: 'Intégration Outlook',
    connectOutlookDescription: 'Connectez votre compte Microsoft Outlook pour envoyer des e-mails directement depuis NEO',
    loadingOutlookStatus: 'Chargement du statut Outlook...',
    outlookComingSoon: 'L\'intégration Outlook arrive bientôt.',
    outlookConnected: 'Outlook connecté',
    outlookNotConnected: 'Outlook non connecté',
    connectOutlook: 'Connecter Outlook',
    emailSignature: 'Signature e-mail',
    saveSignature: 'Enregistrer la signature',
    clear: 'Effacer',
    error: 'Erreur',
    configureWorkspacePreferences: 'Configurez votre espace de travail et vos préférences',
    profile: 'Profil',
    emails: 'E-mails',
    schedule: 'Planning',
    personalization: 'Personnalisation',
    events: 'Événements',
    productFit: 'Adéquation produit',
    completingGmailConnection: 'Finalisation de la connexion Gmail...',
    authorizationFailedOrCancelled: 'L\'autorisation a échoué ou a été annulée',
    gmailConnectedClosingWindow: 'Gmail connecté. Fermeture de la fenêtre...',
    failedToConnectGmail: 'Échec de la connexion Gmail',
    pleaseWait: 'Veuillez patienter...',
    windowWillCloseAutomatically: 'Cette fenêtre se fermera automatiquement.',
    youCanCloseThisWindow: 'Vous pouvez fermer cette fenêtre.',
    completingOutlookConnection: 'Finalisation de la connexion Outlook...',
    outlookConnectedClosingWindow: 'Outlook connecté. Fermeture de la fenêtre...',
    failedToConnectOutlook: 'Échec de la connexion Outlook',
    connectionFailed: 'Échec de la connexion',
    gmailDisconnected: 'Gmail déconnecté',
    gmailAccountDisconnected: 'Votre compte Gmail a été déconnecté.',
    disconnectNotApplied: 'Déconnexion non appliquée',
    backendStillReportsGmailConnected: 'Le backend signale toujours Gmail comme connecté. Veuillez réessayer ; si cela persiste, une correction backend est probablement nécessaire.',
    outlookDisconnected: 'Outlook déconnecté',
    outlookAccountDisconnected: 'Votre compte Outlook a été déconnecté.',
    backendStillReportsOutlookConnected: 'Le backend signale toujours Outlook comme connecté. Veuillez réessayer ; si cela persiste, une correction backend est probablement nécessaire.',
    unsavedChanges: 'Modifications non enregistrées',
    howToCopyOutlookSignature: 'Comment copier votre signature Outlook :',
    pasteSignatureHere: 'Collez votre signature ici...',
    whyConnectOutlook: 'Pourquoi connecter Outlook ?',
    outlookWorksWith365: 'Compatible avec les comptes Microsoft 365 et Outlook.com',
    connectedAs: 'Connecté en tant que',
    gmailConnectedSuccessfully: 'Gmail connecté avec succès',
    outlookConnectedSuccessfully: 'Outlook connecté avec succès',
    emailAddressUpdatedLocalOnly: 'Adresse e-mail mise à jour (local uniquement)',
    usingInSessionBackendRejectedAlias: 'Utilisée dans cette session. Le backend a rejeté la sauvegarde de preferred_outlook_alias (400) :',
    copySignatureAndPasteBelow: 'Copiez votre signature depuis Outlook et collez-la ci-dessous',
    outlookSignatureStep1: 'Ouvrez Outlook -> Fichier -> Options -> Courrier -> Signatures',
    outlookSignatureStep2: 'Sélectionnez votre signature, appuyez sur Ctrl+A (tout sélectionner), puis Ctrl+C (copier)',
    outlookSignatureStep3: 'Cliquez dans la zone ci-dessous puis appuyez sur Ctrl+V (coller)',
    signatureSavedWillBeUsed: 'Signature enregistrée et utilisée dans vos e-mails',
    preEvent: 'Pré-événement',
    dayOf: 'Jour J',
    postEvent: 'Post-événement',
    dayLabel: 'Jour',
    noGoalSet: 'Aucun objectif défini',
    asset: 'ressource',
    dayOffset: 'Décalage (jours)',
    negativeBeforeEvent: 'Négatif = avant l’événement',
    channel: 'Canal',
    useAiSignals: 'Utiliser les signaux IA',
    includeBookingLink: 'Inclure le lien de réservation',
    autoInsertAvailabilityLink: "Insère automatiquement le lien de disponibilité de l'événement dans le message.",
    addAvailabilityLinkToEnable: "Ajoutez un lien de disponibilité dans les paramètres de l'événement pour activer ceci.",
    touchGoal: 'Objectif du contact',
    touchGoalPlaceholder: 'Quel est l’objectif de ce message ?',
    messagingNotes: 'Notes de message',
    messagingNotesPlaceholder: 'Points spécifiques à inclure, ton souhaité, éléments à mentionner ou éviter...',
    instructionsPassedToAiTouch: "Ces instructions seront transmises directement à l'IA pour ce contact spécifique",
    collateralToShare: 'Ressources à partager',
    warmUpReading: 'Lecture de mise en confiance',
    noAssetsConfiguredInPersonalization: 'Aucune ressource configurée dans Paramètres - Personnalisation',
    selected: 'sélectionné(s)',
    failedToLoadEvents: 'Échec du chargement des événements',
    missingRequiredFields: 'Champs requis manquants',
    eventsSaved: 'Événements enregistrés',
    eventScheduleUpdated: "Votre planning d'événements a été mis à jour",
    eventScheduleUpdatedLocal: "Votre planning d'événements a été mis à jour (stockage local)",
    saveFailed: 'Échec de l’enregistrement',
    failedToSaveEvents: "Échec de l'enregistrement des événements",
    eventsAndConferences: 'Événements et conférences',
    trackEventsAndConferences: 'Suivez les événements et conférences auxquels vous participez',
    usingLocalStorageApiUnavailable: 'Utilisation du stockage local (API indisponible',
    myEvents: 'Mes événements',
    addConferencesTradeShows: 'Ajoutez des conférences, salons professionnels et prises de parole',
    addEvent: 'Ajouter un événement',
    noEventsAddedYet: 'Aucun événement ajouté',
    addEventsToEnableEventMessaging: 'Ajoutez des événements pour activer la messagerie liée aux événements',
    event: 'Événement',
    collapse: 'Réduire',
    expand: 'Développer',
    eventName: "Nom de l'événement",
    eventNameExample: 'ex. EDUCAUSE 2025',
    city: 'Ville',
    cityExample: 'ex. Denver',
    venue: 'Lieu',
    venueExample: 'ex. Centre de congrès',
    startDate: 'Date de début',
    endDate: 'Date de fin',
    boothNumber: 'Numéro de stand',
    boothNumberExample: 'ex. Hall A, Stand 123',
    availabilityLink: 'Lien de disponibilité',
    availabilityLinkPlaceholder: 'https://calendly.com/...',
    meetingOffer: 'Proposition de rendez-vous',
    meetingOfferExample: 'ex. démo produit de 15 minutes',
    messagingGuidelines: 'Consignes de messagerie',
    customInstructionsForAi: "Instructions personnalisées pour les messages générés par l'IA à chaque phase",
    preEventGuidelines: 'Consignes pré-événement',
    preEventGuidelinesExample: "ex. Mentionnez notre nouvelle analytique IA lancée à l'événement. Mettez en avant la démo en direct.",
    infoToIncludeBeforeEvent: 'Informations à inclure dans les messages avant l’événement',
    duringEventGuidelines: "Consignes pendant l'événement",
    duringEventGuidelinesExample: "ex. Proposez des démos sur place. Mentionnez que les créneaux restants sont limités.",
    postEventGuidelines: 'Consignes post-événement',
    postEventGuidelinesExample: "ex. Référencez la session de questions-réponses. Proposez l'enregistrement et les slides. Mentionnez l'offre spéciale post-événement.",
    sessionDetailsOptional: 'Détails de session (optionnel)',
    ifSpeakingOrWorkshop: 'Si vous animez une session ou un atelier',
    sessionTitle: 'Titre de session',
    sessionTitleExample: "ex. IA dans l'enseignement supérieur : et ensuite ?",
    sessionDescription: 'Description de session',
    sessionDescriptionPlaceholder: 'Brève description pour attirer les participants...',
    postEventAssets: 'Ressources post-événement',
    linksToShareFollowUp: 'Liens à partager dans les messages de suivi',
    sessionRecordingLink: "Lien de l'enregistrement",
    slidesLink: 'Lien des slides',
    postEventResourceLink: 'Ressource post-événement (livre blanc, etc.)',
    messageSequence: 'Séquence de messages',
    defineTouchesOutreachSequence: 'Définissez les contacts de votre séquence de prospection',
    usingDefaultPlaybook: 'Playbook par défaut utilisé. Activez le playbook personnalisé pour définir votre propre séquence.',
    createCustomSequence: 'Créer une séquence personnalisée',
    addTouch: 'Ajouter un contact',
    targetRegion: 'Région cible',
    targetCountries: 'Pays cibles',
    writeInNativeLanguage: 'Rédiger dans la langue nationale de chaque pays',
    writeInNativeLanguageHelp: 'Désactivé par défaut : tous les messages partent en anglais. Une fois activé, chaque message est rédigé dans la langue du pays du compte destinataire (portugais, espagnol ou français ; anglais ailleurs).',
    deselectAll: 'Tout désélectionner',
    selectAllNoRestrictions: 'Tout sélectionner (sans restrictions)',
    allCountriesSelectedStrong: 'Tous les pays sélectionnés',
    noGeoRestrictions: 'Aucune restriction géographique ne sera appliquée.',
    personasAnyCountryIncluded: "Les personas de n'importe quel pays seront inclus.",
    fixRequiredFieldsBeforeSaving: 'Corrigez les champs requis avant d’enregistrer',
    saveEvents: 'Enregistrer les événements',
    urlPlaceholder: 'https://...',
    settingsSavedSuccessfully: 'Paramètres enregistrés avec succès !',
    failedToSaveSettingsTryAgain: "Échec de l'enregistrement des paramètres. Veuillez réessayer.",
    usingLocalStorageApiUnavailableShort: 'Utilisation du stockage local (API indisponible)',
    languageBrandVoice: 'Langue et ton de marque',
    configureNeoCommunicationTenant: 'Configurez comment NEO communique pour ce tenant',
    english: 'Anglais',
    spanish: 'Espagnol',
    french: 'Français',
    german: 'Allemand',
    brazilianPortuguese: 'Portugais brésilien',
    arabic: 'Arabe',
    additionalStylePreferencesPlaceholder: "Règles de style supplémentaires qui remplacent les valeurs par défaut. Exemples : phrases courtes ; pas de jargon ; orthographe UK ; jamais de points d'exclamation ; commencer par une question",
    stylePreferencesOverrideHelp: "Ces préférences remplacent les règles de style/ton par défaut mais ne peuvent pas contourner les règles de sécurité QA (liste blanche d'URL, expressions interdites, limites de longueur, validation des placeholders).",
    companyProduct: 'Entreprise et produit',
    customizeCompanyProductPositioning: "Personnalisez la façon dont votre entreprise et produit sont positionnés. Laissez vide pour utiliser les valeurs par défaut du tenant.",
    companyNameExample: 'ex. Acme Learning',
    productNameExample: 'ex. Programmes d’enrichissement Future Skills',
    coreValuePropositionExample: 'ex. Programmes personnalisés qui engagent et inspirent les étudiants',
    leaveBlankUseTenantDefaultValueProp: 'Laissez vide pour utiliser la proposition de valeur par défaut du tenant',
    keyPerformanceIndicators: 'Indicateurs clés de performance (KPI)',
    defineMetricsThatMatter: 'Définissez les métriques qui comptent pour les clients de ce tenant',
    kpiNamePlaceholder: 'Nom du KPI (ex. Engagement étudiant)',
    kpiDescriptionPlaceholder: 'Description (ex. +45% d’utilisateurs actifs quotidiens)',
    productOptional: 'Produit (optionnel)',
    addKpi: 'Ajouter un KPI',
    messagingHooksDescription: 'Arguments clés et propositions de valeur par archétype.',
    useProductSelectorForHooks: 'Utilisez le sélecteur Produit pour aligner les hooks avec des produits spécifiques.',
    selectPersona: 'Sélectionner une persona',
    universalAllPersonas: 'Universel (toutes les personas)',
    champion: 'Promoteur',
    influencer: 'Influenceur',
    evaluatorTechnical: 'Évaluateur (technique)',
    endUser: 'Utilisateur final',
    hookUsedForPersonaType: 'Ce hook sera utilisé uniquement pour ce type de persona',
    hookText: 'Texte du hook',
    hookTextExample: 'ex. Enrichissement générateur de revenus sans investissement initial',
    caseStudiesClientReferences: 'Études de cas et références clients',
    caseStudiesClientReferencesDescription: 'Ajoutez des études de cas complètes (avec liens) ou des références clients (nom uniquement)',
    type: 'Type',
    selectType: 'Sélectionner un type',
    caseStudyWithLink: 'Étude de cas (avec lien)',
    clientReferenceNameOnly: 'Référence client (nom uniquement)',
    institutionName: "Nom de l'institution",
    institutionNameExample: 'ex. Oundle School',
    clientReferenceUsagePrefix: 'Sera utilisé comme : "Des écoles comme',
    clientReferenceUsageSuffix: 'ont constaté..."',
    caseStudyTitle: "Titre de l'étude de cas",
    caseStudyTitleExample: 'ex. Success Story Université de Melbourne',
    caseStudyUrlPlaceholder: 'https://example.com/case-study',
    descriptionOptional: 'Description (optionnelle)',
    briefOutcomeSummary: 'Bref résumé des résultats',
    addCaseStudyClientReference: 'Ajouter une étude de cas / référence client',
    usefulResourcesDocumentation: 'Ressources utiles et documentation pour ce tenant',
    linkNamePlaceholder: 'Nom du lien (ex. Démo produit)',
    linkUrlPlaceholder: 'URL (ex. https://example.com/demo)',
    addProductLink: 'Ajouter un lien produit',
    educationalResources: 'Ressources pédagogiques',
    educationalResourcesLead:
      'Contenu de mise en confiance en début de cadence, avant toute demande commerciale.',
    educationalResourcesExamples:
      'Exemples : articles de référence, guides d’autorité tiers, contenus « à quoi ressemble l’excellence ».',
    resourceTitle: 'Titre',
    educationalResourceTitlePlaceholder:
      'ex., Strategic Considerations for a Successful Student Survey Tool',
    educationalResourceUrlPlaceholder: 'https://exemple.com/article',
    addEducationalResource: '+ Ajouter une ressource pédagogique',
    invalidHttpUrl: 'Saisissez une URL http(s) valide',
    educationalResourceTitleTooLong: 'Le titre doit comporter au plus 200 caractères',
    educationalProductContextUnset: 'Non défini (équivalent à ALL)',
    selectResources: 'Sélectionner des ressources…',
    noEducationalResourcesYet:
      'Aucune ressource enregistrée — ajoutez-en via Paramètres → Ressources Pédagogiques',
    educationalResourcesHelp:
      'Optionnel. Choisissez les articles ou vidéos à citer pour ce contact. Vide = le moteur choisit.',
    saveSettings: 'Enregistrer les paramètres',
    eg: 'ex.',
  

    // Auto-filled missing keys
    momentumScoreAccount: 'Score de dynamique (niveau compte)',
    momentumScoreDesc: 'Le score de dynamique reflète l\'évolution hebdomadaire de l\'organisation qui rend l\'environnement plus ou moins favorable à l\'engagement, à l\'expansion ou au renouvellement du client.',
    signalsCaptured: 'Signaux captés :',
    scoreDerivation: 'Calcul du score :',
    personaActivityScore: 'Score d\'activité de la persona (niveau individuel)',
    personaActivityDesc: 'Le score d\'activité de la persona mesure dans quelle mesure un contact contribue de façon visible et concrète au changement numérique, opérationnel ou stratégique dans les domaines liés à la solution du client.',
    filterBySignal: 'Filtrer par type de signal',
    allTime: 'Toute la période',
    last1Day: 'Dernier jour',
    last7Days: '7 derniers jours',
    last14Days: '14 derniers jours',
    last30Days: '30 derniers jours',
    last90Days: '90 derniers jours',
    minScore: 'Score minimum',
    recently: 'récemment',
    filterByFactor: 'Filtrer par facteur',
    chooseEvent: 'Choisir un événement',
    daysAway: 'jours restants',
    today: 'Aujourd\'hui',
    allSectors: 'Tous les secteurs',
    sectors: 'Secteurs',
    steps: 'Étapes',
    clone: 'Dupliquer',
    standardPlaybooks: 'Playbooks standard',
    deletePlaybook: 'Supprimer le playbook',
    deletePlaybookConfirm: 'Voulez-vous vraiment supprimer',
    cannotUndo: 'Cette action est irréversible.',
    playbookDeleted: 'Playbook supprimé',
    failedToDelete: 'Impossible de supprimer le playbook',
    selectAccountFor: 'Sélectionner un compte pour',
    chooseAccountToApply: 'Choisissez le compte auquel appliquer ce playbook',
    noEventSelected: 'Aucun événement sélectionné',
    selectEventFirst: 'Sélectionnez d\'abord un événement ou ajoutez des événements dans Paramètres',
    noEligibleAccounts: 'Aucun compte éligible',
    deleteSegmentConfirm: 'Voulez-vous vraiment supprimer',
    failedToDeleteSegment: 'Impossible de supprimer le segment',
    tieringSnapshot: 'Aperçu par niveau',
    aiReadinessIndex: 'Indice de préparation à l\'IA 2025',
    // [2026-10-29] i18n audit: keys added
    financialSituation: 'Situation financière',
    marketConditions: 'Conditions du marché',
    sessionInviteBlockedTitle: 'Détails de la session requis pour l\'invitation',
    openEventsSettings: 'Ouvrir les paramètres des événements',
    sessionInviteReady: 'Titre et description de la session configurés',
    quickConnectRequiresAttendees: 'Nécessite au moins un participant confirmé',
    myDeals: 'Mes opportunités',
    routeToBuy: 'Voie d\'achat',
    productFitCategories: 'Catégories',
    productFitCategoriesDesc: 'Même liste de catégories que les Achats pour votre tenant (opportunités ouvertes et clôturées).',
    productFitCategoriesLoading: 'Chargement des catégories d\'achats…',
    productFitCategoriesFallback: 'Impossible de charger les catégories d\'achats ; affichage de la taxonomie complète. Enregistrez à nouveau une fois les Achats chargés, ou vérifiez l\'API.',
    productFitNoCategoriesSelected: 'Aucune catégorie sélectionnée : le profil ne sera associé à aucune opportunité par catégorie.',
    editSignatureBelowOrPullFromGmail: 'Modifiez votre signature ci-dessous ou récupérez celle de votre compte Gmail.',
    pasteOrTypeSignatureHere: 'Collez votre signature ici ou saisissez-la directement.',
    useGmailSignature: 'Utiliser la signature Gmail',
    noGmailSignatureFound: 'Aucune signature Gmail trouvée',
    selectAliasFirstOrSetInGmail: 'Sélectionnez d\'abord un alias ou définissez une signature dans les paramètres Gmail.',
    sessionInviteEventBanner: 'L\'invitation à la session nécessite un titre et une description de session pour l\'événement sélectionné. Modifiez l\'événement dans Paramètres → Événements et complétez les détails de la session.',
    sessionInviteMissingDetailsTip: 'Astuce : cet événement n\'a pas encore de détails de session. Les messages mentionneront l\'événement de façon générique ; ajoutez un titre de session dans Paramètres → Événements pour des invitations plus efficaces.',
    sessionInviteRequiresSessionDetails: 'Nécessite un titre et une description de session dans Paramètres → Événements',
    sessionDetailsSessionInviteNote: '(Le titre et la description de la session sont nécessaires pour utiliser le playbook d\'invitation à la session dans Playbooks.)',
    status: 'Statut',
    replies: 'Réponses',
    brandVoiceDescription: 'Description de la voix de marque',
    brandVoiceDescriptionPlaceholder: 'Décrivez le ton de votre marque, p. ex. chaleureux, expert, concis',
    additionalStylePreferencesOverride: 'Préférences de style supplémentaires (prioritaires)',
    companyName: 'Nom de l\'entreprise',
    coreValueProposition: 'Proposition de valeur principale',
    messagingHooks: 'Accroches de message',
    targetPersona: 'Persona cible',
    economicBuyer: 'Décideur économique',
    addMessagingHook: 'Ajouter une accroche',
    url: 'URL',
    productLinks: 'Liens produit',
    timezone: 'Fuseau horaire',
    caseStudy: 'étude de cas',
    customPlaybook: 'Playbook personnalisé',
    // [2026-10-29] i18n: UI chrome keys (market / procurement / account / playbooks)
    mkt12w: '12 sem',
    mkt4w: '4 sem',
    mktAboutEnrichment: 'À propos de l\'enrichissement',
    mktAboutLabel: 'À propos de {label}',
    mktAccountBarometer: 'Baromètre des comptes',
    mktAccountPageNotFound: 'Page du compte introuvable',
    mktAccountsAffected: 'Comptes concernés',
    mktAccountsAffectedTip: 'Nombre de vos comptes cibles où au moins un signal mentionne ce concurrent. Chaque compte n\'est compté qu\'une fois, même avec plusieurs signaux.',
    mktAccountsExaminedOf: '{examined} sur {total} comptes examinés',
    mktAccountsInMotion: 'Comptes en mouvement',
    mktAccountsInMotionHint: 'Institutions dont les signaux évoluent. Indiquées « en mouvement » sauf preuve d\'achat explicite.',
    mktAcrossNCountries: 'dans {n} pays',
    mktAcrossNProgrammes: 'sur {n} programmes',
    mktAdd: 'Ajouter',
    mktAddPastedVendors: 'Ajouter les fournisseurs collés',
    mktAddToAccounts: 'Ajouter aux comptes',
    mktAddToPilot: 'Ajouter au pilote',
    mktAddedLoadingNow: 'Ses contacts, signaux et historique sont en cours de chargement.',
    mktAddedToAccounts: 'Ajouté aux comptes',
    mktAddedToYourAccounts: 'Ajouté à vos comptes',
    mktAdding: 'Ajout…',
    mktAddressable: 'Adressable',
    mktAdjacent: 'Adjacent',
    mktAdjacentMarket: 'Marché adjacent',
    mktAdjacentPlayer: 'Acteur adjacent',
    mktAdjacentVendor: 'Fournisseur adjacent',
    mktAfter: 'Après',
    mktAiDef1: 'Exploration. Rien de publié et aucun signe d\'utilisation d\'outils d\'IA pour l\'instant.',
    mktAiDef2: 'Pilote. Quelque chose est en cours, sans politique pour l\'encadrer.',
    mktAiDef3: 'Politique en place. Publiée et approuvée, mais nous ne voyons pas qu\'elle soit appliquée.',
    mktAiDef4: 'Adoption encadrée. La politique et l\'usage réel sont tous deux attestés.',
    mktAiDef5: 'Intégrée. L\'IA fait partie de leur façon d\'enseigner et d\'évaluer.',
    mktAiEmbedded: 'Intégrée',
    mktAiExploring: 'Exploration',
    mktAiGovernedAdoption: 'Adoption encadrée',
    mktAiPiloting: 'Pilote',
    mktAiPolicyInPlace: 'Politique en place',
    mktAiReadiness: 'Maturité IA',
    mktAiReadinessBelow: 'Maturité IA inférieure à',
    mktAiReadinessIndex: 'Indice de maturité IA',
    mktAiReadinessWholeMarketHint: 'Maturité IA sur l\'ensemble du marché, pas seulement vos opportunités.',
    mktAiScale: 'Nous examinons deux éléments, la maturité de leur politique et la profondeur de l\'adoption des outils, et retenons le plus élevé. Les niveaux 4 et 5 exigent les deux ; une politique seule plafonne à 3. Tout ce qui n\'a pas été confirmé depuis 18 mois perd un niveau.',
    mktAllCategories: 'Toutes les catégories',
    mktAllCountries: 'Tous les pays',
    mktAllEntitledCountries: 'Tous les pays autorisés',
    mktAllProgrammes: 'Tous les programmes ({n})',
    mktAlreadyFunded: 'Déjà financés',
    mktAlreadyOnYourList: 'Déjà dans votre liste',
    mktAlsoPriority: 'Également prioritaires',
    mktAlsoPriorityHint: 'Comptes éligibles déjà prioritaires dans votre marché.',
    mktAmount: 'Montant',
    mktAnnualRunRate: 'Rythme annuel {annual}.',
    mktAwardCeiling: 'Plafond d\'attribution',
    mktAwardCeilingHint: 'La fourchette qu\'une seule attribution peut atteindre, selon les termes du financeur.',
    mktBackOnTeamSettings: 'Retour aux paramètres de l\'équipe.',
    mktBandCapacity: 'capacité {band}',
    mktBarometerScore: 'Score du baromètre',
    mktBarometerScoreAccountTip: 'Proximité du concurrent de 0 à 100. Calculée à partir de la force du signal × l\'atténuation liée à l\'ancienneté. Plus élevé = présence concurrente plus forte sur ce compte. Le sens (favorable/défavorable) est indiqué séparément sous Impact.',
    mktBarometerScorePersonaTip: 'Proximité du concurrent de 0 à 100 pour ce persona. Calculée à partir de la force du signal × l\'atténuation liée à l\'ancienneté. Plus élevé = présence concurrente plus forte. Le sens est indiqué séparément sous Impact.',
    mktBarrier: 'Obstacle',
    mktBefore: 'Avant',
    mktBuyingEvidence: 'Preuves d\'achat',
    mktBuyingSignals: 'Signaux d\'achat',
    mktByCountryN: 'Par pays ({n})',
    mktCandidates: 'Candidats',
    mktChangeAFilterFirst: 'Modifiez d\'abord un filtre.',
    mktChooseProductCategory: 'Choisissez une catégorie de produit',
    mktCloseCountryPanel: 'Fermer le panneau du pays',
    mktClosingSoon: 'Clôture prochaine',
    mktClosingSoonHint: 'Appels ouverts dont la date limite tombe dans les 45 prochains jours.',
    mktCompetitor: 'Concurrent',
    mktCompetitorLocked: 'Concurrent verrouillé — ne jamais remplacer',
    mktCompetitorLockedHint: 'Les institutions utilisant ces fournisseurs sont exclues de vos listes d\'opportunités. Ils restent dans la liste des concurrents, vous ne cherchez simplement pas à les gagner.',
    mktCompetitorSet: 'Concurrents',
    mktCompetitorSetHint: 'Si un fournisseur figure sur cette liste, qu\'il est présent dans une institution et qu\'il s\'y passe quelque chose, c\'est une opportunité de remplacement. Sans lui, le même compte est considéré comme une coexistence. Un nom par fournisseur suffit, nous gérons les variantes.',
    mktCompetitorView: 'Vue par concurrent',
    mktCompetitorsPresent: 'Concurrents présents',
    mktConfirmEmptyCompetitorSet: 'Si cette liste est vide, rien ne peut être un remplacement. Votre marché se limitera aux comptes sans solution actuelle. Enregistrer quand même ?',
    mktContextOnly: 'Contexte uniquement',
    mktContractScope: 'Périmètre du contrat',
    mktContractScopeUnknownHint: 'Personne ne l\'a encore établi. Cela ne veut pas dire que le déploiement est restreint : nous l\'excluons du score plutôt que de le pénaliser.',
    mktCouldNotAddAccount: 'Impossible d\'ajouter le compte',
    mktCouldNotAddThisAccount: 'Impossible d\'ajouter ce compte',
    mktCouldNotCompleteAction: 'Impossible de terminer l\'action',
    mktCouldNotLoadAccountsInMotion: 'Impossible de charger les comptes en mouvement',
    mktCouldNotLoadCategory: 'Impossible de charger cette catégorie',
    mktCouldNotLoadCompetitorData: 'Impossible de charger les données concurrentielles — réessayez.',
    mktCouldNotLoadFunding: 'Impossible de charger les financements',
    mktCouldNotLoadIgnored: 'Impossible de charger les comptes ignorés',
    mktCouldNotLoadMarketData: 'Impossible de charger les données du marché. Veuillez actualiser.',
    mktCouldNotLoadOpportunities: 'Impossible de charger les opportunités',
    mktCouldNotLoadPlayers: 'Impossible de charger les acteurs',
    mktCouldNotLoadPreferences: 'Impossible de charger les préférences',
    mktCouldNotLoadRenewalRisk: 'Impossible de charger le risque de renouvellement',
    mktCouldNotLoadSummary: 'Impossible de charger le résumé du marché',
    mktCouldNotMoveAccountBack: 'Impossible de remettre le compte',
    mktCouldNotOpenAccount: 'Impossible d\'ouvrir le compte',
    mktCouldNotRemoveAccount: 'Impossible de retirer le compte.',
    mktCouldNotResolveAccount: 'Impossible de retrouver ce compte dans votre liste de comptes pour l\'instant.',
    mktCouldNotSave: 'Impossible d\'enregistrer',
    mktCouldNotSwitch: 'Impossible de basculer',
    mktCountries: 'Pays',
    mktCoverage: 'Couverture',
    mktCurrentVersion: 'actuelle',
    mktDeadlineSpendBy: 'Date limite / à dépenser avant',
    mktDisplacement: 'Remplacement',
    mktDisqualifiers: 'Critères d\'exclusion',
    mktEligibleAccounts: 'Comptes éligibles',
    mktEligibleSpend: 'Dépenses éligibles',
    mktEmptyCompetitorSetWarning: 'Si cette liste est vide, rien ne peut être un remplacement. Votre marché se limitera aux comptes sans solution actuelle.',
    mktEnrichRequestToast: 'Nous traiterons votre demande sous 48 heures et vous préviendrons quand elle sera prête.',
    mktEnrichTooltip: 'Envoie ce compte à l\'équipe de recherche pour combler les manques : quel fournisseur, quelle étendue, la fin du contrat et leur satisfaction. Votre score ne changera pas aujourd\'hui. Au retour de la recherche, le compte est réévalué et vous le verrez dans Ce qui a changé.',
    mktEnrichmentRequested: 'Enrichissement demandé',
    mktEnrichmentRequestedOn: 'Demandé le {date}. Nous réévaluerons ce compte au retour de la recherche.',
    mktEstSpendBy: 'à dépenser avant (est.)',
    mktEstimatedValue: 'Valeur estimée',
    mktEvidence: 'Preuves',
    mktExaminedOf: '{examined} sur {total} examinés',
    mktExaminedSuffix: ' examinés',
    mktExcludeCurrentCustomers: 'Exclure les clients actuels',
    mktExistingCustomer: 'Client existant',
    mktExistingExpansion: 'Existants / expansion',
    mktExpansion: 'Expansion',
    mktFavourable: 'Favorable',
    mktFavourableDisplacementOpps: 'Opportunités de remplacement favorables',
    mktFinancialBandBelow: 'Niveau financier inférieur à',
    mktFinancialBandBelowHint: 'Marge estimée pour financer de nouvelles initiatives, fondée sur les dépenses de fonctionnement et les réserves par étudiant ainsi que la marge opérationnelle, comparée aux pairs nationaux du même secteur. Élevée signifie davantage de marge pour investir ; Faible peut signaler une pression budgétaire. Cela reflète une capacité de dépense, pas un budget déjà réservé aux logiciels.',
    mktFinancialCapacity: 'Capacité financière',
    mktFinancialWholeMarketHint: 'Capacité financière sur l\'ensemble du marché. La marge dont disposent les institutions pour financer quelque chose de nouveau, pas leurs dépenses EdTech.',
    mktFitAddUnscoredFallback: 'Ce compte figure dans vos Comptes, mais Marché n\'avait pas de score pour lui.',
    mktFlat: 'Stable',
    mktFullAssessment: 'Évaluation complète',
    mktFunder: 'Financeur',
    mktFunding: 'Financements',
    mktFundingAccountsHint: 'Comptes que ce financement peut atteindre. Comment présenter interroge NEO avec le programme, le montant et l\'éligibilité de cette ligne.',
    mktFundingEligibility: 'Éligibilité aux financements',
    mktFundingEligible: 'Éligibles',
    mktFundingEligibleHint: 'Comptes pouvant consacrer ce financement à votre catégorie, parmi les comptes de ce marché.',
    mktFundingNotSwitchedOn: 'Financements n\'est pas encore activé pour ce client.',
    mktGapClientsOnly: 'Dans votre liste de clients. Nous n\'avons pas confirmé quel produit ils utilisent.',
    mktGapStackOnly: 'Utilise votre produit d\'après le registre des fournisseurs, mais ne figure pas dans votre liste de clients.',
    mktGradeDocumented: 'Documenté',
    mktGradeDocumentedHint: 'L\'institution a publié un défaut précis du produit du fournisseur, généralement dans une déclaration d\'accessibilité.',
    mktGradeExplicit: 'Explicite',
    mktGradeExplicitHint: 'Une instance de gouvernance, un sénat, un comité ou une association étudiante a consigné la plainte officiellement.',
    mktGradeImplied: 'Implicite',
    mktGradeImpliedHint: 'Nous avons identifié des difficultés dans le processus couvert par ce fournisseur, mais personne n\'a dit vouloir en changer.',
    mktGradeReported: 'Signalé',
    mktGradeReportedHint: 'La plainte est publiée quelque part, mais pas lors d\'une réunion où se prennent les décisions.',
    mktGradeTiming: 'Calendrier',
    mktGradeTimingHint: 'Leur contrat expire dans votre fenêtre de renouvellement : il repartira en appel d\'offres, quoi qu\'ils pensent du fournisseur.',
    mktGreenfield: 'Sans solution actuelle',
    mktGreenfieldAdjacent: 'Sans solution / adjacents',
    mktHasOpportunities: 'A des opportunités',
    mktHeld: 'En attente',
    mktHeldHint: 'En attente ne signifie pas exclu. Nous attendons l\'effectif complet avant d\'appliquer votre minimum, plutôt que d\'écarter un compte sur un chiffre partiel.',
    mktHideCountryDetail: 'Masquer le détail par pays',
    mktHideOlderEvents: 'Masquer les événements anciens et hors catégorie',
    mktHighProximityAccounts: 'Comptes à forte proximité',
    mktHighestInYourMarket: 'Les plus élevés de votre marché : {names}',
    mktHowMuchWeHaveLookedAt: 'Ce que nous avons examiné',
    mktHowToPitch: 'Comment présenter →',
    mktIgnore: 'Ignorés',
    mktIgnoreAccount: 'Ignorer le compte',
    mktIgnoreHint: 'Comptes que nous avons examinés puis écartés. Ce n\'est pas la même chose que Reste du marché : ce sont des comptes que personne n\'a encore regardés.',
    mktIgnoreNotSwitchedOn: 'Cette liste présente les comptes que nous avons examinés puis écartés, et non ceux que personne n\'a regardés. Elle n\'est pas encore activée pour ce client.',
    mktImpactAccountTip: 'Indique si les preuves concurrentielles sur ce compte vous sont défavorables (Défavorable), favorables (Favorable — p. ex. insatisfaction / opportunité de remplacement), mitigées ou purement contextuelles.',
    mktImpactPersonaTip: 'Indique si les preuves concurrentielles pour ce persona vous sont défavorables (Défavorable), favorables (Favorable), mitigées ou purement contextuelles.',
    mktInHouse: 'Interne',
    mktInOurFavour: 'En notre faveur',
    mktInPilot: 'Dans le pilote',
    mktInPlay: 'En jeu',
    mktInScope: '({label})',
    mktInThisMarket: 'dans ce marché',
    mktInYourMarket: 'Dans votre marché',
    mktIncumbent: 'Fournisseur en place',
    mktIncumbentSentimentLine: 'Perception du fournisseur en place : {sentiment}',
    mktInstitutionShare: 'Part des institutions',
    mktInstitutions: 'Institutions',
    mktKeySectorTrends: 'Tendances clés du secteur',
    mktLastRefreshed: 'Dernière actualisation',
    mktLatestSignal: 'Dernier signal',
    mktLatestSignalAccountTip: 'Date du signal concurrent le plus récent sur ce compte.',
    mktLatestSignalPersonaTip: 'Date du signal concurrent le plus récent pour ce persona.',
    mktLatestUpdate: 'Dernière actualité',
    mktLatestUpdateTip: 'Résumé de la preuve la plus récente parmi tous les signaux de ce concurrent.',
    mktLeaving: 'Sortants : {names}',
    mktLens: 'Vue',
    mktLimited: 'Limitée',
    mktLiveSignal: 'Signal actif',
    mktLmsChipTitle: 'Ce n\'est pas une catégorie que vous vendez. Elle figure ici car tout le reste s\'y connecte.',
    mktLmsFamiliesNote: 'Votre vue uniquement. Des noms de famille, pas les libellés bruts des fournisseurs : Open LMS et Totara sont distincts de Moodle.',
    mktLmsShareHint: 'La part des institutions utilisant chaque LMS. C\'est un nombre d\'institutions, pas un chiffre d\'affaires.',
    mktLoadingEllipsis: 'Chargement…',
    mktLockPastedVendors: 'Verrouiller les fournisseurs collés',
    mktMainThemes: 'Thèmes principaux',
    mktMainThemesTip: 'Thèmes extraits des tags Themes: des signaux et des tags de thème des actualités du marché. Ils décrivent ce dont parle le concurrent, pas la force du signal.',
    mktMapTipEmpty: '{label}. Chargé, mais rien ne correspond dans cette catégorie.',
    mktMapTipNOpportunities: '{label}. {count} opportunités.',
    mktMapTipNotLoaded: '{label}. Nous n\'avons pas encore chargé ce marché.',
    mktMapTipOneOpportunity: '{label}. {count} opportunité.',
    mktMarkRead: 'Marquer comme lu',
    mktMarketEvents: 'Événements du marché',
    mktMarketEventsTip: 'Nombre d\'activités de marché attribuables à ce concurrent — événements régionaux, adoptions externes et autres mouvements sans lien avec vos comptes cibles.',
    mktMarketIsRescoring: 'Le marché est en cours de réévaluation. Vous pouvez continuer à consulter cette vue.',
    mktMarketOpportunity: 'Opportunité de marché',
    mktMarketPreferences: 'Préférences du marché',
    mktMarketRecomputed: 'Marché recalculé.',
    mktMarketScore: 'Score de marché',
    mktMarketSummary: 'Synthèse du marché',
    mktMarketTracker: 'Suivi du marché',
    mktMatchesForms: 'Correspond à {forms}',
    mktMatching: 'Correspondance…',
    mktMaximumEnrolment: 'Effectif maximal',
    mktMinimumEnrolment: 'Effectif minimal',
    mktMinimumEnrolmentHint: 'Lorsque nous n\'avons qu\'un effectif partiel, nous mettons le compte en attente au lieu de l\'écarter, afin qu\'un chiffre partiel n\'exclue jamais une grande institution.',
    mktMinimumPain: 'Difficulté minimale',
    mktMinimumPainHint: 'Noté de 0 à 5. Le degré de clarté avec lequel un problème doit être exprimé pour qu\'un compte fasse partie de votre marché. 2 : une simple mention ou une intention de corriger quelque chose. 3 : un défaut déclaré. 4 : un défaut précis, clairement décrit. 5 : une défaillance aux conséquences explicites, comme un manquement réglementaire. La plupart des équipes sont à 3 ; passer à 4 réduit les preuves retenues d\'environ 40 %.',
    mktMix: 'Répartition',
    mktMixed: 'Mitigé',
    mktMoveBackToMyList: 'Remettre dans ma liste',
    mktMovedBackToYourList: 'Remis dans votre liste',
    mktMovedToIgnore: 'Déplacé dans Ignorés',
    mktMoving: 'Déplacement…',
    mktNAccounts: '{n} comptes',
    mktNAccountsExcluded: '{n} comptes exclus',
    mktNCategories: '{n} catégories',
    mktNDays: '{n} jours',
    mktNHaveWonBefore: '{n} l\'ont déjà obtenu',
    mktNHeldPendingEnrolment: '{n} en attente d\'un effectif',
    mktNInMarket: '{n} dans le marché',
    mktNInYourMarket: '{n} dans votre marché',
    mktNLmsCells: '{n} cellules LMS',
    mktNMarketEvents: '{n} événements du marché',
    mktNMatchingQuery: '{n} correspondant à « {query} »',
    mktNNewSignals: '{n} nouveaux signaux',
    mktNOfTotalSetAside: '{n} sur {total} comptes écartés',
    mktNPersonas: '{n} personas',
    mktNSelected: '{n} sélectionnés',
    mktNSetAside: '{n} comptes écartés',
    mktNSignals: '{n} signaux',
    mktNamesYourCategories: 'Cite vos catégories ({n})',
    mktNeoDidNotSayWhy: 'NEO n\'a pas indiqué pourquoi.',
    mktNeoView: 'Avis de NEO',
    mktNetImpact: 'Impact net',
    mktNetImpactTip: 'Indique si les preuves concurrentielles vous sont défavorables (Défavorable), favorables (Favorable, p. ex. insatisfaction / opportunité de remplacement), mitigées (Mitigé) ou purement informatives (Contexte uniquement).',
    mktNewSignals90d: 'Nouveaux signaux (90 derniers jours)',
    mktNewSignals90dTip: 'Signaux datés des 90 derniers jours.',
    mktNo: 'Non',
    mktNoAccountsInGroup: 'Aucun compte dans ce groupe pour le moment.',
    mktNoAccountsInMotionFor: 'Aucun compte en mouvement pour {scope}.',
    mktNoAccountsInView: 'Aucun compte dans cette vue.',
    mktNoAccountsMatchQuery: 'Aucun compte ne correspond à « {query} ».',
    mktNoAccountsMatchSearch: 'Aucun compte ne correspond à cette recherche.',
    mktNoAccountsSetAside: 'Aucun compte écarté pour ce marché pour l\'instant.',
    mktNoAccountsWithReason: 'Aucun compte avec ce motif dans le filtre actuel.',
    mktNoAiCompositionFor: 'Aucune répartition de maturité IA pour {scope}.',
    mktNoAtRisk: 'Aucun compte à risque connu dans cette vue pour l\'instant.',
    mktNoAtRiskForCountry: 'Aucun compte à risque connu dans cette vue pour {country} pour l\'instant.',
    mktNoCachedDataWait20: 'Aucune donnée en cache. Veuillez patienter 20 minutes avant d\'actualiser.',
    mktNoCompetitorEvidence: 'Aucune preuve concurrentielle chargée pour l\'instant.',
    mktNoFinancialCompositionFor: 'Aucune répartition de capacité financière pour {scope}.',
    mktNoLmsDataFor: 'Aucune donnée fournisseurs LMS pour {scope}',
    mktNoPriorVersions: 'Aucune version antérieure.',
    mktNoProgrammesInView: 'Aucun programme dans cette vue.',
    mktNoRecentChanges: 'Aucun changement récent sur le marché.',
    mktNoRenewalEvidence: 'Aucune preuve de renouvellement enregistrée pour l\'instant.',
    mktNoSectorValues: 'Aucun secteur renseigné sur les comptes pour l\'instant.',
    mktNoVendorDataForCategory: 'Aucune donnée fournisseurs pour cette catégorie',
    mktNotAnOpportunity: 'Pas une opportunité',
    mktNotLoadedYet: 'Pas encore chargé',
    mktNothingChangedAlreadyThere: 'Rien n\'a changé — il y était déjà.',
    mktNothingClassifiedYet: 'Rien n\'est encore classé dans cette vue.',
    mktNothingInListYet: 'Rien dans cette liste pour l\'instant.',
    mktNothingLocked: 'Rien n\'est verrouillé. Tous les fournisseurs de votre liste de concurrents sont inclus.',
    mktNothingToPreview: 'Rien à afficher pour ce compte pour l\'instant.',
    mktNothingToPreviewTitle: 'Rien à prévisualiser',
    mktOfN: 'sur {n}',
    mktOfSeparator: ' sur ',
    mktOneAccount: '{n} compte',
    mktOneMarketEvent: '{n} événement du marché',
    mktOneNewSignal: '{n} nouveau signal',
    mktOnePersona: '{n} persona',
    mktOneSignal: '{n} signal',
    mktOpenRound: 'Appel ouvert',
    mktOpenRounds: 'Appels ouverts',
    mktOpenSalesMap: 'Ouvrir la SalesMap',
    mktOpenSetup: 'Ouvrir la configuration',
    mktOpening: 'Ouverture…',
    mktOpportunities: 'Opportunités',
    mktOpportunity: 'Opportunité',
    mktOtherN: 'Autres ({n})',
    mktOurCategories: 'Nos catégories',
    mktOurCategoriesLine: 'Nos catégories : {categories}',
    mktOurs: 'Le nôtre',
    mktPageNOfM: 'Page {n} sur {m}',
    mktPageSubtitle: 'Votre opportunité de marché personnalisée et les dernières informations GTM.',
    mktPasteVendorsPlaceholder: 'Collez des fournisseurs, un par ligne ou séparés par des virgules',
    mktPasteVendorsToLockPlaceholder: 'Collez les fournisseurs à verrouiller, un par ligne ou séparés par des virgules',
    mktPersona: 'Persona',
    mktPersonaBarometer: 'Baromètre des personas',
    mktPersonasAffected: 'Personas concernés',
    mktPersonasAffectedTip: 'Nombre de personas cibles avec une preuve concurrentielle directe — cités, tagués, nommés dans une étude de cas ou sollicités par un acteur concurrent. Les personas liés au compte mais non nommés personnellement ne comptent pas.',
    mktPersonasWithCompetitorLinks: 'Personas liés à des concurrents',
    mktPlayersChartTooltip: 'La part des institutions de votre marché utilisant chaque fournisseur dans cette catégorie. C\'est un nombre d\'institutions, pas un chiffre d\'affaires. Le total peut dépasser 100 % car un compte peut utiliser plusieurs fournisseurs.',
    mktPleaseTryAgain: 'Veuillez réessayer',
    mktPreferences: 'Préférences',
    mktPreferencesSaved: 'Préférences enregistrées',
    mktPrefsIntro: 'Définissez les règles qui déterminent ce qui compte comme une opportunité.',
    mktPrefsOnOwnSettings: 'Vous utilisez vos propres paramètres. Ceux de l\'équipe diffèrent.',
    mktPrefsOnTeamSettings: 'Vous utilisez les paramètres de l\'équipe. Enregistrer créera les vôtres.',
    mktPreviewChange: 'Prévisualiser la modification',
    mktPreviewCounts: 'Dans votre marché {sam} · Priorité {priority} · Exclus {excluded}',
    mktPreviewFailed: 'Échec de l\'aperçu',
    mktPreviewMoved: 'Entrées {in} · sorties {out} · promus {promoted} · rétrogradés {demoted}',
    mktPreviewing: 'Aperçu en cours…',
    mktPreviouslyFunded: 'Financements antérieurs',
    mktPriority: 'Priorité',
    mktPriorityScoreThreshold: 'Seuil de score de priorité',
    mktPriorityScoreThresholdHint: 'Le score qu\'un compte doit atteindre pour être signalé comme Priorité. Il combine l\'adéquation de l\'institution avec ce que vous vendez et ce qui s\'y passe réellement. Vers 60, vous obtenez des institutions au bon profil même si rien ne bouge encore ; vers 70, la plupart auront aussi un problème déclaré ; à partir de 75, les deux sont forts et la liste est courte. Cela change votre point de départ, pas qui fait partie de votre marché.',
    mktProductCategory: 'Catégorie de produit',
    mktProgramme: 'Programme',
    mktProgrammes: 'Programmes',
    mktProgrammesHint: 'S\'ouvre sur les programmes qui citent l\'une de vos catégories ; les deux autres vues l\'élargissent. Un programme avec 0 compte éligible apparaît quand même : c\'est justement l\'écart qui compte. Une date limite n\'est affichée que si l\'appel est ouvert ; un programme actif indique la date à laquelle les fonds alloués doivent être dépensés, ce n\'est pas un appel auquel on candidate. Pré-annoncé signifie à surveiller, pas que la clôture est proche.',
    mktProvisional: 'Provisoire',
    mktQueueIntroAdjacent: 'Affiche les fournisseurs déjà présents avec lesquels vous n\'êtes pas en concurrence, pour comprendre l\'écosystème technologique existant et où votre solution pourrait s\'intégrer.',
    mktQueueIntroInMarket: 'De vraies ouvertures dans des institutions moins adaptées. À travailler une fois la Priorité passée en revue.',
    mktQueueIntroPriority: 'Vos meilleures ouvertures en ce moment : sans solution actuelle, remplacement ou expansion.',
    mktQueueIntroRest: 'Le reste du marché auquel vous pourriez vendre. Il manque des éléments pour les évaluer. Les meilleures adéquations apparaissent en premier.',
    mktRangeOfTotal: '{from}–{to} sur {total}',
    mktRareEvent: 'Événement rare',
    mktRareEventMeans: 'Un événement rare et qualifiant a relevé l\'axe d\'activité en cours de ce score.',
    mktRateLimitActive: '⏸️ Limite de requêtes active',
    mktRateLimitReached: '⚠️ Limite de requêtes atteinte',
    mktRecalculateFailed: 'Échec du recalcul',
    mktRecalculateMarket: 'Recalculer le marché',
    mktRecalculated: 'Recalculé',
    mktRecalculating: 'Recalcul…',
    mktRecalculationComplete: 'Recalcul terminé',
    mktRecency: 'Récence',
    mktRecommendation: 'Recommandation',
    mktRecommendationTip: 'Ouvre la SalesMap de ce compte. Lorsque la proximité atteint 50, une analyse concurrentielle se synchronise automatiquement avec la ligne current_vendor de la SalesMap.',
    mktRecomputedCounts: 'Dans votre marché {sam} · Priorité {priority}.',
    mktRelationship: 'Relation',
    mktRemove: 'Retirer',
    mktRemoveSkipNotFromMarket: 'Ce compte n\'a pas été ajouté depuis Marché. Utilisez plutôt Désengager.',
    mktRemoveSkipNotInAccounts: 'Ce compte ne figure pas dans votre liste.',
    mktRenewalDate: 'Date de renouvellement',
    mktRenewalNotSwitchedOn: 'Cet onglet couvre les institutions qui utilisent déjà votre produit : il indique qui vous pourriez perdre plutôt que qui vous pourriez gagner. Il n\'est pas encore activé pour ce client.',
    mktRenewalOpen: 'Renouvellement ouvert',
    mktRenewalWindow: 'Fenêtre de renouvellement',
    mktReplacingInHouse: 'Remplacement d\'une solution interne',
    mktRequestEnrichment: 'Demander un enrichissement',
    mktRequesting: 'Demande en cours…',
    mktRescoringTheMarket: 'Recalcul des scores du marché',
    mktRestOfMarket: 'Reste du marché',
    mktRestoredToAccounts: 'Restauré dans les comptes',
    mktReturnsOn: 'Revient dans votre liste le {date}',
    mktRisk: 'Risque',
    mktRiskConfirmed: 'Risque confirmé',
    mktRiskConfirmedMeans: 'Ils ont dit être mécontents de nous, ou leur contrat se termine dans l\'horizon.',
    mktRiskHint: 'Des faits précis sur ce qui ne va pas (une plainte, une insatisfaction, un défaut produit publié ou une fin de contrat), pas un score. La couleur distingue toujours confirmé et à surveiller.',
    mktRiskLookedNothingFound: 'Vérifié · rien trouvé',
    mktRiskNotYetChecked: 'Pas encore vérifié',
    mktRiskQuietMeans: 'Nous avons vérifié et rien trouvé. C\'est une vraie réponse, pas une lacune.',
    mktRiskUnknownMeans: 'Personne n\'a examiné ce compte. Ce n\'est pas la même chose qu\'un compte calme.',
    mktRiskWatch: 'À surveiller',
    mktRiskWatchMeans: 'Un défaut de notre produit a été publié ici, ou un concurrent s\'est installé dans une catégorie voisine.',
    mktRollBack: 'Revenir à cette version',
    mktRubricFitHint: 'L\'adéquation d\'une institution avec ce que vous vendez : ce qu\'elle utilise aujourd\'hui et sa remplaçabilité, sa taille et sa disposition apparente au changement. Augmentez-le pour privilégier les comptes au bon profil même quand rien ne s\'y passe encore.',
    mktRubricHistory: 'Historique de la grille',
    mktRubricSignalsHint: 'Les signes qu\'ils bougent vraiment : un problème déclaré avec leur solution actuelle, plus des achats, un budget, un renouvellement à venir ou une nouvelle attribution. Augmentez-le pour privilégier ce qui se passe plutôt que les comptes qui semblent bons sur le papier.',
    mktRubricWeights: 'Pondérations de la grille',
    mktRunningAgainstUs: 'En notre défaveur',
    mktSaveForEveryone: 'Enregistrer pour tous',
    mktSaveForMe: 'Enregistrer pour moi',
    mktSavedRescoreFailed: 'Enregistré, mais la réévaluation a échoué',
    mktSaving: 'Enregistrement…',
    mktScore: 'Score',
    mktScoresMayReflectPrevious: 'Les scores peuvent encore refléter les paramètres précédents.',
    mktSearchAccounts: 'Rechercher des comptes',
    mktSearchAccountsByName: 'Rechercher des comptes par nom',
    mktSearchByAccountName: 'Rechercher par nom de compte',
    mktSectorExclude: 'Secteurs exclus',
    mktSelectSectors: 'Sélectionner des secteurs',
    mktSectors: 'Secteurs',
    mktSectorsHint: 'Les types d’établissement qui composent votre marché. Choisissez-en un ou plusieurs.',
    mktNOfMSelected: '{n} sur {m} sélectionnés',
    mktSelectAll: 'Tout sélectionner',
    mktClearAll: 'Effacer',
    mktChooseOneSector: 'Choisissez au moins un secteur.',
    mktRescoreStillRunning: 'Recalcul en cours',
    mktRescoreStillRunningHint: 'Vos réglages sont enregistrés. Le marché est encore en cours de recalcul ; actualisez la page dans quelques minutes.',
    mktSelectedAccount: 'Compte sélectionné',
    mktSetUpMarket: 'Configurer Marché',
    mktSetupBodyEditor: 'Marché est activé pour ce client, mais personne n\'a encore choisi de catégorie de produit. Choisissez-en une et nous construirons votre liste d\'opportunités.',
    mktSetupBodyViewer: 'Marché est activé, mais un administrateur doit choisir une catégorie de produit avant que quelque chose puisse s\'afficher ici.',
    mktShare: 'Part',
    mktShareCaption: 'Le total des parts peut dépasser 100 %. Les fournisseurs marginaux sous {pct} % sont regroupés dans Autres (grisé).',
    mktShowNOlderEvents: 'Afficher {n} événements anciens ou hors catégorie',
    mktShowOneOlderEvent: 'Afficher {n} événement ancien ou hors catégorie',
    mktShowingAccountsOnPage: 'Affichage des comptes de cette page.',
    mktShowingCachedDataRetry20: 'Affichage des données en cache. Nouvel essai automatique dans 20 minutes.',
    mktSpendBy: 'à dépenser avant',
    mktSpendEstimateTitle: 'Notre estimation de la durée des fonds, pas une date publiée par le financeur',
    mktStage: 'Étape',
    mktStale: 'Obsolète',
    mktStanding: 'Situation',
    mktStateBuiltInHouse: 'Développement interne',
    mktStateLmsNative: 'Natif du LMS',
    mktStateNothingInPlace: 'Rien en place',
    mktStateOpenSource: 'Open source',
    mktStateSuiteModule: 'Module de suite',
    mktStatus: 'Statut',
    mktStatusEmpty: 'vide',
    mktStatusNotLoaded: 'non chargé',
    mktStrongestAccounts: 'Comptes les plus exposés',
    mktStrongestEvidence: 'Preuve la plus forte',
    mktStrongestEvidenceAccountTip: 'Le signal individuel le mieux noté pour ce compte — ce qui explique l\'essentiel de la proximité.',
    mktStrongestEvidencePersonaTip: 'Le signal individuel le mieux noté pour ce persona — ce qui explique l\'essentiel de la proximité.',
    mktStrongestPersonas: 'Personas les plus exposés',
    mktSubset: 'Sous-ensemble : {measure}',
    mktSumValue: 'Somme : {value}',
    mktSwitching: 'Basculement…',
    mktTabCompetitors: 'Concurrents',
    mktTabRenewalRisk: 'Risque de renouvellement',
    mktTargetThresholds: 'Seuils d\'opportunité',
    mktThisMarket: 'ce marché',
    mktTipAddressable: 'Celles de cette liste auxquelles vous pourriez réellement vendre. Si ce chiffre est égal au Marché total, aucune règle d\'exclusion n\'a encore été définie : rien n\'est filtré.',
    mktTipAiReadiness: 'Jusqu\'où cette institution a intégré l\'IA dans l\'enseignement et l\'évaluation, de 1 (simple exploration) à 5 (intégrée à son fonctionnement).',
    mktTipContractScope: 'Le degré d\'implantation de leur fournisseur actuel : toute l\'institution, plusieurs facultés, un département ou un essai. Plus c\'est large, plus l\'affaire est importante et plus il est difficile à déloger.',
    mktTipEvidence: 'La part des informations habituelles dont nous disposons sur ce compte, jugée par rapport à des institutions similaires plutôt qu\'à un dossier parfait. Quand elles sont minces, nous modérons le score au lieu de l\'estimer à la hausse. Cela mesure notre certitude, pas la qualité du compte.',
    mktTipExamined: 'La part de ce marché réellement examinée. Un chiffre bas signifie que la recherche n\'a pas été faite, pas que le marché est vide.',
    mktTipFinancial: 'La marge dont dispose cette institution pour financer quelque chose de nouveau, par rapport à d\'autres de même taille et de même type. Ce n\'est pas une mesure de ses dépenses EdTech.',
    mktTipGrade: 'La solidité des éléments indiquant que leur fournisseur actuel pourrait perdre le compte. Le plus fort : une plainte consignée au procès-verbal d\'une instance de gouvernance. Le plus faible : une difficulté que nous déduisons de leur fonctionnement, sans que personne ne se soit plaint.',
    mktTipInMarket: 'Comptes où nous avons trouvé matière à travailler : soit leur fournisseur actuel semble pouvoir perdre le compte, soit rien n\'a été acheté et il existe un vrai besoin. Cela ne signifie pas qu\'une affaire est ouverte.',
    mktTipIncumbent: 'Le fournisseur qu\'ils utilisent aujourd\'hui dans cette catégorie. Les institutions ont généralement des fournisseurs différents selon les catégories : c\'est celui qui compte pour cette affaire, pas le seul qu\'elles ont.',
    mktTipKnown: 'Toutes les institutions dont nous avons une fiche dans les pays que vous couvrez. Rien n\'a encore été filtré.',
    mktTipMomentum: 'Dynamique d\'achat sur les 4 et 12 dernières semaines. Un score négatif indique des signaux défavorables ; un score positif, des signaux favorables.',
    mktTipPriority: 'Les comptes de votre marché dont l\'adéquation est assez élevée pour commencer par eux. Commencez ici.',
    mktTipScore: 'Sur 100. L\'adéquation de l\'institution avec ce que vous vendez, combinée à ce qui s\'y passe en ce moment, puis modérée si les preuves sont minces.',
    mktTipType: 'Le type d\'affaire que ce serait. Survolez un badge pour voir ce qu\'il signifie.',
    mktTopCompetitor: 'Principal concurrent',
    mktTopCompetitorAccountTip: 'Concurrent ayant la plus forte contribution pondérée des signaux sur ce compte. Pondérée par la force et la récence du signal.',
    mktTopCompetitorPersonaTip: 'Concurrent ayant la plus forte contribution pondérée des signaux pour ce persona. Pondérée par la force et la récence du signal.',
    mktTotalCompetitorSignals: 'Total des signaux concurrents',
    mktTotalFundingEligibility: 'Éligibilité totale aux financements',
    mktTotalFundingEligibilityHint: 'Ce que cette institution pourrait demander sur l\'ensemble des appels ouverts auxquels elle est éligible. Un plafond, pas une prévision : l\'éligibilité est une inscription vérifiée, et rien n\'indique qu\'une candidature ait été déposée.',
    mktTotalMarket: 'Marché total',
    mktTrajectory24m: 'Trajectoire (24 m)',
    mktTrajectoryTooltip: 'Score de proximité recalculé à chaque mois sur les 24 derniers mois. Barres en hausse = nouveaux signaux. Barres en baisse = preuves qui vieillissent.',
    mktTriggers: 'Déclencheurs',
    mktUnfavourable: 'Défavorable',
    mktUnfavourableBlockers: 'Freins concurrentiels défavorables',
    mktUnknownVendor: 'Fournisseur inconnu',
    mktUpcomingWindows: 'Fenêtres à venir',
    mktUpdate: 'Mise à jour',
    mktUpdatingCompetitorSet: 'Mise à jour des concurrents et des filtres…',
    mktUseTeamSettings: 'Utiliser les paramètres de l\'équipe',
    mktUserSelected: 'Sélectionné par l\'utilisateur',
    mktUsingCachedDataRetryIn: 'Données en cache utilisées. Nouvel essai dans {time}.',
    mktUsingTeamSettings: 'Paramètres de l\'équipe utilisés',
    mktValueWholeContract: 'La valeur affichée couvre tout le contrat, pas une année. Cela représente {annual} par an.',
    mktValueWholeContractOverYears: 'La valeur affichée couvre tout le contrat, pas une année. Cela représente {annual} par an sur {years} ans.',
    mktWhatChanged: 'Ce qui a changé',
    mktWhatItIsWorth: 'Ce que cela représente',
    mktWhatThisMeans: 'Ce que cela signifie',
    mktWhatThisMeansFundingHint: 'Le même ton que la synthèse de l\'aperçu. L\'éligibilité n\'est pas du pipeline.',
    mktWhoIsInPlace: 'Qui est en place',
    mktWhoMightILose: 'Qui pourrais-je perdre',
    mktWholeMarket: 'Marché entier',
    mktWhy: 'Pourquoi',
    mktWhyNow: 'Pourquoi maintenant',
    mktWhyTheyQualify: 'Pourquoi ils sont éligibles',
    mktWhyThisScore: 'Pourquoi ce score',
    mktWindowClosed: 'fenêtre close',
    mktNoFixedDeadline: 'pas de date limite fixe',
    mktStandingProgramme: 'programme permanent',
    mktDateNotPublished: 'date pas encore publiée',
    mktClosedOn: 'clos le {date}',
    mktNoDeadlinePublished: 'date limite non publiée',
    mktWonBefore: 'Déjà obtenu',
    mktWorkingAgainst: 'Facteurs défavorables',
    mktWorkingInFavour: 'Facteurs favorables',
    mktWorthALook: 'À examiner',
    mktYes: 'Oui',
    mktYourPilotIsFull: 'Votre pilote est complet',
    procToastDataIncomplete: 'Certaines données peuvent être incomplètes',
    procToastTimeoutPage: 'Le serveur a expiré sur une page de résultats. Affichage de données partielles.',
    procToastTimeoutRecords: 'Le serveur a expiré lors du chargement d\'enregistrements supplémentaires. Affichage de résultats partiels.',
    procToastLoadingDeals: 'Chargement des opportunités',
    procToastFetchingInitialData: 'Récupération des données initiales',
    procToastLoadingMoreDeals: 'Chargement d\'autres opportunités',
    procToastFetchingLiveRadarDeals: 'Récupération des opportunités du Radar en direct',
    procToastFetchedLiveDeals: '{n} opportunités en direct récupérées…',
    procToastFailedLiveDeals: 'Échec du chargement des opportunités en direct',
    procLoadingRestOfMarket: 'Chargement du reste du marché…',
    procToastFetchingLabelDeals: 'Récupération des opportunités {label}…',
    procToastProcessingDealsLoaded: 'Traitement… {n} opportunités chargées',
    procToastFailedLabelDeals: 'Échec du chargement des opportunités {label}',
    procToastLabelReady: '{label} prêt',
    procToastFetchedVisible: '{fetched} récupérées, {visible} visibles avec les filtres actifs.',
    procToastStatusClearFailed: 'Échec de l\'effacement du statut',
    procToastStatusUpdateFailed: 'Échec de la mise à jour du statut',
    procToastValueOverrideFailed: 'Échec du remplacement de la valeur',
    procRetired: 'Retiré',
    procDealActivityUnavailable: 'Activité des opportunités indisponible — les badges de statut et de consultation peuvent être incomplets. Essayez d\'actualiser.',
    procMapNoDeals: 'Aucune opportunité',
    procMapTotalValueUsd: 'Valeur totale (USD)',
    procMapTop: 'Principales',
    procMapMetric: 'Indicateur de la carte :',
    procMapTotalValue: 'Valeur totale',
    procMapCountries: 'Pays',
    procMapClearSelection: 'Effacer la sélection',
    procRtbRouteFramework: 'accord-cadre',
    procRtbRouteConsortium: 'consortium',
    procRtbRouteCooperative: 'coopérative',
    procRtbRouteBuyingOrg: 'centrale d\'achat',
    procRtbRouteProgramme: 'programme',
    procRtbThroughFramework: 'Via un accord-cadre',
    procRtbThroughReseller: 'Via un revendeur',
    procRtbDirectToSupplier: 'En direct auprès du fournisseur',
    procAllCategories: 'Toutes les catégories',
    procYourCategories: 'Vos catégories',
    procRtbSelectCategoriesForLens: 'Sélectionnez des catégories pour utiliser ce filtre',
    procFailedToLoad: 'Échec du chargement',
    procRtbSubtitle: 'Comment ce marché achète réellement — les dispositifs d\'achat et qui vend par leur intermédiaire.',
    procRtbCoverageNote: 'Couvre tous les acheteurs publics du marché, tous secteurs confondus — pas seulement l\'enseignement supérieur.',
    procRtbLoadingCountry: 'Chargement de {country}…',
    procRtbMoreCountries: 'Autres pays',
    procRtbMoreCountriesOption: 'Autres pays…',
    procRtbHowCountryBuys: 'Comment achète {country}',
    procLoading: 'Chargement…',
    procUpdating: 'Mise à jour…',
    procRtbVehiclesThatMatter: 'Dispositifs d\'achat clés',
    procRtbNoFrameworkMine: 'Aucun accord-cadre identifié ne couvre vos catégories (marché : {country}).',
    procRtbNoFrameworkAll: 'Aucun accord-cadre identifié ne couvre ces opportunités (marché : {country}).',
    procRtbDealsLower: 'opportunités',
    procRtbBuyersLower: 'acheteurs',
    procRtbSuppliersLower: 'fournisseurs',
    procRtbMakersLower: 'fabricants',
    procRtbValueLower: 'valeur',
    procRtbRenewIn12Mo: 'renouvellement sous 12 mois',
    procRtbInsideVehicle: 'Au sein du dispositif',
    procRtbWhoSellsThroughIt: 'Qui vend par son intermédiaire (opportunités)',
    procRtbResellersOnIt: 'Revendeurs référencés (opportunités)',
    procRtbNoResellerOnVehicle: 'Aucun revendeur enregistré sur ce dispositif',
    procRtbAreYouOnIt: 'Y êtes-vous référencé ?',
    procRtbYesSoldThroughIt: 'Oui — votre produit est vendu par son intermédiaire',
    procRtbNotOnRecord: 'Non enregistré',
    procRtbNotConfigured: 'Non configuré pour ce client',
    procRtbResellersIn: 'Revendeurs — {country}',
    procRtbResellerShare: '{n} % des opportunités dont la voie est connue passent par un revendeur',
    procRtbNoResellerMine: 'Aucun revendeur ne vend dans vos catégories (marché : {country}) — toutes les opportunités enregistrées sont passées en direct par l\'éditeur.',
    procRtbNoResellerAll: 'Aucun revendeur ne vend ici (marché : {country}) — toutes les opportunités enregistrées sont passées en direct par l\'éditeur.',
    procRtbNotStated: 'non précisé',
    procRtbInsideReseller: 'Au sein du revendeur',
    procRtbMakersItCarries: 'Fabricants distribués (opportunités)',
    procRtbRecordNamesOnlyReseller: 'L\'enregistrement ne nomme que le revendeur, jamais le fabricant',
    procRtbMakerNamedPct: 'Fabricant nommé dans {n} % de ses opportunités — les autres ne mentionnent que le revendeur',
    procRtbSellsThrough: 'Vend via',
    procRtbDirectToBuyer: 'En direct auprès de l\'acheteur — aucun dispositif enregistré',
    procRtbCarriesYourProduct: 'Distribue-t-il votre produit ?',
    procYes: 'Oui',
    procRtbUpTo: 'jusqu\'à {amount}',
    procRtbAbove: 'au-delà de {amount}',
    procRtbHowBigBeforeTender: 'À partir de quel montant on passe en appel d\'offres',
    procRtbTypicalAcross: 'Typique sur {n} acheteurs',
    procRtbMostRange: 'la plupart {from}–{to}',
    procRtbNationalRule: 'Règle nationale',
    procMonths24Plus: '24+ mois',
    procVendorsForSelectedCategories: 'Fournisseurs des catégories sélectionnées',
    procUpdatingResults: 'Mise à jour des résultats',
    procClickHeadersToSort: 'Cliquez sur les en-têtes de colonne pour trier',
    procFilterAppliesTableOnly: '— ce filtre s\'applique uniquement à ce tableau ; la carte et les totaux ci-dessus ne sont pas filtrés.',
    procBundledContract: 'Contrat groupé',
    procNoRationaleProvided: 'Aucune justification fournie.',
    procShowingRangeDeals: 'Affichage de {from} à {to} sur {total} opportunités',
    procShowingRangeAccounts: 'Affichage de {from} à {to} sur {total} comptes',
    procMatchingDealsOnePerAccount: '{n} opportunités correspondantes ; le tableau affiche la principale opportunité de chaque compte',
    procMapNoClosedDeals: 'Aucune opportunité conclue',
    procTopVendorsByDeals: 'Principaux fournisseurs par opportunités',
    procTopVendorsByValue: 'Principaux fournisseurs par valeur',
    procClear: 'Effacer',
    procTop3DealsByValue: 'Les 3 principales opportunités par valeur',
    procMarketShare: 'Part de marché',
    procPieAccountsPct: '{n} comptes ({pct} %)',
    procMarketShareByVendor: 'Part de marché par fournisseur',
    procNoIncumbentDataCountry: 'Aucune donnée sur les fournisseurs en place pour ce pays.',
    procClickCountryForMarketShare: 'Cliquez sur un pays de la carte pour voir la part de marché.',
    procWin0_6Mo: '0–6 mois',
    procWin6_12Mo: '6–12 mois',
    procWin12_24Mo: '12–24 mois',
    procWin24PlusMo: '24+ mois',
    procWinNoEndDate: 'Sans date de fin',
    procHowLabelIsMeasured: 'Méthode de calcul : {label}',
    procMoveFrom: 'de',
    procMoveTo: 'vers',
    procPlusNMore: '+{n} de plus',
    procCouldNotLoadChanges: 'Impossible de charger les changements.',
    procWon: 'Gagnés',
    procLost: 'Perdus',
    procNoRecordedChanges: 'Aucun changement enregistré.',
    procWonLostCounts: 'gagnés {won} · perdus {lost}',
    procEstablishedRelationships: '{n} relations établies',
    procLoadingCategoryIntel: 'Chargement des données de catégorie…',
    procNoMeasuredHistory: 'Pas encore d\'historique mesuré pour les catégories sélectionnées.',
    procRcViewReplacement: 'Cycle de remplacement',
    procRcViewExpiry: 'Fin de contrat',
    procRcIntro: 'Équipements installés par âge, du plus ancien au plus récent. L\'âge court depuis le dernier achat du système ; les contrats de maintenance et les consommables ne le remettent pas à zéro. Un équipement est généralement remplacé après 5 à 7 ans, bien après la fin de son contrat.',
    procRcKpiInstalled: 'Équipements installés',
    procRcKpiInstalledSub: 'issus de {n} marchés attribués',
    procRcKpiWindow: 'En fenêtre de remplacement',
    procRcKpiWindowSub: '{n} ans ou plus depuis l\'achat',
    procRcKpiDue: 'À remplacer ou en retard',
    procRcKpiDueSub: '{n} ans ou plus depuis l\'achat',
    procRcKpiOwn: 'Votre parc vieillissant',
    procRcKpiOwnSub: 'vos installations de {n} ans ou plus',
    procRcWindowFilter: 'Fenêtre de remplacement :',
    procRcInstalled: 'Installé',
    procRcAge: 'Âge',
    procRcWindow: 'Fenêtre de remplacement',
    procRcServiceEnds: 'Fin du contrat de maintenance',
    procRcYourKit: 'Votre équipement',
    procRcVia: 'via {reseller}',
    procRcPurchases: '{n} achats du système',
    procRcFloorHelp: 'Aucun achat du système n\'est publié ; daté à partir du plus ancien contrat de maintenance ou de fourniture, l\'équipement a donc au moins cet âge.',
    procRcYears: '{n} ans',
    procRcShowing: '{shown} sur {total} équipements installés',
    procRcBandOverdue: 'En retard',
    procRcBandDue: 'À remplacer',
    procRcBandPlan: 'À planifier',
    procRcBandMid: 'Mi-vie',
    procRcBandNew: 'Récent',
    procRcBandUndated: 'Non daté',
    procRcRangeOverdue: '12 ans et +',
    procRcRangeDue: '7–12 ans',
    procRcRangePlan: '5–7 ans',
    procRcRangeMid: '3–5 ans',
    procRcRangeNew: 'moins de 3 ans',
    procRcRangeUndated: 'sans date',
    procRcRangeYears: '{a}–{b} ans',
    procRcRangeOver: '{a} ans et +',
    procRcRangeUnder: 'moins de {a} ans',
    procRcSincePurchase: "{n} ans ou plus depuis l'achat",
    procRcOwnYearsOld: 'vos installations de {n} ans ou plus',
    procRcRadarSubtitle: 'L\'âge du parc installé, qui détient les équipements vieillissants, et ce qu\'ont fait les établissements lors de leur dernier remplacement.',
    procRcKpiDated: '{n} avec une date d\'achat',
    procRcUpCompetitor: 'Parc concurrent à remplacer',
    procRcUpCompetitorSub: 'installations concurrentes de {n} ans ou plus',
    procRcUpOwn: 'Votre parc à renouveler',
    procRcUpOwnSub: 'vos installations de {n} ans ou plus',
    procRcSwitchRate: 'Changement de fabricant au remplacement',
    procRcSwitchTooFew: '{n} remplacements observés ; un taux en demande {min}',
    procRcSwitchSub: '{switched} ont changé · {retained} ont gardé le même fabricant',
    procRcMatrixTitle: 'Parc installé par âge et sentiment',
    procRcMatrixHelp: 'Chaque case compte des équipements installés. L\'âge court depuis le dernier achat du système.',
    procRcTotal: 'Total',
    procRcSuppliersTitle: 'Qui détient les équipements vieillissants',
    procRcSuppliersHelp: 'Installations par fabricant ; la partie ambre a {n} ans ou plus.',
    procRcAgeingOf: '{ageing} sur {n} vieillissants',
    procRcMovesTitle: 'Changements de fabricant au remplacement',
    procRcMovesHelp: 'Un rachat ne compte comme remplacement que s\'il intervient {n} ans ou plus après le précédent. {events} observés.',
    procRcNoMoves: 'Aucun changement de fabricant enregistré au remplacement pour l\'instant.',
    procRcInstallsAcross: '{installs} équipements installés dans {accounts} établissements, {scope}.',
    procRcSwitchHelp: 'Chaque fois qu\'un établissement rachète le même type de système au moins {n} ans après son dernier achat, NEO le compte comme un remplacement : même fabricant conservé, ou changement. Des achats plus rapprochés sont traités comme du matériel supplémentaire, pas comme un remplacement.',
    procRcMakersPerAccount: 'Fabricants par compte',
    procRcAgeHelp: 'L\'âge court depuis le dernier achat du système lui-même. Les contrats de maintenance et les consommables ne le remettent pas à zéro. Ce type d\'équipement est généralement remplacé après 5 à 7 ans.',
    procRcPipelineTitle: 'Pipeline de remplacement',
    procRcPipelineHint: 'Équipements installés par années depuis l\'achat',
    procRcPipelineNoteBefore: 'Parc concurrent en fenêtre de remplacement :',
    procRcPipelineNoteAfter: 'installations ont {n} ans ou plus.',
    procRcNetHint: 'Changements de fabricant au remplacement : gagnés moins perdus',
    procRcAgeingByMaker: 'Parc vieillissant par fabricant',
    procRcAgeingByCountry: 'Parc vieillissant par pays',
    procRcAgeingByType: 'Parc vieillissant par type d\'établissement',
    procRcAgeingHint: 'Part des installations de {n} ans ou plus',
    procRcAgeingEmpty: 'Trop peu d\'installations pour une ventilation.',
    procRcOfInstalls: 'sur {n} installations',
    procRcGridTitle: 'Position du titulaire par fenêtre de remplacement',
    procDisplacementSubtitle: 'À quelle fréquence les fournisseurs en place perdent réellement cette catégorie, et quels contrats rouvrent ensuite.',
    procAwardsAcrossInstitutions: '{deals} attributions sur {scope} et {accounts} établissements.',
    procNMarkets: '{n} marchés',
    procLastUpdatedDate: 'Dernière mise à jour : {date}',
    procGlobal: 'Mondial',
    procHighlights: 'Points clés',
    procAnnualChurn: 'Taux de remplacement annuel',
    procChurnSubSplit: 'des relations fournisseurs — {switched} % ont changé de fournisseur, {stopped} % ont cessé d\'acheter la catégorie',
    procChurnSubPerYear: 'des relations fournisseurs prenant fin chaque année',
    procChurnHelpIntro: 'Nous ne comptons que les fournisseurs auprès desquels un établissement a acheté au moins deux fois.',
    procChurnHelpSwitchedLabel: 'Changement de fournisseur :',
    procChurnHelpSwitchedBody: 'L\'établissement a continué d\'acheter dans la catégorie mais a changé de fournisseur — un gain potentiel.',
    procChurnHelpStoppedLabel: 'Arrêt des achats :',
    procChurnHelpStoppedBody: 'L\'établissement a cessé d\'acheter dans la catégorie. Aucun fournisseur n\'a gagné ; considérez ce chiffre comme une limite haute, car les renouvellements non publiés apparaissent de la même manière.',
    procChurnHelpAnnualLabel: 'Taux annuel :',
    procChurnHelpAnnualBody: 'Moyenne sur toutes les années disponibles, pas d\'une année sur l\'autre.',
    procChurnHelpMultiLabel: 'Comptes multi-fournisseurs :',
    procChurnHelpMultiBody: 'Perdre un fournisseur peut signifier perdre une place dans un panel, pas le client entier.',
    procSuppliersPerAccount: 'Fournisseurs par compte',
    procPctBuyersUseOne: '{n} % des acheteurs n\'en utilisent qu\'un',
    procRebuyCycle: 'Cycle de rachat',
    procUnitMo: 'mois',
    procMedianGapBetweenAwards: 'intervalle médian entre attributions',
    procReopeningWithin12Mo: 'Réouverture sous 12 mois',
    procLiveContractsAcrossMarkets: 'contrats en cours sur {n} marchés',
    procValueInNext12Mo: 'Valeur sur les 12 prochains mois',
    procFromNExpiringContracts: 'issus de {n} contrats arrivant à échéance',
    procRenewalPipeline: 'Pipeline de renouvellements',
    procLiveContractsByTimeToExpiry: 'contrats en cours par délai avant échéance',
    procPipelineNoteBefore: '{n} contrats rouvrent dans les six mois. Avec un taux de remplacement annuel de {churn} %, environ',
    procPipelineNoteAfter: 'de ces fournisseurs en place perdront réellement le compte — les autres seront réattribués ou discrètement prolongés.',
    procNetPositionChange: 'Variation nette de position',
    procAccountsWonMinusLost: 'comptes gagnés moins perdus',
    procTooFewRepeatAwards: 'Trop peu d\'attributions répétées sur ce marché pour mesurer le mouvement net.',
    procNetWinners: 'Gagnants nets',
    procNetLosers: 'Perdants nets',
    procNetPositionNote: 'Positions gagnées moins positions perdues sur l\'ensemble des changements de fournisseur enregistrés. Les perdants sont les opportunités — un fournisseur en place qui perd des comptes un à un.',
    procChurnByCountry: 'Taux de remplacement par pays',
    procTopMarketsByVolume: 'principaux marchés par volume',
    procChurnByCountryEmpty: 'Pas assez d\'historique répété sur un même marché pour établir un taux fiable.',
    procChurnByInstitutionType: 'Taux de remplacement par type d\'établissement',
    procAnnualEstablishedRelationships: 'annuel, relations établies',
    procChurnByInstitutionEmpty: 'Le type d\'établissement n\'est pas renseigné sur assez de comptes dans cette catégorie.',
    procIncumbentByDecisionWindow: 'Situation du fournisseur en place, par fenêtre de décision',
    procIncumbent: 'Fournisseur en place',
    procAccountLower: 'compte',
    procAccountsLower: 'comptes',
    procSentimentIncumbent: 'Fournisseur en place : {sentiment}',
    procNoContractEndDate: 'aucune date de fin de contrat enregistrée',
    procContractEndsIn: 'le contrat se termine dans {window}',
    procAmountTotal: '{amount} au total',
    procLoadingAccounts: 'Chargement des comptes…',
    procNoAccountsForCell: 'Aucun compte enregistré pour cette cellule.',
    procContractEnd: 'Fin du contrat',
    procContractValue: 'Valeur du contrat',
    procWhy: 'Pourquoi',
    procDealCount: 'Nombre d\'opportunités',
    procNegativeSharePct: 'Part négative (%)',
    procNoDisplacementData: 'Aucune donnée de remplacement trouvée avec les filtres actuels.',
    procTryAdjustingFilters: 'Essayez de modifier les filtres de catégorie ou de pays.',
    procCategoryRiskMosaic: 'Mosaïque des risques par catégorie',
    procCategoryRiskMosaicHelp: 'Risque de remplacement par catégorie et tranche d\'échéance. Cliquez sur un segment ou une case pour voir les comptes.',
    procMetric: 'Indicateur',
    procBucketMonths: '{b} mois',
    procAtRiskValueShare: 'À risque : {value} ({pct} %)',
    procDealLower: 'opportunité',
    procPctOfCell: '{pct} % de la cellule',
    procShowTop15: 'Afficher les 15 premières',
    procShowAllCategories: 'Afficher toutes les catégories',
    procRationaleHighlights: 'Justifications clés',
    procSearchRationale: 'Rechercher dans la justification...',
    procNoAccountsMatch: 'Aucun compte ne correspond.',
    procLess: 'Moins',
    procReadMore: 'Lire la suite',
    procYearLabel: 'Année : {year}',
    procYou: 'vous',
    procEnrolment: 'Effectifs',
    procUsdPerStudentYear: '$/étudiant/an',
    procOverTermWholeContract: 'sur {months} mois · {amount}/étudiant pour tout le contrat',
    procBenchEmptyIntro: 'Aucune donnée de référence pour les filtres actuels. Les opportunités doivent avoir',
    procBenchFieldEnrolment: 'effectifs',
    procBenchFieldDealValue: 'valeur de l\'opportunité (USD)',
    procBenchFieldSupplier: 'fournisseur',
    procBenchAndANumeric: 'et une',
    procBenchFieldInitialTerm: 'durée initiale numérique',
    procBenchEmptyOutro: 'pour que le prix puisse être ramené à l\'année.',
    procBenchExcludedNoTermOne: '{n} opportunité correspond aux filtres mais n\'a pas de durée de contrat ; elle ne peut donc pas être annualisée.',
    procBenchExcludedNoTermMany: '{n} opportunités correspondent aux filtres mais n\'ont pas de durée de contrat ; elles ne peuvent donc pas être annualisées.',
    procBenchTitle: 'Effectifs vs $ par étudiant et par an',
    procBenchTitleMedian: 'Médiane du $ par étudiant et par an',
    procBenchAnnualised: 'Annualisé selon la durée du contrat.',
    procTrendMedianNote: 'Chaque ligne est la médiane annuelle : un contrat atypique ne peut pas la déplacer.',
    procTrendShortTermNote: 'Les contrats de moins de 4 mois sont exclus : leur prix ne peut pas être lu comme un prix annuel.',
    procVendorVsCompetitors: '{vendor} face aux concurrents.',
    procScatter: 'Nuage de points',
    procTrendByCategory: 'Tendance par catégorie',
    procTrendByVendor: 'Tendance par fournisseur',
    procTrendSuppliers: 'Fournisseurs',
    procTrendRuleAll: 'Tous les fournisseurs',
    procTrendRuleExclude: 'Exclure la sélection',
    procTrendRuleOnly: 'Uniquement la sélection',
    procTrendRuleSearch: 'Rechercher des fournisseurs',
    procTrendRuleSummaryExclude: '{n} exclus',
    procTrendRuleSummaryOnly: 'Uniquement {n}',
    procTrendRuleClear: 'Effacer',
    procTrendRuleDeals: '{n} contrats',
    procTrendRuleHint: 'S\'applique uniquement à Tendance par catégorie. Enregistré pour cette sélection de catégories.',
    procTrendLegendHint: 'Cliquez sur un fournisseur dans la légende pour mettre sa courbe en évidence.',
    procTrendLegendClear: 'Afficher toutes les courbes',
    procResetView: 'Réinitialiser la vue',
    procFxFootnote: '* Un taux de change fixe en USD est appliqué à toutes les devises.',
    procWeakFit: 'Adéquation faible',
    procNoFit: 'Aucune adéquation',
    procLowEvidence: 'Peu de preuves',
    procFitOutOfScopeTitle: 'La catégorie de l\'opportunité ne correspond à aucun profil produit actif',
    procFitColumnTitle: 'Hors périmètre = la catégorie de l\'opportunité ne correspond à aucun profil produit actif',
    acctRemovedFromListTitle: '{name} retiré de votre liste',
    acctAccountLower: 'compte',
    acctRemovedFromListDesc: 'Vous pouvez le rajouter à tout moment depuis Marché.',
    acctCouldNotRemoveAccount: 'Impossible de retirer le compte',
    acctPleaseTryAgainNoDot: 'Veuillez réessayer',
    acctSaved: 'Enregistré',
    acctTargetProductUpdated: 'Produit cible mis à jour',
    acctCouldNotSaveTargetProduct: 'Impossible d\'enregistrer le produit cible',
    acctTabContent: 'Contenu',
    acctTabProducts: 'Produits',
    acctTabFinancialsAssets: 'Finances et actifs',
    acctTabSalesMap: 'Carte des ventes',
    acctTabCompetitorsCount: 'Concurrents ({n})',
    acctEnrichmentUnavailable: 'Enrichissement indisponible',
    acctMarketEnrichmentNotEnabled: 'L\'enrichissement de marché n\'est pas activé pour ce tenant.',
    acctCouldNotRequestEnrichment: 'Impossible de demander l\'enrichissement',
    acctMarketLensNotConfigured: 'La vue marché n\'est pas encore configurée.',
    acctEnrichmentAlreadyRequested: 'Enrichissement déjà demandé',
    acctEnrichmentRequested: 'Enrichissement demandé',
    acctTryAgainLater: 'Réessayez plus tard',
    acctRequesting: 'Demande en cours…',
    acctRequestPersonaEnrichment: 'Demander l\'enrichissement des personas',
    acctMeddpiccIdentifyPain: 'Identifier la douleur',
    acctMeddpiccMetrics: 'Métriques',
    acctMeddpiccPainTriggerTiming: 'Déclencheur · Calendrier',
    acctMeddpiccCompetitionIncumbent: 'Concurrence / Fournisseur en place',
    acctMeddpiccEconomicBuyer: 'Acheteur économique',
    acctMeddpiccDecisionProcess: 'Processus de décision',
    acctMeddpiccPaperProcess: 'Processus administratif',
    acctMeddpiccChampion: 'Champion',
    acctMeddpiccTipPainNeed: 'Quel problème les pousse à vouloir changer ?',
    acctMeddpiccTipImpactScale: 'Échelle et impact quantifiés — uniquement des chiffres.',
    acctMeddpiccTipWhyNow: 'Pourquoi maintenant — quel déclencheur daté ouvre la fenêtre ?',
    acctMeddpiccTipCurrentVendor: 'Qui ils utilisent aujourd\'hui et ce qu\'ils en pensent.',
    acctMeddpiccTipBuyerOwner: 'Qui peut financer et valider.',
    acctMeddpiccTipDecisionProcess: 'Qui approuve, dans quel ordre, via quels comités.',
    acctMeddpiccTipPaperProcess: 'Voie d\'achat, accord-cadre, renouvellement, exigences contractuelles.',
    acctMeddpiccTipChampion: 'Défenseur interne — responsable opérationnel avec un gain personnel.',
    acctSalesPointPainNeed: 'Douleur / Besoin',
    acctSalesPointImpactScale: 'Impact / Échelle',
    acctSalesPointWhyNow: 'Pourquoi maintenant / Calendrier',
    acctSalesPointCurrentVendor: 'Fournisseur actuel / Concurrence',
    acctSalesPointBuyerOwner: 'Acheteur / Responsable',
    acctSalesPointDecisionProcess: 'Processus de décision',
    acctSalesPointPaperProcess: 'Achats / Processus administratif',
    acctSalesPointChampion: 'Champion / Persona clé',
    acctSalesPointRecommendedNextMove: 'Prochaine action recommandée',
    acctStrengthTipMissing: 'Aucune preuve utile n\'est encore enregistrée pour ce point.',
    acctStrengthTipVeryLow: 'Générique ou vague — pas clairement liée à ce compte.',
    acctStrengthTipLow: 'Quelques signaux pertinents, mais surtout déduits.',
    acctStrengthTipMedium: 'Étayée par des preuves, mais des détails clés manquent.',
    acctStrengthTipHigh: 'Preuves récentes, précises et alignées sur la catégorie.',
    acctStrengthTipVeryHigh: 'Fortement étayée et validée (rare en V1).',
    acctStrengthMissing: 'Manquant',
    acctStrengthVeryLow: 'Très faible',
    acctStrengthVeryHigh: 'Très élevé',
    acctKeepDerived: 'Conserver la valeur calculée',
    acctEdited: 'Modifié',
    acctAutoDetected: 'Détecté automatiquement',
    acctAutoDetectedTooltip: 'Signalé automatiquement par le module Competitor Intel. Les modifications du commercial remplaceront cette justification.',
    acctShowLessLower: 'afficher moins',
    acctShowMoreLower: 'afficher plus',
    acctSourcesCount: 'Sources ({n})',
    acctRepLower: 'commercial',
    acctTeamNoteFrom: 'Note d\'équipe de {author}',
    acctClearOverride: 'Supprimer la modification',
    acctRationale: 'Justification',
    acctRationalePlaceholder: 'Pourquoi le savons-nous ? Ex. : « Confirmé lors d\'un appel avec X le Y — ils ont dit Z »',
    acctOverrideScoreOptional: 'Modifier le score (facultatif)',
    acctRationaleRequired: 'Ajoutez une justification avant d\'enregistrer.',
    acctSalesMapUpdated: 'Carte des ventes mise à jour',
    acctCouldNotSaveEdit: 'Impossible d\'enregistrer votre modification',
    acctOverrideCleared: 'Modification supprimée',
    acctCouldNotClearOverride: 'Impossible de supprimer la modification',
    acctGetAdvice: 'Obtenir un conseil',
    acctCouldNotLoadSalesMap: 'Impossible de charger la carte des ventes',
    acctCouldNotRefreshSalesMap: 'Impossible d’actualiser la carte des ventes — affichage des données précédentes.',
    acctSalesMapTitle: 'Carte des ventes — {account}',
    acctGeneratedAt: 'Généré le {date}',
    acctPrepareForMeeting: 'Préparer la réunion',
    acctSuggestNextMove: 'Suggérer la prochaine action',
    acctSalesMapSparseBanner: 'Ce compte a encore peu de preuves enregistrées. La plupart des lignes manquent — le bot dispose tout de même du contexte de marché du pays si vous cliquez sur Obtenir un conseil.',
    acctNbmTarget: 'Cible',
    acctNbmSuggestedRole: 'Rôle suggéré à cibler',
    acctNbmNoNamedContactTypically: 'Aucun contact nommé — généralement : {examples}',
    acctNbmNoNamedContact: 'Aucun contact nommé.',
    acctNbmFindOneFirst: 'Trouvez-en un avant de prendre contact. Ouvrez l\'onglet Personas.',
    acctNextBestMove: 'Prochaine meilleure action',
    acctNbmAskNext: 'À demander lors de votre prochain échange',
    acctNeoRecommendation: 'Recommandation NEO',
    acctProfileOutreach: 'Prise de contact depuis le profil',
    acctDiyPlaybook: 'Playbook DIY',
    acctPlaybookTrackingSoon: 'Suivi des Playbooks bientôt disponible',
    acctPlaybookTrackingSoonDesc: 'Le suivi de l\'engagement des Playbooks sera disponible une fois l\'API backend déployée.',
    acctNoPlaybooksOrOutreach: 'Aucun Playbook ni prise de contact',
    acctNoPlaybooksOrOutreachDesc: 'Ce compte n\'est inscrit à aucun Playbook et n\'a aucun historique de prise de contact.',
    acctEnrolledPersonasOne: 'Inscrits : {n} persona',
    acctEnrolledPersonasMany: 'Inscrits : {n} personas',
    acctCompletedOn: 'Terminé : {date}',
    acctStartedOn: 'Démarré : {date}',
    acctEnrolledPersonasLabel: 'Personas inscrits :',
    acctTouchOf: 'Contact {current} sur {total}',
    acctNextOn: 'Suivant : {date}',
    acctLoadingAdHocOutreach: 'Chargement des prises de contact ad hoc…',
    acctActivePlaybooks: 'Playbooks actifs',
    acctCompletedPlaybooks: 'Playbooks terminés',
    acctScheduledPending: 'Planifiés — en attente',
    acctScheduledFor: 'Planifié le {date}',
    acctAdHocMessages: 'Messages ad hoc',
    acctMsgCountOne: '{n} message',
    acctMsgCountMany: '{n} messages',
    acctManualCompletion: 'Réalisé manuellement',
    acctNotesLoadErrorContext: 'Impossible de charger les notes dans ce contexte. Essayez de rafraîchir la page.',
    acctNotesLoadErrorGeneric: 'Impossible de charger les notes. Veuillez réessayer.',
    acctCouldNotSaveThisNote: 'Impossible d\'enregistrer cette note.',
    acctFailedToSaveNote: 'Échec de l\'enregistrement de la note.',
    acctCannotDeleteNote: 'Vous ne pouvez pas supprimer cette note.',
    acctFailedToDeleteNote: 'Échec de la suppression de la note.',
    acctNoteAuthorYou: 'Vous',
    acctNoteAuthorTeam: 'Équipe',
    acctNoteAuthorTeammate: 'Coéquipier',
    acctLoadingNotes: 'Chargement des notes...',
    acctAddNote: 'Ajouter une note',
    acctWriteNotePlaceholder: 'Rédigez votre note ici...',
    acctSaveNote: 'Enregistrer la note',
    acctNoNotesYet: 'Aucune note pour l\'instant. Ajoutez votre première note ci-dessus.',
    acctDeleteNote: 'Supprimer la note',
    acctCouldNotLoadCompetitorData: 'Impossible de charger les données concurrentielles — réessayez.',
    acctCompetitorsAtThisAccount: 'Concurrents sur ce compte',
    acctSignalTimeline: 'Chronologie des signaux',
    acctCompetitorsLinkedToPersona: 'Concurrents liés à ce persona',
    acctCompSigAccountReference: 'Compte cité dans un contenu concurrent',
    acctCompSigAccountAdoption: 'Adoption d\'un concurrent sur le compte',
    acctCompSigPartnerEcosystem: 'Écosystème de partenaires du concurrent',
    acctCompSigProcurementSignal: 'Signal d\'achat',
    acctCompSigEventOverlap: 'Chevauchement d\'événements',
    acctCompSigDisplacementOpportunity: 'Opportunité de remplacement',
    acctCompSigWeaknessSignal: 'Faiblesse du concurrent',
    acctCompSigPersonaInteraction: 'Persona sollicité par un concurrent',
    acctCompSigPersonaReference: 'Persona cité dans un contenu concurrent',
    acctCompSigPersonaAdoption: 'Persona lié à un outil concurrent',
    acctCompSigGeneric: 'Signal concurrent',
    sigUpdEcosystemSignal: 'Signal d\'écosystème',
    sigUpdMarketEvent: 'Événement de marché',
    sigUpdRegionalEvent: 'Événement régional',
    sigUpdProductRelease: 'Sortie de produit',
    sigUpdProductLaunch: 'Lancement de produit',
    sigUpdPressRelease: 'Communiqué de presse',
    sigUpdPartnershipAnnouncement: 'Annonce de partenariat',
    sigUpdInvestorNews: 'Actualités investisseurs',
    sigUpdWebinarEvent: 'Webinaire',
    sigUpdVendorMetricClaim: 'Chiffre revendiqué par le fournisseur',
    sigUpdExternalAccountAdoption: 'Adoption par un compte externe',
    sigUpdExternalReferenceAccount: 'Compte de référence externe',
    sigUpdMarketCommentary: 'Commentaire de marché',
    sigUpdConferenceRecap: 'Compte rendu de conférence',
    acctImpactUnfavourable: 'Défavorable',
    acctImpactFavourable: 'Favorable',
    acctImpactMixed: 'Mitigé',
    acctImpactContextOnly: 'Contexte uniquement',
    acctProximityElevated: 'Accru',
    acctProximityWatch: 'À surveiller',
    acctProximityNoEvidence: 'Aucune preuve',
    acctJustNow: 'à l\'instant',
    acctMoreInfo: 'Plus d\'infos',
    acctProximityScoreTooltip: 'De 0 à 100. Augmente lorsque de nouveaux signaux concurrents arrivent sur ce compte/persona. Diminue naturellement à mesure que les preuves vieillissent.',
    acctImpactTooltip: 'Orientation des preuves concurrentielles. Défavorable = mauvais pour vous (concurrent bien installé). Favorable = bon pour vous (insatisfaction / opportunité de remplacement). Mitigé = les deux types de signaux sont présents. Contexte uniquement = informatif.',
    acctLabelTooltip: 'Libellé de la tranche de score : 0 = Aucune preuve, 1-24 = Faible, 25-49 = À surveiller, 50-74 = Accru, 75-100 = Élevé.',
    acctTopCompetitorTooltip: 'Concurrent ayant la plus forte contribution pondérée de signaux sur ce compte/persona.',
    acctLatestSignalTooltip: 'Date du signal concurrent le plus récent.',
    acctTrajectoryTooltip: 'Score de proximité recalculé chaque mois sur les 24 derniers mois. Barres en hausse = nouveaux signaux. En baisse = preuves qui vieillissent.',
    acctFlatTrajectoryTooltip: 'Stable à {n} sur les 24 derniers mois — les preuves datent de plus d’un an.',
    acctSourceBadgeAccount: 'Signal de compte',
    acctSourceBadgeAccountTooltip: 'Lié à l’un de vos comptes cibles. Contribue au baromètre des comptes.',
    acctSourceBadgePersona: 'Signal de persona',
    acctSourceBadgePersonaTooltip: 'Lié à l’un de vos personas cibles. Contribue au baromètre des personas.',
    acctSourceBadgeMarket: 'Veille marché — hors compte cible',
    acctSourceBadgeMarketTooltip: 'Activité concurrente sur votre marché qui n’a pas touché vos comptes cibles. Ne contribue à AUCUN baromètre.',
    acctCardHeaderTooltipAccount: 'Degré d’exposition de ce compte aux concurrents. Calculé à partir de la force du signal × la décroissance liée à l’ancienneté. Score plus élevé = présence concurrente plus forte.',
    acctCardHeaderTooltipPersona: 'Degré d’exposition de ce persona aux concurrents. Calculé à partir de la force du signal × la décroissance liée à l’ancienneté. Score plus élevé = présence concurrente plus forte.',
    acctCompetitorFallback: 'Concurrent',
    acctStrengthValue: 'Force : {value}',
    acctShowMore: 'Afficher plus',
    acctRecommendedAction: 'Action recommandée',
    acctBreakdownSignals: 'signaux : {n}',
    acctBreakdownStrongest: 'le plus fort : {value}',
    acctBreakdownLatest: 'le plus récent : {value}',
    acctCompetitiveProximity: 'Proximité concurrentielle',
    acctCompetitorSignalsTitle: 'Signaux concurrents',
    acctHelpFor: 'Aide : {title}',
    acctProximityScore: 'Score de proximité',
    acctProximityScoreHelp: 'Aide sur le score de proximité',
    acctLabelHelp: 'Aide sur le libellé',
    acctImpactHelp: 'Aide sur l\'impact',
    acctTrajectory24m: 'Trajectoire 24 m',
    acctTrajectoryHelp: 'Aide sur la trajectoire',
    acctTopCompetitorValue: 'Principal : {value}',
    acctTopCompetitorHelp: 'Aide sur le principal concurrent',
    acctLatestValue: 'Dernier : {value}',
    acctLatestSignalHelp: 'Aide sur le dernier signal',
    acctLinkedPersonas: 'Personas liés :',
    sigTypeFundingNews: 'Actualités de financement',
    sigTypeLeadershipChange: 'Changement de direction',
    sigTypeStrategicAnnouncement: 'Annonce stratégique',
    sigTypePartnership: 'Partenariat',
    sigTypeTechnologyAdoption: 'Adoption technologique',
    sigTypeProcurementUpdate: 'Mise à jour d\'achat',
    sigTypeAcquisition: 'Acquisition',
    sigTypeContractAward: 'Attribution de marché',
    sigTypeProfileUpdate: 'Mise à jour du profil',
    sigTypeRoleChange: 'Changement de poste',
    sigTypeAchievement: 'Réussite',
    sigTypeSocialActivity: 'Activité sur les réseaux',
    sigTypeIncumbent: 'Fournisseur en place',
    sigTypeBuying: 'Achat',
    sigTypeDisplacement: 'Remplacement',
    sigTypeRenewalWindow: 'Fenêtre de renouvellement',
    sigTypeBudgetAllocation: 'Allocation budgétaire',
    sigTypeInitiative: 'Initiative',
    sigTypeRisk: 'Risque',
    sigTypeOrgChange: 'Changement organisationnel',
    sigTypeAccreditorResult: 'Résultat d\'accréditation',
    sigTypeGrantAward: 'Attribution de subvention',
    sigTypeJobPosting: 'Offre d\'emploi',
    sigTypeEstateEvent: 'Projet immobilier',
    sigTypeFinancialPressure: 'Pression financière',
    sigTypeEnrolmentPressure: 'Pression sur les inscriptions',
    sigTypeSystemShortcoming: 'Lacune du système',
    sigTypeVendorDispute: 'Litige fournisseur',
    sigTypeComplianceUpdate: 'Mise à jour de conformité',
    sigTypeBoardUpdate: 'Actualité du conseil',
    sigTypeGreenfield: 'Sans solution actuelle',
    sigMarketUpdate: 'Actualité du marché',
    sigCompetitorFilter: 'Concurrent : {name}',
    sigMarketActivity: 'Activité du marché',
    sigAccountSignals: 'Signaux de comptes',
    sigPersonaSignals: 'Signaux de personas',
    sigCompetitorSignals: 'Signaux concurrents',
    sigNoMarketActivity: 'Aucun signal d\'activité du marché pour l\'instant.',
    chatCopy: 'Copier',
    chatCopied: 'Copié',
    chatTalkingAbout: 'À propos de {account}',
    chatSourcesLabel: 'Sources :',
    pbBoothMeetingPurpose: 'Générez des visites sur le stand et des démos en personne.',
    pbSessionInvitePurpose: 'Générez de la participation à une session de conférence.',
    pbVipOutreachPurpose: '20 minutes avec des cibles de niveau direction.',
    pbQuickConnectPurpose: 'Prise de contact légère entre pairs pour les participants confirmés.',
    pbPersonaScopeAny: 'Tout persona',
    pbPersonaScopeExecutiveOnly: 'Direction uniquement',
    pbPersonaScopeConfirmedAttendees: 'Participants précédents et confirmés uniquement',
    homeWeakFit: 'Adéquation limitée',
    homeNoFit: 'Aucune adéquation',
    homeNotScored: 'Non évalué',
    // [2026-10-29] i18n: UI chrome keys (accounts list, account signals, archetypes)
    acctArchExecutiveBuyer: 'Décideur exécutif',
    acctArchEvaluator: 'Évaluateur',
    acctNoContactsYet: 'Aucun contact pour l\'instant.',
    acctNoSignalsForAccount: 'Aucun signal trouvé pour ce compte.',
    acctUntitledSignal: 'Signal sans titre',
    acctNoDescriptionAvailable: 'Aucune description disponible',
    acctSignalUpdate: 'Mise à jour',
    acctImpactLabel: 'Impact :',
    acctMomentumScoreChanged: 'Le score de dynamique a varié de {n} points',
    acctsColProduct: 'Produit',
    acctsColAccountTier: 'Niveau du compte',
    acctsColTargetProducts: 'Produits cibles',
    acctsColSubscribers: 'Abonnés',
    acctsTargetProductPlaceholder: 'Produit cible',
    procTeamStatusAria: 'Statut de l\'équipe',
    // [2026-10-29] i18n: chat table/chart block labels (lib/chatBlockLabels.js)
    chatTblWhatTheyRunToday: 'Ce qu\'ils utilisent aujourd\'hui',
    chatTblWhatChangedRecently: 'Changements récents',
    chatColCategory: 'Catégorie',
    chatColVendor: 'Fournisseur',
    chatColHowTheyHoldIt: 'Mode de détention',
    chatColDeployment: 'Déploiement',
    chatColSentiment: 'Sentiment',
    chatColEvidence: 'Preuve',
    chatColDate: 'Date',
    chatColWhatHappened: 'Ce qui s\'est passé',
    chatColSource: 'Source',
    chatBadgeCommercialProduct: 'Produit commercial',
    chatBadgeNativeToLms: 'Intégré à leur LMS',
    chatBadgeBuiltInHouse: 'Développé en interne',
    chatBadgeOpenSource: 'Open source',
    chatBadgeNothingInPlace: 'Aucune solution',
    chatBadgeInstitutionWide: 'Toute l\'institution',
    chatBadgeSeveralUnits: 'Plusieurs unités',
    chatBadgeOneUnit: 'Une unité',
    chatBadgePilot: 'Pilote',
    chatBadgePositive: 'Positif',
    chatBadgeNeutral: 'Neutre',
    chatBadgeNegative: 'Négatif',
    chatChartWhichLms: 'Quel LMS le marché utilise',
    chatChartFinancialHeadroom: 'Marge financière sur le marché',
    chatChartAiReadiness: 'Préparation à l\'IA sur le marché',
    chatChartMarketSplit: 'Répartition du marché',
    chatChartWhoHolds: 'Qui détient {cat} sur le marché',
    chatUnitInstitutions: 'établissements',
    chatUnitAccounts: 'comptes',
    chatSliceLow: 'Faible',
    chatSliceModerate: 'Modérée',
    chatSliceHigh: 'Élevée',
    chatSliceOther: 'Autres',
    chatSliceLevel: 'Niveau {n}',
    chatSlicePriorityNow: 'Priorité immédiate',
    chatSliceInYourMarket: 'Dans votre marché',
    chatSliceRestOfMarket: 'Reste du marché',
    chatSliceAdjacentMarket: 'Marché adjacent',
    chatDateUnknown: 'date inconnue',
    chatNoteVendor: '{n} établissements adressables sur {total} ont un fournisseur {cat} enregistré. Les fournisseurs sous 5 % sont regroupés dans Autres.',
    chatNoteLms: '{n} établissements adressables sur {total} ont un LMS enregistré. Regroupés par famille : Blackboard compte comme Anthology et Canvas comme Instructure. Open LMS et Totara restent distincts de Moodle.',
    chatNoteCapacity: '{n} établissements adressables sur {total} ont une tranche de capacité.',
    chatNoteReadiness: '{n} établissements adressables sur {total} ont un score de préparation à l\'IA.',
    chatNoteMix: '{n} comptes classés.',
  },
  
  nl: {
    home: 'Home',
    market: 'Markt',
    accounts: 'Accounts',
    people: 'Personen',
    signals: 'Signalen',
    procurement: 'Inkoop',
    playbooks: 'Handboeken',
    settings: 'Instellingen',
    help: 'Help',
    admin: 'Beheerder',
    segments: 'Segmenten',
    analytics: 'Analyses',
    outbox: 'Postvak uit',
    logout: 'Uitloggen',
    
    // Common
    loading: 'Laden...',
    save: 'Opslaan',
    cancel: 'Annuleren',
    edit: 'Bewerken',
    delete: 'Verwijderen',
    search: 'Zoeken',
    filter: 'Filteren',
    export: 'Exporteren',
    retry: 'Opnieuw proberen',
    refresh: 'Vernieuwen',
    backTo: 'Terug naar',
    viewProfile: 'Profiel bekijken',
    
    // Headers
    dashboard: 'Dashboard',
    accountOverview: 'Accountoverzicht',
    
    // Tabs
    overview: 'Overzicht',
    details: 'Details',
    activity: 'Activiteit',
    notes: 'Notities',
    connections: 'Connections',
    connectionsEmpty: 'No known connections yet for this person.',
    connectionInferred: 'inferred',
    personas: 'Personas',
    departments: 'Afdelingen',
    
    // Account Page
    techStack: 'Technologie stack',
    liveDeals: 'Actieve deals',
    decisionWindow: 'Beslissingsvenster',
    renewalDate: 'Verlengingsdatum',
    momentum: 'Momentum',
    strategicPriorities: 'Strategische prioriteiten',
    institutionalChallenges: 'Institutionele uitdagingen',
    studentExpectations: 'Verwachtingen van studenten',
    keyContacts: 'Belangrijke contacten',
    procurementProfile: 'Inkoopprofiel',
    accountNotFound: 'Account niet gevonden',
    accountNotFoundDesc: 'Het gevraagde account kon niet worden geladen of bestaat niet.',
    rateLimitReached: 'Tariflimiet bereikt. Gecachte gegevens worden weergegeven. Wacht 5 minuten voordat u het opnieuw probeert.',
    showingCachedData: 'Gecachte gegevens worden weergegeven. Klik op vernieuwen voor de nieuwste updates.',
    
    // Charts & Metrics
    fitScore: 'Passende score',
    excellent: 'Uitstekend',
    strong: 'Sterk',
    moderate: 'Gemiddeld',
    low: 'Laag',
    scoreRationale: 'Score motivering',
    noRationaleAvailable: 'Geen motivering beschikbaar.',
    riskRationale: 'Risico motivering',
    
    // People & Personas
    personasOrganizedBy: 'Personas zijn georganiseerd op rol in het inkoopcomité en gerangschikt op hun invloed en passende score. Klik op een contact om hun volledige profiel en activiteit te bekijken.',
    addedToWatchlist: 'Toegevoegd aan watchlist',
    removedFromWatchlist: 'Verwijderd uit watchlist',
    
    // Procurement
    procurementFriendliness: 'Inkoop vriendelijkheid',
    friendly: 'Vriendelijk',
    standard: 'Standaard',
    bureaucratic: 'Bureaucratisch',
    monitor: 'Monitoren',
    
    // Decision Window
    lessThan12Months: '<12 maanden',
    months12to24: '12-24 maanden',
    moreThan24Months: '>24 maanden',
    notApplicable: 'N.v.t.',
    decisionWindowRationale: 'Beslissingsvenster motivering',
    
    // Signals
    momentumUpdate: 'Momentum update',
    momentumScoreChanged: 'Momentum score gewijzigd met',
    points: 'punten',
    
    // Market Intelligence
    marketIntelligence: 'Marktkansen',
    macroTrends: 'Macro trends en kansen in uw doelmarkten',
    buyingActivity: 'Koopactiviteit',
    players: 'Dynamiek',
    
    // Filters
    allCountries: 'Alle landen',
    filterByCountry: 'Filter op land',
    allDepartments: 'Alle afdelingen',
    filterByDepartment: 'Filter op afdeling',
    
    // Status
    live: 'Actief',
    closed: 'Gesloten',
    awarded: 'Toegekend',
    cancelled: 'Geannuleerd',
    
    // Common - additional
    noDataAvailable: 'Geen gegevens beschikbaar.',
    
    // Confidence levels
    high: 'Hoog',
    medium: 'Gemiddeld',
    urgencyHigh: 'Hoge urgentie',
    urgencyMedium: 'Gemiddelde urgentie',
    urgencyLow: 'Lage urgentie',
    priorityHigh: 'Hoge prioriteit',
    priorityMedium: 'Gemiddelde prioriteit',
    priorityLow: 'Lage prioriteit',
    priorityTooltipLead: 'Prioriteit combineert hoe goed deze persoon past met hoe vers het signaal is.',
    
    // Procurement Profile
    fieldMetric: 'Veld / Metriek',
    value: 'Waarde',
    source: 'Bron',
    confidence: 'Betrouwbaarheid',
    procurementIntelligence: 'Uitgebreide inkoop-intelligentie en leverancierspad-analyse voor deze instelling.',
    procurementProfileNA: 'Inkoopprofielgegevens zijn niet beschikbaar voor dit account.',
    
    // Account Header
    students: 'studenten',
    employees: 'medewerkers',
    accountFitRationale: 'Account fit motivering',
    excellentFit: 'Uitstekende fit',
    strongFit: 'Sterke fit',
    moderateFit: 'Gemiddelde fit',
    lowFit: 'Lage fit',
    client: 'klant',
    buyingModelRationale: 'Koopmodel motivering',
    bvoClassification: 'BVO-classificatie',
    incumbentSentiment: 'Zittende leverancier sentiment',
    procurementFriendlyLabel: 'Toegankelijk',
    procurementStandardLabel: 'Standaard',
    procurementBureaucraticLabel: 'Bureaucratisch',
    procurementMonitorLabel: 'Monitoren',
    bvoBudgetDriven: 'Budgetgedreven',
    bvoValueSeeker: 'Waardezoeker',
    bvoOutcomeFocused: 'Resultaatgericht',
    bvoInnovationDriven: 'Innovatiegedreven',
    bvoRiskAverse: 'Risicomijdend',
    bvoComplianceFocused: 'Compliancegericht',
    sentimentAtRisk: 'Risico',
    sentimentPositive: 'Positief',
    sentimentNeutral: 'Neutraal',
    sentimentNegative: 'Negatief',
    sentimentDissatisfied: 'Ontevreden',
    unknown: 'Onbekend',
    numberOfEmployees: 'Aantal medewerkers',
    departmentFitRationale: 'Fit motivering',
    
    // Procurement Friendliness
    friendlyDesc: 'Deze instelling heeft gestroomlijnde inkoopprocessen en staat over het algemeen open voor nieuwe leveranciersrelaties.',
    standardDesc: 'Deze instelling volgt typische inkoopprocedures met standaard evaluatietijdlijnen.',
    bureaucraticDesc: 'Deze instelling heeft complexe inkoopprocessen die langere verkoopcycli en aanvullende goedkeuringen kunnen vereisen.',
    monitorDesc: 'Deze instelling heeft uitdagende inkoopdynamiek die zorgvuldige monitoring en strategische betrokkenheid vereist.',
    procurementProcessClassification: 'Inkoopprocesclassificatie voor deze instelling.',
    
    // Decision Window
    decisionWindowMonitor: 'Beslissingsvenster monitor',
    decisionWindowDesc: 'Toont wanneer een contract waarschijnlijk wordt herzien. Vervaldatums worden geschat met behulp van inkooprecords, typische contractcycli en verlengingsmeldingen. Wanneer gegevens ontbreken, projecteren we op basis van toekenningsdatums en standaardpatronen, altijd getagd met betrouwbaarheidsniveaus.',
    
    // Tech Stack
    noTechData: 'Geen gespecificeerde technologiegegevens',
    
    // Tenant-specific fields
    pedagogicalModel: 'Pedagogisch en cultureel model',
    digitalLandscape: 'Digitaal landschap',
    digitalFunding: 'Digitale financieringsmogelijkheden',
    learningInitiatives: 'Leerontwikkelingsinitiatieven',
    patientCareGaps: 'Lacunes in patiëntenzorg',
    decisionDynamics: 'Besluitvormingsdynamiek van belanghebbenden',
    contentCoverage: 'Inhoudsdekking',
    clientOpportunities: 'Klantmogelijkheden',
    decisionMakers: 'Beslissers',
    engagementHooks: 'Betrokkenheidshaakjes',
    recommendedTactics: 'Aanbevolen tactieken',
    
    // People Page
    keyDecisionMakers: 'Belangrijke beslissers en beïnvloeders',
    searchPeople: 'Zoek personen op naam, rol of universiteit...',
    name: 'Naam',
    account: 'Account',
    country: 'Land',
    department: 'Afdeling',
    archetype: 'Archetype',
    pursuit: 'Vervolg',
    socialScore: 'Sociale score',
    noPeopleFound: 'Geen personen gevonden',
    adjustFilters: 'Probeer uw zoekopdracht of filters aan te passen.',
    formerClient: 'Voormalige klant',
    previouslyWorked: 'Heeft eerder gewerkt voor een van onze klanten',
    socialPresence: 'Meet de zichtbaarheid en invloed van de persona op sociale platforms zoals LinkedIn.',
    personaFitRationale: 'Persona fit motivering',
    strategic: 'Strategisch',
    supportive: 'Ondersteunend',
    passive: 'Passief',
    showContactInfo: 'Contactgegevens tonen',
    satisfied: 'Tevreden',
    neutral: 'Neutraal',
    frustrated: 'Gefrustreerd',
    exploringAlternatives: 'Alternatieven verkennen',
    
    // Signals Page
    trackSignals: 'Volg realtime accountmomentum en persona-activiteit om kansen te identificeren en op het juiste moment te betrekken.',
    allSignals: 'Alle signalen',
    noAccountSignals: 'Geen accountsignalen gevonden',
    noPersonaSignals: 'Geen persona-signalen gevonden',
    
    // Playbooks Page
    structuredEngagement: 'Gestructureerde betrokkenheidsstrategieën voor verschillende scenario\'s',
    createPlaybook: 'Handboek maken',
    searchPlaybooks: 'Handboeken zoeken...',
    allFactors: 'Alle factoren',
    eventPlaybooks: 'Evenementhandboeken',
    targetedSequences: 'Voer gerichte sequenties uit voor uw aankomende evenementen',
    noUpcomingEvents: 'Geen aankomende evenementen',
    addEventsInSettings: 'Evenementen toevoegen in Instellingen',
    selectEvent: 'Evenement selecteren',
    previewAndApply: 'Voorbeeld en toepassen',
    yourCustomPlaybooks: 'Uw aangepaste handboeken',
    custom: 'Aangepast',
    goal: 'Doel',
    enrolledAccounts: 'Ingeschreven accounts',
    untouchedAccounts: 'Onaangeroerde accounts',
    noPlaybooks: 'Geen handboeken gevonden die overeenkomen met uw criteria',
    
    // Segments Page
    createManageExport: 'Doellijsten maken, beheren en exporteren',
    buildNewSegment: 'Nieuw segment bouwen',
    rules: 'regels',
    created: 'Gemaakt',
    viewResults: 'Resultaten bekijken',
    noSegmentsYet: 'Nog geen segmenten',
    getStartedSegment: 'Kom op gang door uw eerste segment te bouwen.',
    deleteSegment: 'Segment verwijderen',
    segmentDeleted: 'Segment succesvol verwijderd',
    authRequired: 'Authenticatie vereist',
    loginToAccess: 'Log in om toegang te krijgen tot segmenten.',
    logIn: 'Inloggen',
    
    // Home Page
    yourWatchlist: 'Uw watchlist',
    watchlistTooltip: 'Uw watchlist volgt belangrijke personas waarmee u actief bezig bent. Deze personen zijn gemarkeerd voor voortdurende relatieopbouw en krijgen prioriteit in uw workflow.',
    open: 'Openen',
    untrack: 'Stop met volgen',
    moreTracked: 'meer gevolgde personas…',
    refreshing: 'Vernieuwen…',
    
    // Procurement Page
    dealsTenders: 'Deals en aanbestedingen',
    trackTenders: 'Volg openstaande aanbestedingen en gesloten deals op de markt.',
    searchKeyword: 'Zoeken op trefwoord, titel, instelling...',
    liveDealsCount: 'Actieve deals',
    recentlyClosed: 'Onlangs gesloten',
    closedDeals: 'Gesloten deals',
    allInstitutions: 'Alle instellingen',
    filterByInstitution: 'Filter op instelling',
    allCategories: 'Alle categorieën',
    filterByCategory: 'Filter op categorie',
    selectAll: 'Alles selecteren',
    clearAll: 'Alles wissen',
    typeToSearch: 'Typ om te zoeken...',
    category: 'Categorie',
    vendor: 'Leverancier',
    institutionType: 'Type instelling',
    sentiment: 'Sentiment',
    listOnly: 'Alleen lijst',
    mapPlusList: 'Kaart + Lijst',
    allDeadlines: 'Alle deadlines',
    days0_7: '0–7 dagen',
    days8_30: '8–30 dagen',
    days31_90: '31–90 dagen',
    days90Plus: '90+ dagen',
    months0_6: '0–6 maanden',
    months6_12: '6–12 maanden',
    months12_24: '12–24 maanden',
    unknownExpired: 'Onbekend / Verlopen',
    allVendors: 'Alle leveranciers',
    exit: 'Exit',
    filterByExitLeverage: 'Filter op exit-leverage',
    some: 'Enig',
    noSignal: 'Geen signaal',
    loadingDeals: 'Offertes laden...',
    loadingContractIntelligence: 'Contractintelligentie laden...',
    loadingMoreDeals: 'Meer offers laden...',
    loadedDeals: '{count} {label} deals geladen',
    outOfScope: 'Buiten scope',
    pending: 'In behandeling',
    insufficientInfo: 'Onvoldoende info',
    liveRadar: 'Live radar',
    recentlyExpired: 'Recent verlopen',
    contractIntelligence: 'Contractintelligentie',
    displacementRadar: 'Verdringingsradar',
    benchmarking: 'Benchmarking',
    title: 'Titel',
    institution: 'Instelling',
    deadline: 'Deadline',
    productFitPanel: 'Productfit',
    noLiveDeals: 'Geen live aanbiedingen gevonden',
    product: 'Product',
    reseller: 'Wederverkoper',
    procurementMethod: 'Aanbestedingsprocedure',
    framework: 'Raamovereenkomst',
    productName: 'Productnaam',
    supplier: 'Leverancier',
    predictedExpiry: 'Verwachte verloop',
    productFitProfileDesc: 'Definieer productprofielen voor fit-scoring. Meerdere profielen kunnen tegelijk actief zijn: ze sturen Light Fit op Live Radar (op categorie) en u kiest één profiel voor Deep Fit op een aanbesteding.',
    createProfile: 'Profiel maken',
    editProfile: 'Profiel bewerken',
    newProfile: 'Nieuw profiel',
    productFitName: 'Naam',
    productFitPrimaryCategory: 'Primaire categorie',
    productFitCategoryTags: 'Categorietags (komma\'s gescheiden)',
    productFitStrongSignals: 'Sterke fit-signalen (één per regel)',
    productFitNegativeSignals: 'Negatieve signalen (Hard: of Soft: voorvoegsel, één per regel)',
    productFitDeploymentModels: 'Implementatiemodellen',
    productFitCloud: 'Cloud',
    productFitSelfHosted: 'Self-hosted',
    productFitHybrid: 'Hybride',
    productFitIntegrations: 'Integraties (komma\'s gescheiden)',
    productFitSecurity: 'Beveiligingscertificeringen (komma\'s gescheiden)',
    productFitKeyFeatures: 'Belangrijkste functies (komma\'s gescheiden)',
    productFitUpdate: 'Bijwerken',
    productFitCreate: 'Create',
    productFitProfiles: 'Profielen',
    productFitNoProfiles: 'Nog geen profielen. Maak er hierboven een aan.',
    productFitSetAsActive: 'Instellen als actief',
    productFitDeleteProfile: 'Profiel verwijderen?',
    productFitDeleteConfirm: 'Dit verwijdert permanent "{name}". Deze actie kan niet ongedaan worden gemaakt.',
    productFitSelectPrimaryCategory: 'Selecteer primaire categorie',
    productFitNone: 'Geen',
    productFitActive: 'Actief',
    productFitDeactivate: 'Instellen als inactief',
    productFitActivateError: 'Profiel activeren mislukt',
    productFitDeactivateError: 'Profiel deactiveren mislukt',
    loadingProductProfiles: 'Productprofielen laden...',

    // Person Profile
    backToPeople: 'Terug naar Personen',
    back: 'Terug',
    personNotFound: 'Persoon niet gevonden',
    personNotFoundDesc: 'De gevraagde persoon kon niet worden geladen of bestaat niet.',
    signalsCount: 'Signalen',
    interactions: 'Interacties',
    primaryInterests: 'Primaire interesses',
    formerEmployers: 'Voormalige werkgevers',
    education: 'Opleiding',
    professionalMemberships: 'Professionele lidmaatschappen',
    publicSpeakingEvents: 'Spreken in het openbaar en evenementen',
    recentEventsAttended: 'Recente evenementen bijgewoond',
    awardsRecognition: 'Prijzen en erkenning',
    publications: 'Publicaties',
    publicationContext: 'Publicatie',
    firstAuthorBadge: 'Eerste auteur',
    seniorAuthorBadge: 'Senior auteur',
    relevanceTierVeryStrong: 'Zeer sterke match',
    relevanceTierStrong: 'Sterke match',
    showAllPublications: 'Alles tonen ({count})',
    showLess: 'Minder tonen',
    removeFromPursuit: '✓ Verwijderen uit vervolg',
    addToPursuit: 'Toevoegen aan vervolg',
    assignMarketTier: 'Marktniveau toewijzen',
    tier: 'Niveau',
    selectTier: 'Selecteer een niveau',
    saveChanges: 'Wijzigingen opslaan',
    atAGlance: 'In één oogopslag',
    keyInitiatives: 'Belangrijke initiatieven en aandachtsgebieden.',
    noInitiatives: 'Geen initiatieven vermeld.',
    
    // AI Outreach
    aiOutreach: 'AI-outreach',
    channel: 'Kanaal',
    email: 'E-mail',
    linkedin: 'LinkedIn',
    call: 'Oproep',
    tone: 'Toon',
    relationshipBuilder: 'Relatiebouwer',
    thoughtLeadership: 'Thought leadership',
    challenger: 'Uitdager',
    context: 'Context',
    autoDetect: 'Beste context automatisch detecteren',
    personaInitiative: 'Persona-initiatieven en activiteit',
    institutionalChange: 'Institutionele veranderingen en momentum',
    strategicPriority: 'Strategische prioriteiten',
    procurementSignal: 'Inkoop- en financieringssignalen',
    customContext: 'Aangepaste context (optioneel)',
    addContext: 'Voeg specifieke context, gespreksonderwerpen of instructies toe...',
    generating: 'Genereren...',
    generateMessage: 'Bericht genereren',
    collateralToShare: 'Materiaal om te delen',
    warmUpReading: 'Warm-up lectuur',
    
    // Home Cards
    recommendedAccounts: 'Aanbevolen accounts',
    recommendedAccountsTooltip: 'Hoogst scorende accounts op basis van fit en strategische afstemming.',
    viewAll: 'Alles bekijken',
    noAccounts: 'Geen accounts',
    recommendedPersonas: 'Aanbevolen personas',
    recommendedPersonasTooltip: 'Meest relevante mensen op basis van invloed en rolafstemming.',
    noPersonas: 'Geen personas',
    
    // Action Center
    priorityActions: 'Prioriteitsacties',
    deals: 'Deals',
    all: 'Alles',
    myTasks: 'Mijn taken',
    newLeads: 'Nieuwe leads',
    updated: 'Bijgewerkt',
    loadingRecommendations: 'Uw gepersonaliseerde aanbevelingen laden',
    analyzingPipeline: 'Uw pipeline analyseren...',
    dueToday: 'Vandaag te doen',
    completeTouchVia: 'Contact voltooien',
    via: 'via',
    openDraft: 'Concept openen',
    noContactOnFileOpenPersonas: 'Geen contact geregistreerd bij dit account — open het tabblad Personas om er een toe te voegen.',
    openLinkedIn: 'LinkedIn openen',
    callNow: 'Nu bellen',
    done: 'Klaar',
    whyNow: 'Waarom nu?',
    nextSteps: 'Volgende stappen:',
    tracked: 'Gevolgd',
    fit: 'Fit',
    draftOutreach: 'Concept outreach',
    draftMessage: 'Concept opstellen',
    noTasksDue: 'Geen taken vandaag te doen',
    noNewLeads: 'Nog geen nieuwe leads',
    noActionsYet: 'Nog geen acties',
    hitRefresh: 'Klik op Vernieuwen en ik haal wat er voor u komt.',
    hotMovers: 'Meest actief (laatste 2 weken)',
    personaUpdates: 'Persona-updates (laatste 2 weken)',
    noSignals2Weeks: 'Geen signalen in de afgelopen 2 weken',
    liveDealsTitle: 'Actieve deals',
    noDeadline: 'Geen deadline',
    daysLeft: 'd over',
    expired: 'Verlopen',
    noLiveDealsCategories: 'Geen actieve deals in uw geselecteerde categorieën.',
    noLiveDealsFound: 'Geen actieve deals gevonden.',
    decisionWindowRadar: 'Beslissingsvenster radar',
    decisionWindowTooltip: 'Toont wanneer een contract waarschijnlijk wordt herzien op basis van inkooprecords en verlengingspatronen.',
    accountsApproaching: 'Accounts die verlenging/beslissingshorizonten naderen',
    noAccountsInBand: 'Geen accounts in deze band.',
    noAccountsWithWindows: 'Geen accounts met beslissingsvensters gevonden.',
    
    // Market Page
    marketMomentumTrend: 'Markt momentum trend (12 weken)',
    noMomentumData: 'Geen momentumgegevens beschikbaar',
    
    // Filters
    
    // Greetings
    goodMorning: 'Goedemorgen',
    goodAfternoon: 'Goedemiddag',
    goodEvening: 'Goedenavond',
    
    // Accounts Page
    manageExploreInstitutions: 'Doelinstellingen beheren en verkennen',
    searchAccounts: 'Accounts zoeken...',
    accountFitScore: 'Account fit score',
    renewalRisk: 'Verlengingsrisico',
    expansionOpps: 'Uitbreidingskansen',
    fariaProducts: 'Faria-producten',
    departmentFitScore: 'Afdeling fit score',
    noAccountsFound: 'Geen accounts gevonden',
    
    // Market Components
    topAccounts: 'Top accounts',
    topPersonas: 'Top personas',
    tieringTooltip: 'Accounts worden in eerste instantie ingedeeld op basis van hun Account Fit Score. Tiering helpt je te prioriteren waar je je op richt — zodat je meer tijd besteedt aan accounts met het hoogste potentieel, gerichter outreach doet en betere resultaten behaalt. Je kunt op elk moment de tier per afzonderlijk account aanpassen.',
    topInstitutionsAI: 'Top instellingen op basis van AI-adoptie en implementatie',
    topHospitalsAI: 'Top ziekenhuizen op basis van AI-adoptie en implementatie',
    topSchoolsAI: 'Top internationale scholen op basis van AI-adoptie en implementatie',
    topEnterprisesAI: 'Top ondernemingen op basis van AI-adoptie en implementatie',
    topCompaniesAI: 'Top bedrijven op basis van AI-adoptie en implementatie',
    topAuthoritiesAI: 'Top onderwijsautoriteiten op basis van AI-adoptie en implementatie',
    marketFitDistribution: 'Marktfitdistributie',
    accountFitComposition: 'Accountfitscore samenstelling over 10-punts banden',
    topBuyers: 'Top kopers',
    universitiesMostVendors: 'Universiteiten met de meeste nieuwe leveranciersimplementaties',
    enterprisesMostVendors: 'Ondernemingen met de meeste nieuwe leveranciersimplementaties',
    keyBuyingAreas: 'Belangrijke aankoopgebieden',
    functionalInvestment: 'Functionele categorieën met de meeste investering',
    procurementComplexity: 'Inkoopcomplexiteit',
    distributionProfiles: 'Distributie van inkoopprofielen',
    procurementRoutes: 'Inkooproutes',
    howInstitutionsBuy: 'Hoe instellingen technologie kopen',
    marketShare: 'Marktaandeel',
    marketWinners: 'Marktwinnaars',
    vendorsGaining: 'Leveranciers die nieuwe institutionele deals winnen',
    headlineTrends: 'Belangrijkste trends',
    marketInsights: 'Marktinzichten'
  },
  
  de: {
    home: 'Startseite',
    market: 'Markt',
    accounts: 'Konten',
    people: 'Personen',
    signals: 'Signale',
    procurement: 'Beschaffung',
    playbooks: 'Leitfäden',
    settings: 'Einstellungen',
    help: 'Hilfe',
    admin: 'Administrator',
    segments: 'Segmente',
    analytics: 'Analytics',
    outbox: 'Postausgang',
    logout: 'Abmelden',
    
    // Common
    loading: 'Laden...',
    save: 'Speichern',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    search: 'Suchen',
    filter: 'Filtern',
    export: 'Exportieren',
    retry: 'Wiederholen',
    refresh: 'Aktualisieren',
    backTo: 'Zurück zu',
    viewProfile: 'Profil anzeigen',
    
    // Headers
    dashboard: 'Dashboard',
    accountOverview: 'Kontoübersicht',
    
    // Tabs
    overview: 'Übersicht',
    details: 'Details',
    activity: 'Aktivität',
    notes: 'Notizen',
    connections: 'Connections',
    connectionsEmpty: 'No known connections yet for this person.',
    connectionInferred: 'inferred',
    personas: 'Personas',
    departments: 'Abteilungen',
    
    // Account Page
    techStack: 'Technologie-Stack',
    liveDeals: 'Aktive Angebote',
    decisionWindow: 'Entscheidungsfenster',
    renewalDate: 'Verlängerungsdatum',
    momentum: 'Dynamik',
    strategicPriorities: 'Strategische Prioritäten',
    institutionalChallenges: 'Institutionelle Herausforderungen',
    studentExpectations: 'Erwartungen der Studenten',
    keyContacts: 'Wichtige Kontakte',
    procurementProfile: 'Beschaffungsprofil',
    accountNotFound: 'Konto nicht gefunden',
    accountNotFoundDesc: 'Das angeforderte Konto konnte nicht geladen werden oder existiert nicht.',
    rateLimitReached: 'Ratenlimit erreicht. Zeige gecachte Daten an. Bitte warten Sie 5 Minuten, bevor Sie es erneut versuchen.',
    showingCachedData: 'Zeige gecachte Daten an. Klicken Sie auf Aktualisieren für die neuesten Updates.',
    
    // Charts & Metrics
    fitScore: 'Fit-Score',
    excellent: 'Ausgezeichnet',
    strong: 'Stark',
    moderate: 'Moderat',
    low: 'Niedrig',
    scoreRationale: 'Score-Begründung',
    noRationaleAvailable: 'Keine Begründung verfügbar.',
    riskRationale: 'Risiko-Begründung',
    
    // People & Personas
    personasOrganizedBy: 'Personas sind nach Rolle im Einkaufskomitee organisiert und nach ihrem Einfluss und Fit-Score geordnet. Klicken Sie auf einen Kontakt, um sein vollständiges Profil und seine Aktivität anzuzeigen.',
    addedToWatchlist: 'Zur Beobachtungsliste hinzugefügt',
    removedFromWatchlist: 'Von der Beobachtungsliste entfernt',
    
    // Procurement
    procurementFriendliness: 'Beschaffungsfreundlichkeit',
    friendly: 'Freundlich',
    standard: 'Standard',
    bureaucratic: 'Bürokratisch',
    monitor: 'Überwachen',
    
    // Decision Window
    lessThan12Months: '<12 Monate',
    months12to24: '12-24 Monate',
    moreThan24Months: '>24 Monate',
    notApplicable: 'N/A',
    decisionWindowRationale: 'Entscheidungsfenster Begründung',
    
    // Signals
    momentumUpdate: 'Dynamik-Update',
    momentumScoreChanged: 'Dynamik-Score geändert um',
    points: 'Punkte',
    
    // Market Intelligence
    marketIntelligence: 'Marktchancen',
    macroTrends: 'Makro-Trends und Chancen in Ihren Zielmärkten',
    buyingActivity: 'Kaufaktivität',
    players: 'Dynamik',
    
    // Filters
    allCountries: 'Alle Länder',
    filterByCountry: 'Nach Land filtern',
    allDepartments: 'Alle Abteilungen',
    filterByDepartment: 'Nach Abteilung filtern',
    
    // Status
    live: 'Aktiv',
    closed: 'Geschlossen',
    awarded: 'Vergeben',
    cancelled: 'Abgesagt',
    
    // Common - additional
    noDataAvailable: 'Keine Daten verfügbar.',
    
    // Confidence levels
    high: 'Hoch',
    medium: 'Mittel',
    urgencyHigh: 'Hohe Dringlichkeit',
    urgencyMedium: 'Mittlere Dringlichkeit',
    urgencyLow: 'Niedrige Dringlichkeit',
    priorityHigh: 'Hohe Priorität',
    priorityMedium: 'Mittlere Priorität',
    priorityLow: 'Niedrige Priorität',
    priorityTooltipLead: 'Priorität verbindet, wie gut diese Person passt, mit der Aktualität des Signals.',
    
    // Procurement Profile
    fieldMetric: 'Feld / Metrik',
    value: 'Wert',
    source: 'Quelle',
    confidence: 'Vertrauen',
    procurementIntelligence: 'Umfassende Beschaffungsintelligenz und Lieferantenpfadanalyse für diese Institution.',
    procurementProfileNA: 'Beschaffungsprofildaten sind für dieses Konto nicht verfügbar.',
    
    // Account Header
    students: 'Studenten',
    employees: 'Mitarbeiter',
    accountFitRationale: 'Konto-Fit-Begründung',
    excellentFit: 'Ausgezeichnete Passform',
    strongFit: 'Starke Passform',
    moderateFit: 'Moderate Passform',
    lowFit: 'Niedrige Passform',
    client: 'Kunde',
    buyingModelRationale: 'Kaufmodell-Begründung',
    bvoClassification: 'BVO-Klassifizierung',
    incumbentSentiment: 'Amtsinhaber-Stimmung',
    procurementFriendlyLabel: 'Zugänglich',
    procurementStandardLabel: 'Standard',
    procurementBureaucraticLabel: 'Bürokratisch',
    procurementMonitorLabel: 'Beobachten',
    bvoBudgetDriven: 'Budgetorientiert',
    bvoValueSeeker: 'Wertsucher',
    bvoOutcomeFocused: 'Ergebnisorientiert',
    bvoInnovationDriven: 'Innovationsorientiert',
    bvoRiskAverse: 'Risikoscheu',
    bvoComplianceFocused: 'Compliance-orientiert',
    sentimentAtRisk: 'Gefährdet',
    sentimentPositive: 'Positiv',
    sentimentNeutral: 'Neutral',
    sentimentNegative: 'Negativ',
    sentimentDissatisfied: 'Unzufrieden',
    unknown: 'Unbekannt',
    numberOfEmployees: 'Anzahl der Mitarbeiter',
    departmentFitRationale: 'Fit-Begründung',
    
    // Procurement Friendliness
    friendlyDesc: 'Diese Institution hat optimierte Beschaffungsprozesse und ist im Allgemeinen offen für neue Lieferantenbeziehungen.',
    standardDesc: 'Diese Institution folgt typischen Beschaffungsverfahren mit standardmäßigen Bewertungszeitplänen.',
    bureaucraticDesc: 'Diese Institution hat komplexe Beschaffungsprozesse, die längere Verkaufszyklen und zusätzliche Genehmigungen erfordern können.',
    monitorDesc: 'Diese Institution hat herausfordernde Beschaffungsdynamiken, die eine sorgfältige Überwachung und strategisches Engagement erfordern.',
    procurementProcessClassification: 'Beschaffungsprozessklassifizierung für diese Institution.',
    
    // Decision Window
    decisionWindowMonitor: 'Entscheidungsfenster-Monitor',
    decisionWindowDesc: 'Zeigt, wann ein Vertrag wahrscheinlich überprüft wird. Ablaufdaten werden anhand von Beschaffungsunterlagen, typischen Vertragszyklen und Verlängerungsmitteilungen geschätzt. Wenn Daten fehlen, projizieren wir basierend auf Vergabedaten und Standardmustern, immer mit Vertrauensstufen gekennzeichnet.',
    
    // Tech Stack
    noTechData: 'Keine angegebenen Technologiedaten',
    
    // Tenant-specific fields
    pedagogicalModel: 'Pädagogisches und kulturelles Modell',
    digitalLandscape: 'Digitale Landschaft',
    digitalFunding: 'Digitale Finanzierungsmöglichkeiten',
    learningInitiatives: 'Lernentwicklungsinitiativen',
    patientCareGaps: 'Lücken in der Patientenversorgung',
    decisionDynamics: 'Stakeholder-Entscheidungsdynamik',
    contentCoverage: 'Inhaltsabdeckung',
    clientOpportunities: 'Kundenmöglichkeiten',
    decisionMakers: 'Entscheidungsträger',
    engagementHooks: 'Engagement-Hooks',
    recommendedTactics: 'Empfohlene Taktiken',
    
    // People Page
    keyDecisionMakers: 'Wichtige Entscheidungsträger und Einflussnehmer',
    searchPeople: 'Personen nach Name, Rolle oder Universität suchen...',
    name: 'Name',
    account: 'Konto',
    country: 'Land',
    department: 'Abteilung',
    archetype: 'Archetyp',
    pursuit: 'Verfolgung',
    socialScore: 'Soziale Punktzahl',
    noPeopleFound: 'Keine Personen gefunden',
    adjustFilters: 'Versuchen Sie, Ihre Suche oder Filter anzupassen.',
    formerClient: 'Ehemaliger Kunde',
    previouslyWorked: 'Hat zuvor für einen unserer Kunden gearbeitet',
    socialPresence: 'Misst die Sichtbarkeit und den Einfluss der Persona auf sozialen Plattformen wie LinkedIn.',
    personaFitRationale: 'Persona-Fit-Begründung',
    strategic: 'Strategisch',
    supportive: 'Unterstützend',
    passive: 'Passiv',
    showContactInfo: 'Kontaktinformationen anzeigen',
    satisfied: 'Zufrieden',
    neutral: 'Neutral',
    frustrated: 'Frustriert',
    exploringAlternatives: 'Alternativen erkunden',
    
    // Signals Page
    trackSignals: 'Verfolgen Sie das Konto-Momentum und die Persona-Aktivität in Echtzeit, um Chancen zu identifizieren und im richtigen Moment zu engagieren.',
    allSignals: 'Alle Signale',
    noAccountSignals: 'Keine Konto-Signale gefunden',
    noPersonaSignals: 'Keine Persona-Signale gefunden',
    
    // Playbooks Page
    structuredEngagement: 'Strukturierte Engagement-Strategien für verschiedene Szenarien',
    createPlaybook: 'Leitfaden erstellen',
    searchPlaybooks: 'Leitfäden suchen...',
    allFactors: 'Alle Faktoren',
    eventPlaybooks: 'Event-Leitfäden',
    targetedSequences: 'Führen Sie gezielte Sequenzen für Ihre bevorstehenden Veranstaltungen aus',
    noUpcomingEvents: 'Keine bevorstehenden Veranstaltungen',
    addEventsInSettings: 'Veranstaltungen in Einstellungen hinzufügen',
    selectEvent: 'Event auswählen',
    previewAndApply: 'Vorschau und anwenden',
    yourCustomPlaybooks: 'Ihre benutzerdefinierten Leitfäden',
    custom: 'Benutzerdefiniert',
    goal: 'Ziel',
    enrolledAccounts: 'Eingeschriebene Konten',
    untouchedAccounts: 'Unberührte Konten',
    noPlaybooks: 'Keine Leitfäden gefunden, die Ihren Kriterien entsprechen',
    
    // Segments Page
    createManageExport: 'Ziellisten erstellen, verwalten und exportieren',
    buildNewSegment: 'Neues Segment erstellen',
    rules: 'Regeln',
    created: 'Erstellt',
    viewResults: 'Ergebnisse anzeigen',
    noSegmentsYet: 'Noch keine Segmente',
    getStartedSegment: 'Beginnen Sie mit der Erstellung Ihres ersten Segments.',
    deleteSegment: 'Segment löschen',
    segmentDeleted: 'Segment erfolgreich gelöscht',
    authRequired: 'Authentifizierung erforderlich',
    loginToAccess: 'Bitte melden Sie sich an, um auf Segmente zuzugreifen.',
    logIn: 'Anmelden',
    
    // Home Page
    yourWatchlist: 'Ihre Beobachtungsliste',
    watchlistTooltip: 'Ihre Beobachtungsliste verfolgt wichtige Personas, mit denen Sie aktiv in Kontakt stehen. Diese Personen sind für den kontinuierlichen Beziehungsaufbau gekennzeichnet und in Ihrem Workflow priorisiert.',
    open: 'Öffnen',
    untrack: 'Nicht mehr verfolgen',
    moreTracked: 'weitere verfolgte Personas…',
    refreshing: 'Aktualisierung…',
    
    // Procurement Page
    dealsTenders: 'Deals und Ausschreibungen',
    trackTenders: 'Verfolgen Sie offene Ausschreibungen und abgeschlossene Deals auf dem Markt.',
    searchKeyword: 'Suche nach Stichwort, Titel, Institution...',
    liveDealsCount: 'Aktive Deals',
    recentlyClosed: 'Kürzlich geschlossen',
    closedDeals: 'Geschlossene Deals',
    allInstitutions: 'Alle Institutionen',
    filterByInstitution: 'Nach Institution filtern',
    allCategories: 'Alle Kategorien',
    filterByCategory: 'Nach Kategorie filtern',
    selectAll: 'Alle auswählen',
    clearAll: 'Alle löschen',
    typeToSearch: 'Tippen zum Suchen...',
    category: 'Kategorie',
    vendor: 'Anbieter',
    institutionType: 'Institutionstyp',
    sentiment: 'Stimmung',
    listOnly: 'Nur Liste',
    mapPlusList: 'Karte + Liste',
    allDeadlines: 'Alle Fristen',
    days0_7: '0–7 Tage',
    days8_30: '8–30 Tage',
    days31_90: '31–90 Tage',
    days90Plus: '90+ Tage',
    months0_6: '0–6 Monate',
    months6_12: '6–12 Monate',
    months12_24: '12–24 Monate',
    unknownExpired: 'Unbekannt / Abgelaufen',
    allVendors: 'Alle Anbieter',
    exit: 'Exit',
    filterByExitLeverage: 'Nach Exit-Hebel filtern',
    some: 'Einige',
    noSignal: 'Kein Signal',
    loadingDeals: 'Angebote werden geladen...',
    loadingContractIntelligence: 'Vertragsintelligenz wird geladen...',
    loadingMoreDeals: 'Weitere Angebote werden geladen...',
    loadedDeals: '{count} {label} Angebote geladen',
    outOfScope: 'Außerhalb des Bereichs',
    pending: 'Ausstehend',
    insufficientInfo: 'Unzureichende Info',
    liveRadar: 'Live-Radar',
    recentlyExpired: 'Kürzlich abgelaufen',
    contractIntelligence: 'Vertragsintelligenz',
    displacementRadar: 'Verdrängungs-Radar',
    benchmarking: 'Benchmarking',
    title: 'Titel',
    institution: 'Einrichtung',
    deadline: 'Frist',
    productFitPanel: 'Produktpassung',
    noLiveDeals: 'Keine Live-Angebote gefunden',
    product: 'Produkt',
    reseller: 'Wiederverkäufer',
    procurementMethod: 'Vergabeverfahren',
    framework: 'Rahmenvertrag',
    productName: 'Produktname',
    supplier: 'Anbieter',
    predictedExpiry: 'Voraussichtliches Ablaufdatum',
    productFitProfileDesc: 'Definieren Sie Produktprofile für Fit-Bewertung. Mehrere Profile können gleichzeitig aktiv sein: sie steuern Light Fit im Live Radar (Kategorie) und Sie wählen ein Profil für Deep Fit bei einer Ausschreibung.',
    createProfile: 'Profil erstellen',
    editProfile: 'Profil bearbeiten',
    newProfile: 'Neues Profil',
    productFitName: 'Name',
    productFitPrimaryCategory: 'Primäre Kategorie',
    productFitCategoryTags: 'Kategorietags (kommagetrennt)',
    productFitStrongSignals: 'Starke Fit-Signale (eins pro Zeile)',
    productFitNegativeSignals: 'Negative Signale (Hard: oder Soft: Präfix, eins pro Zeile)',
    productFitDeploymentModels: 'Bereitstellungsmodelle',
    productFitCloud: 'Cloud',
    productFitSelfHosted: 'Self-hosted',
    productFitHybrid: 'Hybrid',
    productFitIntegrations: 'Integrationen (kommagetrennt)',
    productFitSecurity: 'Sicherheitszertifizierungen (kommagetrennt)',
    productFitKeyFeatures: 'Hauptfunktionen (kommagetrennt)',
    productFitUpdate: 'Aktualisieren',
    productFitCreate: 'Erstellen',
    productFitProfiles: 'Profile',
    productFitNoProfiles: 'Noch keine Profile. Erstellen Sie oben eines.',
    productFitSetAsActive: 'Als aktiv setzen',
    productFitDeleteProfile: 'Profil löschen?',
    productFitDeleteConfirm: 'Dies wird "{name}" dauerhaft löschen. Diese Aktion kann nicht rückgängig gemacht werden.',
    productFitSelectPrimaryCategory: 'Primäre Kategorie auswählen',
    productFitNone: 'Keine',
    productFitActive: 'Aktiv',
    productFitDeactivate: 'Als inaktiv setzen',
    productFitActivateError: 'Profil konnte nicht aktiviert werden',
    productFitDeactivateError: 'Profil konnte nicht deaktiviert werden',
    loadingProductProfiles: 'Produktprofile werden geladen...',

    // Person Profile
    backToPeople: 'Zurück zu Personen',
    back: 'Zurück',
    personNotFound: 'Person nicht gefunden',
    personNotFoundDesc: 'Die angeforderte Person konnte nicht geladen werden oder existiert nicht.',
    signalsCount: 'Signale',
    interactions: 'Interaktionen',
    primaryInterests: 'Hauptinteressen',
    formerEmployers: 'Frühere Arbeitgeber',
    education: 'Ausbildung',
    professionalMemberships: 'Professionelle Mitgliedschaften',
    publicSpeakingEvents: 'Öffentliche Reden und Veranstaltungen',
    recentEventsAttended: 'Kürzlich besuchte Veranstaltungen',
    awardsRecognition: 'Auszeichnungen und Anerkennungen',
    publications: 'Publications',
    publicationContext: 'Publication',
    firstAuthorBadge: 'First author',
    seniorAuthorBadge: 'Senior author',
    relevanceTierVeryStrong: 'Very strong match',
    relevanceTierStrong: 'Strong match',
    showAllPublications: 'Show all ({count})',
    showLess: 'Show less',
    removeFromPursuit: '✓ Aus Verfolgung entfernen',
    addToPursuit: 'Zur Verfolgung hinzufügen',
    assignMarketTier: 'Marktniveau zuweisen',
    tier: 'Niveau',
    selectTier: 'Wählen Sie ein Niveau',
    saveChanges: 'Änderungen speichern',
    atAGlance: 'Auf einen Blick',
    keyInitiatives: 'Wichtige Initiativen und Schwerpunkte.',
    noInitiatives: 'Keine Initiativen aufgeführt.',
    
    // AI Outreach
    aiOutreach: 'KI-Kontaktaufnahme',
    channel: 'Kanal',
    email: 'E-Mail',
    linkedin: 'LinkedIn',
    call: 'Anruf',
    tone: 'Ton',
    relationshipBuilder: 'Beziehungsaufbau',
    thoughtLeadership: 'Thought Leadership',
    challenger: 'Herausforderer',
    context: 'Kontext',
    autoDetect: 'Besten Kontext automatisch erkennen',
    personaInitiative: 'Persona-Initiativen und Aktivität',
    institutionalChange: 'Institutionelle Veränderungen und Dynamik',
    strategicPriority: 'Strategische Prioritäten',
    procurementSignal: 'Beschaffungs- und Fördersignale',
    customContext: 'Benutzerdefinierter Kontext (optional)',
    addContext: 'Spezifischen Kontext, Gesprächspunkte oder Anweisungen hinzufügen...',
    generating: 'Wird generiert...',
    generateMessage: 'Nachricht generieren',
    collateralToShare: 'Material zum Teilen',
    warmUpReading: 'Einstiegslektüre',
    
    // Home Cards
    recommendedAccounts: 'Empfohlene Konten',
    recommendedAccountsTooltip: 'Am höchsten bewertete Konten basierend auf Passform und strategischer Ausrichtung.',
    viewAll: 'Alle anzeigen',
    noAccounts: 'Keine Konten',
    recommendedPersonas: 'Empfohlene Personas',
    recommendedPersonasTooltip: 'Relevanteste Personen basierend auf Einfluss und Rollenausrichtung.',
    noPersonas: 'Keine Personas',
    
    // Action Center
    priorityActions: 'Prioritätsaktionen',
    deals: 'Deals',
    all: 'Alle',
    myTasks: 'Meine Aufgaben',
    newLeads: 'Neue Leads',
    updated: 'Aktualisiert',
    loadingRecommendations: 'Ihre personalisierten Empfehlungen werden geladen',
    analyzingPipeline: 'Ihre Pipeline wird analysiert...',
    dueToday: 'Fällig heute',
    completeTouchVia: 'Kontakt abschließen',
    via: 'über',
    openDraft: 'Entwurf öffnen',
    noContactOnFileOpenPersonas: 'Kein Kontakt für dieses Konto hinterlegt — öffnen Sie den Tab Personas, um einen hinzuzufügen.',
    openLinkedIn: 'LinkedIn öffnen',
    callNow: 'Jetzt anrufen',
    done: 'Erledigt',
    whyNow: 'Warum jetzt?',
    nextSteps: 'Nächste Schritte:',
    tracked: 'Verfolgt',
    fit: 'Passform',
    draftOutreach: 'Entwurf für Kontaktaufnahme',
    draftMessage: 'Nachricht entwerfen',
    noTasksDue: 'Keine Aufgaben heute fällig',
    noNewLeads: 'Noch keine neuen Leads',
    noActionsYet: 'Noch keine Aktionen',
    hitRefresh: 'Klicken Sie auf Aktualisieren und ich hole, was als Nächstes für Sie ansteht.',
    hotMovers: 'Aktivste (letzte 2 Wochen)',
    personaUpdates: 'Persona-Updates (letzte 2 Wochen)',
    noSignals2Weeks: 'Keine Signale in den letzten 2 Wochen',
    liveDealsTitle: 'Aktive Deals',
    noDeadline: 'Keine Frist',
    daysLeft: 'T übrig',
    expired: 'Abgelaufen',
    noLiveDealsCategories: 'Keine aktiven Deals in Ihren ausgewählten Kategorien.',
    noLiveDealsFound: 'Keine aktiven Deals gefunden.',
    decisionWindowRadar: 'Entscheidungsfenster-Radar',
    decisionWindowTooltip: 'Zeigt, wann ein Vertrag wahrscheinlich überprüft wird, basierend auf Beschaffungsunterlagen und Verlängerungsmustern.',
    accountsApproaching: 'Konten, die sich Verlängerungs-/Entscheidungshorizonten nähern',
    noAccountsInBand: 'Keine Konten in diesem Band.',
    noAccountsWithWindows: 'Keine Konten mit Entscheidungsfenstern gefunden.',
    
    // Market Page
    marketMomentumTrend: 'Marktdynamik-Trend (12 Wochen)',
    noMomentumData: 'Keine Dynamik-Daten verfügbar',
    
    // Filters
    
    // Greetings
    goodMorning: 'Guten Morgen',
    goodAfternoon: 'Guten Tag',
    goodEvening: 'Guten Abend',
    
    // Accounts Page
    manageExploreInstitutions: 'Zielinstitutionen verwalten und erkunden',
    searchAccounts: 'Konten suchen...',
    accountFitScore: 'Konto-Fit-Score',
    renewalRisk: 'Verlängerungsrisiko',
    expansionOpps: 'Expansionsmöglichkeiten',
    fariaProducts: 'Faria-Produkte',
    departmentFitScore: 'Abteilungs-Fit-Score',
    noAccountsFound: 'Keine Konten gefunden',
    
    // Market Components
    topAccounts: 'Top-Konten',
    topPersonas: 'Top-Personas',
    tieringTooltip: 'Konten werden zunächst anhand ihres Account-Fit-Scores in Tier-Stufen eingeteilt. Tiering hilft Ihnen, Prioritäten zu setzen — damit Sie mehr Zeit in die vielversprechendsten Accounts investieren, gezielter arbeiten und bessere Ergebnisse erzielen. Sie können die Tier-Stufe jederzeit für jeden einzelnen Account manuell anpassen.',
    topInstitutionsAI: 'Top-Institutionen nach KI-Einführung und Implementierung',
    topHospitalsAI: 'Top-Krankenhäuser nach KI-Einführung und Implementierung',
    topSchoolsAI: 'Top internationale Schulen nach KI-Einführung und Implementierung',
    topEnterprisesAI: 'Top-Unternehmen nach KI-Einführung und Implementierung',
    topCompaniesAI: 'Top-Firmen nach KI-Einführung und Implementierung',
    topAuthoritiesAI: 'Top-Bildungsbehörden nach KI-Einführung und Implementierung',
    marketFitDistribution: 'Markt-Fit-Verteilung',
    accountFitComposition: 'Konto-Fit-Score-Zusammensetzung über 10-Punkte-Bänder',
    topBuyers: 'Top-Käufer',
    universitiesMostVendors: 'Universitäten mit den meisten neuen Lieferantenimplementierungen',
    enterprisesMostVendors: 'Unternehmen mit den meisten neuen Lieferantenimplementierungen',
    keyBuyingAreas: 'Wichtige Einkaufsbereiche',
    functionalInvestment: 'Funktionskategorien mit der meisten Investition',
    procurementComplexity: 'Beschaffungskomplexität',
    distributionProfiles: 'Verteilung der Beschaffungsprofile',
    procurementRoutes: 'Beschaffungswege',
    howInstitutionsBuy: 'Wie Institutionen Technologie kaufen',
    marketShare: 'Marktanteil',
    marketWinners: 'Marktgewinner',
    vendorsGaining: 'Anbieter, die neue institutionelle Geschäfte gewinnen',
    headlineTrends: 'Haupttrends',
    marketInsights: 'Markteinblicke'
  },
  
  ar: {
    home: 'الرئيسية',
    market: 'السوق',
    accounts: 'الحسابات',
    people: 'الأشخاص',
    signals: 'الإشارات',
    procurement: 'المشتريات',
    playbooks: 'دليل العمل',
    settings: 'الإعدادات',
    help: 'المساعدة',
    admin: 'المسؤول',
    segments: 'الشرائح',
    analytics: 'التحليلات',
    outbox: 'صندوق الصادر',
    logout: 'تسجيل الخروج',
    
    // Common
    loading: 'جارٍ التحميل...',
    save: 'حفظ',
    cancel: 'إلغاء',
    edit: 'تعديل',
    delete: 'حذف',
    search: 'بحث',
    filter: 'تصفية',
    export: 'تصدير',
    retry: 'إعادة المحاولة',
    refresh: 'تحديث',
    backTo: 'العودة إلى',
    viewProfile: 'عرض الملف الشخصي',
    
    // Headers
    dashboard: 'لوحة القيادة',
    accountOverview: 'نظرة عامة على الحساب',
    
    // Tabs
    overview: 'نظرة عامة',
    details: 'التفاصيل',
    activity: 'النشاط',
    notes: 'الملاحظات',
    connections: 'Connections',
    connectionsEmpty: 'No known connections yet for this person.',
    connectionInferred: 'inferred',
    personas: 'الشخصيات',
    departments: 'الأقسام',
    
    // Account Page
    techStack: 'المجموعة التقنية',
    liveDeals: 'الصفقات النشطة',
    decisionWindow: 'نافذة القرار',
    renewalDate: 'تاريخ التجديد',
    momentum: 'الزخم',
    strategicPriorities: 'الأولويات الاستراتيجية',
    institutionalChallenges: 'التحديات المؤسسية',
    studentExpectations: 'توقعات الطلاب',
    keyContacts: 'جهات الاتصال الرئيسية',
    procurementProfile: 'ملف المشتريات',
    accountNotFound: 'الحساب غير موجود',
    accountNotFoundDesc: 'تعذر تحميل الحساب المطلوب أو أنه غير موجود.',
    rateLimitReached: 'تم الوصول إلى حد المعدل. عرض البيانات المخزنة مؤقتًا. يرجى الانتظار 5 دقائق قبل إعادة المحاولة.',
    showingCachedData: 'عرض البيانات المخزنة مؤقتًا. انقر على تحديث للحصول على آخر التحديثات.',
    
    // Charts & Metrics
    fitScore: 'درجة الملاءمة',
    excellent: 'ممتاز',
    strong: 'قوي',
    moderate: 'متوسط',
    low: 'منخفض',
    scoreRationale: 'مبرر الدرجة',
    noRationaleAvailable: 'لا يوجد مبرر متاح.',
    riskRationale: 'مبرر المخاطر',
    
    // People & Personas
    personasOrganizedBy: 'يتم تنظيم الشخصيات حسب دورها في لجنة الشراء وتصنيفها حسب تأثيرها ودرجة الملاءمة. انقر على أي جهة اتصال لعرض ملفها الشخصي الكامل ونشاطها.',
    addedToWatchlist: 'تمت الإضافة إلى قائمة المراقبة',
    removedFromWatchlist: 'تمت الإزالة من قائمة المراقبة',
    
    // Procurement
    procurementFriendliness: 'سهولة المشتريات',
    friendly: 'ودود',
    standard: 'قياسي',
    bureaucratic: 'بيروقراطي',
    monitor: 'مراقبة',
    
    // Decision Window
    lessThan12Months: '<12 شهرًا',
    months12to24: '12-24 شهرًا',
    moreThan24Months: '>24 شهرًا',
    notApplicable: 'غير متاح',
    decisionWindowRationale: 'مبرر نافذة القرار',
    
    // Signals
    momentumUpdate: 'تحديث الزخم',
    momentumScoreChanged: 'تغيرت درجة الزخم بمقدار',
    points: 'نقاط',
    
    // Market Intelligence
    marketIntelligence: 'فرص السوق',
    macroTrends: 'الاتجاهات الكلية والفرص في أسواقك المستهدفة',
    buyingActivity: 'نشاط الشراء',
    players: 'الديناميكيات',
    
    // Filters
    allCountries: 'جميع البلدان',
    filterByCountry: 'تصفية حسب البلد',
    allDepartments: 'جميع الأقسام',
    filterByDepartment: 'تصفية حسب القسم',
    
    // Status
    live: 'نشط',
    closed: 'مغلق',
    awarded: 'ممنوح',
    cancelled: 'ملغى',
    
    // Common - additional
    noDataAvailable: 'لا توجد بيانات متاحة.',
    
    // Confidence levels
    high: 'عالي',
    medium: 'متوسط',
    urgencyHigh: 'أولوية عالية',
    urgencyMedium: 'أولوية متوسطة',
    urgencyLow: 'أولوية منخفضة',
    priorityHigh: 'أولوية عالية',
    priorityMedium: 'أولوية متوسطة',
    priorityLow: 'أولوية منخفضة',
    priorityTooltipLead: 'تجمع الأولوية بين مدى ملاءمة هذا الشخص وحداثة الإشارة.',
    
    // Procurement Profile
    fieldMetric: 'الحقل / المقياس',
    value: 'القيمة',
    source: 'المصدر',
    confidence: 'الثقة',
    procurementIntelligence: 'ذكاء المشتريات الشامل وتحليل مسار الموردين لهذه المؤسسة.',
    procurementProfileNA: 'بيانات ملف المشتريات غير متاحة لهذا الحساب.',
    
    // Account Header
    students: 'طلاب',
    employees: 'موظفين',
    accountFitRationale: 'مبرر ملاءمة الحساب',
    excellentFit: 'ملاءمة ممتازة',
    strongFit: 'ملاءمة قوية',
    moderateFit: 'ملاءمة متوسطة',
    lowFit: 'ملاءمة منخفضة',
    client: 'عميل',
    buyingModelRationale: 'مبرر نموذج الشراء',
    bvoClassification: 'تصنيف BVO',
    incumbentSentiment: 'مشاعر البائع الحالي',
    procurementFriendlyLabel: 'ودود',
    procurementStandardLabel: 'قياسي',
    procurementBureaucraticLabel: 'بيروقراطي',
    procurementMonitorLabel: 'مراقبة',
    bvoBudgetDriven: 'موجه بالميزانية',
    bvoValueSeeker: 'باحث عن القيمة',
    bvoOutcomeFocused: 'موجه بالنتائج',
    bvoInnovationDriven: 'موجه بالابتكار',
    bvoRiskAverse: 'متجنب للمخاطر',
    bvoComplianceFocused: 'موجه بالامتثال',
    sentimentAtRisk: 'معرض للخطر',
    sentimentPositive: 'إيجابي',
    sentimentNeutral: 'محايد',
    sentimentNegative: 'سلبي',
    sentimentDissatisfied: 'غير راضٍ',
    unknown: 'غير معروف',
    numberOfEmployees: 'عدد الموظفين',
    departmentFitRationale: 'مبرر الملاءمة',
    
    // Procurement Friendliness
    friendlyDesc: 'لدى هذه المؤسسة عمليات مشتريات مبسطة وهي بشكل عام متقبلة لعلاقات البائعين الجدد.',
    standardDesc: 'تتبع هذه المؤسسة إجراءات المشتريات النموذجية مع جداول التقييم القياسية.',
    bureaucraticDesc: 'لدى هذه المؤسسة عمليات مشتريات معقدة قد تتطلب دورات مبيعات أطول وموافقات إضافية.',
    monitorDesc: 'لدى هذه المؤسسة ديناميكيات مشتريات صعبة تتطلب مراقبة دقيقة ومشاركة استراتيجية.',
    procurementProcessClassification: 'تصنيف عملية المشتريات لهذه المؤسسة.',
    
    // Decision Window
    decisionWindowMonitor: 'مراقب نافذة القرار',
    decisionWindowDesc: 'يوضح متى من المحتمل أن يكون العقد قيد المراجعة. يتم تقدير تواريخ الانتهاء باستخدام سجلات المشتريات ودورات العقود النموذجية وإشعارات التجديد. عندما تكون البيانات مفقودة، نقوم بالتوقع بناءً على تواريخ المنح والأنماط القياسية، مع الوسم دائمًا بمستويات الثقة.',
    
    // Tech Stack
    noTechData: 'لا توجد بيانات تقنية محددة',
    
    // Tenant-specific fields
    pedagogicalModel: 'النموذج التربوي والثقافي',
    digitalLandscape: 'المشهد الرقمي',
    digitalFunding: 'فرص التمويل الرقمي',
    learningInitiatives: 'مبادرات تطوير التعلم',
    patientCareGaps: 'فجوات رعاية المرضى',
    decisionDynamics: 'ديناميكيات قرار أصحاب المصلحة',
    contentCoverage: 'تغطية المحتوى',
    clientOpportunities: 'فرص العملاء',
    decisionMakers: 'صانعو القرار',
    engagementHooks: 'خطافات المشاركة',
    recommendedTactics: 'التكتيكات الموصى بها',
    
    // People Page
    keyDecisionMakers: 'صناع القرار الرئيسيون والمؤثرون',
    searchPeople: 'البحث عن أشخاص بالاسم أو الدور أو الجامعة...',
    name: 'الاسم',
    account: 'الحساب',
    country: 'الدولة',
    department: 'القسم',
    archetype: 'النمط',
    pursuit: 'المتابعة',
    socialScore: 'النقاط الاجتماعية',
    noPeopleFound: 'لم يتم العثور على أشخاص',
    adjustFilters: 'حاول تعديل البحث أو الفلاتر.',
    formerClient: 'عميل سابق',
    previouslyWorked: 'عمل سابقاً لدى أحد عملائنا',
    socialPresence: 'يقيس رؤية الشخصية وتأثيرها على المنصات الاجتماعية مثل LinkedIn.',
    personaFitRationale: 'مبرر ملاءمة الشخصية',
    strategic: 'استراتيجي',
    supportive: 'داعم',
    passive: 'سلبي',
    showContactInfo: 'إظهار معلومات الاتصال',
    satisfied: 'راضٍ',
    neutral: 'محايد',
    frustrated: 'محبط',
    exploringAlternatives: 'استكشاف البدائل',
    
    // Signals Page
    trackSignals: 'تتبع زخم الحساب ونشاط الشخصية في الوقت الفعلي لتحديد الفرص والمشاركة في الوقت المناسب.',
    allSignals: 'جميع الإشارات',
    noAccountSignals: 'لم يتم العثور على إشارات حساب',
    noPersonaSignals: 'لم يتم العثور على إشارات شخصية',
    
    // Playbooks Page
    structuredEngagement: 'استراتيجيات مشاركة منظمة لسيناريوهات مختلفة',
    createPlaybook: 'إنشاء دليل',
    searchPlaybooks: 'البحث في الأدلة...',
    allFactors: 'جميع العوامل',
    eventPlaybooks: 'أدلة الأحداث',
    targetedSequences: 'قم بتشغيل تسلسلات مستهدفة لأحداثك القادمة',
    noUpcomingEvents: 'لا توجد أحداث قادمة',
    addEventsInSettings: 'إضافة أحداث في الإعدادات',
    selectEvent: 'اختر حدثاً',
    previewAndApply: 'معاينة وتطبيق',
    yourCustomPlaybooks: 'أدلتك المخصصة',
    custom: 'مخصص',
    goal: 'الهدف',
    enrolledAccounts: 'الحسابات المسجلة',
    untouchedAccounts: 'الحسابات غير المستخدمة',
    noPlaybooks: 'لم يتم العثور على أدلة تطابق معاييرك',
    
    // Segments Page
    createManageExport: 'إنشاء وإدارة وتصدير قوائم الأهداف',
    buildNewSegment: 'بناء شريحة جديدة',
    rules: 'قواعد',
    created: 'تم الإنشاء',
    viewResults: 'عرض النتائج',
    noSegmentsYet: 'لا توجد شرائح بعد',
    getStartedSegment: 'ابدأ بإنشاء شريحتك الأولى.',
    deleteSegment: 'حذف الشريحة',
    segmentDeleted: 'تم حذف الشريحة بنجاح',
    authRequired: 'المصادقة مطلوبة',
    loginToAccess: 'الرجاء تسجيل الدخول للوصول إلى الشرائح.',
    logIn: 'تسجيل الدخول',
    
    // Home Page
    yourWatchlist: 'قائمة المراقبة الخاصة بك',
    watchlistTooltip: 'تتبع قائمة المراقبة الخاصة بك الشخصيات الرئيسية التي تتفاعل معها بنشاط. يتم وضع علامة على هؤلاء الأفراد لبناء العلاقات المستمرة ويتم إعطاؤهم الأولوية في سير عملك.',
    open: 'فتح',
    untrack: 'إلغاء التتبع',
    moreTracked: 'المزيد من الشخصيات المتتبعة…',
    refreshing: 'جارٍ التحديث…',
    
    // Procurement Page
    dealsTenders: 'الصفقات والمناقصات',
    trackTenders: 'تتبع المناقصات المفتوحة والصفقات المغلقة في السوق.',
    searchKeyword: 'البحث بالكلمة الرئيسية أو العنوان أو المؤسسة...',
    liveDealsCount: 'الصفقات النشطة',
    recentlyClosed: 'مغلقة مؤخراً',
    closedDeals: 'الصفقات المغلقة',
    allInstitutions: 'جميع المؤسسات',
    filterByInstitution: 'تصفية حسب المؤسسة',
    allCategories: 'جميع الفئات',
    filterByCategory: 'تصفية حسب الفئة',
    selectAll: 'تحديد الكل',
    clearAll: 'مسح الكل',
    typeToSearch: 'اكتب للبحث...',
    category: 'الفئة',
    vendor: 'المورد',
    institutionType: 'نوع المؤسسة',
    sentiment: 'المشاعر',
    listOnly: 'القائمة فقط',
    mapPlusList: 'الخريطة + القائمة',
    allDeadlines: 'جميع المواعيد',
    days0_7: '0–7 أيام',
    days8_30: '8–30 يوم',
    days31_90: '31–90 يوم',
    days90Plus: '90+ يوم',
    months0_6: '0–6 أشهر',
    months6_12: '6–12 شهر',
    months12_24: '12–24 شهر',
    unknownExpired: 'غير معروف / منتهي',
    allVendors: 'جميع الموردين',
    exit: 'الخروج',
    filterByExitLeverage: 'تصفية حسب رافعة الخروج',
    some: 'بعض',
    noSignal: 'لا إشارة',
    loadingDeals: 'جاري تحميل العروض...',
    loadingContractIntelligence: 'جاري تحميل ذكاء العقود...',
    loadingMoreDeals: 'جاري تحميل المزيد من العروض...',
    loadedDeals: 'تم تحميل {count} عروض {label}',
    outOfScope: 'خارج النطاق',
    pending: 'قيد الانتظار',
    insufficientInfo: 'معلومات غير كافية',
    liveRadar: 'الرادار المباشر',
    recentlyExpired: 'منتهية الصلاحية مؤخرًا',
    contractIntelligence: 'ذكاء العقود',
    displacementRadar: 'رادار الإزاحة',
    benchmarking: 'المقارنة المعيارية',
    title: 'العنوان',
    institution: 'المؤسسة',
    deadline: 'الموعد النهائي',
    productFitPanel: 'ملاءمة المنتج',
    noLiveDeals: 'لم يتم العثور على عروض مباشرة',
    product: 'المنتج',
    reseller: 'الموزع',
    procurementMethod: 'طريقة الشراء',
    framework: 'الاتفاقية الإطارية',
    productName: 'اسم المنتج',
    supplier: 'المورد',
    predictedExpiry: 'انتهاء الصلاحية المتوقع',
    productFitProfileDesc: 'حدد ملفات تعريف المنتج لمسح الملاءمة. يمكن تفعيل عدة ملفات في وقت واحد: تدعم Light Fit في Live Radar (حسب الفئة) وتختار ملفًا واحدًا عند تشغيل Deep Fit على مناقصة.',
    createProfile: 'إنشاء ملف تعريف',
    editProfile: 'تحرير الملف الشخصي',
    newProfile: 'ملف تعريف جديد',
    productFitName: 'الاسم',
    productFitPrimaryCategory: 'الفئة الأساسية',
    productFitCategoryTags: 'وسوم الفئة (مفصولة بفواصل)',
    productFitStrongSignals: 'إشارات الملاءمة القوية (واحدة في كل سطر)',
    productFitNegativeSignals: 'إشارات سلبية (بادئة Hard: أو Soft:، واحدة في كل سطر)',
    productFitDeploymentModels: 'نماذج النشر',
    productFitCloud: 'سحابة',
    productFitSelfHosted: 'مستضاف ذاتيًا',
    productFitHybrid: 'هجين',
    productFitIntegrations: 'التكاملات (مفصولة بفواصل)',
    productFitSecurity: 'شهادات الأمان (مفصولة بفواصل)',
    productFitKeyFeatures: 'الميزات الرئيسية (مفصولة بفواصل)',
    productFitUpdate: 'تحديث',
    productFitCreate: 'إنشاء',
    productFitProfiles: 'الملفات الشخصية',
    productFitNoProfiles: 'لا توجد ملفات تعريف بعد. أنشئ واحدًا أعلاه.',
    productFitSetAsActive: 'تعيين كنشط',
    productFitDeleteProfile: 'حذف الملف الشخصي؟',
    productFitDeleteConfirm: 'سيؤدي هذا إلى حذف "{name}" نهائيًا. لا يمكن التراجع عن هذا الإجراء.',
    productFitSelectPrimaryCategory: 'اختر الفئة الأساسية',
    productFitNone: 'لا شيء',
    productFitActive: 'نشط',
    productFitDeactivate: 'تعيين كغير نشط',
    productFitActivateError: 'تعذر تفعيل الملف الشخصي',
    productFitDeactivateError: 'تعطيل الملف الشخصي لم ينجح',
    loadingProductProfiles: 'جاري تحميل ملفات تعريف المنتج...',

    // Person Profile
    backToPeople: 'العودة إلى الأشخاص',
    back: 'العودة',
    personNotFound: 'الشخص غير موجود',
    personNotFoundDesc: 'تعذر تحميل الشخص المطلوب أو أنه غير موجود.',
    signalsCount: 'إشارات',
    interactions: 'تفاعلات',
    primaryInterests: 'الاهتمامات الأساسية',
    formerEmployers: 'أصحاب العمل السابقون',
    education: 'التعليم',
    professionalMemberships: 'العضويات المهنية',
    publicSpeakingEvents: 'الخطابات العامة والأحداث',
    recentEventsAttended: 'الأحداث الأخيرة التي تم حضورها',
    awardsRecognition: 'الجوائز والتقدير',
    publications: 'المنشورات',
    publicationContext: 'منشور',
    firstAuthorBadge: 'المؤلف الأول',
    seniorAuthorBadge: 'مؤلف أقدم',
    relevanceTierVeryStrong: 'تطابق قوي جداً',
    relevanceTierStrong: 'تطابق قوي',
    showAllPublications: 'عرض الكل ({count})',
    showLess: 'عرض أقل',
    removeFromPursuit: '✓ إزالة من المتابعة',
    addToPursuit: 'إضافة إلى المتابعة',
    assignMarketTier: 'تعيين مستوى السوق',
    tier: 'المستوى',
    selectTier: 'اختر مستوى',
    saveChanges: 'حفظ التغييرات',
    atAGlance: 'نظرة سريعة',
    keyInitiatives: 'المبادرات الرئيسية ومجالات التركيز.',
    noInitiatives: 'لا توجد مبادرات مدرجة.',
    
    // AI Outreach
    aiOutreach: 'التواصل بالذكاء الاصطناعي',
    channel: 'القناة',
    email: 'البريد الإلكتروني',
    linkedin: 'لينكد إن',
    call: 'مكالمة',
    tone: 'النبرة',
    relationshipBuilder: 'بناء العلاقات',
    thoughtLeadership: 'قيادة فكرية',
    challenger: 'المتحدي',
    context: 'السياق',
    autoDetect: 'الكشف التلقائي عن أفضل سياق',
    personaInitiative: 'مبادرات ونشاط الشخصية',
    institutionalChange: 'التغييرات المؤسسية والزخم',
    strategicPriority: 'الأولويات الاستراتيجية',
    procurementSignal: 'إشارات المشتريات والتمويل',
    customContext: 'سياق مخصص (اختياري)',
    addContext: 'إضافة سياق محدد أو نقاط نقاش أو تعليمات...',
    generating: 'جارٍ الإنشاء...',
    generateMessage: 'إنشاء رسالة',
    collateralToShare: 'مواد للمشاركة',
    warmUpReading: 'قراءة تمهيدية',
    
    // Home Cards
    recommendedAccounts: 'الحسابات الموصى بها',
    recommendedAccountsTooltip: 'الحسابات الأعلى تسجيلاً بناءً على الملاءمة والتوافق الاستراتيجي.',
    viewAll: 'عرض الكل',
    noAccounts: 'لا توجد حسابات',
    recommendedPersonas: 'الشخصيات الموصى بها',
    recommendedPersonasTooltip: 'الأشخاص الأكثر صلة بناءً على التأثير وتوافق الدور.',
    noPersonas: 'لا توجد شخصيات',
    
    // Action Center
    priorityActions: 'الإجراءات ذات الأولوية',
    deals: 'الصفقات',
    all: 'الكل',
    myTasks: 'مهامي',
    newLeads: 'عملاء محتملون جدد',
    updated: 'تم التحديث',
    loadingRecommendations: 'تحميل توصياتك الشخصية',
    analyzingPipeline: 'تحليل خط أنابيبك...',
    dueToday: 'مستحق اليوم',
    completeTouchVia: 'إكمال الاتصال',
    via: 'عبر',
    openDraft: 'فتح المسودة',
    noContactOnFileOpenPersonas: 'لا يوجد جهة اتصال مسجلة لهذا الحساب — افتح تبويب الشخصيات لإضافة واحدة.',
    openLinkedIn: 'فتح لينكد إن',
    callNow: 'اتصل الآن',
    done: 'تم',
    whyNow: 'لماذا الآن؟',
    nextSteps: 'الخطوات التالية:',
    tracked: 'متتبع',
    fit: 'الملاءمة',
    draftOutreach: 'مسودة التواصل',
    draftMessage: 'صياغة رسالة',
    noTasksDue: 'لا توجد مهام مستحقة اليوم',
    noNewLeads: 'لا يوجد عملاء محتملون جدد بعد',
    noActionsYet: 'لا توجد إجراءات بعد',
    hitRefresh: 'اضغط على تحديث وسأحضر ما هو التالي لك.',
    hotMovers: 'الأكثر نشاطاً (آخر أسبوعين)',
    personaUpdates: 'تحديثات الشخصيات (آخر أسبوعين)',
    noSignals2Weeks: 'لا توجد إشارات في آخر أسبوعين',
    liveDealsTitle: 'الصفقات النشطة',
    noDeadline: 'لا يوجد موعد نهائي',
    daysLeft: 'ي متبقية',
    expired: 'منتهي الصلاحية',
    noLiveDealsCategories: 'لا توجد صفقات نشطة في الفئات المحددة.',
    noLiveDealsFound: 'لم يتم العثور على صفقات نشطة.',
    decisionWindowRadar: 'رادار نافذة القرار',
    decisionWindowTooltip: 'يوضح متى من المحتمل أن يكون العقد قيد المراجعة بناءً على سجلات المشتريات وأنماط التجديد.',
    accountsApproaching: 'الحسابات التي تقترب من آفاق التجديد/القرار',
    noAccountsInBand: 'لا توجد حسابات في هذا النطاق.',
    noAccountsWithWindows: 'لم يتم العثور على حسابات بنوافذ قرار.',
    
    // Greetings
    goodMorning: 'صباح الخير',
    goodAfternoon: 'مساء الخير',
    goodEvening: 'مساء الخير',
    
    // Accounts Page
    manageExploreInstitutions: 'إدارة واستكشاف المؤسسات المستهدفة',
    searchAccounts: 'البحث في الحسابات...',
    accountFitScore: 'درجة ملاءمة الحساب',
    renewalRisk: 'مخاطر التجديد',
    expansionOpps: 'فرص التوسع',
    fariaProducts: 'منتجات فاريا',
    departmentFitScore: 'درجة ملاءمة القسم',
    noAccountsFound: 'لم يتم العثور على حسابات',
    
    // Market Components
    topAccounts: 'أفضل الحسابات',
    topPersonas: 'أفضل الشخصيات',
    tieringSnapshot: 'لقطة المستويات',
    tieringTooltip: 'يتم تصنيف الحسابات مبدئيًا إلى مستويات بناءً على درجة ملاءمة الحساب. يساعد التصنيف إلى مستويات على تحديد الأولويات والتركيز — بحيث تقضي وقتًا أكبر مع الحسابات الأعلى احتمالًا للنجاح، وتنفّذ تواصلاً أكثر استهدافًا، وتحقق نتائج أفضل. يمكنك تعديل مستوى أي حساب في أي وقت لكل حساب على حدة.',
    aiReadinessIndex: 'مؤشر الجاهزية للذكاء الاصطناعي 2025',
    topInstitutionsAI: 'أفضل المؤسسات من حيث تبني وتطبيق الذكاء الاصطناعي',
    topHospitalsAI: 'أفضل المستشفيات من حيث تبني وتطبيق الذكاء الاصطناعي',
    topSchoolsAI: 'أفضل المدارس الدولية من حيث تبني وتطبيق الذكاء الاصطناعي',
    topEnterprisesAI: 'أفضل المؤسسات من حيث تبني وتطبيق الذكاء الاصطناعي',
    topCompaniesAI: 'أفضل الشركات من حيث تبني وتطبيق الذكاء الاصطناعي',
    topAuthoritiesAI: 'أفضل السلطات التعليمية من حيث تبني وتطبيق الذكاء الاصطناعي',
    marketFitDistribution: 'توزيع ملاءمة السوق',
    accountFitComposition: 'تكوين درجة ملاءمة الحساب عبر نطاقات من 10 نقاط',
    topBuyers: 'أفضل المشترين',
    universitiesMostVendors: 'الجامعات التي لديها أكبر عدد من عمليات نشر البائعين الجدد',
    enterprisesMostVendors: 'المؤسسات التي لديها أكبر عدد من عمليات نشر البائعين الجدد',
    keyBuyingAreas: 'مجالات الشراء الرئيسية',
    functionalInvestment: 'الفئات الوظيفية ذات الاستثمار الأكبر',
    procurementComplexity: 'تعقيد المشتريات',
    distributionProfiles: 'توزيع ملفات المشتريات',
    procurementRoutes: 'مسارات المشتريات',
    howInstitutionsBuy: 'كيف تشتري المؤسسات التكنولوجيا',
    marketShare: 'حصة السوق',
    marketWinners: 'الفائزون في السوق',
    vendorsGaining: 'البائعون الذين يكتسبون صفقات مؤسسية جديدة',
    headlineTrends: 'الاتجاهات الرئيسية',
    marketInsights: 'رؤى السوق'
  }
};

/** Dispatched after Settings → Language save so mounted pages re-read ui-language. */
export const UI_LANGUAGE_CHANGED_EVENT = 'neo-ui-language-changed';

// Hook to get translations
export function useTranslation() {
  const user = useContext(UserContext);
  const currentTenant = useContext(CurrentTenantContext);
  const [languageRevision, setLanguageRevision] = useState(0);

  useEffect(() => {
    const bump = () => setLanguageRevision((n) => n + 1);
    window.addEventListener(UI_LANGUAGE_CHANGED_EVENT, bump);
    const onStorage = (e) => {
      if (e.key === 'ui-language') bump();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(UI_LANGUAGE_CHANGED_EVENT, bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const language = useMemo(
    () => resolvePlatformUiLanguage(user, currentTenant),
    [user, currentTenant, languageRevision]
  );
  
  const t = useCallback((key, vars) => {
    let str = translations[language]?.[key] ?? translations.en[key];
    if (str === undefined || str === null || str === '') {
      str = humanizeTranslationKey(key);
    }
    if (vars && typeof str === 'string') {
      Object.entries(vars).forEach(([k, v]) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v ?? ''));
      });
    }
    return str;
  }, [language]);
  
  return { t, language };
}

// Helper function for direct translation
export function translate(key, language = null) {
  if (language != null && language !== '') {
    const explicit = String(language);
    const s = translations[explicit]?.[key] ?? translations.en[key];
    if (s === undefined || s === null || s === '') return humanizeTranslationKey(key);
    return s;
  }
  const lang = typeof window !== 'undefined' ? resolvePlatformUiLanguage(null, null) : 'en';
  const s = translations[lang]?.[key] ?? translations.en[key];
  if (s === undefined || s === null || s === '') return humanizeTranslationKey(key);
  return s;
}
